import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
  CircularProgress,
  LinearProgress,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  TablePagination,
  InputAdornment,
  Popover,
  Tooltip as MuiTooltip,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PhoneIcon from '@mui/icons-material/Phone';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import EmailIcon from '@mui/icons-material/Email';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import FeedbackIcon from '@mui/icons-material/Feedback';
import QrCodeIcon from '@mui/icons-material/QrCode';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GetAppIcon from '@mui/icons-material/GetApp';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplyIcon from '@mui/icons-material/Reply';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreIcon from '@mui/icons-material/Restore';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Chip from '@mui/material/Chip';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import ResidentesTab from './tabs/ResidentesTab';
import EncuestasTab from './tabs/EncuestasTab';
import {
  compareNatural,
  formatFechaOnlyMexico,
  formatFechaHoraMexicoCentral,
  formatFechaSoloMexicoCentralFromInstant,
  formatHoraMexicoCentralFromInstant,
} from './utils/helpers';

// URL del backend: prioridad a variable de entorno, luego localhost, luego producción (Railway).
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return String(process.env.REACT_APP_API_URL).trim().replace(/\/$/, '');
  }
  const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  if (isLocal) return 'http://127.0.0.1:3000';
  return 'https://condominios-production.up.railway.app';
};

/** Versión del cliente web para compatibilidad con PRIVACIDAD_MIN_APP_VERSION en el backend. */
const WEB_APP_VERSION = (process.env.REACT_APP_VERSION || '1.0.15').trim();
const CLIENT_API_HEADERS = {
  'X-App-Version': WEB_APP_VERSION,
  'X-App-Platform': 'web',
};

function mergeClientApiHeaders(existing = {}) {
  return { ...CLIENT_API_HEADERS, ...existing };
}

/** Concepto mensual: rango de claves YYYY-MM permitidas por vigencia (inclusive). */
function minMaxPeriodoMensualPorVigenciaConcepto(concepto) {
  if (!concepto || String(concepto.tipo || '').toLowerCase() !== 'mensual') return null;
  const d0 = concepto.vigencia_desde ? String(concepto.vigencia_desde).slice(0, 10) : null;
  const d1 = concepto.vigencia_hasta ? String(concepto.vigencia_hasta).slice(0, 10) : null;
  if (!d0 && !d1) return null;
  return { minKey: d0 ? d0.slice(0, 7) : '1900-01', maxKey: d1 ? d1.slice(0, 7) : '9999-12' };
}

/** Recorta un par de claves mensuales al rango [minKey, maxKey] (orden cronológico por string YYYY-MM). */
function ajustarRangoMesualAVigencia(pkDesde, pkHasta, minKey, maxKey) {
  let a = (pkDesde || '').trim();
  let b = (pkHasta || '').trim();
  if (a > b) [a, b] = [b, a];
  if (a < minKey) a = minKey;
  if (b > maxKey) b = maxKey;
  if (a > b) return { desde: minKey, hasta: maxKey };
  return { desde: a, hasta: b };
}

/** Opciones de mes para resumen financiero (API: nombre en mayúsculas). */
const MESES_RESUMEN_FINANZAS = [
  { value: '', label: 'Todo el año' },
  { value: 'ENERO', label: 'Enero' },
  { value: 'FEBRERO', label: 'Febrero' },
  { value: 'MARZO', label: 'Marzo' },
  { value: 'ABRIL', label: 'Abril' },
  { value: 'MAYO', label: 'Mayo' },
  { value: 'JUNIO', label: 'Junio' },
  { value: 'JULIO', label: 'Julio' },
  { value: 'AGOSTO', label: 'Agosto' },
  { value: 'SEPTIEMBRE', label: 'Septiembre' },
  { value: 'OCTUBRE', label: 'Octubre' },
  { value: 'NOVIEMBRE', label: 'Noviembre' },
  { value: 'DICIEMBRE', label: 'Diciembre' },
];

const API_BASE_URL = getApiBaseUrl();

// URL absoluta para fotos de vehículos/mascotas (evita doble barra y rutas mal formadas)
const getFotoFullUrl = (fotoUrl) => {
  if (!fotoUrl || typeof fotoUrl !== 'string') return '';
  const t = fotoUrl.trim();
  if (!t) return '';
  if (t.startsWith('http://') || t.startsWith('https://')) return t;
  // Usar la constante global API_BASE_URL ya calculada
  const base = API_BASE_URL.replace(/\/$/, '');
  // El backend guarda la ruta como '/uploads/vehiculos/...' o 'uploads/...'; una sola barra entre base y path
  const path = t.startsWith('/') ? t : `/${t}`;
  return `${base}${path}`;
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#0061FF',
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
  },
});

const SUPERUSER_LOGIN_EMAIL = 'superadmin1@acceso6d.com';

const App = () => {
  const [user, setUser] = useState(null);
  const [residentes, setResidentes] = useState([]);
  const [residentesPaginacion, setResidentesPaginacion] = useState({ pagina: 0, registrosPorPagina: 25, total: 0 });
  const [residentesBusqueda, setResidentesBusqueda] = useState('');
  const [residentesOrden, setResidentesOrden] = useState({ por: 'propiedad', dir: 'asc' });
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(true);
  const [privacyConsentRequired, setPrivacyConsentRequired] = useState(false);
  const [avisoPrivacidad, setAvisoPrivacidad] = useState(null);
  const [privacyCheckboxAccepted, setPrivacyCheckboxAccepted] = useState(false);
  const [privacyCategorias, setPrivacyCategorias] = useState([]);
  const [loadingPrivacyAccept, setLoadingPrivacyAccept] = useState(false);
  const [loginCodigo, setLoginCodigo] = useState(() => (typeof window !== 'undefined' && localStorage.getItem('loginCodigo')) || '');
  const [loginEmail, setLoginEmail] = useState(() => (typeof window !== 'undefined' && localStorage.getItem('loginEmail')) || '');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginShowPassword, setLoginShowPassword] = useState(false);
  const [loginRememberMe, setLoginRememberMe] = useState(() => !!(typeof window !== 'undefined' && (localStorage.getItem('loginEmail') || localStorage.getItem('loginCodigo'))));
  const loginCodigoRef = React.useRef(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [excelErrores, setExcelErrores] = useState([]);
  const [showErroresDialog, setShowErroresDialog] = useState(false);
  const [excelResultado, setExcelResultado] = useState(null);
  const [eliminarTodoPagos, setEliminarTodoPagos] = useState(false);
  const [eliminarTodoGastos, setEliminarTodoGastos] = useState(false);
  const [añoCargaExcelGastos, setAñoCargaExcelGastos] = useState(() => new Date().getFullYear());
  const [loadingCargaExcelPagos, setLoadingCargaExcelPagos] = useState(false);
  const [loadingCargaExcelGastos, setLoadingCargaExcelGastos] = useState(false);

  // Función helper para obtener el token de manera consistente
  const getAuthToken = React.useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.warn('Token no encontrado en localStorage');
      // Si no hay token y hay un usuario, limpiar todo
      if (user) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setUser(null);
        setShowLogin(true);
      }
    }
    return token;
  }, [user]);

  // Función helper para manejar errores de autenticación y redirigir al login
  const handleAuthError = React.useCallback((response) => {
    // Solo 401 = no autorizado (token inválido/expirado). 403 = prohibido (sin permiso para esta acción)
    // no debe cerrar sesión: ej. desbloquear residente con adeudos devuelve 403 con mensaje específico.
    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      setUser(null);
      setShowLogin(true);
      setLoading(false);
      setError('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
      return true;
    }
    return false;
  }, []);

  // Función helper para hacer peticiones con manejo automático de autenticación
  const fetchWithAuth = React.useCallback(async (url, options = {}) => {
    const token = getAuthToken();

    if (!token) {
      setShowLogin(true);
      setError('No hay sesión activa. Por favor, inicia sesión.');
      throw new Error('No hay sesión activa');
    }

    const headers = mergeClientApiHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    });

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Manejar errores de autenticación automáticamente
      if (handleAuthError(response)) {
        throw new Error('Sesión expirada');
      }

      return response;
    } catch (error) {
      // Si es un error de red, no redirigir al login
      if (error.message === 'Sesión expirada') {
        throw error;
      }
      throw error;
    }
  }, [getAuthToken, handleAuthError]);
  const [showModalResidente, setShowModalResidente] = useState(false);
  const [residenteEditando, setResidenteEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    propiedad: '',
    telefono: '',
    telefono_no_marcar: false,
    telefono_orden: '',
    categoria: 'Inquilino',
    rol: 'residente',
  });
  const [showModalAltaMasiva, setShowModalAltaMasiva] = useState(false);
  const [altaMasivaCantidad, setAltaMasivaCantidad] = useState(10);
  const [altaMasivaPrefijo, setAltaMasivaPrefijo] = useState('Casa');
  const [altaMasivaProgreso, setAltaMasivaProgreso] = useState(null);
  const [altaMasivaCredenciales, setAltaMasivaCredenciales] = useState([]);
  const [showModalCredenciales, setShowModalCredenciales] = useState(false);
  const [showModalContactos, setShowModalContactos] = useState(false);
  const [contactos, setContactos] = useState([]);
  const [formContacto, setFormContacto] = useState({
    nombre: '',
    telefono: '',
    email: '',
    tipo: 'contacto',
    no_marcar: false,
    orden: '',
    password: '',
  });
  const [enviarInvitacionContacto, setEnviarInvitacionContacto] = useState(false);
  const [contactoEditando, setContactoEditando] = useState(null);
  const [contactoReenviandoId, setContactoReenviandoId] = useState(null);
  const [contactoBajandoAccesoId, setContactoBajandoAccesoId] = useState(null);
  const [contactoReactivandoAccesoId, setContactoReactivandoAccesoId] = useState(null);
  const [showModalVehiculos, setShowModalVehiculos] = useState(false);
  const [vehiculos, setVehiculos] = useState([]);
  const [formVehiculo, setFormVehiculo] = useState({ color: '', marca: '', modelo: '', placas: '', tag_id: '', foto_url: '' });
  const [vehiculoEditando, setVehiculoEditando] = useState(null);
  const [subiendoFotoVehiculo, setSubiendoFotoVehiculo] = useState(false);
  const [showModalMascotas, setShowModalMascotas] = useState(false);
  const [mascotas, setMascotas] = useState([]);
  const [formMascota, setFormMascota] = useState({ tipo: 'Perro', raza: '', tamaño: 'Mediano', nombre: '', foto_url: '' });
  const [mascotaEditando, setMascotaEditando] = useState(null);
  const [subiendoFotoMascota, setSubiendoFotoMascota] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [adjuntosPropiedad, setAdjuntosPropiedad] = useState([]);
  const [adjuntosPropiedadFiles, setAdjuntosPropiedadFiles] = useState([]);
  const [loadingAdjuntos, setLoadingAdjuntos] = useState(false);
  const adjuntosPropiedadInputRef = React.useRef(null);
  const [ordenLlamadaPropiedad, setOrdenLlamadaPropiedad] = useState('');
  const [ordenLlamadaItems, setOrdenLlamadaItems] = useState([]);
  const [ordenLlamadaLoading, setOrdenLlamadaLoading] = useState(false);
  const [ordenLlamadaSaving, setOrdenLlamadaSaving] = useState(false);
  const ALL_ADMIN_MENU_INDICES = React.useMemo(() => Array.from({ length: 19 }, (_, i) => i), []);
  const ADMIN_TAB_STORAGE_KEY = 'adminTabActual';
  const [tabActual, setTabActual] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const savedTab = Number(localStorage.getItem(ADMIN_TAB_STORAGE_KEY));
    return Number.isInteger(savedTab) && savedTab >= 0 && savedTab < 19 ? savedTab : 0;
  }); // índice lógico del menú (0=Residentes, 1=Catálogos, 2=Orden llamada, ...)
  const visibleAdminMenuIndices = React.useMemo(() => {
    const permisos = user?.admin_menu_permisos;
    if (Array.isArray(permisos) && permisos.length > 0) {
      return permisos.filter((i) => Number.isInteger(i) && i >= 0 && i < 19).sort((a, b) => a - b);
    }
    return ALL_ADMIN_MENU_INDICES;
  }, [user?.admin_menu_permisos, ALL_ADMIN_MENU_INDICES]);
  const [alertas, setAlertas] = useState([]);
  const [loadingAlertas, setLoadingAlertas] = useState(false);
  // Catálogos: inquilinos, mascotas, vehículos (agrupados por casa)
  const [catalogoTipo, setCatalogoTipo] = useState('inquilinos');
  const [catalogoBusqueda, setCatalogoBusqueda] = useState('');
  const [catalogoInquilinos, setCatalogoInquilinos] = useState([]); // array de { propiedad, residentes, contactos }
  const [catalogoMascotas, setCatalogoMascotas] = useState([]); // array de { propiedad, items }
  const [catalogoVehiculos, setCatalogoVehiculos] = useState([]); // array de { propiedad, items }
  const [catalogoPagina, setCatalogoPagina] = useState(0);
  const [catalogoTotalCasas, setCatalogoTotalCasas] = useState(0);
  const [catalogoLoading, setCatalogoLoading] = useState(false);
  const CATALOGO_POR_PAGINA = 20;
  const [contadorAlertas, setContadorAlertas] = useState(0);
  const [estadisticas, setEstadisticas] = useState(null);
  const [dashboardSeccion, setDashboardSeccion] = useState('pases');
  const [estadisticasFinanzas, setEstadisticasFinanzas] = useState(null);
  const [finanzasResumenAño, setFinanzasResumenAño] = useState(() => new Date().getFullYear());
  const [finanzasResumenMes, setFinanzasResumenMes] = useState('');
  const [finanzasResumenData, setFinanzasResumenData] = useState(null);
  const [loadingFinanzasResumen, setLoadingFinanzasResumen] = useState(false);
  const [estadisticasResidentes, setEstadisticasResidentes] = useState(null);
  const [estadisticasServicios, setEstadisticasServicios] = useState(null);
  const [estadisticasParticipacion, setEstadisticasParticipacion] = useState(null);
  const [estadisticasAccesosResumen, setEstadisticasAccesosResumen] = useState(null);
  const [estadisticasDocumentos, setEstadisticasDocumentos] = useState(null);
  const [loadingDashboardSeccion, setLoadingDashboardSeccion] = useState(false);
  
  // Estados para gestión de proveedores
  const [categoriasServicios, setCategoriasServicios] = useState([]);
  const [nombresServicios, setNombresServicios] = useState([]);
  const [categoriaSeleccionadaFiltro, setCategoriaSeleccionadaFiltro] = useState('');
  const [categoriaEditando, setCategoriaEditando] = useState(null);
  const [nombreEditando, setNombreEditando] = useState(null);
  const [showDialogCategoria, setShowDialogCategoria] = useState(false);
  const [showDialogNombre, setShowDialogNombre] = useState(false);
  const [nuevaCategoriaNombre, setNuevaCategoriaNombre] = useState('');
  const [nuevaCategoriaHoraInicio, setNuevaCategoriaHoraInicio] = useState('');
  const [nuevaCategoriaHoraFin, setNuevaCategoriaHoraFin] = useState('');
  const [nuevaCategoriaAplicaDiasFestivos, setNuevaCategoriaAplicaDiasFestivos] = useState(false);
  const [nuevoNombreServicio, setNuevoNombreServicio] = useState('');
  const [nuevoNombreCategoriaId, setNuevoNombreCategoriaId] = useState('');
  
  // Estados para Directorio
  const [directoryCategories, setDirectoryCategories] = useState([]);
  const [condominiosList, setCondominiosList] = useState([]);
  const [condominioFormNombre, setCondominioFormNombre] = useState('');
  const [condominioFormDireccion, setCondominioFormDireccion] = useState('');
  const [condominioFormCodigo, setCondominioFormCodigo] = useState('');
  const [loadingCondominios, setLoadingCondominios] = useState(false);
  const [loadingCrearCondominio, setLoadingCrearCondominio] = useState(false);
  // Superuser: gestión de usuarios por condominio
  const [selectedCondominioIdForUsers, setSelectedCondominioIdForUsers] = useState('');
  const [usuariosList, setUsuariosList] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [openModalCrearAdmin, setOpenModalCrearAdmin] = useState(false);
  const [formCrearUsuario, setFormCrearUsuario] = useState({
    email: '',
    nombre: '',
    password: '',
    rol: 'admin',
    admin_menu_permisos: ALL_ADMIN_MENU_INDICES,
  });
  const [openModalResetPassword, setOpenModalResetPassword] = useState(false);
  const [usuarioResetId, setUsuarioResetId] = useState(null);
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [openModalOlvideContrasena, setOpenModalOlvideContrasena] = useState(false);
  const [openModalPermisosMenu, setOpenModalPermisosMenu] = useState(false);
  const [usuarioPermisosEdit, setUsuarioPermisosEdit] = useState(null);
  const [adminMenuPermisosTemp, setAdminMenuPermisosTemp] = useState(ALL_ADMIN_MENU_INDICES);
  const [openModalCambiarPasswordAdmin, setOpenModalCambiarPasswordAdmin] = useState(false);
  const [passwordActualAdmin, setPasswordActualAdmin] = useState('');
  const [nuevaPasswordAdmin, setNuevaPasswordAdmin] = useState('');
  const [confirmarNuevaPasswordAdmin, setConfirmarNuevaPasswordAdmin] = useState('');
  const [olvideContrasenaEmail, setOlvideContrasenaEmail] = useState('');
  const [olvideContrasenaCodigo, setOlvideContrasenaCodigo] = useState('');
  const [loadingOlvideContrasena, setLoadingOlvideContrasena] = useState(false);
  const [olvideContrasenaEnviado, setOlvideContrasenaEnviado] = useState(false);
  // Flujo de restablecer/completar contraseña desde URL con token
  // Inicializar desde URL para que esté disponible en el primer render (evita mostrar login en lugar del formulario)
  const [tokenInvitacion, setTokenInvitacion] = useState(() => {
    if (typeof window === 'undefined') return null;
    const path = window.location.pathname || '';
    if (!/invitacion/i.test(path)) return null;
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    return (t && t.trim()) || null;
  });
  const [invitacionData, setInvitacionData] = useState(null);
  const [loadingInvitacion, setLoadingInvitacion] = useState(false);
  const [invitacionError, setInvitacionError] = useState(null);
  const [invitacionCompletada, setInvitacionCompletada] = useState(false);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [loadingCompletarInvitacion, setLoadingCompletarInvitacion] = useState(false);
  const [resetPasswordShowPassword, setResetPasswordShowPassword] = useState(false);
  const [directoryProviders, setDirectoryProviders] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [showDialogDirectoryCategory, setShowDialogDirectoryCategory] = useState(false);
  const [showDialogDirectoryProvider, setShowDialogDirectoryProvider] = useState(false);
  const [directoryCategoryEditando, setDirectoryCategoryEditando] = useState(null);
  const [directoryProviderEditando, setDirectoryProviderEditando] = useState(null);
  const [formDirectoryCategory, setFormDirectoryCategory] = useState({ name: '', icon_name: '', active: true });
  const [formDirectoryProvider, setFormDirectoryProvider] = useState({ 
    category_id: '', name: '', phone: '', description: '', photo_url: '', is_verified: true, active: true 
  });
  const [diasMaxRecurrentes, setDiasMaxRecurrentes] = useState(7);
  const [numeroEstacionamientos, setNumeroEstacionamientos] = useState(50);
  const [limiteVisitasRecurrentes, setLimiteVisitasRecurrentes] = useState(2);
  const [limiteVisitasOcasionales, setLimiteVisitasOcasionales] = useState(2);
  
  // Estados para Amenidades (FDD v2)
  const [amenidadesList, setAmenidadesList] = useState([]);
  const [loadingAmenidades, setLoadingAmenidades] = useState(false);
  const [amenidadEditando, setAmenidadEditando] = useState(null);
  const [openModalAmenidad, setOpenModalAmenidad] = useState(false);
  const [formAmenidad, setFormAmenidad] = useState({
    nombre: '', descripcion: '', reglamento: '', imagen_url: '', modo_aprobacion: 'manual',
    max_reservas_concurrentes_por_residente: 3, cancelacion_min_horas_antelacion: 2,
    unidad_reserva: 'dia', max_unidades_reserva: 1, activo: true
  });
  const [horariosAmenidad, setHorariosAmenidad] = useState([]);
  const [bloqueosAmenidad, setBloqueosAmenidad] = useState([]);
  const [reservasPendientes, setReservasPendientes] = useState([]);
  const [loadingReservasPendientes, setLoadingReservasPendientes] = useState(false);
  const [calendarioAmenidades, setCalendarioAmenidades] = useState({ reservas: [], bloqueos: [] });
  const [loadingCalendario, setLoadingCalendario] = useState(false);
  const [amenidadesSeccion, setAmenidadesSeccion] = useState('listado'); // listado | aprobaciones | rechazos_cancelaciones | calendario
  const [filtroPendientesAmenidadId, setFiltroPendientesAmenidadId] = useState('');
  const [filtroPendientesFechaDesde, setFiltroPendientesFechaDesde] = useState('');
  const [filtroPendientesFechaHasta, setFiltroPendientesFechaHasta] = useState('');

  const [reservasRechazadasCanceladas, setReservasRechazadasCanceladas] = useState([]);
  const [loadingReservasRechazadasCanceladas, setLoadingReservasRechazadasCanceladas] = useState(false);
  const [filtroIncidenciasAmenidadId, setFiltroIncidenciasAmenidadId] = useState('');
  const [filtroIncidenciasFechaDesde, setFiltroIncidenciasFechaDesde] = useState('');
  const [filtroIncidenciasFechaHasta, setFiltroIncidenciasFechaHasta] = useState('');

  const [calendarioFechaDesde, setCalendarioFechaDesde] = useState(new Date().toISOString().slice(0, 10));
  const [calendarioFechaHasta, setCalendarioFechaHasta] = useState('');
  const [openModalRechazar, setOpenModalRechazar] = useState(false);
  const [reservaRechazarId, setReservaRechazarId] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [nuevoBloqueoAmenidad, setNuevoBloqueoAmenidad] = useState({ fecha_inicio: '', fecha_fin: '', motivo: '' });
  const [listadoPropiedades, setListadoPropiedades] = useState([]);
  const [formReservaAdmin, setFormReservaAdmin] = useState({ propiedad: '', amenidad_id: '', fecha_reserva: '', hora_inicio: '09:00', hora_fin: '10:00' });
  const [creandoReservaAdmin, setCreandoReservaAdmin] = useState(false);

  // Estados para Pases (mes/año por defecto: mes actual)
  const [pases, setPases] = useState([]);
  const [filtroPasesUsado, setFiltroPasesUsado] = useState('');
  const [filtroPasesPropiedad, setFiltroPasesPropiedad] = useState('');
  const [filtroPasesMes, setFiltroPasesMes] = useState(() => new Date().getMonth() + 1);
  const [filtroPasesAño, setFiltroPasesAño] = useState(() => new Date().getFullYear());
  const [pasesPagina, setPasesPagina] = useState(0);
  const [pasesRegistrosPorPagina, setPasesRegistrosPorPagina] = useState(10);
  const [pasesTotal, setPasesTotal] = useState(0);
  // Refs para leer valores actuales de filtros al hacer clic en Filtrar (evita estado desactualizado por batching de React)
  const pasesFiltrosRef = useRef({ usado: '', propiedad: '', mes: new Date().getMonth() + 1, año: new Date().getFullYear() });
  // Detalle de ingresos (usos) por pase — se muestra en un diálogo
  const [paseIngresosDialogOpen, setPaseIngresosDialogOpen] = useState(false);
  const [paseIngresosLoading, setPaseIngresosLoading] = useState(false);
  const [paseIngresosError, setPaseIngresosError] = useState('');
  const [paseIngresosSeleccionado, setPaseIngresosSeleccionado] = useState(null);
  const [paseIngresos, setPaseIngresos] = useState([]);
  const [paseIngresosTotalUsos, setPaseIngresosTotalUsos] = useState(0);
  // Estados para Notificaciones de servicios (admin)
  const [notificaciones, setNotificaciones] = useState([]);
  const [filtroNotifEstado, setFiltroNotifEstado] = useState('');
  const [filtroNotifPropiedad, setFiltroNotifPropiedad] = useState('');
  const [filtroNotifMes, setFiltroNotifMes] = useState(() => new Date().getMonth() + 1);
  const [filtroNotifAño, setFiltroNotifAño] = useState(() => new Date().getFullYear());
  const [notifPagina, setNotifPagina] = useState(0);
  const [notifRegistrosPorPagina, setNotifRegistrosPorPagina] = useState(10);
  const [notifTotal, setNotifTotal] = useState(0);
  const notifFiltrosRef = useRef({ estado: '', propiedad: '', mes: new Date().getMonth() + 1, año: new Date().getFullYear() });

  // Estados para Visitas manuales (guardia) - admin
  const [visitasManuales, setVisitasManuales] = useState([]);
  const [filtroVisitaEstado, setFiltroVisitaEstado] = useState('');
  const [filtroVisitaPropiedad, setFiltroVisitaPropiedad] = useState('');
  const [filtroVisitaMes, setFiltroVisitaMes] = useState(() => new Date().getMonth() + 1);
  const [filtroVisitaAño, setFiltroVisitaAño] = useState(() => new Date().getFullYear());
  const [visitasPagina, setVisitasPagina] = useState(0);
  const [visitasRegistrosPorPagina, setVisitasRegistrosPorPagina] = useState(10);
  const [visitasTotal, setVisitasTotal] = useState(0);
  const [filtroVisitaSoloOficial, setFiltroVisitaSoloOficial] = useState('');
  const [dialogVisitaManualDetalle, setDialogVisitaManualDetalle] = useState(null);
  const visitasFiltrosRef = useRef({
    estado: '',
    propiedad: '',
    mes: new Date().getMonth() + 1,
    año: new Date().getFullYear(),
    solo_oficial: '',
  });

  // Mantener refs de filtros sincronizados con el estado (p. ej. al abrir la pestaña o tras Limpiar)
  useEffect(() => {
    pasesFiltrosRef.current = { usado: filtroPasesUsado, propiedad: filtroPasesPropiedad, mes: filtroPasesMes, año: filtroPasesAño };
  }, [filtroPasesUsado, filtroPasesPropiedad, filtroPasesMes, filtroPasesAño]);
  useEffect(() => {
    notifFiltrosRef.current = { estado: filtroNotifEstado, propiedad: filtroNotifPropiedad, mes: filtroNotifMes, año: filtroNotifAño };
  }, [filtroNotifEstado, filtroNotifPropiedad, filtroNotifMes, filtroNotifAño]);
  useEffect(() => {
    visitasFiltrosRef.current = {
      estado: filtroVisitaEstado,
      propiedad: filtroVisitaPropiedad,
      mes: filtroVisitaMes,
      año: filtroVisitaAño,
      solo_oficial: filtroVisitaSoloOficial,
    };
  }, [filtroVisitaEstado, filtroVisitaPropiedad, filtroVisitaMes, filtroVisitaAño, filtroVisitaSoloOficial]);

  // Estados para Ingresos (antes Pagos)
  const [pagos, setPagos] = useState([]);
  const [pagoEditando, setPagoEditando] = useState(null);
  const [showDialogPago, setShowDialogPago] = useState(false);
  const [creandoPago, setCreandoPago] = useState(false);
  const [pagosPaginacion, setPagosPaginacion] = useState({
    pagina: 0,
    registrosPorPagina: 10,
    total: 0
  });
  const [filtrosIngresos, setFiltrosIngresos] = useState({
    fechaInicio: '',
    fechaFin: '',
    mes: '',
    año: '',
    propiedad: '',
    estatus_recibo: ''
  });
  const [capitalInicialEdit, setCapitalInicialEdit] = useState('');
  const [capitalInicialGuardando, setCapitalInicialGuardando] = useState(false);
  const [capitalSnapshotInvalidando, setCapitalSnapshotInvalidando] = useState(false);
  const [instruccionesPago, setInstruccionesPago] = useState({
    numero_cuenta: '',
    instrucciones: '',
    referencia: '',
    enviar_comprobante: ''
  });
  const [instruccionesPagoGuardando, setInstruccionesPagoGuardando] = useState(false);
  // Llave peatonal: Shelly y API Key
  const [shellyDispositivos, setShellyDispositivos] = useState([]);
  const [loadingShelly, setLoadingShelly] = useState(false);
  const [showModalShelly, setShowModalShelly] = useState(false);
  const [shellyEditando, setShellyEditando] = useState(null);
  const [formShelly, setFormShelly] = useState({ nombre: '', host: '', auth_key: '', device_id: '', activo: true });
  const [showModalApiKey, setShowModalApiKey] = useState(false);
  const [apiKeyGenerada, setApiKeyGenerada] = useState('');
  const [adminNavCollapsed, setAdminNavCollapsed] = useState(false);
  const [formPago, setFormPago] = useState({
    fecha: '',
    mes: '',
    propiedad: '',
    folio: '',
    concepto: '',
    categoria_concepto: '',
    descripcion: '',
    monto: '',
    recargos: '',
    depositado: false,
    concepto_pago_id: '',
    estatus_recibo: 'vigente',
  });
  const [propiedadDestinatarios, setPropiedadDestinatarios] = useState('');
  const [destinatariosRecibo, setDestinatariosRecibo] = useState(null);
  const [loadingDestinatarios, setLoadingDestinatarios] = useState(false);
  const [nuevoEmailRecibo, setNuevoEmailRecibo] = useState('');
  const [openModalEnviarReciboIngreso, setOpenModalEnviarReciboIngreso] = useState(false);
  const [enviarReciboIngresoId, setEnviarReciboIngresoId] = useState(null);
  const [emailsSeleccionadosRecibo, setEmailsSeleccionadosRecibo] = useState([]);
  const [enviandoReciboIngreso, setEnviandoReciboIngreso] = useState(false);

  // Estados para Egresos (antes Gastos)
  const [gastos, setGastos] = useState([]);
  const [gastoEditando, setGastoEditando] = useState(null);
  const [showDialogGasto, setShowDialogGasto] = useState(false);
  const [gastosPaginacion, setGastosPaginacion] = useState({
    pagina: 0,
    registrosPorPagina: 10,
    total: 0
  });
  const [filtrosEgresos, setFiltrosEgresos] = useState({
    fechaInicio: '',
    fechaFin: '',
    mes: '',
    año: '',
    propiedad: '',
    estatus_recibo: ''
  });
  const [formGasto, setFormGasto] = useState({
    fecha: '',
    mes: '',
    concepto: '',
    tesoreria: '',
    condicion: '',
    subtotal: '',
    iva: '',
    total: '',
    metodo_pago: '',
    estatus_recibo: 'vigente',
  });
  const [destinatariosEgresos, setDestinatariosEgresos] = useState(null);
  const [loadingDestinatariosEgresos, setLoadingDestinatariosEgresos] = useState(false);
  const [nuevoEmailEgreso, setNuevoEmailEgreso] = useState('');
  const [openModalEnviarReciboEgreso, setOpenModalEnviarReciboEgreso] = useState(false);
  const [enviarReciboEgresoId, setEnviarReciboEgresoId] = useState(null);
  const [emailsSeleccionadosEgreso, setEmailsSeleccionadosEgreso] = useState([]);
  const [enviandoReciboEgreso, setEnviandoReciboEgreso] = useState(false);

  // Estados para Cobranza (conceptos de pago, tabla de cobranza, morosidad)
  const [conceptosPago, setConceptosPago] = useState([]);
  const [tablaCobranzaData, setTablaCobranzaData] = useState({ propiedades: [], meses: [], celdas: {}, concepto: null, total_propiedades: 0 });
  const [morosidadData, setMorosidadData] = useState({ en_mora: [], al_corriente: [], total_en_mora: 0, total_al_corriente: 0 });
  const [morosidadPagina, setMorosidadPagina] = useState(0);
  const [morosidadPageSize, setMorosidadPageSize] = useState(10);
  const [morosidadFiltroBusqueda, setMorosidadFiltroBusqueda] = useState('');
  const [morosidadFiltroMontoMin, setMorosidadFiltroMontoMin] = useState('');
  const [morosidadFiltroGravedadMin, setMorosidadFiltroGravedadMin] = useState('');
  const [morosidadPopoverAnchor, setMorosidadPopoverAnchor] = useState(null);
  const [morosidadPopoverRow, setMorosidadPopoverRow] = useState(null);
  const [morosidadDetalleRow, setMorosidadDetalleRow] = useState(null);
  const [morosidadEnviandoRecordatorio, setMorosidadEnviandoRecordatorio] = useState(null);
  const MOROSIDAD_FETCH_LIMIT = 500;
  const [anoCobranza, setAnoCobranza] = useState(new Date().getFullYear());
  const [conceptoPagoSeleccionado, setConceptoPagoSeleccionado] = useState('');
  const [tablaCobranzaPageSize, setTablaCobranzaPageSize] = useState(10);
  const [tablaCobranzaPagina, setTablaCobranzaPagina] = useState(0);
  const [filtroPropiedadCobranza, setFiltroPropiedadCobranza] = useState('');
  const [filtroDesdeCobranza, setFiltroDesdeCobranza] = useState('');
  const [filtroHastaCobranza, setFiltroHastaCobranza] = useState('');
  const [filtroEstatusCobranza, setFiltroEstatusCobranza] = useState('todos');
  const [mesInicioVisibleCobranza, setMesInicioVisibleCobranza] = useState(null);
  const [mesFinVisibleCobranza, setMesFinVisibleCobranza] = useState(null);
  const [pagoDesdeCobranza, setPagoDesdeCobranza] = useState(false);
  /** Datos de la celda al abrir pago desde Cobranza (desglose y abonos). */
  const [cobranzaResumenPeriodo, setCobranzaResumenPeriodo] = useState(null);
  const [showDialogPagarVariosPeriodos, setShowDialogPagarVariosPeriodos] = useState(false);
  const [propiedadPagarVariosPeriodos, setPropiedadPagarVariosPeriodos] = useState('');
  const [formPagarVariosPeriodos, setFormPagarVariosPeriodos] = useState({
    fecha: new Date().toISOString().split('T')[0],
    depositado: false,
    periodo_desde: '',
    periodo_hasta: '',
  });
  const [creandoPagoVariosPeriodos, setCreandoPagoVariosPeriodos] = useState(false);
  const [previewVariosRango, setPreviewVariosRango] = useState(null);
  const [previewVariosRangoErr, setPreviewVariosRangoErr] = useState('');
  const [loadingPreviewVariosRango, setLoadingPreviewVariosRango] = useState(false);
  const [showDialogRevertirPago, setShowDialogRevertirPago] = useState(false);
  const [pagoARevertir, setPagoARevertir] = useState(null);
  const [subTabCobranza, setSubTabCobranza] = useState(0);
  const [showDialogConceptoPago, setShowDialogConceptoPago] = useState(false);
  const [conceptoPagoEditando, setConceptoPagoEditando] = useState(null);
  const [formConceptoPago, setFormConceptoPago] = useState({
    nombre: '', tipo: 'mensual', monto: '', recargo_morosidad_tipo: 'porcentaje', recargo_morosidad_valor: '',
    tipo_fecha_limite: 'dia_mes', dia_limite_pago: 10, fecha_limite: '', mes_inicio_periodo: 1, activo: true,
    vigencia_desde: '', vigencia_hasta: '',
  });
  
  // Estados para Documentos
  const [documentos, setDocumentos] = useState([]);
  const [documentoEditando, setDocumentoEditando] = useState(null);
  const [showDialogDocumento, setShowDialogDocumento] = useState(false);
  const [formDocumento, setFormDocumento] = useState({
    nombre_mostrar: '',
    activo: true,
  });
  const [archivoDocumento, setArchivoDocumento] = useState(null);
  
  // Estados para Encuestas
  const [encuestasList, setEncuestasList] = useState([]);
  const [loadingEncuestas, setLoadingEncuestas] = useState(false);
  const [errorEncuestas, setErrorEncuestas] = useState('');
  // Estados para Sugerencias
  const [sugerencias, setSugerencias] = useState([]);
  const [sugerenciaDialog, setSugerenciaDialog] = useState(null);
  const [sugerenciaDialogTexto, setSugerenciaDialogTexto] = useState('');
  const [sugerenciaDialogSaving, setSugerenciaDialogSaving] = useState(false);
  const [sugerenciaDialogPublishing, setSugerenciaDialogPublishing] = useState(false);
  // Estados para Solicitudes de eliminación de datos
  const [solicitudesEliminacion, setSolicitudesEliminacion] = useState([]);
  const [solicitudEliminacionFiltro, setSolicitudEliminacionFiltro] = useState(''); // '' | 'pendiente' | 'aprobada' | 'rechazada'
  const [revisarSolicitudId, setRevisarSolicitudId] = useState(null);
  const [revisarSolicitudAccion, setRevisarSolicitudAccion] = useState(null); // 'aprobada' | 'rechazada'
  const [observacionesSolicitud, setObservacionesSolicitud] = useState('');
  const [limiteEstacionamientosPorCasa, setLimiteEstacionamientosPorCasa] = useState(2);
  const [validarLimiteEstacionamientosPorCasa, setValidarLimiteEstacionamientosPorCasa] = useState(true);
  const [estadisticasPorDia, setEstadisticasPorDia] = useState([]);
  const [estadisticasPorHora, setEstadisticasPorHora] = useState([]);
  const [estadisticasAccesos, setEstadisticasAccesos] = useState([]);
  const [razonesDenegacion, setRazonesDenegacion] = useState([]);
  const [loadingEstadisticas, setLoadingEstadisticas] = useState(false);
  
  // Contadores derivados para indicadores visuales por pestaña
  const conteoReservasAmenidadesPendientes = Array.isArray(reservasPendientes) ? reservasPendientes.length : 0;
  const conteoComentariosDirectorioPendientes = Array.isArray(pendingReviews) ? pendingReviews.length : 0;
  const conteoSugerenciasNoVistas = Array.isArray(sugerencias) ? sugerencias.filter((s) => !s.vista).length : 0;
  const conteoSolicitudesEliminacionPendientes = Array.isArray(solicitudesEliminacion)
    ? solicitudesEliminacion.filter((s) => s.estado === 'pendiente').length
    : 0;
  const esAdminCondominio = user?.rol === 'admin' && !!user?.condominio_id;
  // Encuestas activas que tienen al menos una respuesta por revisar (respuestas que no se han “registrado” en el flujo del admin)
  const conteoEncuestasConRespuestasPendientes = encuestasList.filter(
    (e) => e.activa && (Number(e.total_envios) || 0) > 0
  ).length;

  
  // Estados para días festivos
  const [diasFestivos, setDiasFestivos] = useState([]);
  const [showModalDiaFestivo, setShowModalDiaFestivo] = useState(false);
  const [diaFestivoEditando, setDiaFestivoEditando] = useState(null);
  const [formDiaFestivo, setFormDiaFestivo] = useState({
    fecha: '',
    descripcion: '',
    activo: true
  });

  useEffect(() => {
    checkStoredAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La ruta /invitacion?token=... se maneja en la SPA (sin redirigir a .html) para evitar que el servidor reescriba la URL y pierda el token.

  // Bandera para saber si estamos en la ruta de invitación (se usa más abajo para mostrar pantalla de enlace incompleto)
  const isOnInvitacionPath = typeof window !== 'undefined' && /invitacion/i.test((window.location.pathname || ''));
  // Si la URL ya trae ?token=, no mostrar "Enlace incompleto" (el token se lee en estado o en el efecto)
  const hasTokenInSearch = typeof window !== 'undefined' && /[?&]token=/.test((window.location.search || ''));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token && token.trim() && showLogin) {
      setTokenInvitacion(token.trim());
    }
  }, [showLogin]);

  // Validar token cuando se detecta en URL
  useEffect(() => {
    if (!tokenInvitacion || !showLogin) return;
    let cancelled = false;
    setInvitacionError(null);
    setLoadingInvitacion(true);
    fetch(`${API_BASE_URL}/auth/invitacion/${tokenInvitacion}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.error) {
          setInvitacionError(data.error || 'Token inválido o expirado.');
          setInvitacionData(null);
        } else {
          setInvitacionData({ email: data.email, nombre: data.nombre, es_restablecer: !!data.es_restablecer });
          setInvitacionError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInvitacionError('Error de conexión. Intenta de nuevo.');
          setInvitacionData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingInvitacion(false);
      });
    return () => { cancelled = true; };
  }, [tokenInvitacion, showLogin]);

  const loadCatalogos = async (tipoOverride, qOverride, paginaOverride) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const tipo = tipoOverride ?? catalogoTipo;
    const q = (qOverride !== undefined ? qOverride : catalogoBusqueda).trim();
    const pagina = paginaOverride !== undefined ? paginaOverride : catalogoPagina;
    const offset = pagina * CATALOGO_POR_PAGINA;
    setCatalogoLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      params.set('limit', String(CATALOGO_POR_PAGINA));
      params.set('offset', String(offset));
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/catalogos/${tipo}?${params.toString()}`);
      if (!response.ok) throw new Error('Error al cargar catálogo');
      const data = await response.json();
      const { grupos = [], totalCasas = 0 } = data;
      if (tipo === 'inquilinos') { setCatalogoInquilinos(grupos); }
      else if (tipo === 'mascotas') { setCatalogoMascotas(grupos); }
      else { setCatalogoVehiculos(grupos); }
      setCatalogoTotalCasas(totalCasas);
    } catch (err) {
      console.error('Error loadCatalogos:', err);
      if (tipo === 'inquilinos') setCatalogoInquilinos([]);
      else if (tipo === 'mascotas') setCatalogoMascotas([]);
      else setCatalogoVehiculos([]);
      setCatalogoTotalCasas(0);
    } finally {
      setCatalogoLoading(false);
    }
  };

  const loadEstadisticas = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        console.error('Token no disponible para cargar estadísticas');
        return;
      }

      setLoadingEstadisticas(true);

      // Cargar todas las estadísticas en paralelo
      const [generalRes, porDiaRes, porHoraRes, accesosRes, razonesRes] = await Promise.all([
        fetchWithAuth(`${API_BASE_URL}/admin/estadisticas/general`),
        fetchWithAuth(`${API_BASE_URL}/admin/estadisticas/por-dia`),
        fetchWithAuth(`${API_BASE_URL}/admin/estadisticas/por-hora`),
        fetchWithAuth(`${API_BASE_URL}/admin/estadisticas/accesos`),
        fetchWithAuth(`${API_BASE_URL}/admin/estadisticas/razones-denegacion`),
      ]);

      const [general, porDia, porHora, accesos, razones] = await Promise.all([
        generalRes.json(),
        porDiaRes.json(),
        porHoraRes.json(),
        accesosRes.json(),
        razonesRes.json(),
      ]);

      setEstadisticas(general);
      setEstadisticasPorDia(porDia);
      setEstadisticasPorHora(porHora);
      setEstadisticasAccesos(accesos);
      setRazonesDenegacion(razones);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      // No mostrar error si es por sesión expirada (ya se manejó arriba)
      if (!error.message?.includes('Sesión expirada')) {
        setError('Error al cargar estadísticas');
      }
    } finally {
      setLoadingEstadisticas(false);
    }
  };

  const loadDashboardSeccion = async (seccion) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setLoadingDashboardSeccion(true);
    try {
      const url = `${API_BASE_URL}/admin/estadisticas/${seccion}`;
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error('Error al cargar');
      const data = await response.json();
      if (seccion === 'finanzas') setEstadisticasFinanzas(data);
      else if (seccion === 'residentes') setEstadisticasResidentes(data);
      else if (seccion === 'servicios') setEstadisticasServicios(data);
      else if (seccion === 'participacion') setEstadisticasParticipacion(data);
      else if (seccion === 'accesos-resumen') setEstadisticasAccesosResumen(data);
      else if (seccion === 'documentos') setEstadisticasDocumentos(data);
    } catch (err) {
      console.error('Error loadDashboardSeccion:', err);
      if (seccion === 'finanzas') setEstadisticasFinanzas(null);
      else if (seccion === 'residentes') setEstadisticasResidentes(null);
      else if (seccion === 'servicios') setEstadisticasServicios(null);
      else if (seccion === 'participacion') setEstadisticasParticipacion(null);
      else if (seccion === 'accesos-resumen') setEstadisticasAccesosResumen(null);
      else if (seccion === 'documentos') setEstadisticasDocumentos(null);
    } finally {
      setLoadingDashboardSeccion(false);
    }
  };

  const loadFinanzasResumen = React.useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setLoadingFinanzasResumen(true);
    try {
      const p = new URLSearchParams();
      p.set('año', String(finanzasResumenAño));
      if (finanzasResumenMes) p.set('mes', finanzasResumenMes);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/finanzas/resumen?${p.toString()}`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cargar resumen de finanzas');
      }
      const data = await response.json();
      setFinanzasResumenData(data);
    } catch (err) {
      console.error('loadFinanzasResumen:', err);
      setFinanzasResumenData(null);
      if (!err.message?.includes('Sesión expirada') && !err.message?.includes('No hay sesión activa')) {
        setError(err.message || 'Error al cargar resumen de finanzas');
      }
    } finally {
      setLoadingFinanzasResumen(false);
    }
  }, [finanzasResumenAño, finanzasResumenMes, fetchWithAuth]);

  const exportarFinanzasResumenCsv = () => {
    const rp = finanzasResumenData?.resumenPeriodo;
    if (!rp) return;
    const sep = ';';
    const num = (v) => String(Number(v ?? 0).toFixed(2)).replace('.', ',');
    const rows = [];
    rows.push(['Concepto', 'Valor'].join(sep));
    rows.push(['Capital inicial (ejercicio)', num(rp.capitalInicialEjercicio)].join(sep));
    rows.push(['Periodo desde', rp.periodoDesde || ''].join(sep));
    rows.push(['Periodo hasta', rp.periodoHasta || ''].join(sep));
    rows.push(['Saldo inicial (libro)', num(rp.saldoInicialPeriodo)].join(sep));
    rows.push(['Total ingresos (monto + recargos, vigentes)', num(rp.totalIngresosPeriodo)].join(sep));
    rows.push(['Total egresos (vigentes)', num(rp.totalEgresosPeriodo)].join(sep));
    rows.push(['Saldo final (libro)', num(rp.saldoFinalPeriodo)].join(sep));
    rows.push([]);
    const det = finanzasResumenData?.detalleMensualPorMes;
    if (det && det.length) {
      rows.push(['Mes', 'Saldo inicial banco', 'Ingresos', 'Egresos', 'Saldo final banco'].join(sep));
      det.forEach((f) => {
        rows.push([f.mes, num(f.saldoInicialBanco), num(f.totalIngresos), num(f.totalEgresos), num(f.saldoFinalBanco)].join(sep));
      });
      const ti = det.reduce((s, f) => s + Number(f.totalIngresos || 0), 0);
      const te = det.reduce((s, f) => s + Number(f.totalEgresos || 0), 0);
      rows.push(['TOTALES AÑO', '', num(ti), num(te), ''].join(sep));
    }
    const bom = '\uFEFF';
    const blob = new Blob([bom + rows.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const suf = finanzasResumenMes ? `_${finanzasResumenMes}` : '';
    a.download = `resumen_finanzas_${finanzasResumenAño}${suf}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const loadConfiguracion = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/configuracion`);
      const data = await response.json();
      if (data.dias_max_recurrentes) {
        setDiasMaxRecurrentes(parseInt(data.dias_max_recurrentes.valor) || 7);
      }
      if (data.numero_estacionamientos) {
        setNumeroEstacionamientos(parseInt(data.numero_estacionamientos.valor) || 50);
      }
      if (data.limite_visitas_recurrentes) {
        setLimiteVisitasRecurrentes(parseInt(data.limite_visitas_recurrentes.valor) || 2);
      }
      if (data.limite_visitas_ocasionales) {
        setLimiteVisitasOcasionales(parseInt(data.limite_visitas_ocasionales.valor) || 2);
      }
      if (data.limite_estacionamientos_por_casa) {
        setLimiteEstacionamientosPorCasa(parseInt(data.limite_estacionamientos_por_casa.valor) || 2);
      }
      if (data.validar_limite_estacionamientos_por_casa) {
        setValidarLimiteEstacionamientosPorCasa(data.validar_limite_estacionamientos_por_casa.valor === 'true');
      }

      // Datos del emisor para los recibos PDF
      setRecibosEmisor({
        nombre: data.recibos_emisor_nombre?.valor ?? '',
        direccion: data.recibos_emisor_direccion?.valor ?? '',
        razon_social: data.recibos_emisor_razon_social?.valor ?? '',
      });
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      setError('Error al cargar configuración');
    }
  };

  const guardarConfiguracion = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/configuracion/dias-max-recurrentes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ valor: diasMaxRecurrentes.toString() }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al guardar configuración');
        return;
      }

      setSuccess('Configuración guardada correctamente');
      loadConfiguracion();
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      setError('Error al guardar configuración');
    }
  };

  const guardarConfiguracionEstacionamientos = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/configuracion/numero-estacionamientos`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ valor: numeroEstacionamientos.toString() }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al guardar configuración');
        return;
      }

      setSuccess('Configuración guardada correctamente');
      loadConfiguracion();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      setError('Error al guardar configuración');
    }
  };

  const loadShellyDispositivos = async () => {
    try {
      setLoadingShelly(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/shelly-dispositivos`);
      const data = await response.json();
      setShellyDispositivos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al cargar Shelly:', err);
      setShellyDispositivos([]);
    } finally {
      setLoadingShelly(false);
    }
  };

  const handleGenerarApiKey = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/llave-acceso/generar-api-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al generar API Key');
        return;
      }
      setApiKeyGenerada(data.api_key || '');
      setShowModalApiKey(true);
      setSuccess('API Key generada. Cópiala ahora, no se mostrará de nuevo.');
    } catch (err) {
      setError('Error al generar API Key');
    }
  };

  const handleGuardarShelly = async () => {
    if (!formShelly.nombre?.trim() || !formShelly.host?.trim() || !formShelly.device_id?.trim()) {
      setError('Nombre, host y device_id son requeridos');
      return;
    }
    if (!shellyEditando && !formShelly.auth_key?.trim()) {
      setError('Auth key es requerido al crear');
      return;
    }
    try {
      setLoadingShelly(true);
      const body = {
        nombre: formShelly.nombre.trim(),
        host: formShelly.host.trim(),
        device_id: formShelly.device_id.trim(),
        activo: formShelly.activo,
      };
      if (formShelly.auth_key?.trim()) body.auth_key = formShelly.auth_key.trim();
      const url = shellyEditando
        ? `${API_BASE_URL}/admin/shelly-dispositivos/${shellyEditando.id}`
        : `${API_BASE_URL}/admin/shelly-dispositivos`;
      const response = await fetchWithAuth(url, {
        method: shellyEditando ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al guardar Shelly');
        return;
      }
      setSuccess(shellyEditando ? 'Shelly actualizado' : 'Shelly agregado');
      setShowModalShelly(false);
      setShellyEditando(null);
      setFormShelly({ nombre: '', host: '', auth_key: '', device_id: '', activo: true });
      loadShellyDispositivos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar Shelly');
    } finally {
      setLoadingShelly(false);
    }
  };

  const handleProbarShelly = async (id) => {
    try {
      setLoadingShelly(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/shelly-dispositivos/${id}/probar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message || 'Shelly respondió correctamente');
      } else {
        setError(data.error || 'Error al probar Shelly');
      }
      setTimeout(() => { setSuccess(''); setError(''); }, 4000);
    } catch (err) {
      setError('Error al probar Shelly');
    } finally {
      setLoadingShelly(false);
    }
  };

  const handleEliminarShelly = async (id) => {
    if (!window.confirm('¿Eliminar este dispositivo Shelly?')) return;
    try {
      setLoadingShelly(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/shelly-dispositivos/${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al eliminar');
        return;
      }
      setSuccess('Shelly eliminado');
      loadShellyDispositivos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al eliminar Shelly');
    } finally {
      setLoadingShelly(false);
    }
  };

  const guardarLimiteVisitasRecurrentes = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/configuracion/limite-visitas-recurrentes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ valor: limiteVisitasRecurrentes.toString() }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al guardar configuración');
        return;
      }

      setSuccess('Límite de visitas recurrentes guardado correctamente');
      loadConfiguracion();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      setError('Error al guardar configuración');
    }
  };

  const guardarInstruccionesPago = async () => {
    setInstruccionesPagoGuardando(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/configuracion/instrucciones-pago`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(instruccionesPago),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al guardar instrucciones de pago');
        return;
      }
      setSuccess('Instrucciones de pago guardadas correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al guardar instrucciones de pago:', error);
      setError('Error al guardar instrucciones de pago');
    } finally {
      setInstruccionesPagoGuardando(false);
    }
  };

  const guardarLimiteVisitasOcasionales = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/configuracion/limite-visitas-ocasionales`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ valor: limiteVisitasOcasionales.toString() }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al guardar configuración');
        return;
      }

      setSuccess('Límite de visitas ocasionales guardado correctamente');
      loadConfiguracion();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      setError('Error al guardar configuración');
    }
  };

  const guardarLimiteEstacionamientosPorCasa = async () => {
    try {
      setError('');
      setLoading(true);

      const response = await fetchWithAuth(`${API_BASE_URL}/admin/configuracion/limite-estacionamientos-por-casa`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ valor: limiteEstacionamientosPorCasa.toString() }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al guardar configuración');
        setLoading(false);
        return;
      }

      setSuccess('Límite de estacionamientos por casa guardado correctamente');
      loadConfiguracion();
      setLoading(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      setError('Error al guardar configuración');
      setLoading(false);
    }
  };

  // Configuración: datos del emisor de los recibos PDF (setRecibosEmisor se usa en loadConfiguracion)
  // eslint-disable-next-line no-unused-vars
  const [recibosEmisor, setRecibosEmisor] = useState({
    nombre: '',
    direccion: '',
    razon_social: '',
  });

  const loadDiasFestivos = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/dias-festivos`);
      const data = await response.json();
      setDiasFestivos(data);
    } catch (error) {
      console.error('Error al cargar días festivos:', error);
      setError('Error al cargar días festivos');
    }
  };

  const handleGuardarDiaFestivo = async () => {
    try {
      setLoading(true);
      const url = diaFestivoEditando
        ? `${API_BASE_URL}/admin/dias-festivos/${diaFestivoEditando.id}`
        : `${API_BASE_URL}/admin/dias-festivos`;
      
      const method = diaFestivoEditando ? 'PUT' : 'POST';
      
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formDiaFestivo),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al guardar día festivo');
        return;
      }

      setSuccess(diaFestivoEditando ? 'Día festivo actualizado correctamente' : 'Día festivo creado correctamente');
      setShowModalDiaFestivo(false);
      setDiaFestivoEditando(null);
      setFormDiaFestivo({ fecha: '', descripcion: '', activo: true });
      loadDiasFestivos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al guardar día festivo:', error);
      setError('Error al guardar día festivo');
    } finally {
      setLoading(false);
    }
  };

  const handleEditarDiaFestivo = (diaFestivo) => {
    setDiaFestivoEditando(diaFestivo);
    setFormDiaFestivo({
      fecha: diaFestivo.fecha,
      descripcion: diaFestivo.descripcion,
      activo: diaFestivo.activo
    });
    setShowModalDiaFestivo(true);
  };

  const handleEliminarDiaFestivo = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este día festivo?')) return;

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/dias-festivos/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al eliminar día festivo');
        return;
      }

      setSuccess('Día festivo eliminado correctamente');
      loadDiasFestivos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al eliminar día festivo:', error);
      setError('Error al eliminar día festivo');
    }
  };

  const handleMarcarAlertaVista = async (id) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/alertas/${id}/marcar-vista`, {
        method: 'PUT',
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al marcar alerta como vista');
        return;
      }

      // Recargar alertas
      const alertasResponse = await fetchWithAuth(`${API_BASE_URL}/admin/alertas`);
      const alertasData = await alertasResponse.json();
      setAlertas(alertasData);

      // Actualizar contador
      const contadorResponse = await fetchWithAuth(`${API_BASE_URL}/admin/alertas/contador`);
      const contadorData = await contadorResponse.json();
      setContadorAlertas(contadorData.count);
    } catch (error) {
      console.error('Error al marcar alerta como vista:', error);
      setError('Error al marcar alerta como vista');
    }
  };

  const loadAlertas = async () => {
    setLoadingAlertas(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/alertas`);
      const data = await response.json();
      setAlertas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar alertas:', error);
      setError('Error al cargar alertas');
      setAlertas([]);
    } finally {
      setLoadingAlertas(false);
    }
  };

  const loadAlertasPendientesCount = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/alertas/contador`);
      const data = await response.json();
      setContadorAlertas(data.count);
    } catch (error) {
      console.error('Error al cargar contador de alertas:', error);
    }
  };

  // Ajustar tabActual si la pestaña actual no está entre las visibles (permisos de menú)
  useEffect(() => {
    if (visibleAdminMenuIndices.length > 0 && !visibleAdminMenuIndices.includes(tabActual)) {
      setTabActual(visibleAdminMenuIndices[0]);
    }
  }, [visibleAdminMenuIndices, tabActual]);

  // Persistir pestaña activa del menú lateral entre recargas
  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(ADMIN_TAB_STORAGE_KEY, String(tabActual));
  }, [tabActual, ADMIN_TAB_STORAGE_KEY]);

  // Cargar datos cuando se cambia de pestaña (solo si el admin tiene condominio, salvo Condominios)
  useEffect(() => {
    if (!esAdminCondominio) {
      if (user?.rol === 'superuser') loadCondominios();
      return;
    }
    if (tabActual === 0) {
      loadResidentes();
    }
    if (tabActual === 1) {
      loadCatalogos();
    }
    if (tabActual === 3) {
      loadConceptosPago();
    }
    if (tabActual === 4) {
      loadPagos();
      (async () => {
        try {
          const response = await fetchWithAuth(`${API_BASE_URL}/admin/configuracion`);
          const data = await response.json();
          setInstruccionesPago({
            numero_cuenta: data.instrucciones_pago_numero_cuenta?.valor ?? '',
            instrucciones: data.instrucciones_pago_texto?.valor ?? '',
            referencia: data.instrucciones_pago_referencia?.valor ?? '',
            enviar_comprobante: data.instrucciones_pago_enviar_comprobante?.valor ?? ''
          });
        } catch (e) {
          console.error('Error al cargar instrucciones de pago:', e);
        }
      })();
      (async () => {
        try {
          const response = await fetchWithAuth(`${API_BASE_URL}/admin/condominio/capital-inicial`);
          if (!response.ok) return;
          const data = await response.json();
          setCapitalInicialEdit(String(data.capital_inicial ?? ''));
        } catch (e) {
          console.error('Error al cargar capital inicial:', e);
        }
      })();
    }
    if (tabActual === 5) {
      loadGastos();
    }
    if (tabActual === 6) {
      loadAmenidades();
    }
    if (tabActual === 7) {
      loadDocumentos();
    }
    if (tabActual === 8) {
      loadPases();
    }
    if (tabActual === 9) {
      loadNotificaciones();
    }
    if (tabActual === 10) {
      loadVisitasManuales();
    }
    if (tabActual === 11) {
      loadCategoriasServicios();
      loadNombresServicios();
    }
    if (tabActual === 13) {
      loadConfiguracion();
      loadDiasFestivos();
      loadShellyDispositivos();
    }
    if (tabActual === 14) {
      loadEstadisticas();
    }
    if (tabActual === 15) {
      loadAlertas();
      loadAlertasPendientesCount();
    }
    if (tabActual === 16) {
      loadSugerencias();
    }
    if (tabActual === 17) {
      loadSolicitudesEliminacion();
    }
    if (tabActual === 18) {
      loadEncuestas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabActual, user, amenidadesSeccion, esAdminCondominio]);

  // Cargar contadores de notificaciones (círculos rojos en las pestañas) al cargar el panel y cada 60 s
  useEffect(() => {
    const tieneCondominio = user?.condominio_id;
    // Solo aplica para administradores de coto; superuser no tiene estas pestañas/endpoints.
    if (!user || user?.rol !== 'admin' || !tieneCondominio) return;
    const loadBadgeCounts = () => {
      loadAlertasPendientesCount();
      loadReservasPendientes();
      loadSugerencias();
      loadSolicitudesEliminacion();
      loadPendingReviews();
    };
    loadBadgeCounts();
    const interval = setInterval(loadBadgeCounts, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ========== FUNCIONES PARA DIRECTORIO ==========
  const loadCondominios = async () => {
    try {
      setLoadingCondominios(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/condominios`);
      const data = await response.json();
      setCondominiosList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al cargar condominios:', err);
      setError('Error al cargar condominios');
    } finally {
      setLoadingCondominios(false);
    }
  };

  const handleCrearCondominio = async (e) => {
    e.preventDefault();
    if (!condominioFormNombre.trim() || !condominioFormCodigo.trim()) {
      setError('Nombre y código son requeridos');
      return;
    }
    try {
      setLoadingCrearCondominio(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/condominios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: condominioFormNombre.trim(),
          direccion: condominioFormDireccion.trim() || undefined,
          codigo: condominioFormCodigo.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al crear condominio');
        return;
      }
      setSuccess('Condominio creado correctamente. Código: ' + (data.codigo || condominioFormCodigo));
      setCondominioFormNombre('');
      setCondominioFormDireccion('');
      setCondominioFormCodigo('');
      loadCondominios();
    } catch (err) {
      setError('Error de conexión al crear condominio');
    } finally {
      setLoadingCrearCondominio(false);
    }
  };

  const loadUsuarios = async (condominioId) => {
    if (!condominioId) { setUsuariosList([]); return; }
    try {
      setLoadingUsuarios(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/usuarios?condominio_id=${condominioId}`);
      const data = await response.json();
      const lista = Array.isArray(data) ? data : [];
      setUsuariosList(lista.filter((u) => u.rol === 'admin'));
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      setError('Error al cargar usuarios del condominio');
      setUsuariosList([]);
    } finally {
      setLoadingUsuarios(false);
    }
  };

  const handleCrearUsuario = async (e) => {
    e.preventDefault();
    const cid = selectedCondominioIdForUsers || formCrearUsuario.condominio_id;
    if (!cid || !formCrearUsuario.email?.trim() || !formCrearUsuario.nombre?.trim() || !formCrearUsuario.password?.trim()) {
      setError('Condominio, email, nombre y contraseña son requeridos');
      return;
    }
    if (formCrearUsuario.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        condominio_id: parseInt(cid, 10),
        email: formCrearUsuario.email.trim(),
        nombre: formCrearUsuario.nombre.trim(),
        password: formCrearUsuario.password,
        rol: formCrearUsuario.rol || 'admin',
      };
      if ((formCrearUsuario.rol || 'admin') === 'admin') {
        payload.admin_menu_permisos = Array.isArray(formCrearUsuario.admin_menu_permisos)
          ? formCrearUsuario.admin_menu_permisos
          : ALL_ADMIN_MENU_INDICES;
      }
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/usuarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al crear usuario');
        return;
      }
      setSuccess('Usuario creado correctamente');
      setOpenModalCrearAdmin(false);
      setFormCrearUsuario({
        email: '',
        nombre: '',
        password: '',
        rol: 'admin',
        admin_menu_permisos: ALL_ADMIN_MENU_INDICES,
      });
      loadUsuarios(selectedCondominioIdForUsers || cid);
    } catch (err) {
      setError('Error de conexión al crear usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!usuarioResetId || !nuevaPassword || nuevaPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/usuarios/${usuarioResetId}/reset-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nueva_password: nuevaPassword }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al restablecer contraseña');
        return;
      }
      setSuccess('Contraseña actualizada');
      setOpenModalResetPassword(false);
      setUsuarioResetId(null);
      setNuevaPassword('');
      loadUsuarios(selectedCondominioIdForUsers);
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleBloquearUsuario = async (usuario) => {
    const nuevoEstado = !usuario.bloqueado;
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/usuarios/${usuario.id}/bloquear`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bloqueado: nuevoEstado }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al cambiar estado');
        return;
      }
      setSuccess(nuevoEstado ? 'Usuario bloqueado' : 'Usuario desbloqueado');
      loadUsuarios(selectedCondominioIdForUsers);
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const toggleAdminMenuPermisoTemp = (index) => {
    setAdminMenuPermisosTemp((prev) => {
      if (!Array.isArray(prev)) return [index];
      return prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index];
    });
  };

  const handleAbrirPermisosMenu = (usuario) => {
    const actuales = Array.isArray(usuario.admin_menu_permisos) && usuario.admin_menu_permisos.length > 0
      ? usuario.admin_menu_permisos
      : ALL_ADMIN_MENU_INDICES;
    setUsuarioPermisosEdit(usuario);
    setAdminMenuPermisosTemp(actuales);
    setOpenModalPermisosMenu(true);
  };

  const handleGuardarPermisosMenu = async () => {
    if (!usuarioPermisosEdit) return;
    if (!usuarioPermisosEdit.nombre || !usuarioPermisosEdit.nombre.trim()) {
      setError('El nombre del administrador es requerido');
      return;
    }
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/usuarios/${usuarioPermisosEdit.id}/permisos-menu`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_menu_permisos: adminMenuPermisosTemp, nombre: usuarioPermisosEdit.nombre }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al actualizar usuario');
        return;
      }
      setSuccess('Usuario actualizado correctamente');
      setOpenModalPermisosMenu(false);
      setUsuarioPermisosEdit(null);
      setAdminMenuPermisosTemp(ALL_ADMIN_MENU_INDICES);
      if (selectedCondominioIdForUsers) {
        loadUsuarios(selectedCondominioIdForUsers);
      }
    } catch (err) {
      setError('Error de conexión al actualizar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarPasswordAdmin = async (e) => {
    e?.preventDefault?.();
    if (!passwordActualAdmin?.trim() || !nuevaPasswordAdmin?.trim()) {
      setError('Contraseña actual y nueva son requeridas');
      return;
    }
    if (nuevaPasswordAdmin.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (nuevaPasswordAdmin !== confirmarNuevaPasswordAdmin) {
      setError('La nueva contraseña y la confirmación no coinciden');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/cambiar-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password_actual: passwordActualAdmin, nueva_password: nuevaPasswordAdmin }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al cambiar contraseña');
        return;
      }
      setSuccess('Contraseña actualizada correctamente');
      setOpenModalCambiarPasswordAdmin(false);
      setPasswordActualAdmin('');
      setNuevaPasswordAdmin('');
      setConfirmarNuevaPasswordAdmin('');
    } catch (err) {
      setError('Error de conexión al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };

  // ========== AMENIDADES (FDD v2) ==========
  const loadAmenidades = async () => {
    try {
      setLoadingAmenidades(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/amenidades`);
      const data = await response.json();
      setAmenidadesList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al cargar amenidades:', err);
      setError('Error al cargar amenidades');
      setAmenidadesList([]);
    } finally {
      setLoadingAmenidades(false);
    }
  };

  const loadHorariosAmenidad = async (amenidadId) => {
    if (!amenidadId) return;
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/amenidades/${amenidadId}/horarios`);
      const data = await response.json();
      setHorariosAmenidad(Array.isArray(data) ? data : []);
    } catch (err) {
      setHorariosAmenidad([]);
    }
  };

  const loadBloqueosAmenidad = async (amenidadId) => {
    if (!amenidadId) return;
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/amenidades/${amenidadId}/bloqueos`);
      const data = await response.json();
      setBloqueosAmenidad(Array.isArray(data) ? data : []);
    } catch (err) {
      setBloqueosAmenidad([]);
    }
  };

  const loadReservasPendientes = async () => {
    try {
      setLoadingReservasPendientes(true);
      const params = new URLSearchParams();
      if (filtroPendientesAmenidadId) params.set('amenidad_id', filtroPendientesAmenidadId);
      if (filtroPendientesFechaDesde) params.set('fecha_desde', filtroPendientesFechaDesde);
      if (filtroPendientesFechaHasta) params.set('fecha_hasta', filtroPendientesFechaHasta);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/reservas-amenidad/pendientes?${params.toString()}`);
      const data = await response.json();
      setReservasPendientes(Array.isArray(data) ? data : []);
    } catch (err) {
      setReservasPendientes([]);
    } finally {
      setLoadingReservasPendientes(false);
    }
  };

  const loadReservasRechazadasCanceladas = async () => {
    try {
      setLoadingReservasRechazadasCanceladas(true);
      const params = new URLSearchParams();
      if (filtroIncidenciasAmenidadId) params.set('amenidad_id', filtroIncidenciasAmenidadId);
      if (filtroIncidenciasFechaDesde) params.set('fecha_desde', filtroIncidenciasFechaDesde);
      if (filtroIncidenciasFechaHasta) params.set('fecha_hasta', filtroIncidenciasFechaHasta);
      const query = params.toString();
      const response = await fetchWithAuth(
        `${API_BASE_URL}/admin/reservas-amenidad/rechazadas-canceladas${query ? `?${query}` : ''}`
      );
      const data = await response.json();
      setReservasRechazadasCanceladas(Array.isArray(data) ? data : []);
    } catch (err) {
      setReservasRechazadasCanceladas([]);
    } finally {
      setLoadingReservasRechazadasCanceladas(false);
    }
  };

  // Mantener actualizado el conteo de reservas pendientes cuando se entra a la pestaña de Amenidades
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (esAdminCondominio && tabActual === 6) {
      loadReservasPendientes();
    }
  }, [tabActual, user, esAdminCondominio]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const loadCalendarioAmenidades = async () => {
    try {
      setLoadingCalendario(true);
      const desde = calendarioFechaDesde || new Date().toISOString().slice(0, 10);
      const hasta = calendarioFechaHasta || desde;
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/reservas-amenidad/calendario?fecha_desde=${desde}&fecha_hasta=${hasta}`);
      const data = await response.json();
      setCalendarioAmenidades({ reservas: data.reservas || [], bloqueos: data.bloqueos || [] });
    } catch (err) {
      setCalendarioAmenidades({ reservas: [], bloqueos: [] });
    } finally {
      setLoadingCalendario(false);
    }
  };

  const loadPropiedades = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/propiedades`);
      const data = await response.json();
      setListadoPropiedades(Array.isArray(data.propiedades) ? data.propiedades : []);
    } catch (err) {
      setListadoPropiedades([]);
    }
  };

  const handleCrearReservaAdmin = async () => {
    if (!formReservaAdmin.propiedad?.trim() || !formReservaAdmin.amenidad_id) {
      setError('Selecciona casa y amenidad');
      return;
    }
    const amenidad = amenidadesList.find((a) => String(a.id) === String(formReservaAdmin.amenidad_id));
    const unidadReserva = amenidad?.unidad_reserva || 'dia';
    if (unidadReserva === 'dia') {
      if (!formReservaAdmin.fecha_reserva) {
        setError('Selecciona la fecha');
        return;
      }
    } else {
      if (!formReservaAdmin.fecha_reserva || !formReservaAdmin.hora_inicio || !formReservaAdmin.hora_fin) {
        setError('Selecciona fecha, hora inicio y hora fin');
        return;
      }
    }
    setCreandoReservaAdmin(true);
    setError('');
    try {
      const body = {
        propiedad: formReservaAdmin.propiedad.trim(),
        amenidad_id: parseInt(formReservaAdmin.amenidad_id, 10),
      };
      if (unidadReserva === 'dia') {
        body.fechas = [formReservaAdmin.fecha_reserva];
      } else {
        body.fecha_reserva = formReservaAdmin.fecha_reserva;
        body.hora_inicio = formReservaAdmin.hora_inicio;
        body.hora_fin = formReservaAdmin.hora_fin;
      }
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/reservas-amenidad`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al crear la reserva');
        return;
      }
      setSuccess('Reserva creada correctamente en nombre del residente');
      setFormReservaAdmin({ propiedad: '', amenidad_id: '', fecha_reserva: '', hora_inicio: '09:00', hora_fin: '10:00' });
      loadReservasPendientes();
      loadCalendarioAmenidades();
    } catch (err) {
      console.error('Error al crear reserva admin:', err);
      setError('Error al crear la reserva');
    } finally {
      setCreandoReservaAdmin(false);
    }
  };

  const handleOpenModalAmenidad = (amenidad) => {
    if (amenidad) {
      setAmenidadEditando(amenidad);
      setFormAmenidad({
        nombre: amenidad.nombre || '',
        descripcion: amenidad.descripcion || '',
        reglamento: amenidad.reglamento || '',
        imagen_url: amenidad.imagen_url || '',
        modo_aprobacion: amenidad.modo_aprobacion || 'manual',
        max_reservas_concurrentes_por_residente: amenidad.max_reservas_concurrentes_por_residente ?? 3,
        cancelacion_min_horas_antelacion: amenidad.cancelacion_min_horas_antelacion ?? 2,
        unidad_reserva: amenidad.unidad_reserva || 'dia',
        max_unidades_reserva: amenidad.max_unidades_reserva ?? 1,
        activo: amenidad.activo !== false
      });
      loadHorariosAmenidad(amenidad.id);
      loadBloqueosAmenidad(amenidad.id);
    } else {
      setAmenidadEditando(null);
      setFormAmenidad({
        nombre: '', descripcion: '', reglamento: '', imagen_url: '', modo_aprobacion: 'manual',
        max_reservas_concurrentes_por_residente: 3, cancelacion_min_horas_antelacion: 2,
        unidad_reserva: 'dia', max_unidades_reserva: 1, activo: true
      });
      setHorariosAmenidad([]);
      setBloqueosAmenidad([]);
    }
    setOpenModalAmenidad(true);
  };

  const handleSaveAmenidad = async (e) => {
    e.preventDefault();
    if (!formAmenidad.nombre?.trim()) {
      setError('El nombre es requerido');
      return;
    }
    try {
      setLoading(true);
      const url = amenidadEditando
        ? `${API_BASE_URL}/admin/amenidades/${amenidadEditando.id}`
        : `${API_BASE_URL}/admin/amenidades`;
      const method = amenidadEditando ? 'PUT' : 'POST';
      const body = {
        nombre: formAmenidad.nombre.trim(),
        descripcion: formAmenidad.descripcion?.trim() || '',
        reglamento: formAmenidad.reglamento?.trim() || '',
        imagen_url: formAmenidad.imagen_url?.trim() || '',
        modo_aprobacion: formAmenidad.modo_aprobacion || 'manual',
        max_reservas_concurrentes_por_residente: Number(formAmenidad.max_reservas_concurrentes_por_residente) || 3,
        cancelacion_min_horas_antelacion: Number(formAmenidad.cancelacion_min_horas_antelacion) || 2,
        unidad_reserva: formAmenidad.unidad_reserva || 'dia',
        max_unidades_reserva: Number(formAmenidad.max_unidades_reserva) || 1,
        activo: formAmenidad.activo
      };
      const response = await fetchWithAuth(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al guardar amenidad');
        return;
      }
      setSuccess(amenidadEditando ? 'Amenidad actualizada' : 'Amenidad creada');
      setOpenModalAmenidad(false);
      loadAmenidades();
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarAmenidad = async (id) => {
    if (!window.confirm('¿Eliminar esta amenidad? Se perderán horarios, bloqueos y reservas asociados.')) return;
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/amenidades/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al eliminar');
        return;
      }
      setSuccess('Amenidad eliminada');
      loadAmenidades();
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHorarioAmenidad = async (amenidadId, diaSemana, horaInicio, horaFin) => {
    if (!amenidadId || !horaInicio || !horaFin) return;
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/amenidades/${amenidadId}/horarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dia_semana: diaSemana, hora_inicio: horaInicio, hora_fin: horaFin })
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al guardar horario');
        return;
      }
      setSuccess('Horario guardado');
      loadHorariosAmenidad(amenidadId);
    } catch (err) {
      setError('Error de conexión');
    }
  };

  const handleEliminarBloqueoAmenidad = async (amenidadId, bloqueoId) => {
    if (!window.confirm('¿Eliminar este bloqueo?')) return;
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/amenidades/${amenidadId}/bloqueos/${bloqueoId}`, { method: 'DELETE' });
      if (!response.ok) {
        setError('Error al eliminar bloqueo');
        return;
      }
      setSuccess('Bloqueo eliminado');
      loadBloqueosAmenidad(amenidadId);
    } catch (err) {
      setError('Error de conexión');
    }
  };

  const handleAgregarBloqueoAmenidad = async (amenidadId) => {
    const { fecha_inicio, fecha_fin, motivo } = nuevoBloqueoAmenidad;
    if (!fecha_inicio || !fecha_fin) {
      setError('Fecha inicio y fecha fin son requeridas');
      return;
    }
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/amenidades/${amenidadId}/bloqueos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fecha_inicio: fecha_inicio + 'T00:00:00', fecha_fin: fecha_fin + 'T23:59:59', motivo: motivo.trim() || null })
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al crear bloqueo');
        return;
      }
      setSuccess('Bloqueo agregado');
      setNuevoBloqueoAmenidad({ fecha_inicio: '', fecha_fin: '', motivo: '' });
      loadBloqueosAmenidad(amenidadId);
    } catch (err) {
      setError('Error de conexión');
    }
  };

  const handleAprobarReserva = async (id) => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/reservas-amenidad/${id}/aprobar`, { method: 'PATCH' });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al aprobar');
        return;
      }
      setSuccess('Reserva aprobada');
      loadReservasPendientes();
      loadCalendarioAmenidades();
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleRechazarReserva = async (e) => {
    e.preventDefault();
    if (!reservaRechazarId) return;
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/reservas-amenidad/${reservaRechazarId}/rechazar`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo_rechazo: motivoRechazo })
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al rechazar');
        return;
      }
      setSuccess('Reserva rechazada');
      setOpenModalRechazar(false);
      setReservaRechazarId(null);
      setMotivoRechazo('');
      loadReservasPendientes();
      loadCalendarioAmenidades();
      loadReservasRechazadasCanceladas();
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelarReservaAdmin = async (id) => {
    if (!id) return;
    if (!window.confirm('¿Cancelar esta reserva de amenidad?')) return;
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/reservas-amenidad/${id}/cancelar`, { method: 'PATCH' });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al cancelar');
        return;
      }
      setSuccess(data.message || 'Reserva cancelada');
      loadReservasPendientes();
      loadCalendarioAmenidades();
      loadReservasRechazadasCanceladas();
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const loadDirectoryCategories = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/directory/categories`);
      const data = await response.json();
      setDirectoryCategories(data);
    } catch (error) {
      console.error('Error al cargar categorías del directorio:', error);
      setError('Error al cargar categorías del directorio');
    }
  };

  const loadDirectoryProviders = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/directory/providers`);
      const data = await response.json();
      setDirectoryProviders(data);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      setError('Error al cargar proveedores');
    }
  };

  // Cargar datos del directorio y comentarios pendientes cuando se entra a la pestaña correspondiente
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (esAdminCondominio && tabActual === 12) {
      loadDirectoryCategories();
      loadDirectoryProviders();
      loadPendingReviews();
    }
  }, [tabActual, user, esAdminCondominio]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const loadPendingReviews = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/directory/reviews/pending`);
      const data = await response.json();
      setPendingReviews(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar comentarios pendientes:', error);
      setError('Error al cargar comentarios pendientes');
      setPendingReviews([]);
    }
  };

  const handleGuardarDirectoryCategory = async () => {
    try {
      setLoading(true);
      const url = directoryCategoryEditando
        ? `${API_BASE_URL}/admin/directory/categories/${directoryCategoryEditando.id}`
        : `${API_BASE_URL}/admin/directory/categories`;
      const method = directoryCategoryEditando ? 'PUT' : 'POST';

      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formDirectoryCategory),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al guardar categoría');
        return;
      }

      setSuccess(directoryCategoryEditando ? 'Categoría actualizada correctamente' : 'Categoría creada correctamente');
      setShowDialogDirectoryCategory(false);
      setDirectoryCategoryEditando(null);
      setFormDirectoryCategory({ name: '', icon_name: '', active: true });
      loadDirectoryCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al guardar categoría:', error);
      setError('Error al guardar categoría');
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarDirectoryProvider = async () => {
    try {
      setLoading(true);
      const url = directoryProviderEditando
        ? `${API_BASE_URL}/admin/directory/providers/${directoryProviderEditando.id}`
        : `${API_BASE_URL}/admin/directory/providers`;
      const method = directoryProviderEditando ? 'PUT' : 'POST';

      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formDirectoryProvider,
          category_id: formDirectoryProvider.category_id ? parseInt(formDirectoryProvider.category_id) : null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al guardar proveedor');
        return;
      }

      setSuccess(directoryProviderEditando ? 'Proveedor actualizado correctamente' : 'Proveedor creado correctamente');
      setShowDialogDirectoryProvider(false);
      setDirectoryProviderEditando(null);
      setFormDirectoryProvider({ 
        category_id: '', name: '', phone: '', description: '', photo_url: '', is_verified: true, active: true 
      });
      loadDirectoryProviders();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al guardar proveedor:', error);
      setError('Error al guardar proveedor');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarDirectoryCategory = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta categoría?')) return;

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/directory/categories/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al eliminar categoría');
        return;
      }

      setSuccess('Categoría eliminada correctamente');
      loadDirectoryCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al eliminar categoría:', error);
      setError('Error al eliminar categoría');
    }
  };

  const handleEliminarDirectoryProvider = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este proveedor?')) return;

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/directory/providers/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al eliminar proveedor');
        return;
      }

      setSuccess('Proveedor eliminado correctamente');
      loadDirectoryProviders();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al eliminar proveedor:', error);
      setError('Error al eliminar proveedor');
    }
  };

  const handleModerarReview = async (reviewId, status) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/directory/reviews/${reviewId}/moderate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al moderar comentario');
        return;
      }

      setSuccess(status === 'approved' ? 'Comentario aprobado' : 'Comentario rechazado');
      loadPendingReviews();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al moderar comentario:', error);
      setError('Error al moderar comentario');
    }
  };

  // Cargar pases (filtros explícitos para evitar estado desactualizado al hacer clic en Filtrar)
  const loadPases = async (opts = {}) => {
    try {
      const usado = opts.usado !== undefined ? opts.usado : filtroPasesUsado;
      const propiedad = opts.propiedad !== undefined ? opts.propiedad : filtroPasesPropiedad;
      const mes = opts.mes !== undefined ? opts.mes : filtroPasesMes;
      const año = opts.año !== undefined ? opts.año : filtroPasesAño;
      // Paginación server-side: solo se pide la página visible; el backend responde { data, total }
      const pagina = opts.pagina ?? 0;
      const pageSize = opts.registrosPorPagina ?? pasesRegistrosPorPagina;
      const params = new URLSearchParams();
      params.append('mes', String(mes));
      params.append('anio', String(año));
      params.append('limit', String(pageSize));
      params.append('offset', String(pagina * pageSize));
      if (usado !== '') params.append('usado', usado);
      if (propiedad) params.append('propiedad', propiedad);
      const url = `${API_BASE_URL}/admin/pases?${params.toString()}`;
      const response = await fetchWithAuth(url);
      const data = await response.json();
      setPases(Array.isArray(data.data) ? data.data : []);
      setPasesTotal(data.total || 0);
      setPasesPagina(pagina);
    } catch (error) {
      console.error('Error al cargar pases:', error);
      setError('Error al cargar pases');
    }
  };

  const handleVerIngresosPase = async (pase) => {
    setPaseIngresosError('');
    setPaseIngresos([]);
    setPaseIngresosTotalUsos(0);
    setPaseIngresosSeleccionado(pase);
    setPaseIngresosDialogOpen(true);
    setPaseIngresosLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/pases/${pase.id}/ingresos`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Error al cargar ingresos');
      setPaseIngresos(data.ingresos || []);
      setPaseIngresosTotalUsos(data.total_usos ?? 0);
    } catch (err) {
      console.error('Error al cargar ingresos del pase:', err);
      setPaseIngresosError(err.message || 'Error al cargar los ingresos de este pase');
    } finally {
      setPaseIngresosLoading(false);
    }
  };

  // Cargar notificaciones de servicios (admin)
  const loadNotificaciones = async (opts = {}) => {
    try {
      const estado = opts.estado !== undefined ? opts.estado : filtroNotifEstado;
      const propiedad = opts.propiedad !== undefined ? opts.propiedad : filtroNotifPropiedad;
      const mes = opts.mes !== undefined ? opts.mes : filtroNotifMes;
      const año = opts.año !== undefined ? opts.año : filtroNotifAño;
      // Paginación server-side: solo se pide la página visible; el backend responde { data, total }
      const pagina = opts.pagina ?? 0;
      const pageSize = opts.registrosPorPagina ?? notifRegistrosPorPagina;
      const params = new URLSearchParams();
      params.append('mes', String(mes));
      params.append('anio', String(año));
      params.append('limit', String(pageSize));
      params.append('offset', String(pagina * pageSize));
      if (estado) params.append('estado', estado);
      if (propiedad) params.append('propiedad', propiedad);
      const url = `${API_BASE_URL}/admin/notificaciones-servicios?${params.toString()}`;
      const response = await fetchWithAuth(url);
      const data = await response.json();
      setNotificaciones(Array.isArray(data.data) ? data.data : []);
      setNotifTotal(data.total || 0);
      setNotifPagina(pagina);
    } catch (err) {
      console.error('Error al cargar notificaciones:', err);
      setError('Error al cargar notificaciones');
    }
  };

  // Cargar visitas manuales registradas por guardias (admin)
  const loadVisitasManuales = async (opts = {}) => {
    try {
      const estado = opts.estado !== undefined ? opts.estado : filtroVisitaEstado;
      const propiedad = opts.propiedad !== undefined ? opts.propiedad : filtroVisitaPropiedad;
      const mes = opts.mes !== undefined ? opts.mes : filtroVisitaMes;
      const año = opts.año !== undefined ? opts.año : filtroVisitaAño;
      const soloOf = opts.solo_oficial !== undefined ? opts.solo_oficial : filtroVisitaSoloOficial;
      // Paginación server-side: solo se pide la página visible; el backend responde { data, total }
      const pagina = opts.pagina ?? 0;
      const pageSize = opts.registrosPorPagina ?? visitasRegistrosPorPagina;
      const params = new URLSearchParams();
      params.append('mes', String(mes));
      params.append('anio', String(año));
      params.append('limit', String(pageSize));
      params.append('offset', String(pagina * pageSize));
      if (estado) params.append('estado', estado);
      if (propiedad) params.append('propiedad', propiedad);
      if (soloOf === '1') params.append('solo_oficial', '1');
      const url = `${API_BASE_URL}/admin/visitas-manuales?${params.toString()}`;
      const response = await fetchWithAuth(url);
      const data = await response.json();
      setVisitasManuales(Array.isArray(data.data) ? data.data : []);
      setVisitasTotal(data.total || 0);
      setVisitasPagina(pagina);
    } catch (err) {
      console.error('Error al cargar visitas manuales:', err);
      setError('Error al cargar visitas manuales');
    }
  };

  // Exportar pases del mes a CSV (fetch sin limit: el estado solo tiene la página visible)
  const exportarPasesCSV = async () => {
    let listaExport = [];
    try {
      const params = new URLSearchParams();
      params.append('mes', String(filtroPasesMes));
      params.append('anio', String(filtroPasesAño));
      if (filtroPasesUsado !== '') params.append('usado', filtroPasesUsado);
      if (filtroPasesPropiedad) params.append('propiedad', filtroPasesPropiedad);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/pases?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Error al exportar pases');
      listaExport = Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error al exportar pases:', error);
      if (error.message !== 'Sesión expirada') setError('Error al exportar pases');
      return;
    }
    const headers = ['ID', 'Visitante', 'Residente', 'Propiedad', 'Tipo', 'Categoría', 'Formato', 'Código Numérico', 'Fecha Validez', 'Fecha Fin Validez', 'Estado', 'Fecha Uso', 'Fecha Entrada', 'Fecha Salida', 'Núm. cono', 'Color cono', 'Guardia', 'Fecha Creación'];
    const rows = listaExport.map((p) => [
      p.id,
      p.visitante_nombre || '',
      p.residente_nombre || '',
      p.propiedad || '',
      p.tipo_pase || '',
      p.categoria_visita || '',
      p.tipo_pase_formato || 'QR',
      p.codigo_numerico || '',
      p.fecha_validez ? formatFechaOnlyMexico(p.fecha_validez) : '',
      p.fecha_fin_validez ? formatFechaOnlyMexico(p.fecha_fin_validez) : '',
      p.usado ? 'Inactivo' : 'Activo',
      p.fecha_uso ? formatFechaHoraMexicoCentral(p.fecha_uso) : '',
      p.bitacora_entrada ? formatFechaHoraMexicoCentral(p.bitacora_entrada) : '',
      p.bitacora_salida ? formatFechaHoraMexicoCentral(p.bitacora_salida) : '',
      p.numero_cono || '',
      p.color_cono || '',
      p.guardia_nombre || '',
      p.created_at ? formatFechaHoraMexicoCentral(p.created_at) : ''
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `pases_${filtroPasesAño}-${String(filtroPasesMes).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Exportar notificaciones del mes a CSV (fetch sin limit: el estado solo tiene la página visible)
  const exportarNotificacionesCSV = async () => {
    let listaExport = [];
    try {
      const params = new URLSearchParams();
      params.append('mes', String(filtroNotifMes));
      params.append('anio', String(filtroNotifAño));
      if (filtroNotifEstado) params.append('estado', filtroNotifEstado);
      if (filtroNotifPropiedad) params.append('propiedad', filtroNotifPropiedad);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/notificaciones-servicios?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Error al exportar notificaciones');
      listaExport = Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error al exportar notificaciones:', error);
      if (error.message !== 'Sesión expirada') setError('Error al exportar notificaciones');
      return;
    }
    const headers = ['ID', 'Residente', 'Propiedad', 'Categoría', 'Servicio', 'Fecha entrada', 'Visitante', 'Placas', 'Estado', 'Fecha ingreso', 'Guardia ingreso', 'Fecha salida', 'Guardia salida', 'Creación'];
    const rows = listaExport.map((n) => [
      n.id,
      n.residente_nombre || '',
      n.propiedad || '',
      n.categoria_nombre || '',
      n.nombre_servicio_nombre || n.nombre_servicio_manual || '',
      n.fecha_entrada ? formatFechaOnlyMexico(n.fecha_entrada) : '',
      n.nombre_visitante || '',
      n.placas_vehiculo || '',
      n.estado || '',
      n.fecha_ingreso ? formatFechaHoraMexicoCentral(n.fecha_ingreso) : '',
      n.guardia_ingreso_nombre || '',
      n.fecha_salida ? formatFechaHoraMexicoCentral(n.fecha_salida) : '',
      n.guardia_salida_nombre || '',
      n.created_at ? formatFechaHoraMexicoCentral(n.created_at) : ''
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `notificaciones_${filtroNotifAño}-${String(filtroNotifMes).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Exportar ingresos (pagos) del mes a CSV/Excel (todas las columnas)
  const exportarIngresosExcel = async () => {
    try {
      if (!filtrosIngresos.mes || !filtrosIngresos.año) {
        setError('Selecciona Mes y Año antes de exportar ingresos.');
        return;
      }
      const mes = filtrosIngresos.mes;
      const año = filtrosIngresos.año;
      const params = new URLSearchParams({ mes, año, limit: '10000', offset: '0' });
      if (filtrosIngresos.fechaInicio) params.append('fecha_inicio', filtrosIngresos.fechaInicio);
      if (filtrosIngresos.fechaFin) params.append('fecha_fin', filtrosIngresos.fechaFin);
      if (filtrosIngresos.propiedad) params.append('propiedad', filtrosIngresos.propiedad);
      if (filtrosIngresos.estatus_recibo === 'vigente' || filtrosIngresos.estatus_recibo === 'cancelado') params.append('estatus_recibo', filtrosIngresos.estatus_recibo);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/pagos?${params.toString()}`);
      if (!response.ok) throw new Error('Error al obtener ingresos');
      const { data } = await response.json();
      const list = Array.isArray(data) ? data : [];
      if (list.length === 0) {
        setError('No hay ingresos para el mes seleccionado. Elige en los filtros un mes y año que tenga movimientos (ej. el mes que ves en la tabla), pulsa Filtrar y luego Exportar Excel (mes).');
        return;
      }
      const headers = Object.keys(list[0]);
      const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = list.map((row) => headers.map((h) => escape(row[h])).join(','));
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ingresos_${año}-${String(mes).padStart(2, '0')}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      setSuccess(`Exportados ${list.length} ingresos`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      if (e.message !== 'Sesión expirada' && e.message !== 'No hay sesión activa') setError('Error al exportar ingresos');
    }
  };

  // Exportar egresos (gastos) del mes a CSV/Excel (todas las columnas)
  const exportarEgresosExcel = async () => {
    try {
      if (!filtrosEgresos.mes || !filtrosEgresos.año) {
        setError('Selecciona Mes y Año antes de exportar egresos.');
        return;
      }
      const mes = filtrosEgresos.mes;
      const año = filtrosEgresos.año;
      const params = new URLSearchParams({ mes, año, limit: '10000', offset: '0' });
      if (filtrosEgresos.fechaInicio) params.append('fecha_inicio', filtrosEgresos.fechaInicio);
      if (filtrosEgresos.fechaFin) params.append('fecha_fin', filtrosEgresos.fechaFin);
      if (filtrosEgresos.estatus_recibo === 'vigente' || filtrosEgresos.estatus_recibo === 'cancelado') params.append('estatus_recibo', filtrosEgresos.estatus_recibo);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/gastos?${params.toString()}`);
      if (!response.ok) throw new Error('Error al obtener egresos');
      const { data } = await response.json();
      const list = Array.isArray(data) ? data : [];
      if (list.length === 0) {
        setError('No hay egresos para el mes seleccionado. Elige en los filtros un mes y año que tenga movimientos (ej. el mes que ves en la tabla), pulsa Filtrar y luego Exportar Excel (mes).');
        return;
      }
      const headers = Object.keys(list[0]);
      const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = list.map((row) => headers.map((h) => escape(row[h])).join(','));
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `egresos_${año}-${String(mes).padStart(2, '0')}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      setSuccess(`Exportados ${list.length} egresos`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      if (e.message !== 'Sesión expirada' && e.message !== 'No hay sesión activa') setError('Error al exportar egresos');
    }
  };

  // Exportar visitas manuales del mes a CSV (fetch sin limit: el estado solo tiene la página visible)
  const exportarVisitasManualesCSV = async () => {
    let listaExport = [];
    try {
      const params = new URLSearchParams();
      params.append('mes', String(filtroVisitaMes));
      params.append('anio', String(filtroVisitaAño));
      if (filtroVisitaEstado) params.append('estado', filtroVisitaEstado);
      if (filtroVisitaPropiedad) params.append('propiedad', filtroVisitaPropiedad);
      if (filtroVisitaSoloOficial === '1') params.append('solo_oficial', '1');
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/visitas-manuales?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Error al exportar visitas manuales');
      listaExport = Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error al exportar visitas manuales:', error);
      if (error.message !== 'Sesión expirada') setError('Error al exportar visitas manuales');
      return;
    }
    const metaVal = (m, key) =>
      m && typeof m === 'object' && m[key] != null && m[key] !== '' ? String(m[key]) : '';
    const fotosUrls = (v) => {
      const arr = Array.isArray(v.fotos_oficiales) ? v.fotos_oficiales : [];
      return arr.map((p) => getFotoFullUrl(p)).filter(Boolean).join(' | ');
    };
    const headers = [
      'ID bitácora',
      'ID pase',
      'Visitante',
      'Residente',
      'Propiedad',
      'Resultado',
      'Motivo denegación',
      'Fecha ingreso',
      'Fecha salida',
      'Con auto',
      'Ocupa estacionamiento',
      'Pasajeros',
      'Personas',
      'Número cono',
      'Color cono',
      'Marca vehículo',
      'Color vehículo',
      'Placas',
      'Guardia',
      'Visita oficial',
      'Tipo autoridad',
      'Institución',
      'Funcionario',
      'Cargo',
      'Tipo documento',
      'Nº expediente/oficio',
      'Fecha documento',
      'Resumen motivo',
      'Acompañamiento admin',
      'Quien recibe',
      'URLs fotos documentos',
    ];
    const rows = listaExport.map((v) => {
      const m = v.visita_oficial_meta;
      return [
        v.id,
        v.pase_id,
        v.visitante_nombre || '',
        v.residente_nombre || '',
        v.propiedad || v.residente_propiedad || '',
        v.resultado || '',
        v.razon_denegacion || '',
        v.fecha_ingreso ? formatFechaHoraMexicoCentral(v.fecha_ingreso) : '',
        v.fecha_salida ? formatFechaHoraMexicoCentral(v.fecha_salida) : '',
        v.con_auto ? 'Sí' : 'No',
        v.ocupa_estacionamiento === false ? 'No' : 'Sí',
        v.numero_pasajeros ?? '',
        v.numero_personas ?? '',
        v.numero_cono || '',
        v.color_cono || '',
        v.vehiculo_marca || '',
        v.vehiculo_color || '',
        v.vehiculo_placas || '',
        v.guardia_nombre || '',
        v.visita_oficial ? 'Sí' : 'No',
        metaVal(m, 'tipo_autoridad'),
        metaVal(m, 'institucion'),
        metaVal(m, 'funcionario_nombre'),
        metaVal(m, 'funcionario_cargo'),
        metaVal(m, 'tipo_documento'),
        metaVal(m, 'numero_expediente'),
        metaVal(m, 'fecha_documento'),
        metaVal(m, 'resumen_motivo'),
        metaVal(m, 'acompanamiento_admin'),
        metaVal(m, 'nombre_quien_recibe'),
        fotosUrls(v),
      ];
    });
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `visitas_manuales_${filtroVisitaAño}-${String(filtroVisitaMes).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Cargar categorías de servicios
  const loadCategoriasServicios = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/categorias-servicios`);
      const data = await response.json();
      setCategoriasServicios(data);
    } catch (error) {
      console.error('Error al cargar categorías de proveedores:', error);
      setError('Error al cargar categorías de proveedores');
    }
  };

  // Cargar nombres de servicios
  const loadNombresServicios = async (categoriaId = null) => {
    try {
      const url = categoriaId 
        ? `${API_BASE_URL}/admin/nombres-servicios?categoria_id=${categoriaId}`
        : `${API_BASE_URL}/admin/nombres-servicios`;
      const response = await fetchWithAuth(url);
      const data = await response.json();
      setNombresServicios(data);
    } catch (error) {
      console.error('Error al cargar nombres de proveedores:', error);
      setError('Error al cargar nombres de proveedores');
    }
  };

  // Crear categoría
  const handleCrearCategoria = async () => {
    try {
      if (!nuevaCategoriaNombre.trim()) {
        setError('El nombre de la categoría es requerido');
        return;
      }
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/categorias-servicios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          nombre: nuevaCategoriaNombre.trim(),
          hora_inicio: nuevaCategoriaHoraInicio || null,
          hora_fin: nuevaCategoriaHoraFin || null,
          aplica_dias_festivos: nuevaCategoriaAplicaDiasFestivos
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al crear categoría');
        return;
      }
      setSuccess('Categoría creada correctamente');
      setNuevaCategoriaNombre('');
      setNuevaCategoriaHoraInicio('');
      setNuevaCategoriaHoraFin('');
      setNuevaCategoriaAplicaDiasFestivos(false);
      setShowDialogCategoria(false);
      loadCategoriasServicios();
    } catch (error) {
      console.error('Error al crear categoría:', error);
      setError('Error al crear categoría');
    }
  };

  // Actualizar categoría
  const handleActualizarCategoria = async () => {
    try {
      if (!categoriaEditando || !nuevaCategoriaNombre.trim()) {
        setError('El nombre de la categoría es requerido');
        return;
      }
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/categorias-servicios/${categoriaEditando.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          nombre: nuevaCategoriaNombre.trim(), 
          activo: categoriaEditando.activo,
          hora_inicio: nuevaCategoriaHoraInicio || null,
          hora_fin: nuevaCategoriaHoraFin || null,
          aplica_dias_festivos: nuevaCategoriaAplicaDiasFestivos
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al actualizar categoría');
        return;
      }
      setSuccess('Categoría actualizada correctamente');
      setCategoriaEditando(null);
      setNuevaCategoriaNombre('');
      setNuevaCategoriaHoraInicio('');
      setNuevaCategoriaHoraFin('');
      setNuevaCategoriaAplicaDiasFestivos(false);
      setShowDialogCategoria(false);
      loadCategoriasServicios();
    } catch (error) {
      console.error('Error al actualizar categoría:', error);
      setError('Error al actualizar categoría');
    }
  };

  // Eliminar categoría
  const handleEliminarCategoria = async (id) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/categorias-servicios/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al eliminar categoría');
        return;
      }
      setSuccess('Categoría eliminada correctamente');
      loadCategoriasServicios();
      loadNombresServicios();
    } catch (error) {
      console.error('Error al eliminar categoría:', error);
      setError('Error al eliminar categoría');
    }
  };

  // Crear nombre de servicio
  const handleCrearNombreServicio = async () => {
    try {
      if (!nuevoNombreServicio.trim() || !nuevoNombreCategoriaId) {
        setError('El nombre y la categoría son requeridos');
        return;
      }
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/nombres-servicios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          nombre: nuevoNombreServicio.trim(),
          categoria_id: parseInt(nuevoNombreCategoriaId),
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al crear nombre de servicio');
        return;
      }
      setSuccess('Nombre de servicio creado correctamente');
      setNuevoNombreServicio('');
      setNuevoNombreCategoriaId('');
      setShowDialogNombre(false);
      loadNombresServicios(categoriaSeleccionadaFiltro || null);
    } catch (error) {
      console.error('Error al crear nombre de servicio:', error);
      setError('Error al crear nombre de servicio');
    }
  };

  // Actualizar nombre de servicio
  const handleActualizarNombreServicio = async () => {
    try {
      if (!nombreEditando || !nuevoNombreServicio.trim()) {
        setError('El nombre es requerido');
        return;
      }
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/nombres-servicios/${nombreEditando.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre: nuevoNombreServicio.trim(), activo: nombreEditando.activo }),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al actualizar nombre de servicio');
        return;
      }
      setSuccess('Nombre de servicio actualizado correctamente');
      setNombreEditando(null);
      setNuevoNombreServicio('');
      setNuevoNombreCategoriaId('');
      setShowDialogNombre(false);
      loadNombresServicios(categoriaSeleccionadaFiltro || null);
    } catch (error) {
      console.error('Error al actualizar nombre de servicio:', error);
      setError('Error al actualizar nombre de servicio');
    }
  };

  // Eliminar nombre de servicio
  const handleEliminarNombreServicio = async (id) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/nombres-servicios/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al eliminar nombre de servicio');
        return;
      }
      setSuccess('Nombre de servicio eliminado correctamente');
      loadNombresServicios(categoriaSeleccionadaFiltro || null);
    } catch (error) {
      console.error('Error al eliminar nombre de servicio:', error);
      setError('Error al eliminar nombre de servicio');
    }
  };

  // ========== FUNCIONES PARA COBRANZA ==========
  const loadConceptosPago = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/conceptos-pago`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Error al cargar conceptos');
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setConceptosPago(list);
    } catch (e) {
      setConceptosPago([]);
      if (e.message !== 'Sesión expirada') setError(e.message || 'Error al cargar conceptos de pago');
    }
  };
  const loadTablaCobranza = async () => {
    try {
      const params = new URLSearchParams({ ano: anoCobranza, limit: tablaCobranzaPageSize, offset: tablaCobranzaPagina * tablaCobranzaPageSize });
      if (conceptoPagoSeleccionado) params.append('concepto_pago_id', conceptoPagoSeleccionado);
      if (filtroPropiedadCobranza.trim()) params.append('filtro_propiedad', filtroPropiedadCobranza.trim());
      if (filtroDesdeCobranza.trim()) params.append('filtro_desde', filtroDesdeCobranza.trim());
      if (filtroHastaCobranza.trim()) params.append('filtro_hasta', filtroHastaCobranza.trim());
      if (filtroEstatusCobranza && filtroEstatusCobranza !== 'todos') params.append('filtro_estatus', filtroEstatusCobranza);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/tabla-cobranza?${params}`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Error al cargar tabla');
      const data = await response.json();
      setTablaCobranzaData(data);
    } catch (e) {
      setTablaCobranzaData({ propiedades: [], meses: [], celdas: {}, concepto: null, total_propiedades: 0 });
      if (e.message !== 'Sesión expirada') setError(e.message || 'Error al cargar tabla de cobranza');
    }
  };
  const loadMorosidad = async () => {
    try {
      const params = new URLSearchParams({ limit: MOROSIDAD_FETCH_LIMIT, offset: 0 });
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/morosidad?${params}`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Error al cargar morosidad');
      const data = await response.json();
      setMorosidadData(data);
      setMorosidadPagina(0);
    } catch (e) {
      setMorosidadData({ en_mora: [], al_corriente: [], total_en_mora: 0, total_al_corriente: 0 });
      if (e.message !== 'Sesión expirada') setError(e.message || 'Error al cargar morosidad');
    }
  };
  // Default concepto a "Cuota mensual" cuando se cargan los conceptos (solo si aún no hay selección)
  useEffect(() => {
    if (conceptosPago.length > 0 && !conceptoPagoSeleccionado) {
      const cuota = conceptosPago.find(c => (c.nombre || '').toLowerCase().includes('cuota mensual')) || conceptosPago.find(c => c.tipo === 'mensual');
      if (cuota) setConceptoPagoSeleccionado(String(cuota.id));
    }
  }, [conceptosPago, conceptoPagoSeleccionado]);
  // Al cambiar año, concepto o filtros en Tabla de cobranza, recargar la tabla
  useEffect(() => {
    if (esAdminCondominio && tabActual === 3 && subTabCobranza === 0) loadTablaCobranza();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- omitir loadTablaCobranza para evitar re-ejecuciones
  }, [subTabCobranza, conceptoPagoSeleccionado, anoCobranza, tablaCobranzaPagina, tablaCobranzaPageSize, filtroPropiedadCobranza, filtroDesdeCobranza, filtroHastaCobranza, filtroEstatusCobranza, esAdminCondominio, tabActual]);
  // Al cambiar pestaña a Morosidad, recargar (filtros y paginación son client-side)
  useEffect(() => {
    if (esAdminCondominio && tabActual === 3 && subTabCobranza === 1) loadMorosidad();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTabCobranza, esAdminCondominio, tabActual]);
  const handleSaveConceptoPago = async () => {
    try {
      const url = conceptoPagoEditando
        ? `${API_BASE_URL}/admin/conceptos-pago/${conceptoPagoEditando.id}`
        : `${API_BASE_URL}/admin/conceptos-pago`;
      const method = conceptoPagoEditando ? 'PUT' : 'POST';
      const body = {
        nombre: formConceptoPago.nombre,
        tipo: formConceptoPago.tipo,
        monto: parseFloat(formConceptoPago.monto) || 0,
        recargo_morosidad_tipo: formConceptoPago.recargo_morosidad_tipo,
        recargo_morosidad_valor: parseFloat(formConceptoPago.recargo_morosidad_valor) || 0,
        tipo_fecha_limite: formConceptoPago.tipo === 'anual' ? 'fecha_especifica' : (formConceptoPago.tipo_fecha_limite || 'dia_mes'),
        dia_limite_pago: formConceptoPago.tipo !== 'anual' ? (parseInt(formConceptoPago.dia_limite_pago, 10) || 10) : (formConceptoPago.tipo_fecha_limite === 'dia_mes' ? parseInt(formConceptoPago.dia_limite_pago, 10) || 10 : null),
        fecha_limite: (formConceptoPago.tipo === 'anual' || formConceptoPago.tipo_fecha_limite === 'fecha_especifica') ? (formConceptoPago.fecha_limite || null) : null,
        mes_inicio_periodo: ['bimestral', 'trimestral', 'semestral', 'anual'].includes(formConceptoPago.tipo) ? (parseInt(formConceptoPago.mes_inicio_periodo, 10) || 1) : null,
        activo: formConceptoPago.activo,
        vigencia_desde: formConceptoPago.vigencia_desde?.trim() ? formConceptoPago.vigencia_desde.trim().slice(0, 10) : null,
        vigencia_hasta: formConceptoPago.vigencia_hasta?.trim() ? formConceptoPago.vigencia_hasta.trim().slice(0, 10) : null,
      };
      const response = await fetchWithAuth(url, { method, body: JSON.stringify(body) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || 'Error al guardar concepto');
        return;
      }
      const warnings = Array.isArray(data.warnings) ? data.warnings : [];
      let msg = conceptoPagoEditando ? 'Concepto actualizado' : 'Concepto creado';
      if (warnings.length) {
        msg += '. ' + warnings.map((w) => (typeof w === 'string' ? w : w.message)).filter(Boolean).join(' ');
      }
      setSuccess(msg);
      setShowDialogConceptoPago(false);
      setConceptoPagoEditando(null);
      setFormConceptoPago({ nombre: '', tipo: 'mensual', monto: '', recargo_morosidad_tipo: 'porcentaje', recargo_morosidad_valor: '', tipo_fecha_limite: 'dia_mes', dia_limite_pago: 10, fecha_limite: '', mes_inicio_periodo: 1, activo: true, vigencia_desde: '', vigencia_hasta: '' });
      loadConceptosPago();
    } catch (e) {
      if (e.message !== 'Sesión expirada') setError(e.message || 'Error al guardar concepto');
    }
  };
  const handleAplicarBloqueoMorosidad = async () => {
    if (!window.confirm('¿Aplicar bloqueo a todos los residentes con adeudos vencidos? Esto bloqueará el acceso hasta que regularicen.')) return;
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/aplicar-bloqueo-morosidad`, { method: 'POST' });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al aplicar bloqueo');
        return;
      }
      const data = await response.json();
      setSuccess(data.message || `Bloqueados: ${data.bloqueados || 0}`);
      loadMorosidad();
      loadResidentes(getAuthToken());
    } catch (e) {
      if (e.message !== 'Sesión expirada') setError(e.message || 'Error al aplicar bloqueo');
    }
  };

  // ========== FUNCIONES PARA PAGOS ==========
  const loadPagos = async (pagina = null, registrosPorPaginaOverride = null) => {
    try {
      const paginaActual = pagina !== null ? pagina : pagosPaginacion.pagina;
      const pageSize = registrosPorPaginaOverride ?? pagosPaginacion.registrosPorPagina;
      const offset = paginaActual * pageSize;
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: offset.toString()
      });
      
      // Agregar filtros
      if (filtrosIngresos.fechaInicio) {
        params.append('fecha_inicio', filtrosIngresos.fechaInicio);
      }
      if (filtrosIngresos.fechaFin) {
        params.append('fecha_fin', filtrosIngresos.fechaFin);
      }
      if (filtrosIngresos.mes) {
        params.append('mes', filtrosIngresos.mes);
      }
      if (filtrosIngresos.año) {
        params.append('año', filtrosIngresos.año);
      }
      if (filtrosIngresos.propiedad) {
        params.append('propiedad', filtrosIngresos.propiedad);
      }
      if (filtrosIngresos.estatus_recibo === 'vigente' || filtrosIngresos.estatus_recibo === 'cancelado') {
        params.append('estatus_recibo', filtrosIngresos.estatus_recibo);
      }
      
      const url = `${API_BASE_URL}/admin/pagos?${params.toString()}`;
      
      console.log('🔍 Cargando ingresos desde:', url);
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error al cargar ingresos:', response.status, errorText);
        setError('Error al cargar ingresos');
        return;
      }
      const data = await response.json();
      console.log('✅ Ingresos recibidos:', data.data?.length || 0, 'registros de', data.total, 'total');
      setPagos(data.data || []);
      setPagosPaginacion(prev => ({
        ...prev,
        total: data.total || 0,
        pagina: paginaActual
      }));
    } catch (error) {
      console.error('❌ Error al cargar ingresos:', error);
      setError('Error al cargar ingresos');
    }
  };

  const guardarCapitalInicialIngresos = async () => {
    const raw = String(capitalInicialEdit ?? '').trim().replace(',', '.');
    const v = raw === '' ? 0 : parseFloat(raw);
    if (Number.isNaN(v)) {
      setError('Capital inicial debe ser un número válido');
      return;
    }
    setCapitalInicialGuardando(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/condominio/capital-inicial`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capital_inicial: v }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || 'Error al guardar capital inicial');
        return;
      }
      setSuccess(data.message || 'Capital inicial guardado');
      setCapitalInicialEdit(String(v));
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError(error.message || 'Error al guardar capital inicial');
      }
    } finally {
      setCapitalInicialGuardando(false);
    }
  };

  const invalidarSnapshotCapitalDisponible = async () => {
    setCapitalSnapshotInvalidando(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/admin/condominio/capital-disponible/invalidar-snapshot`,
        { method: 'POST', body: JSON.stringify({}) }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || 'Error al invalidar snapshot');
        return;
      }
      setSuccess(data.message || 'Listo: el capital se recalculará al abrir gráficos en la app.');
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError(error.message || 'Error al invalidar snapshot');
      }
    } finally {
      setCapitalSnapshotInvalidando(false);
    }
  };

  const handleCrearPago = async () => {
    try {
      if (!formPago.fecha || !formPago.propiedad || !formPago.monto) {
        setError('Fecha, propiedad y monto son requeridos');
        return;
      }
      setCreandoPago(true);
      const body = { ...formPago };
      if (body.concepto_pago_id === '' || body.concepto_pago_id == null) delete body.concepto_pago_id;
      else body.concepto_pago_id = parseInt(body.concepto_pago_id, 10);
      if (pagoDesdeCobranza && cobranzaResumenPeriodo) {
        body.recargos = '0';
      }
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/pagos`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al crear pago');
        return;
      }
      setSuccess('Pago creado correctamente');
      setShowDialogPago(false);
      setCobranzaResumenPeriodo(null);
      if (pagoDesdeCobranza) {
        setPagoDesdeCobranza(false);
        loadTablaCobranza();
      }
      loadPagos();
    } catch (error) {
      console.error('Error al crear pago:', error);
      setError('Error al crear pago');
    } finally {
      setCreandoPago(false);
    }
  };

  const handleActualizarPago = async () => {
    try {
      const body = { ...formPago };
      if (body.concepto_pago_id === '' || body.concepto_pago_id == null) delete body.concepto_pago_id;
      else body.concepto_pago_id = parseInt(body.concepto_pago_id, 10);
      if (body.estatus_recibo !== 'vigente' && body.estatus_recibo !== 'cancelado') body.estatus_recibo = 'vigente';
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/pagos/${pagoEditando.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al actualizar pago');
        return;
      }
      setSuccess('Pago actualizado correctamente');
      setShowDialogPago(false);
      setCobranzaResumenPeriodo(null);
      if (pagoDesdeCobranza) {
        setPagoDesdeCobranza(false);
        loadTablaCobranza();
      }
      loadPagos();
    } catch (error) {
      console.error('Error al actualizar pago:', error);
      setError('Error al actualizar pago');
    }
  };

  const handleCambiarEstatusPago = async (pago, nuevoEstatus) => {
    if (nuevoEstatus !== 'vigente' && nuevoEstatus !== 'cancelado') return;
    try {
      const body = {
        fecha: pago.fecha ? (typeof pago.fecha === 'string' ? pago.fecha.split('T')[0] : new Date(pago.fecha).toISOString().split('T')[0]) : '',
        mes: pago.mes || '',
        propiedad: pago.propiedad || '',
        folio: pago.folio || '',
        concepto: pago.concepto || '',
        categoria_concepto: pago.categoria_concepto || '',
        descripcion: pago.descripcion || '',
        monto: pago.monto,
        recargos: pago.recargos || '',
        depositado: pago.depositado || false,
        estatus_recibo: nuevoEstatus,
      };
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/pagos/${pago.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al cambiar estatus');
        return;
      }
      setSuccess(nuevoEstatus === 'cancelado' ? 'Ingreso cancelado' : 'Ingreso reactivado');
      setTimeout(() => setSuccess(''), 2000);
      loadPagos(pagosPaginacion.pagina, pagosPaginacion.registrosPorPagina);
    } catch (error) {
      console.error('Error al cambiar estatus del pago:', error);
      setError('Error al cambiar estatus');
    }
  };

  const loadDestinatariosRecibo = async (propiedad) => {
    if (!propiedad || !String(propiedad).trim()) {
      setDestinatariosRecibo(null);
      return null;
    }
    setLoadingDestinatarios(true);
    setDestinatariosRecibo(null);
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/recibos/destinatarios?propiedad=${encodeURIComponent(propiedad)}`);
      if (!res.ok) throw new Error('Error al cargar destinatarios');
      const data = await res.json();
      setDestinatariosRecibo(data);
      return data;
    } catch (e) {
      console.error(e);
      setError('Error al cargar correos para recibos');
      setDestinatariosRecibo(null);
      return null;
    } finally {
      setLoadingDestinatarios(false);
    }
  };

  const handleAbrirModalEnviarReciboIngreso = async (pago) => {
    const prop = pago?.propiedad ? String(pago.propiedad) : '';
    setEnviarReciboIngresoId(pago?.id ?? null);
    setPropiedadDestinatarios(prop);
    setNuevoEmailRecibo('');
    setEmailsSeleccionadosRecibo([]);
    setOpenModalEnviarReciboIngreso(true);
    if (prop) {
      const data = await loadDestinatariosRecibo(prop);
      setEmailsSeleccionadosRecibo(Array.isArray(data?.todos) ? data.todos : []);
    }
  };

  const handleEnviarReciboIngresoSeleccionados = async () => {
    if (!enviarReciboIngresoId) return;
    const emails = (emailsSeleccionadosRecibo || []).map((e) => String(e || '').trim()).filter(Boolean);
    if (emails.length === 0) {
      alert('Seleccione al menos un correo.');
      return;
    }
    setEnviandoReciboIngreso(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/admin/recibos/ingreso/${enviarReciboIngresoId}/reenviar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emails })
      });
      if (response.status === 401) {
        handleAuthError(response);
        return;
      }
      const data = await response.json();
      if (response.ok) {
        alert(`Recibo enviado a ${data.enviados} destinatario(s)`);
        setOpenModalEnviarReciboIngreso(false);
        setEnviarReciboIngresoId(null);
      } else {
        alert(data.error || 'Error al enviar el recibo');
      }
    } catch (error) {
      console.error('Error al enviar recibo:', error);
      alert('Error al enviar el recibo');
    } finally {
      setEnviandoReciboIngreso(false);
    }
  };

  const handleAgregarEmailRecibo = async () => {
    const email = (nuevoEmailRecibo || '').trim();
    if (!email || !propiedadDestinatarios) return;
    if (!email.includes('@')) {
      setError('Ingrese un correo válido');
      return;
    }
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/recibos/destinatarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ propiedad: propiedadDestinatarios, email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al agregar');
      setNuevoEmailRecibo('');
      setSuccess('Correo agregado para recibos');
      setTimeout(() => setSuccess(''), 2000);
      loadDestinatariosRecibo(propiedadDestinatarios);
    } catch (e) {
      setError(e.message || 'Error al agregar correo');
    }
  };

  const handleQuitarEmailRecibo = async (id) => {
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/recibos/destinatarios/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Error al quitar');
      setSuccess('Correo quitado de la lista');
      setTimeout(() => setSuccess(''), 2000);
      loadDestinatariosRecibo(propiedadDestinatarios);
    } catch (e) {
      setError(e.message || 'Error al quitar correo');
    }
  };

  const loadDestinatariosEgresos = async () => {
    setLoadingDestinatariosEgresos(true);
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/recibos/egresos-destinatarios`);
      if (!res.ok) throw new Error('Error al cargar correos de egresos');
      const data = await res.json();
      const list = data.correos || [];
      setDestinatariosEgresos(list);
      return list;
    } catch (e) {
      console.error(e);
      setError('Error al cargar correos de egresos');
      setDestinatariosEgresos(null);
      return null;
    } finally {
      setLoadingDestinatariosEgresos(false);
    }
  };

  const handleAbrirModalEnviarReciboEgreso = async (gasto) => {
    setEnviarReciboEgresoId(gasto?.id ?? null);
    setNuevoEmailEgreso('');
    setEmailsSeleccionadosEgreso([]);
    setOpenModalEnviarReciboEgreso(true);
    const list = await loadDestinatariosEgresos();
    const emails = Array.isArray(list) ? list.map((x) => x.email).filter(Boolean) : [];
    setEmailsSeleccionadosEgreso(emails);
  };

  const handleEnviarReciboEgresoSeleccionados = async () => {
    if (!enviarReciboEgresoId) return;
    const emails = (emailsSeleccionadosEgreso || []).map((e) => String(e || '').trim()).filter(Boolean);
    if (emails.length === 0) {
      alert('Seleccione al menos un correo.');
      return;
    }
    setEnviandoReciboEgreso(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/admin/recibos/egreso/${enviarReciboEgresoId}/reenviar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emails })
      });
      if (response.status === 401) {
        handleAuthError(response);
        return;
      }
      const data = await response.json();
      if (response.ok) {
        alert(`Recibo enviado a ${data.enviados} destinatario(s)`);
        setOpenModalEnviarReciboEgreso(false);
        setEnviarReciboEgresoId(null);
      } else {
        alert(data.error || 'Error al enviar el recibo');
      }
    } catch (error) {
      console.error('Error al enviar recibo egreso:', error);
      alert('Error al enviar el recibo');
    } finally {
      setEnviandoReciboEgreso(false);
    }
  };

  const handleEnviarReciboEgresoTodasLasCasas = async () => {
    if (!enviarReciboEgresoId) return;
    setEnviandoReciboEgreso(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/admin/recibos/egreso/${enviarReciboEgresoId}/reenviar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      if (response.status === 401) {
        handleAuthError(response);
        return;
      }
      const data = await response.json();
      if (response.ok) {
        alert(`Recibo enviado a ${data.enviados} destinatario(s)`);
        setOpenModalEnviarReciboEgreso(false);
        setEnviarReciboEgresoId(null);
      } else {
        alert(data.error || 'Error al enviar el recibo');
      }
    } catch (error) {
      console.error('Error al enviar recibo egreso:', error);
      alert('Error al enviar el recibo');
    } finally {
      setEnviandoReciboEgreso(false);
    }
  };

  const handleAgregarEmailEgreso = async () => {
    const email = (nuevoEmailEgreso || '').trim();
    if (!email) return;
    if (!email.includes('@')) {
      setError('Ingrese un correo válido para egresos');
      return;
    }
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/recibos/egresos-destinatarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al agregar correo de egresos');
      setNuevoEmailEgreso('');
      setSuccess('Correo agregado para recibos de egresos');
      setTimeout(() => setSuccess(''), 2000);
      loadDestinatariosEgresos();
    } catch (e) {
      setError(e.message || 'Error al agregar correo de egresos');
    }
  };

  const handleQuitarEmailEgreso = async (id) => {
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/recibos/egresos-destinatarios/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Error al quitar correo de egresos');
      setSuccess('Correo de egresos eliminado');
      setTimeout(() => setSuccess(''), 2000);
      loadDestinatariosEgresos();
    } catch (e) {
      setError(e.message || 'Error al quitar correo de egresos');
    }
  };

  // Cargar correos de egresos al entrar a la pestaña Egresos
  useEffect(() => {
    if (tabActual === 5) {
      loadDestinatariosEgresos();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabActual]);

  const handlePagarVariosPeriodos = async () => {
    if (!propiedadPagarVariosPeriodos || !conceptoPagoSeleccionado || !formPagarVariosPeriodos.fecha) {
      setError('Propiedad, concepto y fecha son requeridos');
      return;
    }
    const pd = (formPagarVariosPeriodos.periodo_desde || '').trim();
    const ph = (formPagarVariosPeriodos.periodo_hasta || '').trim();
    if (!pd || !ph) {
      setError('Indique periodo desde y hasta (mismo formato que en la tabla, ej. 2026-01 o 2026-B1)');
      return;
    }
    setCreandoPagoVariosPeriodos(true);
    setError('');
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/pagos/pagar-rango-periodos`, {
        method: 'POST',
        body: JSON.stringify({
          propiedad: propiedadPagarVariosPeriodos,
          concepto_pago_id: conceptoPagoSeleccionado,
          periodo_desde: pd,
          periodo_hasta: ph,
          fecha: formPagarVariosPeriodos.fecha,
          depositado: formPagarVariosPeriodos.depositado,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al registrar pagos del rango');
        return;
      }
      const creados = data.creados ?? 0;
      setSuccess(
        creados > 0
          ? `Se registraron ${creados} pago(s) (un recibo por periodo).`
          : 'No se registraron pagos; revise el rango y los periodos pendientes.'
      );
      setShowDialogPagarVariosPeriodos(false);
      setPropiedadPagarVariosPeriodos('');
      loadTablaCobranza();
      loadPagos();
    } catch (error) {
      console.error('Error pagar varios periodos:', error);
      setError('Error al registrar pagos del rango');
    } finally {
      setCreandoPagoVariosPeriodos(false);
    }
  };

  useEffect(() => {
    if (!showDialogPagarVariosPeriodos || !propiedadPagarVariosPeriodos || !conceptoPagoSeleccionado) {
      setPreviewVariosRango(null);
      setPreviewVariosRangoErr('');
      setLoadingPreviewVariosRango(false);
      return undefined;
    }
    const pd = (formPagarVariosPeriodos.periodo_desde || '').trim();
    const ph = (formPagarVariosPeriodos.periodo_hasta || '').trim();
    const fechaPrev = formPagarVariosPeriodos.fecha;
    if (!pd || !ph || !fechaPrev) {
      setPreviewVariosRango(null);
      setPreviewVariosRangoErr('');
      return undefined;
    }
    const t = setTimeout(async () => {
      setLoadingPreviewVariosRango(true);
      setPreviewVariosRangoErr('');
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/pagos/preview-rango-periodos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            propiedad: propiedadPagarVariosPeriodos,
            concepto_pago_id: conceptoPagoSeleccionado,
            periodo_desde: pd,
            periodo_hasta: ph,
            fecha: fechaPrev,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          setPreviewVariosRango(null);
          setPreviewVariosRangoErr(data.error || 'No se pudo calcular la vista previa');
          return;
        }
        setPreviewVariosRango(data);
      } catch {
        setPreviewVariosRango(null);
        setPreviewVariosRangoErr('Error al consultar la vista previa');
      } finally {
        setLoadingPreviewVariosRango(false);
      }
    }, 480);
    return () => clearTimeout(t);
  }, [
    showDialogPagarVariosPeriodos,
    propiedadPagarVariosPeriodos,
    conceptoPagoSeleccionado,
    formPagarVariosPeriodos.periodo_desde,
    formPagarVariosPeriodos.periodo_hasta,
    formPagarVariosPeriodos.fecha,
    fetchWithAuth,
  ]);

  const handleEliminarPago = async (id, todoElAno = false) => {
    try {
      const url = `${API_BASE_URL}/admin/pagos/${id}${todoElAno ? '?todo_el_ano=1' : ''}`;
      const response = await fetchWithAuth(url, { method: 'DELETE' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || 'Error al revertir pago');
        return;
      }
      setSuccess(data.message || 'Pago revertido correctamente');
      setShowDialogRevertirPago(false);
      setPagoARevertir(null);
      loadPagos();
      loadTablaCobranza();
    } catch (error) {
      console.error('Error al revertir pago:', error);
      setError('Error al revertir pago');
    }
  };

  const handleCargarExcelPagos = async (file) => {
    if (!file) return;
    setLoadingCargaExcelPagos(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('excel', file);
      formData.append('eliminarTodo', eliminarTodoPagos);
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/admin/pagos/cargar-excel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      if (response.status === 401) {
        handleAuthError(response);
        return;
      }
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          setError(data.error || 'Error al cargar Excel');
        } else {
          const errorText = await response.text();
          console.error('Error al cargar Excel:', response.status, errorText);
          setError(`Error al cargar Excel: ${response.status} ${response.statusText}`);
        }
        return;
      }
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        setExcelResultado({
          exitosos: data.exitosos,
          fallidos: data.fallidos,
          total: data.exitosos + data.fallidos,
          filasEnArchivo: data.filasEnArchivo,
          recibosInsertados: data.recibosInsertados ?? data.exitosos,
          nota: data.nota,
        });
        if (data.fallidos > 0 && data.errores && data.errores.length > 0) {
          setExcelErrores(data.errores);
          setShowErroresDialog(true);
          setSuccess(`⚠️ Carga de ingresos finalizada: ${data.exitosos} recibos insertados, ${data.fallidos} fallidos. Revisa la ventana de errores.`);
        } else {
          setSuccess(`✅ Carga de ingresos finalizada: ${data.exitosos} recibos nuevos (cada fila es un id distinto; el folio puede repetirse).`);
        }
        loadPagos();
      } else {
        setError('Respuesta inválida del servidor');
      }
    } catch (error) {
      console.error('Error al cargar Excel de pagos:', error);
      setError('Error al cargar Excel');
    } finally {
      setLoadingCargaExcelPagos(false);
    }
  };

  // ========== FUNCIONES PARA GASTOS ==========
  const loadGastos = async (pagina = null, registrosPorPaginaOverride = null) => {
    try {
      const paginaActual = pagina !== null ? pagina : gastosPaginacion.pagina;
      const pageSize = registrosPorPaginaOverride ?? gastosPaginacion.registrosPorPagina;
      const offset = paginaActual * pageSize;
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: offset.toString()
      });
      
      // Agregar filtros
      if (filtrosEgresos.fechaInicio) {
        params.append('fecha_inicio', filtrosEgresos.fechaInicio);
      }
      if (filtrosEgresos.fechaFin) {
        params.append('fecha_fin', filtrosEgresos.fechaFin);
      }
      if (filtrosEgresos.mes) {
        params.append('mes', filtrosEgresos.mes);
      }
      if (filtrosEgresos.año) {
        params.append('año', filtrosEgresos.año);
      }
      if (filtrosEgresos.estatus_recibo === 'vigente' || filtrosEgresos.estatus_recibo === 'cancelado') {
        params.append('estatus_recibo', filtrosEgresos.estatus_recibo);
      }
      
      const url = `${API_BASE_URL}/admin/gastos?${params.toString()}`;
      
      console.log('🔍 Cargando egresos desde:', url);
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error al cargar egresos:', response.status, errorText);
        setError('Error al cargar egresos');
        return;
      }
      const data = await response.json();
      console.log('✅ Egresos recibidos:', data.data?.length || 0, 'registros de', data.total, 'total');
      setGastos(data.data || []);
      setGastosPaginacion(prev => ({
        ...prev,
        total: data.total || 0,
        pagina: paginaActual
      }));
    } catch (error) {
      console.error('Error al cargar egresos:', error);
      setError('Error al cargar egresos');
    }
  };

  const handleCrearGasto = async () => {
    try {
      if (!formGasto.fecha || !formGasto.concepto || !formGasto.total) {
        setError('Fecha, concepto y total son requeridos');
        return;
      }
      const metodo = (formGasto.metodo_pago || '').trim().toLowerCase();
      if (!['efectivo', 'tarjeta', 'transferencia'].includes(metodo)) {
        setError('Debe especificar el método de pago: efectivo, tarjeta o transferencia');
        return;
      }
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/gastos`, {
        method: 'POST',
        body: JSON.stringify(formGasto),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al crear gasto');
        return;
      }
      setSuccess('Gasto creado correctamente');
      setShowDialogGasto(false);
      loadGastos();
    } catch (error) {
      console.error('Error al crear gasto:', error);
      setError('Error al crear gasto');
    }
  };

  const handleActualizarGasto = async () => {
    try {
      const metodo = (formGasto.metodo_pago || '').trim().toLowerCase();
      if (!['efectivo', 'tarjeta', 'transferencia'].includes(metodo)) {
        setError('Debe especificar el método de pago: efectivo, tarjeta o transferencia');
        return;
      }
      const body = { ...formGasto };
      if (body.estatus_recibo !== 'vigente' && body.estatus_recibo !== 'cancelado') body.estatus_recibo = 'vigente';
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/gastos/${gastoEditando.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al actualizar gasto');
        return;
      }
      setSuccess('Gasto actualizado correctamente');
      setShowDialogGasto(false);
      loadGastos();
    } catch (error) {
      console.error('Error al actualizar gasto:', error);
      setError('Error al actualizar gasto');
    }
  };

  const handleCambiarEstatusGasto = async (gasto, nuevoEstatus) => {
    if (nuevoEstatus !== 'vigente' && nuevoEstatus !== 'cancelado') return;
    try {
      const body = {
        fecha: gasto.fecha ? gasto.fecha.split('T')[0] : '',
        mes: gasto.mes || '',
        concepto: gasto.concepto || '',
        tesoreria: gasto.tesoreria || '',
        condicion: gasto.condicion || '',
        subtotal: gasto.subtotal,
        iva: gasto.iva,
        total: gasto.total,
        metodo_pago: gasto.metodo_pago || 'transferencia',
        estatus_recibo: nuevoEstatus,
      };
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/gastos/${gasto.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al cambiar estatus');
        return;
      }
      setSuccess(nuevoEstatus === 'cancelado' ? 'Egreso cancelado' : 'Egreso reactivado');
      setTimeout(() => setSuccess(''), 2000);
      loadGastos(gastosPaginacion.pagina, gastosPaginacion.registrosPorPagina);
    } catch (error) {
      console.error('Error al cambiar estatus del gasto:', error);
      setError('Error al cambiar estatus');
    }
  };

  const handleEliminarGasto = async (id) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/gastos/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al eliminar gasto');
        return;
      }
      setSuccess('Gasto eliminado correctamente');
      loadGastos();
    } catch (error) {
      console.error('Error al eliminar gasto:', error);
      setError('Error al eliminar gasto');
    }
  };

  const handleCargarExcelGastos = async (file) => {
    if (!file) return;
    setLoadingCargaExcelGastos(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('excel', file);
      formData.append('eliminarTodo', eliminarTodoGastos);
      formData.append('año', String(añoCargaExcelGastos));
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/admin/gastos/cargar-excel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      if (response.status === 401) {
        handleAuthError(response);
        return;
      }
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Error al cargar Excel');
        return;
      }
      const data = await response.json();
      setExcelResultado({
        exitosos: data.exitosos,
        fallidos: data.fallidos,
        total: data.exitosos + data.fallidos
      });
      if (data.fallidos > 0 && data.errores && data.errores.length > 0) {
        setExcelErrores(data.errores);
        setShowErroresDialog(true);
        setSuccess(`⚠️ Carga de egresos finalizada: ${data.exitosos} exitosos, ${data.fallidos} fallidos. Revisa la ventana de errores.`);
      } else {
        setSuccess(`✅ Carga de egresos finalizada: ${data.exitosos} registros procesados correctamente.`);
      }
      loadGastos();
    } catch (error) {
      console.error('Error al cargar Excel de gastos:', error);
      setError('Error al cargar Excel');
    } finally {
      setLoadingCargaExcelGastos(false);
    }
  };

  // ========== FUNCIONES PARA DOCUMENTOS ==========
  const loadDocumentos = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/documentos`);
      const data = await response.json();
      setDocumentos(data);
    } catch (error) {
      console.error('Error al cargar documentos:', error);
      setError('Error al cargar documentos');
    }
  };

  const handleCrearDocumento = async () => {
    try {
      if (!formDocumento.nombre_mostrar || !archivoDocumento) {
        setError('Nombre y archivo son requeridos');
        return;
      }
      const formData = new FormData();
      formData.append('nombre_mostrar', formDocumento.nombre_mostrar);
      formData.append('documento', archivoDocumento);
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/admin/documentos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      if (response.status === 401) {
        handleAuthError(response);
        return;
      }
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al crear documento');
        return;
      }
      setSuccess('Documento creado correctamente');
      setShowDialogDocumento(false);
      setArchivoDocumento(null);
      loadDocumentos();
    } catch (error) {
      console.error('Error al crear documento:', error);
      setError('Error al crear documento');
    }
  };

  const handleActualizarDocumento = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/documentos/${documentoEditando.id}`, {
        method: 'PUT',
        body: JSON.stringify(formDocumento),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al actualizar documento');
        return;
      }
      setSuccess('Documento actualizado correctamente');
      setShowDialogDocumento(false);
      loadDocumentos();
    } catch (error) {
      console.error('Error al actualizar documento:', error);
      setError('Error al actualizar documento');
    }
  };

  const handleEliminarDocumento = async (id) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/documentos/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al eliminar documento');
        return;
      }
      setSuccess('Documento eliminado correctamente');
      loadDocumentos();
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      setError('Error al eliminar documento');
    }
  };

  // ========== FUNCIONES PARA SUGERENCIAS ==========
  const loadSugerencias = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/sugerencias`);
      const data = await response.json();
      setSugerencias(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar sugerencias:', error);
      setError('Error al cargar sugerencias');
      setSugerencias([]);
    }
  };

  const handleMarcarSugerenciaVista = async (id) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/sugerencias/${id}/marcar-vista`, {
        method: 'PUT',
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al marcar sugerencia como vista');
        return;
      }
      setSuccess('Sugerencia marcada como vista');
      loadSugerencias();
    } catch (error) {
      console.error('Error al marcar sugerencia como vista:', error);
      setError('Error al marcar sugerencia como vista');
    }
  };

  const abrirDialogSugerencia = (sug) => {
    setSugerenciaDialog(sug);
    setSugerenciaDialogTexto(sug.respuesta_admin || '');
  };

  const cerrarDialogSugerencia = () => {
    setSugerenciaDialog(null);
    setSugerenciaDialogTexto('');
    setSugerenciaDialogSaving(false);
    setSugerenciaDialogPublishing(false);
  };

  const guardarBorradorRespuestaSugerencia = async () => {
    if (!sugerenciaDialog) return;
    setSugerenciaDialogSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/admin/sugerencias/${sugerenciaDialog.id}/respuesta-borrador`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ respuesta: sugerenciaDialogTexto }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || 'Error al guardar el borrador');
        return;
      }
      setSuccess('Borrador guardado');
      setSugerenciaDialog(data);
      loadSugerencias();
    } catch (error) {
      console.error('Error al guardar borrador:', error);
      setError('Error al guardar el borrador');
    } finally {
      setSugerenciaDialogSaving(false);
    }
  };

  const publicarRespuestaSugerencia = async () => {
    if (!sugerenciaDialog) return;
    if (!window.confirm('Se publicará la respuesta en la app del residente y, si tiene correo, se enviará un email. ¿Continuar?')) {
      return;
    }
    setSugerenciaDialogPublishing(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/admin/sugerencias/${sugerenciaDialog.id}/publicar-respuesta`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ respuesta: sugerenciaDialogTexto.trim() }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || 'Error al publicar la respuesta');
        return;
      }
      if (data.advertencia) {
        setSuccess(data.advertencia);
      } else {
        setSuccess(data.correoEnviado ? 'Respuesta publicada y correo enviado.' : 'Respuesta publicada.');
      }
      cerrarDialogSugerencia();
      loadSugerencias();
    } catch (error) {
      console.error('Error al publicar respuesta:', error);
      setError('Error al publicar la respuesta');
    } finally {
      setSugerenciaDialogPublishing(false);
    }
  };

  const loadSolicitudesEliminacion = async (estadoFiltro) => {
    try {
      const filtro = estadoFiltro !== undefined ? estadoFiltro : solicitudEliminacionFiltro;
      const params = filtro ? `?estado=${filtro}` : '';
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/solicitudes-eliminacion${params}`);
      const data = await response.json();
      setSolicitudesEliminacion(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar solicitudes de eliminación:', error);
      setError('Error al cargar solicitudes de eliminación');
    }
  };

  const loadEncuestas = async () => {
    setErrorEncuestas('');
    try {
      setLoadingEncuestas(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/encuestas`);
      if (!response.ok) throw new Error('Error al cargar encuestas');
      const data = await response.json();
      setEncuestasList(Array.isArray(data) ? data : []);
    } catch (err) {
      setErrorEncuestas(err.message || 'Error al cargar encuestas');
      setEncuestasList([]);
    } finally {
      setLoadingEncuestas(false);
    }
  };

  const handleCerrarEncuesta = async (id) => {
    if (!window.confirm('¿Cerrar esta encuesta? No se podrán enviar más respuestas.')) return;
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/encuestas/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activa: false }),
      });
      if (!res.ok) throw new Error('Error al cerrar');
      loadEncuestas();
      setSuccess('Encuesta cerrada');
    } catch (e) {
      setError(e.message || 'Error al cerrar encuesta');
    }
  };

  const handleEliminarEncuesta = async (id) => {
    if (!window.confirm('¿Eliminar esta encuesta y todas sus respuestas? Esta acción no se puede deshacer.')) return;
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/encuestas/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Error al eliminar');
      loadEncuestas();
      setSuccess('Encuesta eliminada');
    } catch (e) {
      setError(e.message || 'Error al eliminar encuesta');
    }
  };

  const handleRevisarSolicitudEliminacion = async (id, estado) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/solicitudes-eliminacion/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado, observaciones_admin: observacionesSolicitud || null }),
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al actualizar la solicitud');
        return;
      }
      setSuccess(estado === 'aprobada' ? 'Solicitud aprobada' : 'Solicitud rechazada');
      setRevisarSolicitudId(null);
      setRevisarSolicitudAccion(null);
      setObservacionesSolicitud('');
      loadSolicitudesEliminacion();
      loadAlertasPendientesCount();
    } catch (error) {
      console.error('Error al revisar solicitud:', error);
      setError('Error al actualizar la solicitud');
    }
  };

  const refreshPrivacyConsentStatus = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/usuario/privacidad/estado`, {
        headers: mergeClientApiHeaders({ Authorization: `Bearer ${token}` }),
      });
      const data = await response.json();
      if (!response.ok) return true;
      setPrivacyCategorias(Array.isArray(data.categorias_datos) ? data.categorias_datos : []);
      if (data.exento || !data.requiere_aceptacion) {
        setPrivacyConsentRequired(false);
        setAvisoPrivacidad(null);
        setPrivacyCheckboxAccepted(false);
        return true;
      }
      setPrivacyConsentRequired(true);
      setAvisoPrivacidad(data.aviso_vigente || null);
      return false;
    } catch (error) {
      console.error('Error al verificar privacidad:', error);
      return true;
    }
  };

  const applyPrivacyLoginResponse = (data) => {
    if (data?.requiere_aceptacion_privacidad) {
      setPrivacyConsentRequired(true);
      setAvisoPrivacidad(data.aviso_privacidad || null);
      setPrivacyCheckboxAccepted(false);
      return false;
    }
    setPrivacyConsentRequired(false);
    setAvisoPrivacidad(null);
    setPrivacyCheckboxAccepted(false);
    return true;
  };

  const handleAceptarPrivacidadWeb = async () => {
    if (!privacyCheckboxAccepted) {
      setError('Debes marcar la casilla para aceptar el Aviso de Privacidad.');
      return;
    }
    setLoadingPrivacyAccept(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/usuario/privacidad/aceptar`, {
        method: 'POST',
        headers: mergeClientApiHeaders({
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          canal: 'web_admin',
          plataforma: 'web',
          app_version: WEB_APP_VERSION,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'No se pudo registrar la aceptación.');
        return;
      }
      setPrivacyConsentRequired(false);
      setPrivacyCheckboxAccepted(false);
      await refreshPrivacyConsentStatus(token);
    } catch (err) {
      setError('Error de conexión al registrar la aceptación.');
    } finally {
      setLoadingPrivacyAccept(false);
    }
  };

  const handleRechazarPrivacidadWeb = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    setShowLogin(true);
    setPrivacyConsentRequired(false);
    setAvisoPrivacidad(null);
    setPrivacyCheckboxAccepted(false);
  };

  const checkStoredAuth = async () => {
    try {
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        const userData = JSON.parse(storedUser);
        if (userData.rol === 'admin' || userData.rol === 'superuser') {
          setUser(userData);
          setShowLogin(false);
          const privacyOk = userData.rol === 'superuser' ? true : await refreshPrivacyConsentStatus(storedToken);
          if (!privacyOk) {
            setLoading(false);
            return;
          }
          if (userData.rol === 'admin') {
            if (userData.condominio_id) {
              // Listado ligero de propiedades para selects (los residentes se cargan
              // paginados al abrir su pestaña; ya no se piden los ~13k al iniciar sesión)
              await loadPropiedades();
            } else {
              setResidentes([]);
            }
          }
          setLoading(false);
        } else {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error al verificar autenticación:', error);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      setUser(null);
      setShowLogin(true);
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const LOGIN_TIMEOUT_MS = 60000; // 60 s para dar tiempo al cold start de Railway
    const controller = new AbortController();
    let timeoutId = setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);

    try {
      const body = { email: loginEmail, password: loginPassword };
      const codigoEnviar = (loginCodigo || '').trim();
      const emailNorm = (loginEmail || '').trim().toLowerCase();
      if (!codigoEnviar && emailNorm !== SUPERUSER_LOGIN_EMAIL) {
        setError('El código de condominio es obligatorio.');
        setLoading(false);
        return;
      }
      if (codigoEnviar) {
        body.codigo = codigoEnviar;
      }

      const loginUrl = `${API_BASE_URL}/auth/login`;
      if (process.env.NODE_ENV !== 'production') {
        console.log('Intentando login en:', loginUrl);
      }

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: mergeClientApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const textError = await response.text();
        throw new Error(`El servidor no devolvió JSON (${response.status}): ${textError.slice(0, 100)}`);
      }

      if (!response.ok) {
        setError(data?.error || 'Credenciales incorrectas');
        setLoading(false);
        return;
      }

      if (!['admin', 'superuser'].includes(data.user?.rol)) {
        setError('Acceso denegado: No tienes permisos de administrador.');
        setLoading(false);
        return;
      }

      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      if (loginRememberMe) {
        localStorage.setItem('loginEmail', loginEmail.trim());
        if (codigoEnviar) localStorage.setItem('loginCodigo', codigoEnviar);
      } else {
        localStorage.removeItem('loginEmail');
        localStorage.removeItem('loginCodigo');
      }

      setUser(data.user);
      setShowLogin(false);
      setConceptosPago([]);
      setTablaCobranzaData({ propiedades: [], meses: [], celdas: {}, concepto: null, total_propiedades: 0 });
      setMorosidadData({ en_mora: [], al_corriente: [] });
      setConceptoPagoSeleccionado('');

      const privacyOk = data.user?.rol === 'superuser' ? true : applyPrivacyLoginResponse(data);
      if (!privacyOk) {
        setLoading(false);
        return;
      }

      if (data.user.rol === 'admin' && data.user.condominio_id) {
        // Listado ligero de propiedades para selects (los residentes se cargan
        // paginados al abrir su pestaña; ya no se piden los ~13k al iniciar sesión)
        loadPropiedades();
      } else if (data.user.rol === 'admin') {
        setResidentes([]);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error detallado de login:', err);
      }
      if (err.name === 'AbortError') {
        setError('El servidor tardó demasiado en responder. Reintenta en unos segundos; el backend podría estar despertando.');
      } else {
        setError(err.message || 'No se pudo conectar con el servidor. Revisa tu conexión o los logs del backend.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOlvideContrasena = async (e) => {
    e.preventDefault();
    if (!olvideContrasenaEmail?.trim()) {
      setError('Ingresa tu email');
      return;
    }
    setLoadingOlvideContrasena(true);
    setError('');
    try {
      const body = { email: olvideContrasenaEmail.trim().toLowerCase() };
      if (olvideContrasenaCodigo?.trim()) body.codigo = olvideContrasenaCodigo.trim();
      const res = await fetch(`${API_BASE_URL}/auth/olvide-contrasena`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Error al enviar la solicitud');
        return;
      }
      setOlvideContrasenaEnviado(true);
    } catch (err) {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoadingOlvideContrasena(false);
    }
  };

  const handleVolverAlLogin = () => {
    setTokenInvitacion(null);
    setInvitacionData(null);
    setInvitacionError(null);
    setInvitacionCompletada(false);
    setResetPasswordValue('');
    setResetPasswordConfirm('');
    setError('');
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', window.location.pathname || '/');
    }
  };

  const handleCompletarInvitacion = async (e) => {
    e.preventDefault();
    if (!tokenInvitacion || !invitacionData) return;
    if (resetPasswordValue.length < 6) {
      setInvitacionError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (resetPasswordValue !== resetPasswordConfirm) {
      setInvitacionError('Las contraseñas no coinciden');
      return;
    }
    setLoadingCompletarInvitacion(true);
    setInvitacionError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/completar-registro`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenInvitacion, password: resetPasswordValue }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setInvitacionError(data.error || 'Error al completar el registro');
        return;
      }
      setInvitacionCompletada(true);
      if (typeof window !== 'undefined') {
        window.history.replaceState({}, '', window.location.pathname || '/');
      }
    } catch (err) {
      setInvitacionError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoadingCompletarInvitacion(false);
    }
  };

  // Paginación server-side: pide solo la página visible (patrón de loadPagos). El primer
  // parámetro (token) se conserva por compatibilidad con las llamadas existentes; fetchWithAuth
  // obtiene el token por sí mismo. Sin overrides recarga la página/búsqueda/orden vigentes.
  const loadResidentes = async (token = null, overrides = {}) => {
    try {
      setLoading(true);
      const pagina = overrides.pagina ?? residentesPaginacion.pagina;
      const pageSize = overrides.registrosPorPagina ?? residentesPaginacion.registrosPorPagina;
      const q = (overrides.q ?? residentesBusqueda).trim();
      const orden = overrides.orden ?? residentesOrden;

      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(pagina * pageSize),
        sort: orden.por,
        dir: orden.dir,
      });
      if (q) params.append('q', q);

      const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes?${params.toString()}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || 'Error al cargar residentes');
      }

      const data = await response.json();
      setResidentes(Array.isArray(data.data) ? data.data : []);
      setResidentesPaginacion(prev => ({
        ...prev,
        pagina,
        registrosPorPagina: pageSize,
        total: data.total || 0,
      }));
    } catch (error) {
      console.error('Error al cargar residentes:', error);
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError(error.message || 'Error al cargar residentes');
      }
    } finally {
      setLoading(false);
    }
  };

  const generarPasswordAleatorio = () => {
    const chars = 'abcdefghijkmnpqrstuvwxyz23456789';
    let p = '';
    for (let i = 0; i < 10; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
    return p;
  };

  const handleAltaMasiva = async () => {
    const total = Math.min(500, Math.max(1, parseInt(altaMasivaCantidad, 10) || 1));
    const prefijo = (altaMasivaPrefijo || '').trim();
    const ts = Date.now();
    setAltaMasivaProgreso({ current: 0, total });
    const credenciales = [];
    const BATCH = 10;
    for (let i = 1; i <= total; i++) {
      const nombre = `Residente ${i}`;
      const propiedad = prefijo ? `${prefijo} ${i}` : String(i);
      const email = `alta-masiva-${ts}-${i}@placeholder.local`;
      const password = generarPasswordAleatorio();
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes`, {
          method: 'POST',
          body: JSON.stringify({
            nombre,
            propiedad,
            email,
            password,
            categoria: 'Propietario',
            rol: 'residente',
          }),
        });
        const data = await response.json();
        if (response.ok) {
          credenciales.push({ nombre, propiedad, email, password });
        } else {
          setError(data.error || `Error en ${nombre}`);
        }
      } catch (err) {
        setError(err.message || `Error de conexión al crear ${nombre}`);
      }
      setAltaMasivaProgreso((p) => ({ ...p, current: i }));
      if (i % BATCH === 0 && i < total) {
        await new Promise((r) => setTimeout(r, 50));
      }
    }
    setAltaMasivaProgreso(null);
    setAltaMasivaCredenciales(credenciales);
    setShowModalAltaMasiva(false);
    setShowModalCredenciales(credenciales.length > 0);
    if (credenciales.length > 0) {
      loadResidentes(getAuthToken());
      loadPropiedades();
      setSuccess(`Se crearon ${credenciales.length} residente(s). Revisa las credenciales para el primer inicio de sesión.`);
      setTimeout(() => setSuccess(''), 8000);
    }
  };

  const handleToggleBloqueado = async (residenteId, nuevoEstado) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteId}/bloquear`, {
        method: 'PUT',
        body: JSON.stringify({ bloqueado: nuevoEstado }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Error al actualizar estado');
        return;
      }

      setSuccess(data.message);
      setTimeout(() => setSuccess(''), 3000);

      // Actualizar estado local (bloqueo por propiedad: todas las filas de la misma casa)
      setResidentes((prev) => {
        const target = prev.find((r) => r.id === residenteId);
        const propNorm = target?.propiedad ? String(target.propiedad).trim().toUpperCase() : '';
        if (!propNorm) {
          return prev.map((r) =>
            r.id === residenteId ? { ...r, bloqueado: nuevoEstado } : r
          );
        }
        return prev.map((r) =>
          String(r.propiedad || '').trim().toUpperCase() === propNorm
            ? { ...r, bloqueado: nuevoEstado }
            : r
        );
      });
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError('Error de conexión');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    setResidentes([]);
    setShowLogin(true);
  };

  const handleAbrirModalNuevo = () => {
    setResidenteEditando(null);
    setFormData({
      nombre: '',
      email: '',
      password: '',
      propiedad: '',
      telefono: '',
      telefono_no_marcar: false,
      telefono_orden: '',
      categoria: 'Inquilino',
      rol: 'residente',
    });
    setAdjuntosPropiedad([]);
    setAdjuntosPropiedadFiles([]);
    setShowModalResidente(true);
  };

  const loadAdjuntosPropiedad = async (propiedad) => {
    if (!propiedad || !propiedad.trim()) return;
    setLoadingAdjuntos(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/propiedades/${encodeURIComponent(propiedad.trim())}/adjuntos`);
      const data = await response.json();
      setAdjuntosPropiedad(Array.isArray(data) ? data : []);
    } catch (e) {
      setAdjuntosPropiedad([]);
    } finally {
      setLoadingAdjuntos(false);
    }
  };

  const handleAbrirModalEditar = async (residente) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residente.id}`);

      if (response.ok) {
        const data = await response.json();
        setResidenteEditando(data);
        setContactos(data.contactos || []);
        setFormData({
          nombre: data.nombre || '',
          email: data.email || '',
          password: '',
          propiedad: data.propiedad || '',
          telefono: data.telefono || '',
          telefono_no_marcar: data.telefono_no_marcar === true,
          telefono_orden: data.telefono_orden != null ? String(data.telefono_orden) : '',
          categoria: data.categoria || 'Inquilino',
          rol: data.rol || 'residente',
        });
        setAdjuntosPropiedadFiles([]);
        if (data.propiedad && data.propiedad.trim()) {
          loadAdjuntosPropiedad(data.propiedad);
        } else {
          setAdjuntosPropiedad([]);
        }
        setShowModalResidente(true);
      }
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError('Error al cargar datos del residente');
      }
    }
  };

  const handleSubirAdjuntoPropiedad = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !residenteEditando?.propiedad?.trim()) return;
    if (adjuntosPropiedad.length >= 3) return;
    setLoadingAdjuntos(true);
    try {
      const formDataAdj = new FormData();
      formDataAdj.append('adjunto', file);
      formDataAdj.append('nombre_mostrar', file.name);
      const token = getAuthToken();
      const base = API_BASE_URL.replace(/\/$/, '');
      const response = await fetch(`${base}/admin/propiedades/${encodeURIComponent(residenteEditando.propiedad.trim())}/adjuntos`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formDataAdj,
      });
      if (response.status === 401) {
        handleAuthError(response);
        return;
      }
      if (!response.ok) throw new Error('Error al subir');
      const data = await response.json();
      setAdjuntosPropiedad(Array.isArray(data) ? data : []);
      if (adjuntosPropiedadInputRef.current) adjuntosPropiedadInputRef.current.value = '';
    } catch (err) {
      setError('Error al subir documento');
    } finally {
      setLoadingAdjuntos(false);
    }
  };

  const handleEliminarAdjuntoPropiedad = async (id) => {
    try {
      await fetchWithAuth(`${API_BASE_URL}/admin/adjuntos-propiedad/${id}`, { method: 'DELETE' });
      setAdjuntosPropiedad(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setError('Error al eliminar documento');
    }
  };

  const handleGuardarResidente = async () => {
    try {
      setError('');

      if (!formData.nombre || !formData.email) {
        setError('Nombre y email son requeridos');
        return;
      }

      const url = residenteEditando 
        ? `${API_BASE_URL}/admin/residentes/${residenteEditando.id}`
        : `${API_BASE_URL}/admin/residentes`;
      
      let response;
      if (residenteEditando) {
        // Actualizar
        const updateData = {
          nombre: formData.nombre,
          email: formData.email,
          propiedad: formData.propiedad,
          telefono: formData.telefono,
          telefono_no_marcar: formData.telefono_no_marcar === true,
          categoria: formData.categoria,
          rol: formData.rol,
        };
        // Solo incluir password si se proporcionó
        if (formData.password && formData.password.trim() !== '') {
          updateData.password = formData.password;
        }
        response = await fetchWithAuth(url, {
          method: 'PUT',
          body: JSON.stringify(updateData),
        });
      } else {
        // Crear (sin password - se enviará invitación)
        response = await fetchWithAuth(url, {
          method: 'POST',
          body: JSON.stringify({
            nombre: formData.nombre,
            email: formData.email,
            propiedad: formData.propiedad,
            telefono: formData.telefono,
            telefono_no_marcar: formData.telefono_no_marcar === true,
            categoria: formData.categoria,
            rol: formData.rol,
          }),
        });
      }

      let data;
      try {
        data = await response.json();
        console.log('[DEBUG] Respuesta del servidor:', data);
      } catch (parseError) {
        console.error('Error al parsear respuesta:', parseError);
        setError(`Error del servidor: ${response.status} ${response.statusText}`);
        return;
      }

      if (!response.ok) {
        console.error('[ERROR] Respuesta no OK:', data);
        setError(data.error || 'Error al guardar residente');
        return;
      }

      console.log('[DEBUG] Datos recibidos:', data);
      console.log('[DEBUG] correoEnviado:', data.correoEnviado);
      console.log('[DEBUG] errorCorreo:', data.errorCorreo);

      // Subir adjuntos de propiedad si es nuevo residente Propietario con propiedad
      if (!residenteEditando && formData.categoria === 'Propietario' && formData.propiedad?.trim() && adjuntosPropiedadFiles.length > 0) {
        const propiedadEnc = encodeURIComponent(formData.propiedad.trim());
        const base = API_BASE_URL.replace(/\/$/, '');
        const token = getAuthToken();
        for (const file of adjuntosPropiedadFiles) {
          try {
            const fd = new FormData();
            fd.append('adjunto', file);
            fd.append('nombre_mostrar', file.name);
            const adjRes = await fetch(`${base}/admin/propiedades/${propiedadEnc}/adjuntos`, {
              method: 'POST',
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              body: fd,
            });
            if (adjRes.status === 401) {
              handleAuthError(adjRes);
              return;
            }
          } catch (upErr) {
            console.error('Error al subir adjunto:', upErr);
          }
        }
      }

      setAdjuntosPropiedad([]);
      setAdjuntosPropiedadFiles([]);
      // Cerrar modal primero
      setShowModalResidente(false);
      
      // Mostrar mensaje después de cerrar el modal para que sea visible
      setTimeout(() => {
        if (residenteEditando) {
          setSuccess('Residente actualizado correctamente');
        } else {
          // Nuevo residente - verificar si se envió el correo
          if (data.correoEnviado === true) {
            setSuccess(`Residente creado correctamente. Se ha enviado un correo de invitación a ${formData.email} para que complete su registro.`);
          } else if (data.errorCorreo) {
            setError(`Residente creado correctamente, pero hubo un error al enviar el correo de invitación: ${data.errorCorreo}. Puedes reenviar la invitación más tarde.`);
          } else {
            // Si no hay información sobre el correo, asumir que no se envió (por seguridad)
            setError(`Residente creado correctamente, pero no se pudo enviar el correo de invitación. Puedes reenviar la invitación más tarde.`);
          }
        }
        
        // Limpiar mensajes después de 7 segundos
        setTimeout(() => {
          setSuccess('');
          setError('');
        }, 7000);
      }, 100); // Pequeño delay para asegurar que el modal se cierre primero
      
      const token = getAuthToken();
      if (token) {
        loadResidentes(token);
        loadPropiedades();
      }
    } catch (error) {
      console.error('Error al guardar residente:', error);
      if (error.message === 'Sesión expirada' || error.message === 'No hay sesión activa') {
        // Ya se manejó en fetchWithAuth
        return;
      }
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setError(`Error de conexión: No se pudo conectar al servidor. Verifica que el backend esté corriendo en ${API_BASE_URL}`);
      } else {
        setError(`Error: ${error.message}`);
      }
    }
  };

  const handleReenviarResetPassword = async (residenteId) => {
    if (!window.confirm('¿Enviar correo para restablecer contraseña? El residente recibirá un enlace válido por 24 horas.')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteId}/reenviar-reset-password`, {
        method: 'POST',
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Error al enviar');
        return;
      }

      setSuccess(data.message || 'Correo enviado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError('Error de conexión');
      }
    }
  };

  const handleReenviarInvitacion = async (residenteId) => {
    if (!window.confirm('¿Deseas reenviar la invitación por correo a este usuario?')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteId}/reenviar-invitacion`, {
        method: 'POST',
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Error al reenviar invitación');
        return;
      }

      setSuccess(data.message || 'Invitación reenviada exitosamente');
      setTimeout(() => setSuccess(''), 3000);
      const token = getAuthToken();
      if (token) loadResidentes(token);
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError('Error de conexión');
      }
    }
  };

  const handleDarDeBaja = async (residenteId) => {
    if (!window.confirm('¿Estás seguro de dar de baja a este residente?')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Error al dar de baja');
        return;
      }

      setSuccess(data.message);
      setTimeout(() => setSuccess(''), 3000);
      const token = getAuthToken();
      if (token) {
        loadResidentes(token);
        loadPropiedades();
      }
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError('Error de conexión');
      }
    }
  };

  const handleDarDeBajaAccesoPropiedad = async (residenteId) => {
    if (!window.confirm('¿Dar de baja el acceso a la app para TODOS los residentes de esta propiedad (sin borrar registros)?')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteId}/dar-de-baja-acceso-propiedad`, {
        method: 'PUT',
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Error al dar de baja acceso');
        return;
      }

      setSuccess(data.message || 'Acceso dado de baja para la propiedad');
      setTimeout(() => setSuccess(''), 3000);
      const token = getAuthToken();
      if (token) {
        loadResidentes(token);
        loadPropiedades();
      }
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError('Error de conexión');
      }
    }
  };

  const handleReactivarAccesoPropiedad = async (residenteId) => {
    if (!window.confirm('¿Reactivar el acceso a la app para TODOS los residentes de esta propiedad?')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteId}/reactivar-acceso-propiedad`, {
        method: 'PUT',
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Error al reactivar acceso');
        return;
      }

      setSuccess(data.message || 'Acceso reactivado para la propiedad');
      setTimeout(() => setSuccess(''), 3000);
      const token = getAuthToken();
      if (token) {
        loadResidentes(token);
        loadPropiedades();
      }
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError('Error de conexión');
      }
    }
  };

  const handleAbrirModalContactos = async (residente) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residente.id}`);

      if (response.ok) {
        const data = await response.json();
        setResidenteEditando(data);
        setContactos(data.contactos || []);
        setFormContacto({
          nombre: '',
          telefono: '',
          email: '',
          tipo: 'contacto',
          no_marcar: false,
          orden: '',
          password: '',
        });
        setEnviarInvitacionContacto(false);
        setContactoEditando(null);
        setShowModalContactos(true);
      }
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError('Error al cargar contactos');
      }
    }
  };

  const handleGuardarContacto = async () => {
    try {
      setError('');

      if (!formContacto.nombre || !formContacto.telefono) {
        setError('Nombre y teléfono son requeridos');
        return;
      }

      let response;
      if (contactoEditando) {
        // Actualizar
        const payload = { nombre: formContacto.nombre, telefono: formContacto.telefono, email: formContacto.email, tipo: formContacto.tipo, no_marcar: formContacto.no_marcar };
        if (formContacto.orden !== '' && formContacto.orden != null) payload.orden = Number(formContacto.orden);
        response = await fetchWithAuth(`${API_BASE_URL}/admin/contactos/${contactoEditando.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        const payload = { nombre: formContacto.nombre, telefono: formContacto.telefono, email: formContacto.email, tipo: formContacto.tipo, no_marcar: formContacto.no_marcar };
        if (formContacto.orden !== '' && formContacto.orden != null) payload.orden = Number(formContacto.orden);
        response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}/contactos`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Error al guardar contacto');
        return;
      }

      const contactoId = contactoEditando ? contactoEditando.id : data.id;

      // Si se indicó contraseña temporal y el contacto tiene email, establecer contraseña (crea o actualiza usuario)
      if (formContacto.password && formContacto.password.trim() !== '' && formContacto.email && formContacto.email.trim() !== '') {
        try {
          const passResponse = await fetchWithAuth(
            `${API_BASE_URL}/admin/contactos/${contactoId}/establecer-password`,
            { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: formContacto.password.trim() }) }
          );
          const passData = await passResponse.json();
          if (passResponse.ok) {
            setSuccess(contactoEditando ? 'Contacto actualizado y contraseña establecida' : 'Contacto agregado y contraseña temporal establecida');
          } else {
            setError(passData.error || 'Error al establecer contraseña');
          }
        } catch (e) {
          setError('Error al establecer contraseña');
        }
      } else if (enviarInvitacionContacto && formContacto.email && formContacto.email.trim() !== '') {
        // Si se marcó enviar invitación (y no se usó contraseña temporal), enviar invitación
        try {
          const invitacionResponse = await fetchWithAuth(
            `${API_BASE_URL}/admin/contactos/${contactoId}/enviar-invitacion`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ diasValidezInvitacion: 7 }),
            }
          );

          const invitacionData = await invitacionResponse.json();

          if (invitacionResponse.ok) {
            setSuccess(
              contactoEditando 
                ? 'Contacto actualizado e invitación enviada correctamente' 
                : 'Contacto agregado e invitación enviada correctamente'
            );
          } else {
            if (invitacionData.ya_registrado) {
              setSuccess(
                contactoEditando 
                  ? 'Contacto actualizado correctamente. Ya existe un usuario con este email.' 
                  : 'Contacto agregado correctamente. Ya existe un usuario con este email.'
              );
            } else {
              setSuccess(
                contactoEditando 
                  ? 'Contacto actualizado correctamente, pero hubo un error al enviar la invitación' 
                  : 'Contacto agregado correctamente, pero hubo un error al enviar la invitación'
              );
            }
          }
        } catch (invitacionError) {
          console.error('Error al enviar invitación:', invitacionError);
          setSuccess(
            contactoEditando 
              ? 'Contacto actualizado correctamente, pero hubo un error al enviar la invitación' 
              : 'Contacto agregado correctamente, pero hubo un error al enviar la invitación'
          );
        }
      } else {
        setSuccess(contactoEditando ? 'Contacto actualizado correctamente' : 'Contacto agregado correctamente');
      }
      
      setTimeout(() => setSuccess(''), 5000);
      
      // Recargar contactos
      const contactosResponse = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}`);
      if (contactosResponse.ok) {
        const residenteData = await contactosResponse.json();
        setContactos(residenteData.contactos || []);
      }
      // Si se envió invitación, puede haberse creado un nuevo usuario; recargar lista de residentes
      if (enviarInvitacionContacto) {
        const token = getAuthToken();
        if (token) loadResidentes(token);
      }

      setFormContacto({
        nombre: '',
        telefono: '',
        email: '',
        tipo: 'contacto',
        no_marcar: false,
        orden: '',
        password: '',
      });
      setEnviarInvitacionContacto(false);
      setContactoEditando(null);
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError('Error de conexión');
      }
    }
  };

  const handleReenviarInvitacionContacto = async (contactoId) => {
    setContactoReenviandoId(contactoId);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/admin/contactos/${contactoId}/reenviar-invitacion`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ diasValidezInvitacion: 7 }) }
      );
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message || 'Se envió el correo para restablecer contraseña');
      } else {
        setError(data.error || 'Error al restablecer contraseña');
      }
      setTimeout(() => { setSuccess(''); setError(''); }, 5000);
      const contactosResponse = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}`);
      if (contactosResponse.ok) {
        const residenteData = await contactosResponse.json();
        setContactos(residenteData.contactos || []);
      }
      const token = getAuthToken();
      if (token) loadResidentes(token);
    } catch (e) {
      if (e.message !== 'Sesión expirada') setError('Error de conexión');
      setTimeout(() => setError(''), 5000);
    } finally {
      setContactoReenviandoId(null);
    }
  };

  const handleDarDeBajaAccesoContacto = async (contactoId) => {
    if (!window.confirm('¿Estás seguro de dar de baja el acceso a la app de este contacto (manteniendo el contacto)?')) {
      return;
    }

    if (!residenteEditando?.id) {
      setError('No se pudo determinar el residente principal.');
      return;
    }

    try {
      setContactoBajandoAccesoId(contactoId);
      setError('');

      const response = await fetchWithAuth(
        `${API_BASE_URL}/admin/contactos/${contactoId}/dar-de-baja-acceso`,
        { method: 'PUT', body: JSON.stringify({}) }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Error al dar de baja acceso');
        return;
      }

      setSuccess(data.message || 'Acceso revocado correctamente');
      setTimeout(() => setSuccess(''), 3000);

      // Recargar contactos y residentes (por si deja de aparecer en listados)
      const contactosResponse = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}`);
      if (contactosResponse.ok) {
        const residenteData = await contactosResponse.json();
        setContactos(residenteData.contactos || []);
      }

      const token = getAuthToken();
      if (token) loadResidentes(token);
    } catch (e) {
      if (e.message !== 'Sesión expirada' && e.message !== 'No hay sesión activa') {
        setError(e.message || 'Error de conexión');
      }
      setTimeout(() => setError(''), 5000);
    } finally {
      setContactoBajandoAccesoId(null);
    }
  };

  const handleReactivarAccesoContacto = async (contactoId) => {
    if (!window.confirm('¿Reactivar el acceso a la app para este contacto?')) {
      return;
    }

    if (!residenteEditando?.id) {
      setError('No se pudo determinar el residente principal.');
      return;
    }

    try {
      setContactoReactivandoAccesoId(contactoId);
      setError('');

      const response = await fetchWithAuth(
        `${API_BASE_URL}/admin/contactos/${contactoId}/reactivar-acceso`,
        { method: 'PUT', body: JSON.stringify({}) }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Error al reactivar acceso');
        return;
      }

      setSuccess(data.message || 'Acceso reactivado correctamente');
      setTimeout(() => setSuccess(''), 3000);

      const contactosResponse = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}`);
      if (contactosResponse.ok) {
        const residenteData = await contactosResponse.json();
        setContactos(residenteData.contactos || []);
      }

      const token = getAuthToken();
      if (token) loadResidentes(token);
    } catch (e) {
      if (e.message !== 'Sesión expirada' && e.message !== 'No hay sesión activa') {
        setError(e.message || 'Error de conexión');
      }
      setTimeout(() => setError(''), 5000);
    } finally {
      setContactoReactivandoAccesoId(null);
    }
  };

  const handleEliminarContacto = async (contactoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este contacto?')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/contactos/${contactoId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Error al eliminar contacto');
        return;
      }

      setSuccess(data.message);
      setTimeout(() => setSuccess(''), 3000);
      
      // Recargar contactos
      const contactosResponse = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}`);
      if (contactosResponse.ok) {
        const residenteData = await contactosResponse.json();
        setContactos(residenteData.contactos || []);
      }
    } catch (error) {
      if (error.message !== 'Sesión expirada' && error.message !== 'No hay sesión activa') {
        setError('Error de conexión');
      }
    }
  };

  const handleEditarContacto = (contacto) => {
    setContactoEditando(contacto);
    setFormContacto({
      nombre: contacto.nombre,
      telefono: contacto.telefono,
      email: contacto.email || '',
      tipo: contacto.tipo || 'contacto',
      no_marcar: !!contacto.no_marcar,
      orden: contacto.orden != null ? String(contacto.orden) : '',
      password: '',
    });
    setEnviarInvitacionContacto(false);
  };

  // Reordenar contacto entre los que sí reciben llamadas (no_marcar = false)
  const contactosCallables = React.useMemo(() => {
    return contactos.filter(c => c.activo && !c.no_marcar).sort((a, b) => (a.orden || 0) - (b.orden || 0));
  }, [contactos]);

  const handleSubirOrdenContacto = async (contacto) => {
    const idx = contactosCallables.findIndex(c => c.id === contacto.id);
    if (idx <= 0) return;
    const anterior = contactosCallables[idx - 1];
    if (!anterior) return;
    try {
      await fetchWithAuth(`${API_BASE_URL}/admin/contactos/${contacto.id}`, { method: 'PUT', body: JSON.stringify({ orden: anterior.orden }) });
      await fetchWithAuth(`${API_BASE_URL}/admin/contactos/${anterior.id}`, { method: 'PUT', body: JSON.stringify({ orden: contacto.orden }) });
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}`);
      if (res.ok) { const d = await res.json(); setContactos(d.contactos || []); }
    } catch (e) { setError('Error al cambiar orden'); }
  };

  const handleBajarOrdenContacto = async (contacto) => {
    const idx = contactosCallables.findIndex(c => c.id === contacto.id);
    if (idx < 0 || idx >= contactosCallables.length - 1) return;
    const siguiente = contactosCallables[idx + 1];
    if (!siguiente) return;
    try {
      await fetchWithAuth(`${API_BASE_URL}/admin/contactos/${contacto.id}`, { method: 'PUT', body: JSON.stringify({ orden: siguiente.orden }) });
      await fetchWithAuth(`${API_BASE_URL}/admin/contactos/${siguiente.id}`, { method: 'PUT', body: JSON.stringify({ orden: contacto.orden }) });
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}`);
      if (res.ok) { const d = await res.json(); setContactos(d.contactos || []); }
    } catch (e) { setError('Error al cambiar orden'); }
  };

  // --- Vehículos por propiedad ---
  const handleAbrirModalVehiculos = async (residente) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residente.id}`);
      if (response.ok) {
        const data = await response.json();
        setResidenteEditando(data);
        setVehiculos(data.vehiculos || []);
        setFormVehiculo({ color: '', marca: '', modelo: '', placas: '', tag_id: '', foto_url: '' });
        setVehiculoEditando(null);
        setShowModalVehiculos(true);
      }
    } catch (e) {
      if (e.message !== 'Sesión expirada' && e.message !== 'No hay sesión activa') setError('Error al cargar vehículos');
    }
  };

  const handleUploadFoto = async (file, type) => {
    const token = getAuthToken();
    if (!token || !file) return null;
    const formData = new FormData();
    formData.append('foto', file);
    const res = await fetch(`${API_BASE_URL}/admin/upload/foto?type=${type}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (res.status === 401) {
      handleAuthError(res);
      return null;
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.error || 'Error al subir foto');
      return null;
    }
    const data = await res.json();
    return data.url;
  };

  const handleGuardarVehiculo = async () => {
    try {
      setError('');
      const payload = {
        color: formVehiculo.color || null,
        marca: formVehiculo.marca || null,
        modelo: formVehiculo.modelo || null,
        placas: formVehiculo.placas || null,
        tag_id: formVehiculo.tag_id || null,
        foto_url: formVehiculo.foto_url || null,
      };
      if (vehiculoEditando) {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/vehiculos/${vehiculoEditando.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const data = await response.json();
          setError(data.error || 'Error al actualizar vehículo');
          return;
        }
        setSuccess('Vehículo actualizado');
      } else {
        if (vehiculos.length >= 5) {
          setError('Máximo 5 vehículos por propiedad');
          return;
        }
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}/vehiculos`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const data = await response.json();
          setError(data.error || 'Error al agregar vehículo');
          return;
        }
        setSuccess('Vehículo agregado');
      }
      setTimeout(() => setSuccess(''), 3000);
      const resResidente = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}`);
      if (resResidente.ok) {
        const data = await resResidente.json();
        setVehiculos(data.vehiculos || []);
      }
      setFormVehiculo({ color: '', marca: '', modelo: '', placas: '', tag_id: '', foto_url: '' });
      setVehiculoEditando(null);
    } catch (e) {
      if (e.message !== 'Sesión expirada' && e.message !== 'No hay sesión activa') setError('Error de conexión');
    }
  };

  const handleEliminarVehiculo = async (id) => {
    if (!window.confirm('¿Eliminar este vehículo?')) return;
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/vehiculos/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al eliminar');
        return;
      }
      setSuccess('Vehículo eliminado');
      setTimeout(() => setSuccess(''), 3000);
      const resResidente = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}`);
      if (resResidente.ok) {
        const data = await resResidente.json();
        setVehiculos(data.vehiculos || []);
      }
    } catch (e) {
      if (e.message !== 'Sesión expirada' && e.message !== 'No hay sesión activa') setError('Error de conexión');
    }
  };

  const handleEditarVehiculo = (v) => {
    setVehiculoEditando(v);
    setFormVehiculo({
      color: v.color || '',
      marca: v.marca || '',
      modelo: v.modelo || '',
      placas: v.placas || '',
      tag_id: v.tag_id || '',
      foto_url: v.foto_url || '',
    });
  };

  // --- Mascotas por propiedad ---
  const handleAbrirModalMascotas = async (residente) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residente.id}`);
      if (response.ok) {
        const data = await response.json();
        setResidenteEditando(data);
        setMascotas(data.mascotas || []);
        setFormMascota({ tipo: 'Perro', raza: '', tamaño: 'Mediano', nombre: '', foto_url: '' });
        setMascotaEditando(null);
        setShowModalMascotas(true);
      }
    } catch (e) {
      if (e.message !== 'Sesión expirada' && e.message !== 'No hay sesión activa') setError('Error al cargar mascotas');
    }
  };

  const handleGuardarMascota = async () => {
    try {
      setError('');
      if (!formMascota.tipo || !formMascota.tipo.trim()) {
        setError('El tipo de mascota es requerido');
        return;
      }
      const payload = {
        tipo: formMascota.tipo,
        raza: formMascota.raza || null,
        tamaño: formMascota.tamaño || null,
        nombre: formMascota.nombre || null,
        foto_url: formMascota.foto_url || null,
      };
      if (mascotaEditando) {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/mascotas/${mascotaEditando.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const data = await response.json();
          setError(data.error || 'Error al actualizar mascota');
          return;
        }
        setSuccess('Mascota actualizada');
      } else {
        if (mascotas.length >= 5) {
          setError('Máximo 5 mascotas por propiedad');
          return;
        }
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}/mascotas`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const data = await response.json();
          setError(data.error || 'Error al agregar mascota');
          return;
        }
        setSuccess('Mascota agregada');
      }
      setTimeout(() => setSuccess(''), 3000);
      const resResidente = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}`);
      if (resResidente.ok) {
        const data = await resResidente.json();
        setMascotas(data.mascotas || []);
      }
      setFormMascota({ tipo: 'Perro', raza: '', tamaño: 'Mediano', nombre: '', foto_url: '' });
      setMascotaEditando(null);
    } catch (e) {
      if (e.message !== 'Sesión expirada' && e.message !== 'No hay sesión activa') setError('Error de conexión');
    }
  };

  const handleEliminarMascota = async (id) => {
    if (!window.confirm('¿Eliminar esta mascota?')) return;
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/mascotas/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Error al eliminar');
        return;
      }
      setSuccess('Mascota eliminada');
      setTimeout(() => setSuccess(''), 3000);
      const resResidente = await fetchWithAuth(`${API_BASE_URL}/admin/residentes/${residenteEditando.id}`);
      if (resResidente.ok) {
        const data = await resResidente.json();
        setMascotas(data.mascotas || []);
      }
    } catch (e) {
      if (e.message !== 'Sesión expirada' && e.message !== 'No hay sesión activa') setError('Error de conexión');
    }
  };

  const handleEditarMascota = (m) => {
    setMascotaEditando(m);
    setFormMascota({
      tipo: m.tipo || 'Perro',
      raza: m.raza || '',
      tamaño: m.tamaño || 'Mediano',
      nombre: m.nombre || '',
      foto_url: m.foto_url || '',
    });
  };

  // Pantalla de Login – diseño moderno con marca Acceso 6D
  const BRAND_BLUE = '#0061FF';

  // Mientras la URL tiene token pero aún no se ha cargado en estado, mostrar carga (evita "Enlace incompleto" al abrir invitacion.html?token=...)
  if (showLogin && isOnInvitacionPath && !tokenInvitacion && hasTokenInSearch) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #e8f0fe 0%, #f0f7ff 50%, #ffffff 100%)',
            p: 3,
          }}
        >
          <Card
            sx={{
              width: '100%',
              maxWidth: 440,
              borderRadius: 3,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid',
              borderColor: 'grey.100',
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <img
                  src={`${process.env.PUBLIC_URL || ''}/logo-acceso-6d.png`}
                  alt="Acceso 6D"
                  style={{ height: 64, width: 'auto', objectFit: 'contain' }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">Validando enlace...</Typography>
              <Box sx={{ mt: 2 }}>
                <CircularProgress size={32} sx={{ color: BRAND_BLUE }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }

  // Vista "enlace incompleto" solo cuando están en /invitacion y la URL no trae token (link mal copiado o sin código)
  if (showLogin && isOnInvitacionPath && !tokenInvitacion && !hasTokenInSearch) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #e8f0fe 0%, #f0f7ff 50%, #ffffff 100%)',
            p: 3,
          }}
        >
          <Card
            sx={{
              width: '100%',
              maxWidth: 440,
              borderRadius: 3,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid',
              borderColor: 'grey.100',
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <img
                  src={`${process.env.PUBLIC_URL || ''}/logo-acceso-6d.png`}
                  alt="Acceso 6D"
                  style={{ height: 64, width: 'auto', objectFit: 'contain' }}
                />
              </Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: 'grey.800', textAlign: 'center', mb: 1 }}>
                Enlace incompleto
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Este enlace debe incluir un código de verificación. Si recibiste un correo para restablecer tu contraseña, haz clic en el <strong>enlace completo</strong> del correo (el que empieza con la URL de la app y termina con un código largo).
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                También puedes solicitar un nuevo enlace desde la pantalla de inicio de sesión usando "¿Olvidaste tu contraseña?".
              </Alert>
              <Button
                fullWidth
                variant="contained"
                onClick={() => { window.history.replaceState({}, '', '/'); setTokenInvitacion(null); setError(''); }}
                sx={{
                  py: 1.5,
                  borderRadius: '12px',
                  bgcolor: BRAND_BLUE,
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#0051D6' },
                }}
              >
                Ir a inicio de sesión
              </Button>
            </CardContent>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }

  // Vista de restablecer/completar contraseña cuando hay token en URL
  if (showLogin && tokenInvitacion) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #e8f0fe 0%, #f0f7ff 50%, #ffffff 100%)',
            p: 3,
          }}
        >
          <Card
            sx={{
              width: '100%',
              maxWidth: 440,
              borderRadius: 3,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid',
              borderColor: 'grey.100',
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <img
                  src={`${process.env.PUBLIC_URL || ''}/logo-acceso-6d.png`}
                  alt="Acceso 6D"
                  style={{ height: 64, width: 'auto', objectFit: 'contain' }}
                />
              </Box>
              {loadingInvitacion ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: BRAND_BLUE }} />
                  <Typography sx={{ mt: 2 }}>Validando enlace...</Typography>
                </Box>
              ) : invitacionCompletada ? (
                <>
                  <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: 'grey.800', textAlign: 'center', mb: 1 }}>
                    ¡Listo!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                    Inicia sesión en la app con tu correo y la contraseña que acabas de establecer.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3, fontWeight: 600 }}>
                    Descarga la app Acceso 6D:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
                    <Button
                      component="a"
                      href="https://play.google.com/store/apps/details?id=com.acceso6d.acceso6d&pcampaignid=web_share"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      sx={{
                        py: 1.5,
                        px: 3,
                        borderRadius: '12px',
                        bgcolor: '#34a853',
                        fontWeight: 700,
                        '&:hover': { bgcolor: '#2d9249' },
                      }}
                    >
                      📱 Google Play
                    </Button>
                    <Button
                      component="a"
                      href="https://apps.apple.com/mx/app/acceso-6d/id6759506670"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      sx={{
                        py: 1.5,
                        px: 3,
                        borderRadius: '12px',
                        bgcolor: '#000',
                        fontWeight: 700,
                        '&:hover': { bgcolor: '#333' },
                      }}
                    >
                      🍎 App Store
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" align="center" display="block">
                    Si ya tienes la app, ábrela e inicia sesión.
                  </Typography>
                </>
              ) : invitacionError && !invitacionData ? (
                <>
                  <Typography variant="h6" sx={{ color: 'error.main', textAlign: 'center', mb: 2 }}>
                    {invitacionError}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                    El enlace puede haber expirado o ser inválido. Solicita uno nuevo desde la pantalla de inicio de sesión.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleVolverAlLogin}
                    sx={{
                      py: 1.5,
                      borderRadius: '12px',
                      bgcolor: BRAND_BLUE,
                      fontWeight: 700,
                      '&:hover': { bgcolor: '#0051D6' },
                    }}
                  >
                    Volver a inicio de sesión
                  </Button>
                </>
              ) : invitacionData ? (
                <>
                  <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: 'grey.800', textAlign: 'center', mb: 0.5 }}>
                    {invitacionData.es_restablecer ? 'Restablecer contraseña' : 'Completar Registro'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                    {invitacionData.es_restablecer ? 'Ingresa tu nueva contraseña.' : 'Establece tu contraseña para activar tu cuenta.'}
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, mb: 2 }}>
                    <Typography variant="body2"><strong>Nombre:</strong> {invitacionData.nombre}</Typography>
                    <Typography variant="body2"><strong>Email:</strong> {invitacionData.email}</Typography>
                  </Box>
                  {invitacionError && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setInvitacionError(null)}>
                      {invitacionError}
                    </Alert>
                  )}
                  <Box component="form" onSubmit={handleCompletarInvitacion} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Nueva contraseña"
                      type={resetPasswordShowPassword ? 'text' : 'password'}
                      value={resetPasswordValue}
                      onChange={(e) => setResetPasswordValue(e.target.value)}
                      required
                      minLength={6}
                      placeholder="Mínimo 6 caracteres"
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={resetPasswordShowPassword ? 'Ocultar' : 'Ver'}
                              onClick={() => setResetPasswordShowPassword((p) => !p)}
                              edge="end"
                              size="small"
                            >
                              {resetPasswordShowPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Confirmar contraseña"
                      type="password"
                      value={resetPasswordConfirm}
                      onChange={(e) => setResetPasswordConfirm(e.target.value)}
                      required
                      minLength={6}
                      placeholder="Repite tu contraseña"
                      autoComplete="new-password"
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loadingCompletarInvitacion}
                      sx={{
                        py: 1.5,
                        borderRadius: '12px',
                        bgcolor: BRAND_BLUE,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        '&:hover': { bgcolor: '#0051D6' },
                      }}
                    >
                      {loadingCompletarInvitacion ? <CircularProgress size={26} sx={{ color: 'white' }} /> : (invitacionData.es_restablecer ? 'Restablecer contraseña' : 'Completar registro')}
                    </Button>
                    <Typography
                      component="button"
                      type="button"
                      onClick={handleVolverAlLogin}
                      sx={{ fontSize: '0.875rem', color: BRAND_BLUE, fontWeight: 500, textDecoration: 'none', cursor: 'pointer', background: 'none', border: 'none', mt: 1 }}
                    >
                      Volver a inicio de sesión
                    </Typography>
                  </Box>
                </>
              ) : null}
            </CardContent>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }

  if (showLogin) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #e8f0fe 0%, #f0f7ff 50%, #ffffff 100%)',
            p: 3,
          }}
        >
          <Card
            sx={{
              width: '100%',
              maxWidth: 440,
              borderRadius: 3,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid',
              borderColor: 'grey.100',
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <img
                  src={`${process.env.PUBLIC_URL || ''}/logo-acceso-6d.png`}
                  alt="Acceso 6D"
                  style={{ height: 64, width: 'auto', objectFit: 'contain' }}
                />
              </Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: 'grey.800', textAlign: 'center', mb: 0.5 }}>
                Bienvenido
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Ingresa tus credenciales para administrar tu coto.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  fullWidth
                  inputRef={loginCodigoRef}
                  label="Código de condominio"
                  placeholder="Ej: REAL-VALLE"
                  value={loginCodigo}
                  onChange={(e) => setLoginCodigo(e.target.value)}
                  autoComplete="off"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_BLUE, borderWidth: 2 },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: BRAND_BLUE },
                  }}
                  helperText="Obligatorio. Si marcas recordarme, se guardará para la próxima vez."
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  autoComplete="email"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_BLUE, borderWidth: 2 },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: BRAND_BLUE },
                  }}
                />
                <TextField
                  fullWidth
                  label="Contraseña"
                  type={loginShowPassword ? 'text' : 'password'}
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_BLUE, borderWidth: 2 },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: BRAND_BLUE },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={loginShowPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                          onClick={() => setLoginShowPassword((p) => !p)}
                          edge="end"
                          size="small"
                        >
                          {loginShowPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={loginRememberMe}
                      onChange={(e) => setLoginRememberMe(e.target.checked)}
                      sx={{ '&.Mui-checked': { color: BRAND_BLUE } }}
                    />
                  }
                  label="Recordar mis datos"
                  sx={{ mt: 0.5 }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 1,
                    py: 1.5,
                    borderRadius: '12px',
                    bgcolor: BRAND_BLUE,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.9rem',
                    boxShadow: `0 4px 14px 0 ${BRAND_BLUE}40`,
                    '&:hover': { bgcolor: '#0051D6', boxShadow: `0 6px 20px 0 ${BRAND_BLUE}50` },
                    '&:active': { transform: 'scale(0.98)' },
                  }}
                >
                  {loading ? <CircularProgress size={26} sx={{ color: 'white' }} /> : 'Iniciar sesión'}
                </Button>
                <Box sx={{ mt: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    component="a"
                    href="#"
                    onClick={(e) => { e.preventDefault(); setOlvideContrasenaEmail(loginEmail); setOlvideContrasenaCodigo(loginCodigo || ''); setOlvideContrasenaEnviado(false); setOpenModalOlvideContrasena(true); }}
                    sx={{ fontSize: '0.875rem', color: BRAND_BLUE, fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                  >
                    ¿Olvidaste tu contraseña?
                  </Typography>
                  <Typography
                    component="a"
                    href="mailto:soporte@acceso6d.com"
                    sx={{ fontSize: '0.875rem', color: BRAND_BLUE, fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    ¿Necesitas ayuda técnica?
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Dialog open={openModalOlvideContrasena} onClose={() => { setOpenModalOlvideContrasena(false); setOlvideContrasenaEnviado(false); }} maxWidth="sm" fullWidth>
            <DialogTitle>Restablecer contraseña</DialogTitle>
            <DialogContent>
              {olvideContrasenaEnviado ? (
                <Typography sx={{ py: 2 }}>
                  Si el correo existe en el sistema, recibirás un enlace para restablecer tu contraseña. Revisa tu bandeja de entrada y carpeta de spam.
                </Typography>
              ) : (
                <Box component="form" onSubmit={handleOlvideContrasena} sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    required
                    value={olvideContrasenaEmail}
                    onChange={(e) => setOlvideContrasenaEmail(e.target.value)}
                    placeholder="tu@correo.com"
                  />
                  <TextField
                    label="Código de condominio (opcional)"
                    fullWidth
                    value={olvideContrasenaCodigo}
                    onChange={(e) => setOlvideContrasenaCodigo(e.target.value)}
                    placeholder="Ej. REAL-VALLE"
                    helperText="Si tienes varios condominios, indícalo."
                  />
                  <Button type="submit" variant="contained" disabled={loadingOlvideContrasena} sx={{ bgcolor: BRAND_BLUE }}>
                    {loadingOlvideContrasena ? <CircularProgress size={24} /> : 'Enviar enlace de recuperación'}
                  </Button>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setOpenModalOlvideContrasena(false); setOlvideContrasenaEnviado(false); }}>Cerrar</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </ThemeProvider>
    );
  }

  // Pantalla bloqueante: consentimiento de privacidad (admin web)
  if (!showLogin && privacyConsentRequired && user) {
    const avisoUrl = avisoPrivacidad?.url || 'https://www.acceso6d.com/aviso-privacidad';
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #e8f0fe 0%, #f0f7ff 50%, #ffffff 100%)',
            p: 3,
          }}
        >
          <Card sx={{ width: '100%', maxWidth: 560, borderRadius: 3, p: 1 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
                Aviso de Privacidad
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Para administrar Acceso 6D debes aceptar el tratamiento de tus datos personales conforme al Aviso de Privacidad.
              </Typography>
              {avisoPrivacidad?.resumen_cambios && (
                <Typography variant="body2" sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 2 }}>
                  {avisoPrivacidad.resumen_cambios}
                </Typography>
              )}
              {privacyCategorias.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Datos que utilizamos:
                  </Typography>
                  {privacyCategorias.map((cat) => (
                    <Box key={cat.id} sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{cat.titulo}</Typography>
                      <Typography variant="caption" color="text.secondary">{cat.descripcion}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography
                component="a"
                href={avisoUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'block', textAlign: 'center', mb: 2, color: BRAND_BLUE, fontWeight: 600 }}
              >
                Ver aviso completo
              </Typography>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacyCheckboxAccepted}
                    onChange={(e) => setPrivacyCheckboxAccepted(e.target.checked)}
                  />
                }
                label="He leído y acepto el Aviso de Privacidad y el tratamiento de mis datos personales."
                sx={{ alignItems: 'flex-start', mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleAceptarPrivacidadWeb}
                disabled={loadingPrivacyAccept}
                sx={{ mb: 1, bgcolor: BRAND_BLUE }}
              >
                {loadingPrivacyAccept ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Aceptar y continuar'}
              </Button>
              <Button fullWidth color="error" onClick={handleRechazarPrivacidadWeb}>
                No acepto — Cerrar sesión
              </Button>
            </CardContent>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }

  // Vista Superusuario: solo alta de condominios (no convive con admin en la misma sesión)
  if (user?.rol === 'superuser') {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ flexGrow: 1 }}>
          <AppBar position="static">
            <Toolbar>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Alta de condominios (Superusuario)
              </Typography>
              <Typography variant="body2" sx={{ mr: 2 }}>{user?.nombre}</Typography>
              <Button color="inherit" onClick={handleLogout}>Cerrar Sesión</Button>
            </Toolbar>
          </AppBar>
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
            )}
            <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
              Alta de Condominios
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Crea nuevos condominios (cotos). Cada uno tendrá su propio código (ej. REAL-VALLE) que los usuarios usan al iniciar sesión como administrador.
            </Typography>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Crear nuevo condominio</Typography>
                <Box component="form" onSubmit={handleCrearCondominio} sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500 }}>
                  <TextField label="Nombre" required fullWidth value={condominioFormNombre} onChange={(e) => setCondominioFormNombre(e.target.value)} placeholder="Ej. Real de Valdepeñas 2" />
                  <TextField label="Código" required fullWidth value={condominioFormCodigo} onChange={(e) => setCondominioFormCodigo(e.target.value.toUpperCase().replace(/\s/g, '-'))} placeholder="Ej. REAL-VALLE (único, sin espacios)" helperText="Solo letras, números y guiones. Los usuarios lo usan en el login." />
                  <TextField label="Dirección (opcional)" fullWidth value={condominioFormDireccion} onChange={(e) => setCondominioFormDireccion(e.target.value)} placeholder="Ej. Calle Principal 123" />
                  <Button type="submit" variant="contained" startIcon={<AddIcon />} disabled={loadingCrearCondominio}>
                    {loadingCrearCondominio ? <CircularProgress size={24} /> : 'Crear condominio'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Condominios registrados</Typography>
                {loadingCondominios ? (
                  <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                ) : (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Código</TableCell>
                          <TableCell>Dirección</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {condominiosList.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 3 }}>No hay condominios. Crea uno arriba.</TableCell>
                          </TableRow>
                        ) : (
                          condominiosList.map((c) => (
                            <TableRow key={c.id}>
                              <TableCell>{c.id}</TableCell>
                              <TableCell>{c.nombre}</TableCell>
                              <TableCell><Chip label={c.codigo} size="small" variant="outlined" /></TableCell>
                              <TableCell>{c.direccion || '-'}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mt: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Usuarios por condominio</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Crea administradores para cada fraccionamiento, restablece contraseñas o bloquea el acceso.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                  <TextField
                    select
                    label="Condominio"
                    value={selectedCondominioIdForUsers}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedCondominioIdForUsers(id);
                      loadUsuarios(id || null);
                    }}
                    size="small"
                    sx={{ minWidth: 260 }}
                  >
                    <MenuItem value="">Selecciona un condominio</MenuItem>
                    {condominiosList.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>{c.nombre} ({c.codigo})</MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={!selectedCondominioIdForUsers}
                    onClick={() => {
                      setFormCrearUsuario({ email: '', nombre: '', password: '', rol: 'admin' });
                      setOpenModalCrearAdmin(true);
                    }}
                  >
                    Crear administrador
                  </Button>
                </Box>
                {loadingUsuarios ? (
                  <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                ) : (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Rol</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usuariosList.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                              {selectedCondominioIdForUsers ? 'No hay usuarios en este condominio.' : 'Selecciona un condominio.'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          usuariosList.map((u) => (
                            <TableRow key={u.id}>
                              <TableCell>{u.id}</TableCell>
                              <TableCell>{u.email}</TableCell>
                              <TableCell>{u.nombre}</TableCell>
                              <TableCell><Chip label={u.rol} size="small" variant="outlined" /></TableCell>
                              <TableCell>
                                <Chip
                                  label={u.bloqueado ? 'Bloqueado' : 'Activo'}
                                  size="small"
                                  color={u.bloqueado ? 'error' : 'success'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Button size="small" onClick={() => { setUsuarioResetId(u.id); setNuevaPassword(''); setOpenModalResetPassword(true); }} sx={{ mr: 1 }}>
                                  Restablecer contraseña
                                </Button>
                                <Button size="small" color={u.bloqueado ? 'success' : 'warning'} onClick={() => handleBloquearUsuario(u)}>
                                  {u.bloqueado ? 'Desbloquear' : 'Bloquear'}
                                </Button>
                                {u.rol === 'admin' && (
                                  <Button
                                    size="small"
                                    sx={{ ml: 1 }}
                                    onClick={() => handleAbrirPermisosMenu(u)}
                                  >
                                    Editar usuario
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            <Dialog open={openModalCrearAdmin} onClose={() => setOpenModalCrearAdmin(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Crear administrador</DialogTitle>
              <form onSubmit={handleCrearUsuario}>
                <DialogContent>
                  <TextField fullWidth label="Condominio" value={condominiosList.find(c => String(c.id) === selectedCondominioIdForUsers)?.nombre || ''} disabled sx={{ mb: 2 }} />
                  <TextField fullWidth label="Email" type="email" required value={formCrearUsuario.email} onChange={(e) => setFormCrearUsuario(prev => ({ ...prev, email: e.target.value }))} sx={{ mb: 2 }} />
                  <TextField fullWidth label="Nombre" required value={formCrearUsuario.nombre} onChange={(e) => setFormCrearUsuario(prev => ({ ...prev, nombre: e.target.value }))} sx={{ mb: 2 }} />
                  <TextField fullWidth label="Contraseña" type="password" required value={formCrearUsuario.password} onChange={(e) => setFormCrearUsuario(prev => ({ ...prev, password: e.target.value }))} helperText="Mínimo 6 caracteres" sx={{ mb: 2 }} />
                  <TextField select fullWidth label="Rol" value={formCrearUsuario.rol} onChange={(e) => setFormCrearUsuario(prev => ({ ...prev, rol: e.target.value }))}>
                    <MenuItem value="admin">Administrador</MenuItem>
                    <MenuItem value="guardia">Guardia</MenuItem>
                    <MenuItem value="residente">Residente</MenuItem>
                  </TextField>
                  {(formCrearUsuario.rol || 'admin') === 'admin' && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Menús del panel de administración
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Selecciona a qué secciones del panel podrá acceder este administrador.
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {ALL_ADMIN_MENU_INDICES.map((idx) => {
                          const etiquetas = [
                            'Residentes',
                            'Catálogos',
                            'Orden de llamada',
                            'Cobranza',
                            'Ingresos',
                            'Egresos',
                            'Amenidades',
                            'Documentos',
                            'Pases',
                            'Notificaciones',
                            'Visitas manuales',
                            'Proveedores',
                            'Directorio',
                            'Configuración',
                            'Dashboard',
                            'Alertas',
                            'Sugerencias',
                            'Solicitudes de eliminación',
                            'Encuestas',
                          ];
                          const label = etiquetas[idx] || `Menú ${idx}`;
                          const checked = Array.isArray(formCrearUsuario.admin_menu_permisos)
                            ? formCrearUsuario.admin_menu_permisos.includes(idx)
                            : true;
                          return (
                            <FormControlLabel
                              key={idx}
                              control={
                                <Checkbox
                                  checked={checked}
                                  onChange={() => {
                                    setFormCrearUsuario((prev) => {
                                      const actual = Array.isArray(prev.admin_menu_permisos)
                                        ? prev.admin_menu_permisos
                                        : ALL_ADMIN_MENU_INDICES;
                                      return {
                                        ...prev,
                                        admin_menu_permisos: actual.includes(idx)
                                          ? actual.filter((i) => i !== idx)
                                          : [...actual, idx],
                                      };
                                    });
                                  }}
                                />
                              }
                              label={label}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenModalCrearAdmin(false)}>Cancelar</Button>
                  <Button type="submit" variant="contained" disabled={loading}>{loading ? <CircularProgress size={24} /> : 'Crear'}</Button>
                </DialogActions>
              </form>
            </Dialog>

            <Dialog open={openModalResetPassword} onClose={() => { setOpenModalResetPassword(false); setUsuarioResetId(null); setNuevaPassword(''); }} maxWidth="sm" fullWidth>
              <DialogTitle>Restablecer contraseña</DialogTitle>
              <form onSubmit={handleResetPassword}>
                <DialogContent>
                  <TextField fullWidth label="Nueva contraseña" type="password" required value={nuevaPassword} onChange={(e) => setNuevaPassword(e.target.value)} helperText="Mínimo 6 caracteres" />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setOpenModalResetPassword(false); setUsuarioResetId(null); setNuevaPassword(''); }}>Cancelar</Button>
                  <Button type="submit" variant="contained" disabled={loading || nuevaPassword.length < 6}>{loading ? <CircularProgress size={24} /> : 'Actualizar'}</Button>
                </DialogActions>
              </form>
            </Dialog>

            <Dialog open={openModalPermisosMenu} onClose={() => { setOpenModalPermisosMenu(false); setUsuarioPermisosEdit(null); setAdminMenuPermisosTemp(ALL_ADMIN_MENU_INDICES); }} maxWidth="sm" fullWidth>
              <DialogTitle>Editar administrador</DialogTitle>
              <DialogContent>
                {usuarioPermisosEdit && (
                  <TextField
                    fullWidth
                    label="Nombre"
                    required
                    value={usuarioPermisosEdit.nombre || ''}
                    onChange={(e) => setUsuarioPermisosEdit((prev) => prev ? { ...prev, nombre: e.target.value } : prev)}
                    sx={{ mb: 2 }}
                  />
                )}
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Menús del panel de administración
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Define a qué menús del panel de administración puede acceder este administrador. Todas las opciones marcadas estarán visibles.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {ALL_ADMIN_MENU_INDICES.map((idx) => {
                    const etiquetas = [
                      'Residentes',
                      'Catálogos',
                      'Orden de llamada',
                      'Cobranza',
                      'Ingresos',
                      'Egresos',
                      'Amenidades',
                      'Documentos',
                      'Pases',
                      'Notificaciones',
                      'Visitas manuales',
                      'Proveedores',
                      'Directorio',
                      'Configuración',
                      'Dashboard',
                      'Alertas',
                      'Sugerencias',
                      'Solicitudes de eliminación',
                      'Encuestas',
                    ];
                    const label = etiquetas[idx] || `Menú ${idx}`;
                    const checked = Array.isArray(adminMenuPermisosTemp)
                      ? adminMenuPermisosTemp.includes(idx)
                      : true;
                    return (
                      <FormControlLabel
                        key={idx}
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={() => toggleAdminMenuPermisoTemp(idx)}
                          />
                        }
                        label={label}
                      />
                    );
                  })}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => { setOpenModalPermisosMenu(false); setUsuarioPermisosEdit(null); setAdminMenuPermisosTemp(ALL_ADMIN_MENU_INDICES); }}>
                  Cancelar
                </Button>
                <Button onClick={handleGuardarPermisosMenu} variant="contained" disabled={loading}>
                  {loading ? <CircularProgress size={24} /> : 'Guardar'}
                </Button>
              </DialogActions>
            </Dialog>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  // Panel de Administrador de coto (residentes, dashboard, configuración, etc.; sin alta de condominios)
  const renderMenuItem = (idx, icon, label, badgeCount) => {
    const canShow = ALL_ADMIN_MENU_INDICES.includes(idx) && (Array.isArray(user?.admin_menu_permisos) ? user.admin_menu_permisos.includes(idx) : true);
    if (!canShow) return null;
    const button = (
      <ListItemButton
        selected={tabActual === idx}
        onClick={() => setTabActual(idx)}
        sx={{
          py: 1.25,
          ...(adminNavCollapsed ? { justifyContent: 'center', px: 1 } : {}),
        }}
      >
        <ListItemIcon sx={{ minWidth: adminNavCollapsed ? 0 : 40, justifyContent: 'center' }}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            {icon}
            {adminNavCollapsed && badgeCount != null && badgeCount > 0 && (
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -6,
                  bgcolor: '#d32f2f',
                  color: 'white',
                  borderRadius: '50%',
                  minWidth: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 'bold',
                }}
              >
                {badgeCount > 9 ? '+' : badgeCount}
              </Box>
            )}
          </Box>
        </ListItemIcon>
        {!adminNavCollapsed && <ListItemText primary={label} />}
        {!adminNavCollapsed && badgeCount != null && badgeCount > 0 && (
          <Box sx={{ bgcolor: '#d32f2f', color: 'white', borderRadius: '50%', minWidth: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold' }}>
            {badgeCount > 9 ? '9+' : badgeCount}
          </Box>
        )}
      </ListItemButton>
    );
    return (
      <React.Fragment key={idx}>
        {adminNavCollapsed ? (
          <MuiTooltip title={label} placement="right">
            {button}
          </MuiTooltip>
        ) : (
          button
        )}
      </React.Fragment>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      {(() => {
        const drawerWidthExpanded = 260;
        const drawerWidthCollapsed = 72;
        const drawerWidth = adminNavCollapsed ? drawerWidthCollapsed : drawerWidthExpanded;
        return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
            zIndex: (theme) => theme.zIndex.drawer + 1,
            transition: (theme) => theme.transitions.create(['width', 'margin'], { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.leavingScreen }),
          }}
        >
          <Toolbar>
            <Box component="img" src={`${process.env.PUBLIC_URL || ''}/logo-acceso-6d.png`} alt="Acceso 6D" sx={{ height: 36, width: 'auto', mr: 2, objectFit: 'contain' }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Panel de Administración
              {user?.condominio_id && (user?.condominio_nombre || user?.codigo_condominio) && (
                <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 400, opacity: 0.95 }}>
                  Conectado a: {user.condominio_nombre || user.codigo_condominio}{user.codigo_condominio && user.condominio_nombre ? ` (${user.codigo_condominio})` : ''}
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.nombre}
            </Typography>
            <Button color="inherit" onClick={() => { setPasswordActualAdmin(''); setNuevaPasswordAdmin(''); setConfirmarNuevaPasswordAdmin(''); setOpenModalCambiarPasswordAdmin(true); }}>
              Cambiar contraseña
            </Button>
            <Button color="inherit" onClick={handleLogout}>
              Cerrar Sesión
            </Button>
          </Toolbar>
        </AppBar>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: drawerWidth,
            flexShrink: 0,
            transition: (theme) => theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.leavingScreen }),
            '& .MuiDrawer-paper': (theme) => ({
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: `1px solid ${theme.palette.divider}`,
              top: theme.mixins.toolbar.minHeight,
              height: `calc(100% - ${theme.mixins.toolbar.minHeight}px)`,
              transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.leavingScreen }),
              overflowX: 'hidden',
            }),
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: adminNavCollapsed ? 'center' : 'flex-end', px: 0.5, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
              <IconButton
                size="small"
                onClick={() => setAdminNavCollapsed((c) => !c)}
                aria-label={adminNavCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
              >
                {adminNavCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Box>
            <Box sx={{ overflow: 'auto', py: 1, flex: 1 }}>
            <List dense disablePadding>
              {renderMenuItem(0, <PeopleIcon />, 'Residentes', null)}
              {renderMenuItem(1, <MenuBookIcon />, 'Catálogos', null)}
              {renderMenuItem(2, <FormatListNumberedIcon />, 'Orden de llamada', null)}
              {renderMenuItem(3, <AttachMoneyIcon />, 'Cobranza', null)}
              {renderMenuItem(4, <PaymentIcon />, 'Ingresos', null)}
              {renderMenuItem(5, <ReceiptIcon />, 'Egresos', null)}
              {renderMenuItem(6, <RoomServiceIcon />, 'Amenidades', conteoReservasAmenidadesPendientes > 0 ? conteoReservasAmenidadesPendientes : null)}
              {renderMenuItem(7, <DescriptionIcon />, 'Documentos', null)}
              {renderMenuItem(8, <QrCodeIcon />, 'Pases', null)}
              {renderMenuItem(9, <NotificationsIcon />, 'Notificaciones', null)}
              {renderMenuItem(10, <DirectionsWalkIcon />, 'Visitas manuales', null)}
              {renderMenuItem(11, <SettingsIcon />, 'Proveedores', null)}
              {renderMenuItem(12, <PhoneIcon />, 'Directorio', conteoComentariosDirectorioPendientes > 0 ? conteoComentariosDirectorioPendientes : null)}
              {renderMenuItem(13, <SettingsIcon />, 'Configuración', null)}
              {renderMenuItem(14, <DashboardIcon />, 'Dashboard', null)}
              {renderMenuItem(15, <EmailIcon />, 'Alertas', contadorAlertas > 0 ? contadorAlertas : null)}
              {renderMenuItem(16, <FeedbackIcon />, 'Sugerencias', conteoSugerenciasNoVistas > 0 ? conteoSugerenciasNoVistas : null)}
              {renderMenuItem(17, <PeopleIcon />, 'Solicitudes de eliminación', conteoSolicitudesEliminacionPendientes > 0 ? conteoSolicitudesEliminacionPendientes : null)}
              {renderMenuItem(18, <AssessmentIcon />, 'Encuestas', conteoEncuestasConRespuestasPendientes > 0 ? conteoEncuestasConRespuestasPendientes : null)}
            </List>
            </Box>
          </Box>
        </Drawer>

        <Box component="main" sx={{ flexGrow: 1, overflow: 'auto', width: { sm: `calc(100% - ${drawerWidth}px)` }, transition: (theme) => theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.leavingScreen }) }}>
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2, 
                zIndex: 9999,
                position: 'relative'
              }} 
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 2,
                zIndex: 9999,
                position: 'relative'
              }} 
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          )}

          <Dialog open={openModalCambiarPasswordAdmin} onClose={() => { setOpenModalCambiarPasswordAdmin(false); setPasswordActualAdmin(''); setNuevaPasswordAdmin(''); setConfirmarNuevaPasswordAdmin(''); }} maxWidth="sm" fullWidth>
            <DialogTitle>Cambiar contraseña</DialogTitle>
            <form onSubmit={handleCambiarPasswordAdmin}>
              <DialogContent>
                <TextField fullWidth label="Contraseña actual" type="password" required value={passwordActualAdmin} onChange={(e) => setPasswordActualAdmin(e.target.value)} sx={{ mb: 2 }} />
                <TextField fullWidth label="Nueva contraseña" type="password" required value={nuevaPasswordAdmin} onChange={(e) => setNuevaPasswordAdmin(e.target.value)} helperText="Mínimo 6 caracteres" sx={{ mb: 2 }} />
                <TextField fullWidth label="Confirmar nueva contraseña" type="password" required value={confirmarNuevaPasswordAdmin} onChange={(e) => setConfirmarNuevaPasswordAdmin(e.target.value)} />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => { setOpenModalCambiarPasswordAdmin(false); setPasswordActualAdmin(''); setNuevaPasswordAdmin(''); setConfirmarNuevaPasswordAdmin(''); }}>Cancelar</Button>
                <Button type="submit" variant="contained" disabled={loading || !passwordActualAdmin.trim() || nuevaPasswordAdmin.length < 6 || nuevaPasswordAdmin !== confirmarNuevaPasswordAdmin}>{loading ? <CircularProgress size={24} /> : 'Actualizar'}</Button>
              </DialogActions>
            </form>
          </Dialog>

          {/* Diálogo de pago: siempre en DOM para que funcione desde Ingresos y desde Cobranza */}
          <Dialog open={showDialogPago} onClose={() => { setShowDialogPago(false); setPagoEditando(null); setPagoDesdeCobranza(false); setCobranzaResumenPeriodo(null); }} maxWidth="md" fullWidth>
            <DialogTitle>
              {pagoEditando ? 'Editar Pago' : 'Nuevo Pago'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Fecha"
                    type="date"
                    fullWidth
                    value={formPago.fecha}
                    onChange={(e) => setFormPago({ ...formPago, fecha: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Periodo (mes)"
                    fullWidth
                    value={formPago.mes}
                    onChange={(e) => setFormPago({ ...formPago, mes: e.target.value })}
                    disabled={Boolean(pagoDesdeCobranza && cobranzaResumenPeriodo && !pagoEditando)}
                    helperText={pagoDesdeCobranza && cobranzaResumenPeriodo && !pagoEditando ? 'Definido por la celda de cobranza' : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={[...(listadoPropiedades || [])].sort(compareNatural)}
                    getOptionLabel={(opt) => String(opt ?? '')}
                    value={formPago.propiedad || null}
                    onChange={(e, newValue) => setFormPago({ ...formPago, propiedad: newValue ?? '' })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Propiedad"
                        required
                        placeholder="Escribe para filtrar..."
                      />
                    )}
                    isOptionEqualToValue={(opt, val) => (opt ?? '') === (val ?? '')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Concepto"
                    fullWidth
                    value={formPago.concepto}
                    onChange={(e) => setFormPago({ ...formPago, concepto: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Categoría Concepto"
                    fullWidth
                    value={formPago.categoria_concepto}
                    onChange={(e) => setFormPago({ ...formPago, categoria_concepto: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Descripción"
                    fullWidth
                    multiline
                    rows={3}
                    value={formPago.descripcion}
                    onChange={(e) => setFormPago({ ...formPago, descripcion: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={pagoDesdeCobranza && cobranzaResumenPeriodo && !pagoEditando ? 12 : 6}>
                  <TextField
                    label={pagoDesdeCobranza && cobranzaResumenPeriodo && !pagoEditando ? 'Abono total' : 'Monto'}
                    type="number"
                    fullWidth
                    required
                    value={formPago.monto}
                    onChange={(e) => setFormPago({ ...formPago, monto: e.target.value })}
                    inputProps={{ min: 0, step: '0.01' }}
                  />
                </Grid>
                {!(pagoDesdeCobranza && cobranzaResumenPeriodo && !pagoEditando) ? (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Recargos"
                      type="number"
                      fullWidth
                      value={formPago.recargos}
                      onChange={(e) => setFormPago({ ...formPago, recargos: e.target.value })}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formPago.depositado}
                        onChange={(e) => setFormPago({ ...formPago, depositado: e.target.checked })}
                      />
                    }
                    label="Depositado"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{
                flexDirection: 'column',
                alignItems: 'stretch',
                px: 3,
                pt: 0,
                pb: 2,
                gap: 1.5,
              }}
            >
              {pagoDesdeCobranza && cobranzaResumenPeriodo && !pagoEditando ? (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover', width: '100%', boxSizing: 'border-box' }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600} color="text.secondary">
                    Resumen del periodo
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cuota base:{' '}
                    <strong>
                      ${Number(cobranzaResumenPeriodo.monto_cuota ?? cobranzaResumenPeriodo.monto ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </Typography>
                  {Number(cobranzaResumenPeriodo.recargo_mora) > 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Mora (por reglas del concepto):{' '}
                      <strong>
                        ${Number(cobranzaResumenPeriodo.recargo_mora).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </strong>
                    </Typography>
                  ) : null}
                  <Typography variant="body2" color="text.secondary">
                    Total esperado:{' '}
                    <strong>
                      ${Number(cobranzaResumenPeriodo.total_esperado ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pagado acumulado:{' '}
                    <strong>
                      ${Number(cobranzaResumenPeriodo.total_pagado ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Saldo pendiente:{' '}
                    <strong>
                      ${Number(cobranzaResumenPeriodo.saldo ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </Typography>
                  {cobranzaResumenPeriodo.fecha_limite ? (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      Fecha límite: {cobranzaResumenPeriodo.fecha_limite}
                    </Typography>
                  ) : null}
                  {Array.isArray(cobranzaResumenPeriodo.abonos) && cobranzaResumenPeriodo.abonos.length > 0 ? (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1.5 }}>Recibos vinculados</Typography>
                      <List dense disablePadding sx={{ mt: 0.5 }}>
                        {cobranzaResumenPeriodo.abonos.map((a) => {
                          const sub = (Number(a.monto) || 0) + (Number(a.recargos) || 0);
                          const folioTxt = a.folio_recibo != null ? `Rec. ${a.folio_recibo}` : a.folio || `Id ${a.id}`;
                          return (
                            <ListItem key={a.id} disableGutters sx={{ py: 0.25 }}>
                              <ListItemText
                                primary={folioTxt}
                                secondary={`$${sub.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (cuota ${Number(a.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}, mora ${Number(a.recargos || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })})`}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </>
                  ) : null}
                </Paper>
              ) : null}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
                <Button onClick={() => {
                  setShowDialogPago(false);
                  setPagoEditando(null);
                  setPagoDesdeCobranza(false);
                  setCobranzaResumenPeriodo(null);
                }}>
                  Cancelar
                </Button>
                <Button onClick={pagoEditando ? handleActualizarPago : handleCrearPago} disabled={!pagoEditando && creandoPago}>
                  {!pagoEditando && creandoPago ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Creando…
                    </>
                  ) : pagoEditando ? 'Actualizar' : 'Crear'}
                </Button>
              </Box>
            </DialogActions>
          </Dialog>

          {/* Diálogo Varios pagos (rango de periodos, desde Cobranza) */}
          <Dialog
            open={showDialogPagarVariosPeriodos}
            onClose={() => {
              setShowDialogPagarVariosPeriodos(false);
              setPropiedadPagarVariosPeriodos('');
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Varios pagos</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Periodos consecutivos del concepto (sin saltos). Solo periodos sin pagos ni abonos previos, todos con la misma tarifa vigente. Además, cada periodo debe caer dentro de la vigencia del registro de concepto que eligió en el listado (fechas «desde / hasta» del catálogo). Se genera un recibo por periodo; el monto lo calcula el servidor según cobranza y la fecha de pago (tolerancia de centavos). Sobrepagos o remanentes no se registran aquí: créelos aparte en Ingresos.
              </Typography>
              {(() => {
                const cSel = conceptosPago.find((x) => String(x.id) === String(conceptoPagoSeleccionado));
                if (!cSel) return null;
                const d = cSel.vigencia_desde ? String(cSel.vigencia_desde).slice(0, 10) : null;
                const h = cSel.vigencia_hasta ? String(cSel.vigencia_hasta).slice(0, 10) : null;
                if (!d && !h) return null;
                const vm = minMaxPeriodoMensualPorVigenciaConcepto(cSel);
                return (
                  <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                    Vigencia de este concepto (registro id {cSel.id}): {d || '—'} → {h || 'sin fin'}
                    {vm ? ` · Para mensual, periodos permitidos aprox.: ${vm.minKey} … ${vm.maxKey}` : null}
                  </Alert>
                );
              })()}
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Propiedad" fullWidth value={propiedadPagarVariosPeriodos} InputProps={{ readOnly: true }} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Concepto" fullWidth value={tablaCobranzaData?.concepto?.nombre || ''} InputProps={{ readOnly: true }} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Periodo desde"
                    fullWidth
                    value={formPagarVariosPeriodos.periodo_desde}
                    onChange={(e) => setFormPagarVariosPeriodos({ ...formPagarVariosPeriodos, periodo_desde: e.target.value })}
                    placeholder="2026-01 o 2026-B1"
                    helperText={(() => {
                      const cSel = conceptosPago.find((x) => String(x.id) === String(conceptoPagoSeleccionado));
                      const vm = minMaxPeriodoMensualPorVigenciaConcepto(cSel);
                      if (vm) return `Mensual: use claves entre ${vm.minKey} y ${vm.maxKey} (vigencia del concepto).`;
                      return 'Misma clave que en la tabla; bimestral/trimestral/etc. deben respetar la vigencia del registro (el servidor valida).';
                    })()}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Periodo hasta"
                    fullWidth
                    value={formPagarVariosPeriodos.periodo_hasta}
                    onChange={(e) => setFormPagarVariosPeriodos({ ...formPagarVariosPeriodos, periodo_hasta: e.target.value })}
                    placeholder="2026-03 o 2026-B2"
                    helperText={(() => {
                      const cSel = conceptosPago.find((x) => String(x.id) === String(conceptoPagoSeleccionado));
                      const vm = minMaxPeriodoMensualPorVigenciaConcepto(cSel);
                      if (vm) return `Máximo ${vm.maxKey} para este registro.`;
                      return undefined;
                    })()}
                    size="small"
                  />
                </Grid>
                {tablaCobranzaData?.meses?.length ? (
                  <Grid item xs={12}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const meses = tablaCobranzaData.meses || [];
                        const first = meses[0];
                        const last = meses[meses.length - 1];
                        let pk0 = first ? (first.periodoKey || `${first.year}-${String(first.mes || first.periodIndex || 1).padStart(2, '0')}`) : '';
                        let pk1 = last ? (last.periodoKey || `${last.year}-${String(last.mes || last.periodIndex || 1).padStart(2, '0')}`) : '';
                        const cSel = conceptosPago.find((x) => String(x.id) === String(conceptoPagoSeleccionado));
                        const vm = minMaxPeriodoMensualPorVigenciaConcepto(cSel);
                        if (vm && pk0 && pk1) {
                          const r = ajustarRangoMesualAVigencia(pk0, pk1, vm.minKey, vm.maxKey);
                          pk0 = r.desde;
                          pk1 = r.hasta;
                        }
                        setFormPagarVariosPeriodos((prev) => ({ ...prev, periodo_desde: pk0, periodo_hasta: pk1 }));
                      }}
                    >
                      Usar primer y último periodo del año en pantalla (recortado a vigencia si aplica)
                    </Button>
                  </Grid>
                ) : null}
                <Grid item xs={12}>
                  <TextField
                    label="Referencia (solo año en pantalla)"
                    fullWidth
                    value={anoCobranza}
                    InputProps={{ readOnly: true }}
                    size="small"
                    helperText="Puede escribir periodos de otros años en desde/hasta si aplica."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Fecha"
                    type="date"
                    fullWidth
                    value={formPagarVariosPeriodos.fecha}
                    onChange={(e) => setFormPagarVariosPeriodos({ ...formPagarVariosPeriodos, fecha: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formPagarVariosPeriodos.depositado}
                        onChange={(e) => setFormPagarVariosPeriodos({ ...formPagarVariosPeriodos, depositado: e.target.checked })}
                      />
                    }
                    label="Depositado"
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Vista previa (según fecha de pago y cobranza)
                </Typography>
                {loadingPreviewVariosRango ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={22} />
                    <Typography variant="body2" color="text.secondary">Calculando…</Typography>
                  </Box>
                ) : previewVariosRangoErr ? (
                  <Alert severity="warning" sx={{ py: 0.5 }}>{previewVariosRangoErr}</Alert>
                ) : previewVariosRango?.lineas?.length ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{previewVariosRango.n_periodos}</strong> recibo(s) · Total a registrar:{' '}
                      <strong>${Number(previewVariosRango.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                      {previewVariosRango.concepto_nombre ? ` · ${previewVariosRango.concepto_nombre}` : ''}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Periodo</TableCell>
                            <TableCell align="right">Cuota</TableCell>
                            <TableCell align="right">Mora</TableCell>
                            <TableCell align="right">Monto</TableCell>
                            <TableCell align="right">Recargos</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewVariosRango.lineas.map((row) => (
                            <TableRow key={row.periodoKey}>
                              <TableCell>{row.periodoKey}</TableCell>
                              <TableCell align="right">
                                ${Number(row.monto_cuota ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell align="right">
                                ${Number(row.recargo_mora_esperado ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell align="right">
                                ${Number(row.monto ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell align="right">
                                ${Number(row.recargos ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell align="right">
                                ${Number(row.total_linea ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Indique periodo desde, hasta y fecha para ver el desglose.
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowDialogPagarVariosPeriodos(false);
                  setPropiedadPagarVariosPeriodos('');
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handlePagarVariosPeriodos}
                disabled={
                  creandoPagoVariosPeriodos ||
                  loadingPreviewVariosRango ||
                  !!previewVariosRangoErr ||
                  !previewVariosRango?.lineas?.length
                }
              >
                {creandoPagoVariosPeriodos ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Registrando…
                  </>
                ) : (
                  'Registrar pagos'
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Diálogo Revertir pago: solo este periodo o todo el año */}
          <Dialog open={showDialogRevertirPago} onClose={() => { setShowDialogRevertirPago(false); setPagoARevertir(null); }} maxWidth="sm" fullWidth>
            <DialogTitle>Revertir pago</DialogTitle>
            <DialogContent>
              {pagoARevertir && (
                <>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Folio: <strong>{pagoARevertir.folio}</strong>
                    {pagoARevertir.propiedad && ` · Propiedad ${pagoARevertir.propiedad}`}
                    {pagoARevertir.concepto && ` · ${pagoARevertir.concepto}`}
                    {pagoARevertir.mes && ` · Periodo ${pagoARevertir.mes}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ¿Desea revertir solo este pago o todos los pagos del año para esta propiedad y este concepto?
                  </Typography>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setShowDialogRevertirPago(false); setPagoARevertir(null); }}>Cancelar</Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => pagoARevertir && handleEliminarPago(pagoARevertir.id, false)}
              >
                Solo este periodo
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => pagoARevertir && handleEliminarPago(pagoARevertir.id, true)}
              >
                Todo el año
              </Button>
            </DialogActions>
          </Dialog>

          {tabActual === 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Gestiona residentes, contactos, vehículos y mascotas por propiedad. Alta masiva, bloqueos e invitaciones.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>
              <ResidentesTab
                residentes={residentes}
                total={residentesPaginacion.total}
                page={residentesPaginacion.pagina}
                rowsPerPage={residentesPaginacion.registrosPorPagina}
                ordenPor={residentesOrden.por}
                ordenDir={residentesOrden.dir}
                onPageChange={(newPage) => loadResidentes(null, { pagina: newPage })}
                onRowsPerPageChange={(n) => loadResidentes(null, { pagina: 0, registrosPorPagina: n })}
                onSearch={(q) => { if (q === residentesBusqueda) return; setResidentesBusqueda(q); loadResidentes(null, { q, pagina: 0 }); }}
                onSortChange={(orden) => { setResidentesOrden(orden); loadResidentes(null, { orden, pagina: 0 }); }}
                onNuevoResidente={handleAbrirModalNuevo}
                onAltaMasiva={() => { setAltaMasivaCantidad(10); setAltaMasivaPrefijo('Casa'); setAltaMasivaCredenciales([]); setShowModalAltaMasiva(true); }}
                onEdit={handleAbrirModalEditar}
                onManageContactos={handleAbrirModalContactos}
                onManageVehiculos={handleAbrirModalVehiculos}
                onManageMascotas={handleAbrirModalMascotas}
                onReenviarInvitacion={handleReenviarInvitacion}
                onReenviarResetPassword={handleReenviarResetPassword}
                onDarDeBaja={handleDarDeBaja}
                onToggleBloqueo={handleToggleBloqueado}
                onDarDeBajaAccesoPropiedad={handleDarDeBajaAccesoPropiedad}
                onReactivarAccesoPropiedad={handleReactivarAccesoPropiedad}
              />

          {/* Modal Alta masiva */}
          <Dialog open={showModalAltaMasiva} onClose={() => !altaMasivaProgreso && setShowModalAltaMasiva(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Alta masiva de propiedades</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Se crearán residentes con nombre "Residente 1", "Residente 2"... y propiedad según el prefijo (ej. Casa 1, Casa 2). Máximo 500. La contraseña se mostrará al final para el primer inicio de sesión.
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Número de casas a dar de alta"
                value={altaMasivaCantidad}
                onChange={(e) => setAltaMasivaCantidad(e.target.value)}
                inputProps={{ min: 1, max: 500 }}
                margin="normal"
                helperText="Mín. 1, máx. 500"
              />
              <TextField
                fullWidth
                label="Prefijo de propiedad (opcional)"
                value={altaMasivaPrefijo}
                onChange={(e) => setAltaMasivaPrefijo(e.target.value)}
                margin="normal"
                placeholder="Casa (o vacío para 1, 2, 3...)"
                helperText="Ej: Casa → Casa 1, Casa 2. Vacío → 1, 2, 3"
              />
              {altaMasivaProgreso && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="primary">
                    Creando {altaMasivaProgreso.current} / {altaMasivaProgreso.total}...
                  </Typography>
                  <Box sx={{ width: '100%', mt: 1 }}>
                    <Box component="div" sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                      <Box component="div" sx={{ height: '100%', width: `${(100 * altaMasivaProgreso.current) / altaMasivaProgreso.total}%`, bgcolor: 'primary.main', transition: 'width 0.2s' }} />
                    </Box>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => !altaMasivaProgreso && setShowModalAltaMasiva(false)} disabled={!!altaMasivaProgreso}>
                Cancelar
              </Button>
              <Button variant="contained" onClick={handleAltaMasiva} disabled={!!altaMasivaProgreso}>
                {altaMasivaProgreso ? 'Creando...' : 'Generar'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Modal Credenciales (contraseñas visibles para primer inicio de sesión) */}
          <Dialog open={showModalCredenciales} onClose={() => setShowModalCredenciales(false)} maxWidth="md" fullWidth>
            <DialogTitle>Credenciales para primer inicio de sesión</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Guarda o copia estas credenciales. Los residentes podrán cambiar la contraseña desde la app móvil después de iniciar sesión.
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Nombre</strong></TableCell>
                      <TableCell><strong>Propiedad</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Contraseña</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {altaMasivaCredenciales.map((c, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{c.nombre}</TableCell>
                        <TableCell>{c.propiedad}</TableCell>
                        <TableCell>{c.email}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{c.password}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowModalCredenciales(false)}>Cerrar</Button>
            </DialogActions>
          </Dialog>

          {/* Modal Crear/Editar Residente */}
          <Dialog
            open={showModalResidente}
            onClose={() => setShowModalResidente(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {residenteEditando ? 'Editar Residente' : 'Nuevo Residente'}
            </DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                label="Nombre Completo"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                margin="normal"
                required
              />
              {residenteEditando && (
                <TextField
                  fullWidth
                  label="Nueva Contraseña (dejar vacío para no cambiar)"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  margin="normal"
                  helperText="Solo completa este campo si deseas cambiar la contraseña del usuario"
                />
              )}
              {!residenteEditando && (
                <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
                  Se enviará automáticamente una invitación por correo para que el usuario complete su registro y establezca su contraseña.
                </Alert>
              )}
              <TextField
                fullWidth
                label="Propiedad (ej: Casa 10B)"
                value={formData.propiedad}
                onChange={(e) => setFormData({ ...formData, propiedad: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Teléfono"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                margin="normal"
                placeholder="+52 123 456 7890"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.telefono_no_marcar === true}
                    onChange={(e) => setFormData({ ...formData, telefono_no_marcar: e.target.checked })}
                  />
                }
                label="No marcar este número (no recibir llamadas)"
              />
              {residenteEditando && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  El orden de llamada se define en la pestaña &quot;Orden de llamada&quot;.
                </Typography>
              )}
              <TextField
                fullWidth
                label="Categoría"
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                margin="normal"
                select
                required
                helperText={formData.categoria === 'Propietario' ? 'Solo puede haber un Propietario por propiedad' : ''}
              >
                <MenuItem value="Propietario">Propietario</MenuItem>
                <MenuItem value="Inquilino">Inquilino</MenuItem>
                <MenuItem value="Otro">Otro</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label="Rol"
                value={formData.rol}
                onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
                margin="normal"
                select
                required
                helperText={formData.rol === 'residente' ? 'Puede generar pases QR/códigos numéricos' : formData.rol === 'guardia' ? 'Puede validar códigos QR y numéricos' : ''}
              >
                <MenuItem value="residente">Residente (Generar Pases)</MenuItem>
                <MenuItem value="guardia">Guardia (Validar Códigos)</MenuItem>
              </TextField>

              {(residenteEditando?.propiedad?.trim() || (!residenteEditando && formData.categoria === 'Propietario')) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Documentos de identificación de la propiedad (máx. 3)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {residenteEditando ? 'Subir o eliminar documentos para la casa ' + residenteEditando.propiedad : formData.propiedad?.trim() ? 'Se subirán al crear el residente (casa ' + formData.propiedad.trim() + ').' : 'Indique la propiedad (casa) arriba para poder agregar documentos.'}
                  </Typography>
                  {residenteEditando ? (
                    <>
                      {loadingAdjuntos ? (
                        <Box sx={{ py: 1 }}><CircularProgress size={24} /></Box>
                      ) : (
                        <>
                          {adjuntosPropiedad.length > 0 && (
                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                              <Table size="small">
                                <TableBody>
                                  {adjuntosPropiedad.map((a) => (
                                    <TableRow key={a.id}>
                                      <TableCell><a href={API_BASE_URL.replace(/\/$/, '') + a.archivo_url} target="_blank" rel="noopener noreferrer">{a.nombre_mostrar}</a></TableCell>
                                      <TableCell align="right">
                                        <IconButton size="small" color="error" onClick={() => handleEliminarAdjuntoPropiedad(a.id)}><DeleteIcon fontSize="small" /></IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                          {adjuntosPropiedad.length < 3 && (
                            <>
                              <input type="file" ref={adjuntosPropiedadInputRef} accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleSubirAdjuntoPropiedad} />
                              <Button size="small" variant="outlined" onClick={() => adjuntosPropiedadInputRef.current?.click()} disabled={loadingAdjuntos || adjuntosPropiedad.length >= 3}>Subir documento</Button>
                            </>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {adjuntosPropiedadFiles.length > 0 && (
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                          <Table size="small">
                            <TableBody>
                              {adjuntosPropiedadFiles.map((f, i) => (
                                <TableRow key={i}>
                                  <TableCell>{f.name}</TableCell>
                                  <TableCell align="right">
                                    <IconButton size="small" color="error" onClick={() => setAdjuntosPropiedadFiles(prev => prev.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                      {adjuntosPropiedadFiles.length < 3 && formData.propiedad?.trim() && (
                        <Button size="small" variant="outlined" component="label">
                          Agregar documento
                          <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files?.[0]; if (f && adjuntosPropiedadFiles.length < 3) setAdjuntosPropiedadFiles(prev => [...prev, f]); e.target.value = ''; }} />
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowModalResidente(false)}>
                Cancelar
              </Button>
              <Button onClick={handleGuardarResidente} variant="contained">
                {residenteEditando ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Modal Gestionar Contactos */}
          <Dialog
            open={showModalContactos}
            onClose={() => setShowModalContactos(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Contactos de {residenteEditando?.nombre} - {residenteEditando?.propiedad}
              <Typography variant="caption" display="block" color="text.secondary">
                Máximo 10 contactos por propiedad
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {contactoEditando ? 'Editar Contacto' : 'Agregar Nuevo Contacto'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nombre"
                      value={formContacto.nombre}
                      onChange={(e) => setFormContacto({ ...formContacto, nombre: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Teléfono"
                      value={formContacto.telefono}
                      onChange={(e) => setFormContacto({ ...formContacto, telefono: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email (opcional)"
                      type="email"
                      value={formContacto.email}
                      onChange={(e) => setFormContacto({ ...formContacto, email: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Tipo"
                      value={formContacto.tipo}
                      onChange={(e) => setFormContacto({ ...formContacto, tipo: e.target.value })}
                      select
                    >
                      <MenuItem value="contacto">Contacto</MenuItem>
                      <MenuItem value="inquilino">Inquilino (acceso app)</MenuItem>
                      <MenuItem value="emergencia">Emergencia</MenuItem>
                      <MenuItem value="propietario">Propietario</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!formContacto.no_marcar}
                          onChange={(e) => setFormContacto({ ...formContacto, no_marcar: e.target.checked })}
                        />
                      }
                      label="No marcar este número (ej. propietario que no vive en la propiedad)"
                    />
                  </Grid>
                  {formContacto.email && formContacto.email.trim() !== '' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Contraseña temporal (opcional)"
                          type="password"
                          value={formContacto.password}
                          onChange={(e) => setFormContacto({ ...formContacto, password: e.target.value })}
                          helperText="Mín. 6 caracteres. Si la completas, el usuario podrá entrar con su email y esta contraseña (sin correo de invitación)."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={enviarInvitacionContacto}
                              onChange={(e) => setEnviarInvitacionContacto(e.target.checked)}
                            />
                          }
                          label="Enviar invitación por correo para que establezca su contraseña"
                        />
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
                          Se creará una cuenta y se enviará un correo de invitación a {formContacto.email}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleGuardarContacto}
                      disabled={contactos.filter(c => c.activo).length >= 10 && !contactoEditando}
                    >
                      {contactoEditando ? 'Actualizar' : 'Agregar Contacto'}
                    </Button>
                    {contactoEditando && (
                      <Button
                        variant="outlined"
                        onClick={() => {
setContactoEditando(null);
                        setFormContacto({
                            nombre: '',
                            telefono: '',
                            email: '',
                            tipo: 'contacto',
                            no_marcar: false,
                            orden: '',
                            password: '',
                          });
                          setEnviarInvitacionContacto(false);
                        }}
                        sx={{ ml: 2 }}
                      >
                        Cancelar Edición
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Typography variant="h6" gutterBottom>
                Contactos Registrados ({contactos.filter(c => c.activo).length}/10)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Nombre</strong></TableCell>
                      <TableCell><strong>Tipo</strong></TableCell>
                      <TableCell><strong>Orden Llamada</strong></TableCell>
                      <TableCell><strong>Acciones</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contactos.filter(c => c.activo).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No hay contactos registrados
                        </TableCell>
                      </TableRow>
                    ) : (
                      contactos.filter(c => c.activo).sort((a, b) => (a.orden || 0) - (b.orden || 0)).map((contacto) => {
                        const sinAccesoApp = contacto.email && contacto.email.trim() !== '' && contacto.tiene_acceso_app === false;
                        return (
                        <TableRow key={contacto.id} sx={sinAccesoApp ? { opacity: 0.7 } : undefined}>
                          <TableCell>
                            <span style={sinAccesoApp ? { textDecoration: 'line-through', color: '#757575' } : undefined}>
                              {contacto.nombre}
                            </span>
                          </TableCell>
                          <TableCell>{contacto.tipo === 'contacto' || contacto.tipo === 'inquilino' ? 'Inquilino' : contacto.tipo}</TableCell>
                          <TableCell>{contacto.no_marcar ? 'No marcar' : (contactosCallables.findIndex(c => c.id === contacto.id) + 1) + 'º'}</TableCell>
                          <TableCell>
                            {!contacto.no_marcar && contactosCallables.findIndex(c => c.id === contacto.id) > 0 && (
                              <IconButton size="small" title="Subir prioridad" onClick={() => handleSubirOrdenContacto(contacto)}>
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            )}
                            {!contacto.no_marcar && contactosCallables.findIndex(c => c.id === contacto.id) >= 0 && contactosCallables.findIndex(c => c.id === contacto.id) < contactosCallables.length - 1 && (
                              <IconButton size="small" title="Bajar prioridad" onClick={() => handleBajarOrdenContacto(contacto)}>
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            )}
                            {contacto.email && contacto.email.trim() !== '' && (
                              <MuiTooltip title="Restablecer contraseña (envía correo al usuario)">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleReenviarInvitacionContacto(contacto.id)}
                                    disabled={contactoReenviandoId === contacto.id}
                                  >
                                    {contactoReenviandoId === contacto.id ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <SendIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </MuiTooltip>
                            )}
                            {contacto.email && contacto.email.trim() !== '' && (
                              <MuiTooltip title="Dar de baja acceso en la app (mantiene el contacto)">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDarDeBajaAccesoContacto(contacto.id)}
                                    disabled={contactoBajandoAccesoId === contacto.id}
                                    sx={{ color: '#ed6c02' }}
                                  >
                                    {contactoBajandoAccesoId === contacto.id ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <VisibilityOff fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </MuiTooltip>
                            )}
                            {contacto.email && contacto.email.trim() !== '' && (
                              <MuiTooltip title="Reactivar acceso a la app para este contacto">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleReactivarAccesoContacto(contacto.id)}
                                    disabled={contactoReactivandoAccesoId === contacto.id}
                                    sx={{ color: '#2e7d32' }}
                                  >
                                    {contactoReactivandoAccesoId === contacto.id ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <RestoreIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </MuiTooltip>
                            )}
                            <MuiTooltip title="Editar contacto">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditarContacto(contacto)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </MuiTooltip>
                            <MuiTooltip title="Eliminar contacto (lo quita de la lista)">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleEliminarContacto(contacto.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </MuiTooltip>
                          </TableCell>
                        </TableRow>
                      );})
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowModalContactos(false)}>
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>

          {/* Modal Vehículos */}
          <Dialog open={showModalVehiculos} onClose={() => setShowModalVehiculos(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              Vehículos de {residenteEditando?.nombre} - {residenteEditando?.propiedad}
              <Typography variant="caption" display="block" color="text.secondary">Máximo 5 vehículos por propiedad</Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>{vehiculoEditando ? 'Editar vehículo' : 'Agregar vehículo'}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Color" value={formVehiculo.color} onChange={(e) => setFormVehiculo({ ...formVehiculo, color: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Marca" value={formVehiculo.marca} onChange={(e) => setFormVehiculo({ ...formVehiculo, marca: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Modelo" value={formVehiculo.modelo} onChange={(e) => setFormVehiculo({ ...formVehiculo, modelo: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Placas" value={formVehiculo.placas} onChange={(e) => setFormVehiculo({ ...formVehiculo, placas: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="TAG ID" value={formVehiculo.tag_id} onChange={(e) => setFormVehiculo({ ...formVehiculo, tag_id: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" gutterBottom>Foto</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Button variant="outlined" component="label" startIcon={<PhotoCameraIcon />} disabled={subiendoFotoVehiculo}>
                        {subiendoFotoVehiculo ? 'Subiendo…' : 'Subir foto'}
                        <input type="file" accept="image/*" hidden onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setSubiendoFotoVehiculo(true);
                          const url = await handleUploadFoto(file, 'vehiculo');
                          if (url) setFormVehiculo((prev) => ({ ...prev, foto_url: url }));
                          setSubiendoFotoVehiculo(false);
                          e.target.value = '';
                        }} />
                      </Button>
                      {formVehiculo.foto_url && (
                        <>
                          <Box
                            component="img"
                            src={getFotoFullUrl(formVehiculo.foto_url)}
                            alt="Foto"
                            sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                            onClick={() => { setImagePreviewUrl(getFotoFullUrl(formVehiculo.foto_url)); setImagePreviewError(false); setShowImagePreview(true); }}
                            onError={(e) => { console.log('Error cargando foto (vehículo form):', e.target.src); }}
                          />
                          <Button size="small" color="error" variant="outlined" onClick={() => setFormVehiculo((prev) => ({ ...prev, foto_url: '' }))}>Quitar foto</Button>
                        </>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" onClick={handleGuardarVehiculo} disabled={vehiculos.length >= 5 && !vehiculoEditando}>
                      {vehiculoEditando ? 'Actualizar' : 'Agregar vehículo'}
                    </Button>
                    {vehiculoEditando && (
                      <Button variant="outlined" sx={{ ml: 2 }} onClick={() => { setVehiculoEditando(null); setFormVehiculo({ color: '', marca: '', modelo: '', placas: '', tag_id: '', foto_url: '' }); }}>Cancelar</Button>
                    )}
                  </Grid>
                </Grid>
              </Box>
              <Typography variant="h6" gutterBottom>Vehículos registrados ({vehiculos.length}/5)</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Foto</strong></TableCell>
                      <TableCell><strong>Color</strong></TableCell>
                      <TableCell><strong>Marca</strong></TableCell>
                      <TableCell><strong>Modelo</strong></TableCell>
                      <TableCell><strong>Placas</strong></TableCell>
                      <TableCell><strong>TAG ID</strong></TableCell>
                      <TableCell><strong>Estado</strong></TableCell>
                      <TableCell><strong>Acciones</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vehiculos.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center">No hay vehículos registrados</TableCell></TableRow>
                    ) : (
                      vehiculos.map((v) => (
                        <TableRow key={v.id} sx={v.activo === false ? { opacity: 0.85, bgcolor: 'action.hover' } : undefined}>
                          <TableCell>
                            {v.foto_url ? (
                              <Box
                                component="img"
                                src={getFotoFullUrl(v.foto_url)}
                                alt="Foto"
                                sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                                onClick={() => { setImagePreviewUrl(getFotoFullUrl(v.foto_url)); setImagePreviewError(false); setShowImagePreview(true); }}
                                onError={(e) => { console.log('Error cargando foto (tabla vehículo):', e.target.src); }}
                              />
                            ) : (
                              <Typography variant="caption">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>{v.color || '—'}</TableCell>
                          <TableCell>{v.marca || '—'}</TableCell>
                          <TableCell>{v.modelo || '—'}</TableCell>
                          <TableCell>{v.placas || '—'}</TableCell>
                          <TableCell>{v.tag_id || '—'}</TableCell>
                          <TableCell>
                            {v.activo !== false ? (
                              <Chip size="small" label="Activo" color="success" variant="outlined" />
                            ) : (
                              <Chip size="small" label="Inactivo" color="default" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="primary" onClick={() => handleEditarVehiculo(v)}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => handleEliminarVehiculo(v.id)}><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions><Button onClick={() => setShowModalVehiculos(false)}>Cerrar</Button></DialogActions>
          </Dialog>

          {/* Modal Mascotas */}
          <Dialog open={showModalMascotas} onClose={() => setShowModalMascotas(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              Mascotas de {residenteEditando?.nombre} - {residenteEditando?.propiedad}
              <Typography variant="caption" display="block" color="text.secondary">Máximo 5 mascotas por propiedad</Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>{mascotaEditando ? 'Editar mascota' : 'Agregar mascota'}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth select label="Tipo" value={formMascota.tipo} onChange={(e) => setFormMascota({ ...formMascota, tipo: e.target.value })}>
                      <MenuItem value="Perro">Perro</MenuItem>
                      <MenuItem value="Gato">Gato</MenuItem>
                      <MenuItem value="Otro">Otro</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Raza" value={formMascota.raza} onChange={(e) => setFormMascota({ ...formMascota, raza: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth select label="Tamaño" value={formMascota.tamaño} onChange={(e) => setFormMascota({ ...formMascota, tamaño: e.target.value })}>
                      <MenuItem value="Pequeño">Pequeño</MenuItem>
                      <MenuItem value="Mediano">Mediano</MenuItem>
                      <MenuItem value="Grande">Grande</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Nombre (opcional)" value={formMascota.nombre} onChange={(e) => setFormMascota({ ...formMascota, nombre: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" gutterBottom>Foto</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Button variant="outlined" component="label" startIcon={<PhotoCameraIcon />} disabled={subiendoFotoMascota}>
                        {subiendoFotoMascota ? 'Subiendo…' : 'Subir foto'}
                        <input type="file" accept="image/*" hidden onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setSubiendoFotoMascota(true);
                          const url = await handleUploadFoto(file, 'mascota');
                          if (url) setFormMascota((prev) => ({ ...prev, foto_url: url }));
                          setSubiendoFotoMascota(false);
                          e.target.value = '';
                        }} />
                      </Button>
                      {formMascota.foto_url && (
                        <>
                          <Box
                            component="img"
                            src={getFotoFullUrl(formMascota.foto_url)}
                            alt="Foto"
                            sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                            onClick={() => { setImagePreviewUrl(getFotoFullUrl(formMascota.foto_url)); setImagePreviewError(false); setShowImagePreview(true); }}
                            onError={(e) => { console.log('Error cargando foto (mascota form):', e.target.src); }}
                          />
                          <Button size="small" color="error" variant="outlined" onClick={() => setFormMascota((prev) => ({ ...prev, foto_url: '' }))}>Quitar foto</Button>
                        </>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" onClick={handleGuardarMascota} disabled={mascotas.length >= 5 && !mascotaEditando}>
                      {mascotaEditando ? 'Actualizar' : 'Agregar mascota'}
                    </Button>
                    {mascotaEditando && (
                      <Button variant="outlined" sx={{ ml: 2 }} onClick={() => { setMascotaEditando(null); setFormMascota({ tipo: 'Perro', raza: '', tamaño: 'Mediano', nombre: '', foto_url: '' }); }}>Cancelar</Button>
                    )}
                  </Grid>
                </Grid>
              </Box>
              <Typography variant="h6" gutterBottom>Mascotas registradas ({mascotas.length}/5)</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Foto</strong></TableCell>
                      <TableCell><strong>Tipo</strong></TableCell>
                      <TableCell><strong>Raza</strong></TableCell>
                      <TableCell><strong>Tamaño</strong></TableCell>
                      <TableCell><strong>Nombre</strong></TableCell>
                      <TableCell><strong>Acciones</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mascotas.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">No hay mascotas registradas</TableCell></TableRow>
                    ) : (
                      mascotas.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell>
                            {m.foto_url ? (
                              <Box
                                component="img"
                                src={getFotoFullUrl(m.foto_url)}
                                alt="Foto"
                                sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                                onClick={() => { setImagePreviewUrl(getFotoFullUrl(m.foto_url)); setImagePreviewError(false); setShowImagePreview(true); }}
                                onError={(e) => { console.log('Error cargando foto (tabla mascota):', e.target.src); }}
                              />
                            ) : (
                              <Typography variant="caption">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>{m.tipo}</TableCell>
                          <TableCell>{m.raza || '—'}</TableCell>
                          <TableCell>{m.tamaño || '—'}</TableCell>
                          <TableCell>{m.nombre || '—'}</TableCell>
                          <TableCell>
                            <IconButton size="small" color="primary" onClick={() => handleEditarMascota(m)}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => handleEliminarMascota(m.id)}><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions><Button onClick={() => setShowModalMascotas(false)}>Cerrar</Button></DialogActions>
          </Dialog>

          {/* Modal ver foto en grande */}
          <Dialog open={showImagePreview} onClose={() => setShowImagePreview(false)} maxWidth={false} PaperProps={{ sx: { maxWidth: '95vw', maxHeight: '95vh' } }}>
            <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
              {!imagePreviewError && imagePreviewUrl ? (
                <Box
                  component="img"
                  src={imagePreviewUrl}
                  alt="Vista previa"
                  sx={{ width: '100%', height: 'auto', maxHeight: '90vh', objectFit: 'contain' }}
                  onError={() => setImagePreviewError(true)}
                />
              ) : imagePreviewError ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, color: 'text.secondary' }}>No se pudo cargar la imagen</Box>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowImagePreview(false)}>Cerrar</Button>
            </DialogActions>
          </Dialog>
            </>
          )}

          {tabActual === 1 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Buscar y listar por inquilino, mascota o carro. Resultados agrupados por casa.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                    <Tabs value={catalogoTipo} onChange={(e, v) => { setCatalogoTipo(v); setCatalogoPagina(0); loadCatalogos(v, catalogoBusqueda, 0); }} sx={{ minHeight: 40 }}>
                      <Tab label="Inquilinos" value="inquilinos" />
                      <Tab label="Mascotas" value="mascotas" />
                      <Tab label="Carros" value="vehiculos" />
                    </Tabs>
                    <TextField
                      size="small"
                      placeholder="Buscar..."
                      value={catalogoBusqueda}
                      onChange={(e) => setCatalogoBusqueda(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setCatalogoPagina(0); loadCatalogos(undefined, undefined, 0); } }}
                      sx={{ minWidth: 200 }}
                    />
                    <Button variant="contained" onClick={() => { setCatalogoPagina(0); loadCatalogos(undefined, undefined, 0); }}>Buscar</Button>
                  </Box>
                </CardContent>
              </Card>
              {catalogoLoading ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
              ) : (
                <>
                  {catalogoTipo === 'inquilinos' && (() => {
                    const grupos = catalogoInquilinos; // array de { propiedad, residentes, contactos }
                    if (!grupos || grupos.length === 0) return <Typography color="text.secondary">No hay resultados.</Typography>;
                    return (
                      <>
                        {grupos.map((g) => {
                          const casa = g.propiedad || '(sin casa)';
                          const residentes = g.residentes || [];
                          const contactos = g.contactos || [];
                          const propietarios = contactos.filter(c => c.tipo === 'propietario');
                          const emergencia = contactos.filter(c => c.tipo === 'emergencia');
                          const contactosLista = contactos.filter(c => c.tipo === 'contacto' || c.tipo === 'inquilino');
                          return (
                            <Card key={casa} sx={{ mb: 2 }}>
                              <CardContent>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Casa {casa}</Typography>
                                {residentes.length > 0 && (
                                  <>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Residentes</Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
                                      <Table size="small">
                                        <TableHead><TableRow><TableCell>Nombre</TableCell><TableCell>Categoría</TableCell><TableCell>Teléfono</TableCell><TableCell>Email</TableCell></TableRow></TableHead>
                                        <TableBody>
                                          {residentes.map((r) => (
                                            <TableRow key={r.id}><TableCell>{r.nombre || '—'}</TableCell><TableCell>{r.categoria || '—'}</TableCell><TableCell>{r.telefono || '—'}</TableCell><TableCell>{r.email || '—'}</TableCell></TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </>
                                )}
                                {propietarios.length > 0 && (
                                  <>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Propietarios (contacto)</Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
                                      <Table size="small">
                                        <TableHead><TableRow><TableCell>Nombre</TableCell><TableCell>Teléfono</TableCell><TableCell>Email</TableCell></TableRow></TableHead>
                                        <TableBody>
                                          {propietarios.map((c) => (
                                            <TableRow key={c.id}><TableCell>{c.nombre || '—'}</TableCell><TableCell>{c.telefono || '—'}</TableCell><TableCell>{c.email || '—'}</TableCell></TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </>
                                )}
                                {emergencia.length > 0 && (
                                  <>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Emergencia</Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
                                      <Table size="small">
                                        <TableHead><TableRow><TableCell>Nombre</TableCell><TableCell>Teléfono</TableCell><TableCell>Email</TableCell></TableRow></TableHead>
                                        <TableBody>
                                          {emergencia.map((c) => (
                                            <TableRow key={c.id}><TableCell>{c.nombre || '—'}</TableCell><TableCell>{c.telefono || '—'}</TableCell><TableCell>{c.email || '—'}</TableCell></TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </>
                                )}
                                {contactosLista.length > 0 && (
                                  <>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Contactos</Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                      <Table size="small">
                                        <TableHead><TableRow><TableCell>Nombre</TableCell><TableCell>Teléfono</TableCell><TableCell>Email</TableCell></TableRow></TableHead>
                                        <TableBody>
                                          {contactosLista.map((c) => (
                                            <TableRow key={c.id}><TableCell>{c.nombre || '—'}</TableCell><TableCell>{c.telefono || '—'}</TableCell><TableCell>{c.email || '—'}</TableCell></TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </>
                                )}
                                {residentes.length === 0 && propietarios.length === 0 && emergencia.length === 0 && contactosLista.length === 0 && (
                                  <Typography variant="body2" color="text.secondary">Sin residentes ni contactos.</Typography>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </>
                    );
                  })()}
                  {catalogoTipo === 'mascotas' && (() => {
                    const grupos = catalogoMascotas; // array de { propiedad, items }
                    if (!grupos || grupos.length === 0) return <Typography color="text.secondary">No hay resultados.</Typography>;
                    return grupos.map((g) => (
                      <Card key={g.propiedad} sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Casa {g.propiedad || '(sin casa)'}</Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Nombre</TableCell><TableCell>Tipo</TableCell><TableCell>Raza</TableCell><TableCell>Tamaño</TableCell><TableCell>Residente</TableCell></TableRow></TableHead>
                              <TableBody>
                                {(g.items || []).map((m) => (
                                  <TableRow key={m.id}><TableCell>{m.nombre || '—'}</TableCell><TableCell>{m.tipo || '—'}</TableCell><TableCell>{m.raza || '—'}</TableCell><TableCell>{m.tamaño || '—'}</TableCell><TableCell>{m.nombre_residente || '—'}</TableCell></TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    ));
                  })()}
                  {catalogoTipo === 'vehiculos' && (() => {
                    const grupos = catalogoVehiculos; // array de { propiedad, items }
                    if (!grupos || grupos.length === 0) return <Typography color="text.secondary">No hay resultados.</Typography>;
                    return grupos.map((g) => (
                      <Card key={g.propiedad} sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Casa {g.propiedad || '(sin casa)'}</Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead><TableRow><TableCell>Marca</TableCell><TableCell>Modelo</TableCell><TableCell>Color</TableCell><TableCell>Placas</TableCell><TableCell>Residente</TableCell><TableCell>Estado</TableCell></TableRow></TableHead>
                              <TableBody>
                                {(g.items || []).map((v) => (
                                  <TableRow key={v.id} sx={v.activo === false ? { opacity: 0.85, bgcolor: 'action.hover' } : undefined}>
                                    <TableCell>{v.marca || '—'}</TableCell><TableCell>{v.modelo || '—'}</TableCell><TableCell>{v.color || '—'}</TableCell><TableCell>{v.placas || '—'}</TableCell><TableCell>{v.nombre_residente || '—'}</TableCell>
                                    <TableCell>{v.activo !== false ? <Chip size="small" label="Activo" color="success" variant="outlined" /> : <Chip size="small" label="Inactivo" color="default" variant="outlined" />}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    ));
                  })()}
                  {(catalogoTipo === 'inquilinos' || catalogoTipo === 'mascotas' || catalogoTipo === 'vehiculos') && catalogoTotalCasas > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Casas {catalogoPagina * CATALOGO_POR_PAGINA + 1}–{Math.min(catalogoPagina * CATALOGO_POR_PAGINA + CATALOGO_POR_PAGINA, catalogoTotalCasas)} de {catalogoTotalCasas}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" disabled={catalogoPagina === 0} onClick={() => { setCatalogoPagina(p => p - 1); loadCatalogos(undefined, undefined, catalogoPagina - 1); }}>Anterior</Button>
                        <Button size="small" disabled={(catalogoPagina + 1) * CATALOGO_POR_PAGINA >= catalogoTotalCasas} onClick={() => { setCatalogoPagina(p => p + 1); loadCatalogos(undefined, undefined, catalogoPagina + 1); }}>Siguiente</Button>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {tabActual === 14 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Resumen de pases, finanzas, residentes, servicios, participación, accesos y documentos del condominio.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>
              <Tabs value={dashboardSeccion} onChange={(e, v) => { setDashboardSeccion(v); if (v === 'pases') return; if (v === 'participacion') loadAlertasPendientesCount(); const sec = v === 'accesos' ? 'accesos-resumen' : v; loadDashboardSeccion(sec); if (v === 'finanzas') loadFinanzasResumen(); }} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tab label="Pases y accesos" value="pases" />
                <Tab label="Finanzas" value="finanzas" />
                <Tab label="Residentes" value="residentes" />
                <Tab label="Servicios" value="servicios" />
                <Tab label="Participación" value="participacion" />
                <Tab label="Accesos" value="accesos" />
                <Tab label="Documentos" value="documentos" />
              </Tabs>

              {dashboardSeccion === 'pases' && (loadingEstadisticas ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : estadisticas ? (
                <>
                  {/* Tarjetas de Resumen — desglose QR / Código / Manual */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Total Generados (QR + Código)
                          </Typography>
                          <Typography variant="h4">
                            {estadisticas.totalGenerados ?? 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            QRs Generados
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#1976d2' }}>
                            {estadisticas.totalQR ?? 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Códigos Numéricos
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#4caf50' }}>
                            {estadisticas.totalCodigo ?? 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Visitas Manuales
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#ff9800' }}>
                            {estadisticas.totalManuales ?? 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Generados Hoy (QR + Código)
                          </Typography>
                          <Typography variant="h4" color="primary">
                            {estadisticas.generadosHoy ?? 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Leídos Exitosamente (QR/Código)
                          </Typography>
                          <Typography variant="h4" color="success.main">
                            {estadisticas.leidosExitosos ?? 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Denegados
                          </Typography>
                          <Typography variant="h4" color="error.main">
                            {estadisticas.denegados ?? 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Para Fechas Futuras
                          </Typography>
                          <Typography variant="h4" color="warning.main">
                            {estadisticas.generadosFuturo ?? 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Gráfica: QR vs Código vs Manual */}
                  {estadisticas.porFormato && estadisticas.porFormato.length > 0 && (
                    <Card sx={{ mb: 4 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Comparación: QR vs Código Numérico vs Manual
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={estadisticas.porFormato.map((e) => ({
                                ...e,
                                total: Number(e.total) || 0,
                                nombre:
                                  e.tipo === 'MANUAL' || e.tipo_pase_formato === 'MANUAL'
                                    ? 'Manual'
                                    : e.tipo === 'CODIGO' || e.tipo_pase_formato === 'CODIGO'
                                      ? 'Código numérico'
                                      : 'QR',
                              }))}
                              dataKey="total"
                              nameKey="nombre"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              label={({ nombre, total }) => `${nombre}: ${total}`}
                            >
                              {estadisticas.porFormato.map((entry, index) => {
                                const tipo = entry.tipo || entry.tipo_pase_formato;
                                const color =
                                  tipo === 'MANUAL' ? '#ff9800'
                                    : tipo === 'CODIGO' ? '#4caf50'
                                      : '#1976d2';
                                return <Cell key={`cell-${index}`} fill={color} />;
                              })}
                            </Pie>
                            <Tooltip formatter={(value) => [Number(value), 'Cantidad']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Gráfica: QRs por Casa */}
                  {estadisticas.porCasa && estadisticas.porCasa.length > 0 && (
                    <Card sx={{ mb: 4 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          QRs/Códigos Generados por Casa (Top 20)
                        </Typography>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={estadisticas.porCasa.map((c) => ({ ...c, total: Number(c.total) || 0 }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="propiedad" angle={-45} textAnchor="end" height={100} />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="total" fill="#1976d2" name="QR + Código" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Gráfica: Generados por Día */}
                  {estadisticasPorDia && estadisticasPorDia.length > 0 && (
                    <Card sx={{ mb: 4 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          QRs/Códigos Generados por Día (Últimos 30 días)
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={estadisticasPorDia}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="fecha"
                              tickFormatter={(value) => formatFechaOnlyMexico(value)}
                            />
                            <YAxis />
                            <Tooltip
                              labelFormatter={(value) => formatFechaOnlyMexico(value)}
                            />
                            <Legend />
                            <Line type="monotone" dataKey="total_generados" stroke="#1976d2" name="Generados" />
                            <Line type="monotone" dataKey="total_usados" stroke="#4caf50" name="Usados" />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Gráfica: Generados por Hora */}
                  {estadisticasPorHora && estadisticasPorHora.length > 0 && (
                    <Card sx={{ mb: 4 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          QRs/Códigos Generados por Hora (Últimas 24 horas)
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={estadisticasPorHora}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="hora" 
                              tickFormatter={(value) => formatHoraMexicoCentralFromInstant(value)}
                            />
                            <YAxis />
                            <Tooltip 
                              labelFormatter={(value) => formatFechaHoraMexicoCentral(value)}
                            />
                            <Legend />
                            <Bar dataKey="total_generados" fill="#1976d2" name="Generados" />
                            <Bar dataKey="total_usados" fill="#4caf50" name="Usados" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Gráfica: Intentos de Acceso */}
                  {estadisticasAccesos && estadisticasAccesos.length > 0 && (
                    <Card sx={{ mb: 4 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Intentos de Acceso por Día (Últimos 30 días)
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={estadisticasAccesos}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="fecha"
                              tickFormatter={(value) => formatFechaOnlyMexico(value)}
                            />
                            <YAxis />
                            <Tooltip
                              labelFormatter={(value) => formatFechaOnlyMexico(value)}
                            />
                            <Legend />
                            <Line type="monotone" dataKey="autorizados" stroke="#4caf50" name="Autorizados" />
                            <Line type="monotone" dataKey="denegados" stroke="#f44336" name="Denegados" />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Razones de Denegación */}
                  {razonesDenegacion && razonesDenegacion.length > 0 && (
                    <Card sx={{ mb: 4 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Razones de Denegación
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Razón</strong></TableCell>
                                <TableCell align="right"><strong>Cantidad</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {razonesDenegacion.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell>{item.razon_denegacion}</TableCell>
                                  <TableCell align="right">{item.total}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Alert severity="info">
                  No hay estadísticas disponibles
                </Alert>
              ))}

              {dashboardSeccion === 'finanzas' && (
                loadingDashboardSeccion ? (
                  <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                ) : estadisticasFinanzas ? (
                  <>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography color="textSecondary">Ingresos del mes (calendario)</Typography><Typography variant="h5">${Number(estadisticasFinanzas.ingresosMes || 0).toLocaleString('es-MX')}</Typography>{estadisticasFinanzas.ingresosMesAnterior != null && <Typography variant="caption" color="text.secondary">Mes ant.: ${Number(estadisticasFinanzas.ingresosMesAnterior).toLocaleString('es-MX')}</Typography>}</CardContent></Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography color="textSecondary">Egresos del mes (calendario)</Typography><Typography variant="h5" color="error.main">${Number(estadisticasFinanzas.egresosMes || 0).toLocaleString('es-MX')}</Typography>{estadisticasFinanzas.egresosMesAnterior != null && <Typography variant="caption" color="text.secondary">Mes ant.: ${Number(estadisticasFinanzas.egresosMesAnterior).toLocaleString('es-MX')}</Typography>}</CardContent></Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography color="textSecondary">Balance del mes (calendario)</Typography><Typography variant="h5" color={estadisticasFinanzas.balanceMes >= 0 ? 'success.main' : 'error.main'}>${Number(estadisticasFinanzas.balanceMes || 0).toLocaleString('es-MX')}</Typography></CardContent></Card>
                      </Grid>
                    </Grid>
                    <Card sx={{ mb: 4 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Resumen de libro (recibos vigentes)</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Ingresos = monto + recargos. Egresos = total del gasto. Excluye recibos anulados. Mismo criterio que la app de residentes.
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                          <Grid item xs={12} sm={4} md={3}>
                            <TextField
                              select
                              fullWidth
                              label="Año"
                              size="small"
                              value={finanzasResumenAño}
                              onChange={(e) => setFinanzasResumenAño(Number(e.target.value))}
                            >
                              {Array.from({ length: Math.max(1, new Date().getFullYear() - 2021) }, (_, i) => new Date().getFullYear() - i).map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} sm={4} md={3}>
                            <TextField
                              select
                              fullWidth
                              label="Periodo"
                              size="small"
                              value={finanzasResumenMes}
                              onChange={(e) => setFinanzasResumenMes(e.target.value)}
                            >
                              {MESES_RESUMEN_FINANZAS.map((m) => (
                                <MenuItem key={m.value || 'ano'} value={m.value}>{m.label}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} sm={4} md={6}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              <Button variant="contained" size="small" onClick={() => loadFinanzasResumen()} disabled={loadingFinanzasResumen}>Actualizar resumen</Button>
                              <Button variant="outlined" size="small" startIcon={<GetAppIcon />} onClick={exportarFinanzasResumenCsv} disabled={!finanzasResumenData?.resumenPeriodo}>Exportar CSV</Button>
                            </Box>
                          </Grid>
                        </Grid>
                        {loadingFinanzasResumen ? (
                          <Box display="flex" justifyContent="center" py={2}><CircularProgress size={28} /></Box>
                        ) : finanzasResumenData?.resumenPeriodo ? (
                          <>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="caption" color="text.secondary">Capital inicial (ejercicio)</Typography>
                                <Typography variant="h6">${Number(finanzasResumenData.resumenPeriodo.capitalInicialEjercicio || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="caption" color="text.secondary">Saldo inicial (libro en el periodo)</Typography>
                                <Typography variant="h6">${Number(finanzasResumenData.resumenPeriodo.saldoInicialPeriodo || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="caption" color="text.secondary">Saldo final (libro en el periodo)</Typography>
                                <Typography variant="h6" color="primary.main">${Number(finanzasResumenData.resumenPeriodo.saldoFinalPeriodo || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="caption" color="text.secondary">Total ingresos (periodo)</Typography>
                                <Typography variant="h6" color="success.main">${Number(finanzasResumenData.resumenPeriodo.totalIngresosPeriodo || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="caption" color="text.secondary">Total egresos (periodo)</Typography>
                                <Typography variant="h6" color="error.main">${Number(finanzasResumenData.resumenPeriodo.totalEgresosPeriodo || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="caption" color="text.secondary">Rango</Typography>
                                <Typography variant="body2">{finanzasResumenData.resumenPeriodo.periodoDesde} — {finanzasResumenData.resumenPeriodo.periodoHasta}</Typography>
                              </Grid>
                            </Grid>
                            {finanzasResumenData.detalleMensualPorMes && finanzasResumenData.detalleMensualPorMes.length > 0 && (
                              <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell><strong>Mes</strong></TableCell>
                                      <TableCell align="right"><strong>Saldo inicial</strong></TableCell>
                                      <TableCell align="right"><strong>Ingresos</strong></TableCell>
                                      <TableCell align="right"><strong>Egresos</strong></TableCell>
                                      <TableCell align="right"><strong>Saldo final</strong></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {finanzasResumenData.detalleMensualPorMes.map((row) => (
                                      <TableRow key={row.mes}>
                                        <TableCell>{row.mes.charAt(0) + row.mes.slice(1).toLowerCase()}</TableCell>
                                        <TableCell align="right">${Number(row.saldoInicialBanco || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell align="right">${Number(row.totalIngresos || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell align="right">${Number(row.totalEgresos || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell align="right">${Number(row.saldoFinalBanco || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                      </TableRow>
                                    ))}
                                    <TableRow>
                                      <TableCell><strong>TOTALES</strong></TableCell>
                                      <TableCell align="right">—</TableCell>
                                      <TableCell align="right"><strong>${Number(finanzasResumenData.resumenPeriodo.totalIngresosPeriodo || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong></TableCell>
                                      <TableCell align="right"><strong>${Number(finanzasResumenData.resumenPeriodo.totalEgresosPeriodo || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong></TableCell>
                                      <TableCell align="right">—</TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                          </>
                        ) : (
                          <Alert severity="info">Pulsa «Actualizar resumen» para cargar el periodo seleccionado.</Alert>
                        )}
                      </CardContent>
                    </Card>
                    {estadisticasFinanzas.topCasasIngresos && estadisticasFinanzas.topCasasIngresos.length > 0 && (
                      <Card sx={{ mb: 4 }}><CardContent><Typography variant="h6" gutterBottom>Top 10 casas por ingresos (mes)</Typography><TableContainer component={Paper} variant="outlined"><Table size="small"><TableHead><TableRow><TableCell>Propiedad</TableCell><TableCell align="right">Total</TableCell></TableRow></TableHead><TableBody>{estadisticasFinanzas.topCasasIngresos.map((r, i) => (<TableRow key={i}><TableCell>{r.propiedad}</TableCell><TableCell align="right">${Number(r.total).toLocaleString('es-MX')}</TableCell></TableRow>))}</TableBody></Table></TableContainer></CardContent></Card>
                    )}
                  </>
                ) : <Alert severity="info">No hay datos de finanzas</Alert>
              )}

              {dashboardSeccion === 'residentes' && (
                loadingDashboardSeccion ? (
                  <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                ) : estadisticasResidentes ? (
                  <>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography color="textSecondary">Residentes activos</Typography><Typography variant="h5" color="success.main">{estadisticasResidentes.residentesActivos ?? 0}</Typography></CardContent></Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography color="textSecondary">Residentes bloqueados</Typography><Typography variant="h5" color="error.main">{estadisticasResidentes.residentesBloqueados ?? 0}</Typography></CardContent></Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography color="textSecondary">Invitaciones pendientes</Typography><Typography variant="h5" color="warning.main">{estadisticasResidentes.invitacionesPendientes ?? 0}</Typography></CardContent></Card>
                      </Grid>
                    </Grid>
                    {estadisticasResidentes.topCasasActividad && estadisticasResidentes.topCasasActividad.length > 0 && (
                      <Card sx={{ mb: 4 }}><CardContent><Typography variant="h6" gutterBottom>Top 5 casas por actividad (pases)</Typography><TableContainer component={Paper} variant="outlined"><Table size="small"><TableHead><TableRow><TableCell>Propiedad</TableCell><TableCell align="right">Pases generados</TableCell></TableRow></TableHead><TableBody>{estadisticasResidentes.topCasasActividad.map((r, i) => (<TableRow key={i}><TableCell>{r.propiedad}</TableCell><TableCell align="right">{r.total}</TableCell></TableRow>))}</TableBody></Table></TableContainer></CardContent></Card>
                    )}
                  </>
                ) : <Alert severity="info">No hay datos de residentes</Alert>
              )}

              {dashboardSeccion === 'servicios' && (
                loadingDashboardSeccion ? (
                  <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                ) : estadisticasServicios ? (
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card><CardContent><Typography color="textSecondary">Servicios pendientes por ingresar</Typography><Typography variant="h5" color="warning.main">{estadisticasServicios.serviciosPendientesIngresar ?? 0}</Typography></CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card><CardContent><Typography color="textSecondary">Dentro ahora</Typography><Typography variant="h5" color="primary.main">{estadisticasServicios.serviciosDentroAhora ?? 0}</Typography></CardContent></Card>
                    </Grid>
                  </Grid>
                ) : <Alert severity="info">No hay datos de servicios</Alert>
              )}

              {dashboardSeccion === 'participacion' && (
                loadingDashboardSeccion ? (
                  <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                ) : estadisticasParticipacion ? (
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card><CardContent><Typography color="textSecondary">Sugerencias del mes</Typography><Typography variant="h5">{estadisticasParticipacion.sugerenciasDelMes ?? 0}</Typography></CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card><CardContent><Typography color="textSecondary">Sugerencias sin revisar</Typography><Typography variant="h5" color="warning.main">{estadisticasParticipacion.sugerenciasSinRevisar ?? 0}</Typography></CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card><CardContent><Typography color="textSecondary">Alertas no vistas</Typography><Typography variant="h5" color="error.main">{contadorAlertas ?? 0}</Typography></CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card><CardContent><Typography color="textSecondary">Reseñas directorio pendientes</Typography><Typography variant="h5" color="info.main">{estadisticasParticipacion.reseñasPendientes ?? 0}</Typography></CardContent></Card>
                    </Grid>
                  </Grid>
                ) : <Alert severity="info">No hay datos de participación</Alert>
              )}

              {dashboardSeccion === 'accesos' && (
                loadingDashboardSeccion ? (
                  <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                ) : estadisticasAccesosResumen ? (
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card><CardContent><Typography color="textSecondary">Accesos exitosos hoy</Typography><Typography variant="h5" color="success.main">{estadisticasAccesosResumen.accesosExitososHoy ?? 0}</Typography></CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card><CardContent><Typography color="textSecondary">Tasa de denegación (7 días)</Typography><Typography variant="h5" color="error.main">{estadisticasAccesosResumen.tasaDenegacion7dias ?? 0}%</Typography></CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card><CardContent><Typography color="textSecondary">Hora pico (últimos 7 días)</Typography><Typography variant="h5">{estadisticasAccesosResumen.horaPico ? `${formatHoraMexicoCentralFromInstant(estadisticasAccesosResumen.horaPico.hora)} (${estadisticasAccesosResumen.horaPico.total} accesos)` : '—'}</Typography></CardContent></Card>
                    </Grid>
                  </Grid>
                ) : <Alert severity="info">No hay datos de accesos</Alert>
              )}

              {dashboardSeccion === 'documentos' && (
                loadingDashboardSeccion ? (
                  <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                ) : estadisticasDocumentos ? (
                  <>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography color="textSecondary">Recibos de ingreso (mes)</Typography><Typography variant="h5">{estadisticasDocumentos.recibosIngresoMes ?? 0}</Typography></CardContent></Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card><CardContent><Typography color="textSecondary">Recibos de egreso (mes)</Typography><Typography variant="h5">{estadisticasDocumentos.recibosEgresoMes ?? 0}</Typography></CardContent></Card>
                      </Grid>
                    </Grid>
                    {estadisticasDocumentos.documentosRecientes && estadisticasDocumentos.documentosRecientes.length > 0 && (
                      <Card sx={{ mb: 4 }}><CardContent><Typography variant="h6" gutterBottom>Documentos recientes</Typography><TableContainer component={Paper} variant="outlined"><Table size="small"><TableHead><TableRow><TableCell>Nombre</TableCell><TableCell>Fecha</TableCell></TableRow></TableHead><TableBody>{estadisticasDocumentos.documentosRecientes.map((d) => (<TableRow key={d.id}><TableCell>{d.nombre_mostrar}</TableCell><TableCell>{d.created_at ? formatFechaHoraMexicoCentral(d.created_at) : '—'}</TableCell></TableRow>))}</TableBody></Table></TableContainer></CardContent></Card>
                    )}
                  </>
                ) : <Alert severity="info">No hay datos de documentos</Alert>
              )}
            </>
          )}

          {tabActual === 13 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Configura días festivos, límites de visitas, estacionamientos, dispositivos Shelly y API Key del condominio.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>

              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Pases Recurrentes
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Configura el número máximo de días de validez para pases recurrentes.
                    Los residentes podrán generar pases válidos desde 1 hasta este número de días.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <TextField
                      label="Días Máximos"
                      type="number"
                      value={diasMaxRecurrentes}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        if (value >= 1 && value <= 365) {
                          setDiasMaxRecurrentes(value);
                        }
                      }}
                      inputProps={{ min: 1, max: 365 }}
                      sx={{ width: 200 }}
                    />
                    <Button
                      variant="contained"
                      onClick={guardarConfiguracion}
                      disabled={loading}
                    >
                      Guardar
                    </Button>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    Valor actual: {diasMaxRecurrentes} día{diasMaxRecurrentes !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
              </Card>

              {/* Configuración de Estacionamientos */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Estacionamientos
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Configura el número total de estacionamientos disponibles en el condominio.
                    Cuando se alcance este límite, no se permitirá el ingreso de más visitas con vehículo.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <TextField
                      label="Número de Estacionamientos"
                      type="number"
                      value={numeroEstacionamientos}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        if (value >= 1 && value <= 1000) {
                          setNumeroEstacionamientos(value);
                        }
                      }}
                      inputProps={{ min: 1, max: 1000 }}
                      sx={{ width: 250 }}
                    />
                    <Button
                      variant="contained"
                      onClick={guardarConfiguracionEstacionamientos}
                      disabled={loading}
                    >
                      Guardar
                    </Button>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    Valor actual: {numeroEstacionamientos} estacionamiento{numeroEstacionamientos !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
              </Card>

              {/* Configuración de Límites de Visitas */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Límites de Visitas por Casa
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Configura los límites de pases que se pueden generar por casa.
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Límite de Pases Recurrentes
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Número máximo de pases recurrentes que se pueden generar para el mismo periodo de tiempo por casa.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <TextField
                        label="Límite"
                        type="number"
                        value={limiteVisitasRecurrentes}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          if (value >= 1 && value <= 10) {
                            setLimiteVisitasRecurrentes(value);
                          }
                        }}
                        inputProps={{ min: 1, max: 10 }}
                        sx={{ width: 200 }}
                      />
                      <Button
                        variant="contained"
                        onClick={guardarLimiteVisitasRecurrentes}
                        disabled={loading}
                      >
                        Guardar
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Valor actual: {limiteVisitasRecurrentes} pase{limiteVisitasRecurrentes !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Límite de Pases Ocasionales
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Número máximo de pases ocasionales que se pueden generar para el mismo día por casa.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <TextField
                        label="Límite"
                        type="number"
                        value={limiteVisitasOcasionales}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          if (value >= 1 && value <= 10) {
                            setLimiteVisitasOcasionales(value);
                          }
                        }}
                        inputProps={{ min: 1, max: 10 }}
                        sx={{ width: 200 }}
                      />
                      <Button
                        variant="contained"
                        onClick={guardarLimiteVisitasOcasionales}
                        disabled={loading}
                      >
                        Guardar
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Valor actual: {limiteVisitasOcasionales} pase{limiteVisitasOcasionales !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Límite de Estacionamientos por Casa
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Número máximo de visitas con auto que pueden ingresar por casa. Si se alcanza este límite, aunque el código sea válido, no se permitirá el ingreso con vehículo.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={validarLimiteEstacionamientosPorCasa}
                            onChange={async (e) => {
                              setValidarLimiteEstacionamientosPorCasa(e.target.checked);
                              // Guardar automáticamente al cambiar el checkbox
                              try {
                                setError('');
                                setLoading(true);
                                const response = await fetchWithAuth(`${API_BASE_URL}/admin/configuracion/validar-limite-estacionamientos-por-casa`, {
                                  method: 'PUT',
                                  headers: {
                                    'Content-Type': 'application/json',
                                  },
                                  body: JSON.stringify({ valor: e.target.checked }),
                                });
                                if (!response.ok) {
                                  const data = await response.json();
                                  setError(data.error || 'Error al guardar configuración');
                                  setValidarLimiteEstacionamientosPorCasa(!e.target.checked); // Revertir si falla
                                } else {
                                  setSuccess('Configuración guardada correctamente');
                                  setTimeout(() => setSuccess(''), 3000);
                                }
                                setLoading(false);
                              } catch (error) {
                                console.error('Error al guardar configuración:', error);
                                setError('Error al guardar configuración');
                                setValidarLimiteEstacionamientosPorCasa(!e.target.checked); // Revertir si falla
                                setLoading(false);
                              }
                            }}
                            disabled={loading}
                          />
                        }
                        label="Activar validación de límite por casa"
                        sx={{ mr: 2 }}
                      />
                      <TextField
                        label="Límite"
                        type="number"
                        value={limiteEstacionamientosPorCasa}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          if (value >= 1 && value <= 10) {
                            setLimiteEstacionamientosPorCasa(value);
                          }
                        }}
                        inputProps={{ min: 1, max: 10 }}
                        sx={{ width: 200 }}
                        disabled={!validarLimiteEstacionamientosPorCasa}
                      />
                      <Button
                        variant="contained"
                        onClick={guardarLimiteEstacionamientosPorCasa}
                        disabled={loading || !validarLimiteEstacionamientosPorCasa}
                      >
                        Guardar
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {validarLimiteEstacionamientosPorCasa 
                        ? `Validación activa. Valor actual: ${limiteEstacionamientosPorCasa} estacionamiento${limiteEstacionamientosPorCasa !== 1 ? 's' : ''} por casa.`
                        : 'Validación desactivada. Las casas pueden ingresar visitas con auto hasta el máximo de estacionamientos configurados del condominio.'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Días Festivos */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Días Festivos
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Define los días festivos en los que no se permitirán visitas de tipo "Servicios". Estos días se validarán automáticamente al intentar ingresar.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setDiaFestivoEditando(null);
                        setFormDiaFestivo({ fecha: '', descripcion: '', activo: true });
                        setShowModalDiaFestivo(true);
                      }}
                    >
                      Agregar Día Festivo
                    </Button>
                  </Box>

                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Fecha</strong></TableCell>
                          <TableCell><strong>Descripción</strong></TableCell>
                          <TableCell><strong>Activo</strong></TableCell>
                          <TableCell><strong>Acciones</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {diasFestivos.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              No hay días festivos configurados. Agrega uno para comenzar.
                            </TableCell>
                          </TableRow>
                        ) : (
                          diasFestivos.map((diaFestivo) => {
                            const fechaObj = new Date(diaFestivo.fecha);
                            const fechaFormateada = fechaObj.toLocaleDateString('es-ES', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            });
                            return (
                              <TableRow key={diaFestivo.id}>
                                <TableCell>{fechaFormateada}</TableCell>
                                <TableCell>{diaFestivo.descripcion}</TableCell>
                                <TableCell>
                                  <Switch
                                    checked={diaFestivo.activo}
                                    onChange={async (e) => {
                                      try {
                                        const response = await fetchWithAuth(`${API_BASE_URL}/admin/dias-festivos/${diaFestivo.id}`, {
                                          method: 'PUT',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ activo: e.target.checked })
                                        });
                                        if (response.ok) {
                                          loadDiasFestivos();
                                        }
                                      } catch (error) {
                                        console.error('Error al actualizar día festivo:', error);
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditarDiaFestivo(diaFestivo)}
                                    color="primary"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEliminarDiaFestivo(diaFestivo.id)}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Modal para Horario */}
              {/* Modal para Día Festivo */}
              <Dialog open={showModalDiaFestivo} onClose={() => setShowModalDiaFestivo(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                  {diaFestivoEditando ? 'Editar Día Festivo' : 'Nuevo Día Festivo'}
                </DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                      label="Fecha"
                      type="date"
                      value={formDiaFestivo.fecha}
                      onChange={(e) => setFormDiaFestivo({ ...formDiaFestivo, fecha: e.target.value })}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Descripción"
                      value={formDiaFestivo.descripcion}
                      onChange={(e) => setFormDiaFestivo({ ...formDiaFestivo, descripcion: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Ej: Día de la Independencia, Navidad, etc."
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formDiaFestivo.activo}
                          onChange={(e) => setFormDiaFestivo({ ...formDiaFestivo, activo: e.target.checked })}
                        />
                      }
                      label="Activo"
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowModalDiaFestivo(false)}>Cancelar</Button>
                  <Button onClick={handleGuardarDiaFestivo} variant="contained" disabled={loading}>
                    Guardar
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Instrucciones de pago para residentes (app móvil - "Pagar ahora") */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Instrucciones de pago para residentes</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Estos datos se muestran en la app móvil en &quot;Mis Pagos&quot; al tocar &quot;Pagar ahora&quot; (número de cuenta, referencia, envío de comprobante).
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Número de cuenta"
                        fullWidth
                        value={instruccionesPago.numero_cuenta}
                        onChange={(e) => setInstruccionesPago({ ...instruccionesPago, numero_cuenta: e.target.value })}
                        size="small"
                        placeholder="Ej: 0123456789"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Instrucciones de pago"
                        fullWidth
                        multiline
                        rows={3}
                        value={instruccionesPago.instrucciones}
                        onChange={(e) => setInstruccionesPago({ ...instruccionesPago, instrucciones: e.target.value })}
                        size="small"
                        placeholder="Instrucciones generales para realizar el pago"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Instrucciones de referencia (ej. número de casa)"
                        fullWidth
                        multiline
                        rows={2}
                        value={instruccionesPago.referencia}
                        onChange={(e) => setInstruccionesPago({ ...instruccionesPago, referencia: e.target.value })}
                        size="small"
                        placeholder="Indique que la referencia es el número de casa"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Instrucciones para envío de comprobante"
                        fullWidth
                        multiline
                        rows={2}
                        value={instruccionesPago.enviar_comprobante}
                        onChange={(e) => setInstruccionesPago({ ...instruccionesPago, enviar_comprobante: e.target.value })}
                        size="small"
                        placeholder="A dónde o cómo enviar el comprobante a administración"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        onClick={guardarInstruccionesPago}
                        disabled={instruccionesPagoGuardando}
                      >
                        {instruccionesPagoGuardando ? 'Guardando...' : 'Guardar instrucciones de pago'}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Llave de acceso peatonal */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Llave de acceso peatonal</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Configura los dispositivos Shelly para la puerta peatonal y la API Key que usará el lector QR.
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>API Key del condominio</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      El lector QR usa esta clave en el header X-API-Key. Al regenerar, la anterior deja de funcionar.
                    </Typography>
                    <Button variant="outlined" onClick={handleGenerarApiKey} disabled={loadingShelly}>
                      Generar API Key
                    </Button>
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2">Dispositivos Shelly</Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setShellyEditando(null);
                          setFormShelly({ nombre: '', host: '', auth_key: '', device_id: '', activo: true });
                          setShowModalShelly(true);
                        }}
                      >
                        Agregar Shelly
                      </Button>
                    </Box>
                    {loadingShelly && shellyDispositivos.length === 0 ? (
                      <CircularProgress size={24} />
                    ) : shellyDispositivos.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No hay dispositivos Shelly configurados.</Typography>
                    ) : (
                      <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 800 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Nombre</TableCell>
                              <TableCell>Host</TableCell>
                              <TableCell>Device ID</TableCell>
                              <TableCell>Estado</TableCell>
                              <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {shellyDispositivos.map((s) => (
                              <TableRow key={s.id}>
                                <TableCell>{s.nombre}</TableCell>
                                <TableCell>{s.host}</TableCell>
                                <TableCell>{s.device_id}</TableCell>
                                <TableCell>
                                  <Chip size="small" label={s.activo ? 'Activo' : 'Inactivo'} color={s.activo ? 'success' : 'default'} />
                                </TableCell>
                                <TableCell align="right">
                                  <Button size="small" onClick={() => handleProbarShelly(s.id)} disabled={loadingShelly} sx={{ mr: 0.5 }}>
                                    Probar
                                  </Button>
                                  <IconButton size="small" onClick={() => {
                                    setShellyEditando(s);
                                    setFormShelly({ nombre: s.nombre, host: s.host, auth_key: '', device_id: s.device_id, activo: s.activo });
                                    setShowModalShelly(true);
                                  }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleEliminarShelly(s.id)} disabled={loadingShelly}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </CardContent>
              </Card>

              <Dialog open={showModalApiKey} onClose={() => { setShowModalApiKey(false); setApiKeyGenerada(''); }} maxWidth="sm" fullWidth>
                <DialogTitle>API Key generada</DialogTitle>
                <DialogContent>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Copia esta clave ahora. No se mostrará de nuevo.
                  </Alert>
                  <TextField
                    fullWidth
                    label="API Key"
                    value={apiKeyGenerada}
                    InputProps={{ readOnly: true }}
                    onFocus={(e) => e.target.select()}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setShowModalApiKey(false); setApiKeyGenerada(''); }}>Cerrar</Button>
                </DialogActions>
              </Dialog>

              <Dialog open={showModalShelly} onClose={() => { setShowModalShelly(false); setShellyEditando(null); }} maxWidth="sm" fullWidth>
                <DialogTitle>{shellyEditando ? 'Editar Shelly' : 'Agregar Shelly'}</DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField label="Nombre" fullWidth value={formShelly.nombre} onChange={(e) => setFormShelly({ ...formShelly, nombre: e.target.value })} placeholder="Ej. Puerta peatonal principal" size="small" />
                    <TextField label="Host" fullWidth value={formShelly.host} onChange={(e) => setFormShelly({ ...formShelly, host: e.target.value })} placeholder="shelly-243-eu.shelly.cloud" size="small" />
                    <TextField label="Auth Key" fullWidth type="password" value={formShelly.auth_key} onChange={(e) => setFormShelly({ ...formShelly, auth_key: e.target.value })} placeholder={shellyEditando ? 'Dejar vacío para no cambiar' : 'API Key de Shelly Cloud'} size="small" />
                    <TextField label="Device ID" fullWidth value={formShelly.device_id} onChange={(e) => setFormShelly({ ...formShelly, device_id: e.target.value })} placeholder="ID del dispositivo Shelly" size="small" />
                    <FormControlLabel control={<Switch checked={formShelly.activo} onChange={(e) => setFormShelly({ ...formShelly, activo: e.target.checked })} />} label="Activo" />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setShowModalShelly(false); setShellyEditando(null); }}>Cancelar</Button>
                  <Button variant="contained" onClick={handleGuardarShelly} disabled={loadingShelly}>{shellyEditando ? 'Actualizar' : 'Agregar'}</Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {tabActual === 15 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Revisa las alertas y observaciones de accesos. Marca como vistas las que ya hayas revisado.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>

              {loadingAlertas ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        Alertas Pendientes: {contadorAlertas}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          alertas.forEach(alerta => {
                            if (!alerta.vista) {
                              handleMarcarAlertaVista(alerta.id);
                            }
                          });
                        }}
                        disabled={contadorAlertas === 0}
                      >
                        Marcar Todas como Vistas
                      </Button>
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Fecha</strong></TableCell>
                            <TableCell><strong>Visitante</strong></TableCell>
                            <TableCell><strong>Destino</strong></TableCell>
                            <TableCell><strong>Número de Cono</strong></TableCell>
                            <TableCell><strong>Observaciones</strong></TableCell>
                            <TableCell><strong>Guardia</strong></TableCell>
                            <TableCell><strong>Estado</strong></TableCell>
                            <TableCell><strong>Acciones</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {alertas.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center">
                                No hay alertas pendientes
                              </TableCell>
                            </TableRow>
                          ) : (
                            alertas.map((alerta) => {
                              const fechaFormateada = alerta.created_at
                                ? formatFechaHoraMexicoCentral(alerta.created_at)
                                : '';
                              return (
                                <TableRow 
                                  key={alerta.id}
                                  sx={{ 
                                    backgroundColor: alerta.vista ? 'transparent' : '#fff3cd',
                                    '&:hover': { backgroundColor: alerta.vista ? '#f5f5f5' : '#ffe082' }
                                  }}
                                >
                                  <TableCell>{fechaFormateada}</TableCell>
                                  <TableCell>{alerta.visitante_nombre || 'N/A'}</TableCell>
                                  <TableCell>{alerta.propiedad_destino || 'N/A'}</TableCell>
                                  <TableCell>{alerta.numero_cono || '-'}</TableCell>
                                  <TableCell>
                                    <Box sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
                                      {alerta.observaciones || '-'}
                                    </Box>
                                  </TableCell>
                                  <TableCell>{alerta.guardia_nombre || 'N/A'}</TableCell>
                                  <TableCell>
                                    {alerta.vista ? (
                                      <Box sx={{ color: '#4caf50', fontWeight: 'bold' }}>Vista</Box>
                                    ) : (
                                      <Box sx={{ color: '#d32f2f', fontWeight: 'bold' }}>Pendiente</Box>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {!alerta.vista && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleMarcarAlertaVista(alerta.id)}
                                      >
                                        Marcar como Vista
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {tabActual === 11 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Gestiona categorías y nombres de servicios para el directorio y las notificaciones de servicio.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>

              {/* Categorías de Proveedores */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h6">Categorías de Proveedores</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setCategoriaEditando(null);
                        setNuevaCategoriaNombre('');
                        setNuevaCategoriaHoraInicio('');
                        setNuevaCategoriaHoraFin('');
                        setNuevaCategoriaAplicaDiasFestivos(false);
                        setShowDialogCategoria(true);
                      }}
                    >
                      Agregar Categoría
                    </Button>
                  </Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Horario Inicio</TableCell>
                          <TableCell>Horario Fin</TableCell>
                          <TableCell>Aplica Días Festivos</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoriasServicios.map((cat) => (
                          <TableRow key={cat.id}>
                            <TableCell>{cat.id}</TableCell>
                            <TableCell>{cat.nombre}</TableCell>
                            <TableCell>{cat.hora_inicio ? cat.hora_inicio.substring(0, 5) : '-'}</TableCell>
                            <TableCell>{cat.hora_fin ? cat.hora_fin.substring(0, 5) : '-'}</TableCell>
                            <TableCell>
                              <Switch
                                checked={cat.aplica_dias_festivos || false}
                                disabled
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{cat.activo ? 'Activa' : 'Inactiva'}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setCategoriaEditando(cat);
                                  setNuevaCategoriaNombre(cat.nombre);
                                  setNuevaCategoriaHoraInicio(cat.hora_inicio ? cat.hora_inicio.substring(0, 5) : '');
                                  setNuevaCategoriaHoraFin(cat.hora_fin ? cat.hora_fin.substring(0, 5) : '');
                                  setNuevaCategoriaAplicaDiasFestivos(cat.aplica_dias_festivos || false);
                                  setShowDialogCategoria(true);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (window.confirm(`¿Estás seguro de que deseas eliminar la categoría "${cat.nombre}"?`)) {
                                    handleEliminarCategoria(cat.id);
                                  }
                                }}
                                disabled={cat.nombre === 'Otro'}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Nombres de Proveedores */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h6">Nombres de Proveedores</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setNombreEditando(null);
                        setNuevoNombreServicio('');
                        setNuevoNombreCategoriaId('');
                        setShowDialogNombre(true);
                      }}
                    >
                      Agregar Nombre
                    </Button>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      select
                      label="Filtrar por categoría"
                      value={categoriaSeleccionadaFiltro}
                      onChange={(e) => {
                        setCategoriaSeleccionadaFiltro(e.target.value);
                        loadNombresServicios(e.target.value || null);
                      }}
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="">Todas las categorías</MenuItem>
                      {categoriasServicios.filter(c => c.activo).map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.nombre}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Categoría</TableCell>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {nombresServicios
                          .filter(ns => !categoriaSeleccionadaFiltro || ns.categoria_id === parseInt(categoriaSeleccionadaFiltro))
                          .map((nombre) => (
                            <TableRow key={nombre.id}>
                              <TableCell>{nombre.id}</TableCell>
                              <TableCell>{nombre.categoria_nombre || 'N/A'}</TableCell>
                              <TableCell>{nombre.nombre}</TableCell>
                              <TableCell>{nombre.activo ? 'Activo' : 'Inactivo'}</TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setNombreEditando(nombre);
                                    setNuevoNombreServicio(nombre.nombre);
                                    setNuevoNombreCategoriaId(nombre.categoria_id.toString());
                                    setShowDialogNombre(true);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (window.confirm(`¿Estás seguro de que deseas eliminar el nombre "${nombre.nombre}"?`)) {
                                      handleEliminarNombreServicio(nombre.id);
                                    }
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Diálogo para crear/editar categoría */}
              <Dialog open={showDialogCategoria} onClose={() => setShowDialogCategoria(false)}>
                <DialogTitle>
                  {categoriaEditando ? 'Editar Categoría' : 'Nueva Categoría'}
                </DialogTitle>
                <DialogContent>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Nombre de la categoría"
                    fullWidth
                    variant="standard"
                    value={nuevaCategoriaNombre}
                    onChange={(e) => setNuevaCategoriaNombre(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                    Horarios de Proveedores
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      margin="dense"
                      label="Hora Inicio"
                      type="time"
                      fullWidth
                      variant="standard"
                      value={nuevaCategoriaHoraInicio}
                      onChange={(e) => setNuevaCategoriaHoraInicio(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      label="Hora Fin"
                      type="time"
                      fullWidth
                      variant="standard"
                      value={nuevaCategoriaHoraFin}
                      onChange={(e) => setNuevaCategoriaHoraFin(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={nuevaCategoriaAplicaDiasFestivos}
                        onChange={(e) => setNuevaCategoriaAplicaDiasFestivos(e.target.checked)}
                      />
                    }
                    label="Aplica en días festivos"
                    sx={{ mt: 1 }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => {
                    setShowDialogCategoria(false);
                    setCategoriaEditando(null);
                    setNuevaCategoriaNombre('');
                    setNuevaCategoriaHoraInicio('');
                    setNuevaCategoriaHoraFin('');
                    setNuevaCategoriaAplicaDiasFestivos(false);
                  }}>
                    Cancelar
                  </Button>
                  <Button onClick={categoriaEditando ? handleActualizarCategoria : handleCrearCategoria}>
                    {categoriaEditando ? 'Actualizar' : 'Crear'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Diálogo para crear/editar nombre de servicio */}
              <Dialog open={showDialogNombre} onClose={() => setShowDialogNombre(false)}>
                <DialogTitle>
                  {nombreEditando ? 'Editar Nombre de Servicio' : 'Nuevo Nombre de Servicio'}
                </DialogTitle>
                <DialogContent>
                  <TextField
                    select
                    margin="dense"
                    label="Categoría"
                    fullWidth
                    variant="standard"
                    value={nuevoNombreCategoriaId}
                    onChange={(e) => setNuevoNombreCategoriaId(e.target.value)}
                    disabled={!!nombreEditando}
                    sx={{ mb: 2 }}
                  >
                    {categoriasServicios.filter(c => c.activo).map((cat) => (
                      <MenuItem key={cat.id} value={cat.id.toString()}>
                        {cat.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Nombre del servicio"
                    fullWidth
                    variant="standard"
                    value={nuevoNombreServicio}
                    onChange={(e) => setNuevoNombreServicio(e.target.value)}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => {
                    setShowDialogNombre(false);
                    setNombreEditando(null);
                    setNuevoNombreServicio('');
                    setNuevoNombreCategoriaId('');
                  }}>
                    Cancelar
                  </Button>
                  <Button onClick={nombreEditando ? handleActualizarNombreServicio : handleCrearNombreServicio}>
                    {nombreEditando ? 'Actualizar' : 'Crear'}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {tabActual === 4 && (
            <>
              <Dialog
                open={openModalEnviarReciboIngreso}
                onClose={() => !enviandoReciboIngreso && setOpenModalEnviarReciboIngreso(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Enviar recibo por correo</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Selecciona a quién se enviará el recibo. Puedes agregar correos manuales para esta casa.
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Casa"
                        value={propiedadDestinatarios}
                        onChange={async (e) => {
                          const v = e.target.value;
                          setPropiedadDestinatarios(v);
                          const data = await loadDestinatariosRecibo(v);
                          setEmailsSeleccionadosRecibo(Array.isArray(data?.todos) ? data.todos : []);
                        }}
                      >
                        <MenuItem value="">Seleccionar casa</MenuItem>
                        {[...(listadoPropiedades || [])].sort(compareNatural).map((p) => (
                          <MenuItem key={p} value={p}>{p}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Agregar correo manual"
                        type="email"
                        placeholder="correo@ejemplo.com"
                        value={nuevoEmailRecibo}
                        onChange={(e) => setNuevoEmailRecibo(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAgregarEmailRecibo())}
                        disabled={!propiedadDestinatarios}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleAgregarEmailRecibo}
                        disabled={!propiedadDestinatarios || !nuevoEmailRecibo.trim()}
                        sx={{ width: '100%' }}
                      >
                        Agregar
                      </Button>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setEmailsSeleccionadosRecibo(Array.isArray(destinatariosRecibo?.todos) ? destinatariosRecibo.todos : [])}
                      disabled={!destinatariosRecibo?.todos?.length}
                    >
                      Seleccionar todos
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setEmailsSeleccionadosRecibo([])}
                      disabled={!emailsSeleccionadosRecibo?.length}
                    >
                      Limpiar selección
                    </Button>
                  </Box>

                  {loadingDestinatarios && (
                    <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} /> <Typography variant="body2">Cargando...</Typography>
                    </Box>
                  )}

                  {!loadingDestinatarios && propiedadDestinatarios && (
                    <Box sx={{ mt: 2 }}>
                      {(!destinatariosRecibo?.todos || destinatariosRecibo.todos.length === 0) ? (
                        <Typography variant="body2" color="text.secondary">
                          No hay correos configurados para esta casa. Agrega un residente/contacto con correo o agrega un correo manual.
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Destinatarios ({destinatariosRecibo.todos.length})
                          </Typography>
                          <Box sx={{ maxHeight: 260, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                            {destinatariosRecibo.de_residentes?.map((r, i) => {
                              const email = r.email;
                              const emailNorm = String(email || '').trim().toLowerCase();
                              const checked = emailsSeleccionadosRecibo.includes(emailNorm);
                              return (
                                <FormControlLabel
                                  key={`rsel-${i}`}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={checked}
                                      onChange={(e) => {
                                        setEmailsSeleccionadosRecibo((prev) => {
                                          const set = new Set(prev || []);
                                          if (e.target.checked) set.add(emailNorm); else set.delete(emailNorm);
                                          return Array.from(set);
                                        });
                                      }}
                                    />
                                  }
                                  label={`${email} (residente)`}
                                />
                              );
                            })}
                            {destinatariosRecibo.de_contactos?.map((c) => {
                              const email = c.email;
                              const emailNorm = String(email || '').trim().toLowerCase();
                              const checked = emailsSeleccionadosRecibo.includes(emailNorm);
                              return (
                                <FormControlLabel
                                  key={`csel-${c.id}`}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={checked}
                                      onChange={(e) => {
                                        setEmailsSeleccionadosRecibo((prev) => {
                                          const set = new Set(prev || []);
                                          if (e.target.checked) set.add(emailNorm); else set.delete(emailNorm);
                                          return Array.from(set);
                                        });
                                      }}
                                    />
                                  }
                                  label={`${email} (contacto)`}
                                />
                              );
                            })}
                            {destinatariosRecibo.manuales?.map((m) => {
                              const email = m.email;
                              const emailNorm = String(email || '').trim().toLowerCase();
                              const checked = emailsSeleccionadosRecibo.includes(emailNorm);
                              return (
                                <Box key={`msel-${m.id}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={(e) => {
                                          setEmailsSeleccionadosRecibo((prev) => {
                                            const set = new Set(prev || []);
                                            if (e.target.checked) set.add(emailNorm); else set.delete(emailNorm);
                                            return Array.from(set);
                                          });
                                        }}
                                      />
                                    }
                                    label={`${email} (manual)`}
                                  />
                                  <Button size="small" color="error" onClick={() => handleQuitarEmailRecibo(m.id)}>
                                    Quitar
                                  </Button>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenModalEnviarReciboIngreso(false)} disabled={enviandoReciboIngreso}>Cancelar</Button>
                  <Button
                    variant="contained"
                    onClick={handleEnviarReciboIngresoSeleccionados}
                    disabled={enviandoReciboIngreso || !enviarReciboIngresoId || !emailsSeleccionadosRecibo.length}
                  >
                    {enviandoReciboIngreso ? 'Enviando...' : 'Enviar'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Filtros */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Filtros</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Mes"
                        fullWidth
                        value={filtrosIngresos.mes}
                        onChange={(e) => setFiltrosIngresos({ ...filtrosIngresos, mes: e.target.value })}
                        size="small"
                      >
                        <MenuItem value="">Todos</MenuItem>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                          <MenuItem key={m} value={String(m)}>{new Date(2000, m - 1, 1).toLocaleString('es', { month: 'long' })}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Año"
                        fullWidth
                        value={filtrosIngresos.año}
                        onChange={(e) => setFiltrosIngresos({ ...filtrosIngresos, año: e.target.value })}
                        size="small"
                      >
                        <MenuItem value="">Todos</MenuItem>
                        {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                          <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Autocomplete
                        size="small"
                        options={[null, ...[...(listadoPropiedades || [])].sort(compareNatural)]}
                        value={filtrosIngresos.propiedad ? filtrosIngresos.propiedad : null}
                        onChange={(_e, newValue) => setFiltrosIngresos({ ...filtrosIngresos, propiedad: newValue ?? '' })}
                        getOptionLabel={(opt) => (opt == null || opt === '' ? 'Todas' : String(opt))}
                        isOptionEqualToValue={(a, b) => (a ?? '') === (b ?? '')}
                        renderInput={(params) => (
                          <TextField {...params} label="Casa" placeholder="Escribir para buscar…" />
                        )}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Estatus"
                        fullWidth
                        value={filtrosIngresos.estatus_recibo}
                        onChange={(e) => setFiltrosIngresos({ ...filtrosIngresos, estatus_recibo: e.target.value })}
                        size="small"
                      >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="vigente">Vigente</MenuItem>
                        <MenuItem value="cancelado">Cancelado</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                          setPagosPaginacion(prev => ({ ...prev, pagina: 0 }));
                          loadPagos(0);
                        }}
                        sx={{ height: '40px' }}
                      >
                        Filtrar
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                          setFiltrosIngresos({ fechaInicio: '', fechaFin: '', mes: '', año: '', propiedad: '', estatus_recibo: '' });
                          setPagosPaginacion(prev => ({ ...prev, pagina: 0 }));
                          loadPagos(0);
                        }}
                        sx={{ height: '40px' }}
                      >
                        Limpiar
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h6">Ingresos de Residentes</Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={eliminarTodoPagos}
                            onChange={(e) => setEliminarTodoPagos(e.target.checked)}
                            color="warning"
                            disabled={loadingCargaExcelPagos}
                          />
                        }
                        label="Eliminar todo antes de cargar"
                      />
                      <input
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        id="excel-pagos-upload"
                        type="file"
                        disabled={loadingCargaExcelPagos}
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (f) await handleCargarExcelPagos(f);
                          e.target.value = '';
                        }}
                      />
                      <label htmlFor="excel-pagos-upload" style={{ cursor: loadingCargaExcelPagos ? 'default' : 'pointer' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          component="span"
                          startIcon={loadingCargaExcelPagos ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                          disabled={loadingCargaExcelPagos}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {loadingCargaExcelPagos ? 'Procesando…' : 'Cargar Excel'}
                        </Button>
                      </label>
                      <Button variant="outlined" size="small" startIcon={<GetAppIcon />} onClick={exportarIngresosExcel} sx={{ whiteSpace: 'nowrap' }}>
                        Exportar Excel (mes)
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          loadPropiedades();
                          setPagoEditando(null);
                          setFormPago({
                            fecha: new Date().toISOString().split('T')[0],
                            mes: '',
                            propiedad: '',
                            folio: '',
                            concepto: '',
                            categoria_concepto: '',
                            descripcion: '',
                            monto: '',
                            recargos: '',
                            depositado: false,
                            concepto_pago_id: '',
                            estatus_recibo: 'vigente',
                          });
                          setShowDialogPago(true);
                        }}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Agregar Pago
                      </Button>
                    </Box>
                  </Box>
                  {loadingCargaExcelPagos && (
                    <Box sx={{ width: '100%', mb: 2 }}>
                      <LinearProgress />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Procesando archivo de ingresos en el servidor… Puede tardar varios minutos con archivos grandes.
                      </Typography>
                    </Box>
                  )}
                  <TableContainer component={Paper} sx={{ maxHeight: '55vh', overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Propiedad</TableCell>
                          <TableCell>Folio</TableCell>
                          <TableCell>Folio Recibo</TableCell>
                          <TableCell>Concepto</TableCell>
                          <TableCell>Monto</TableCell>
                          <TableCell>Estatus</TableCell>
                          <TableCell>Depositado</TableCell>
                          <TableCell>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!pagos || pagos.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                              <Typography variant="body1" color="text.secondary">
                                {pagos === null || pagos === undefined ? 'Cargando pagos...' : 'No hay pagos registrados'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          pagos.map((pago) => (
                            <TableRow key={pago.id}>
                              <TableCell>{pago.id}</TableCell>
                              <TableCell>{pago.fecha ? (typeof pago.fecha === 'string' ? pago.fecha.split('T')[0] : new Date(pago.fecha).toISOString().split('T')[0]) : ''}</TableCell>
                              <TableCell>{pago.propiedad}</TableCell>
                              <TableCell>{pago.folio}</TableCell>
                              <TableCell>
                                {pago.folio_recibo 
                                  ? `ING-${String(pago.folio_recibo).padStart(4, '0')}` 
                                  : '-'}
                              </TableCell>
                              <TableCell>{pago.concepto || 'N/A'}</TableCell>
                              <TableCell>${parseFloat(pago.monto || 0).toFixed(2)}</TableCell>
                              <TableCell>{(pago.estatus_recibo || 'vigente') === 'cancelado' ? 'Cancelado' : 'Vigente'}</TableCell>
                              <TableCell>{pago.depositado ? 'Sí' : 'No'}</TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setPagoEditando(pago);
                                    setFormPago({
                                      fecha: pago.fecha ? (typeof pago.fecha === 'string' ? pago.fecha.split('T')[0] : new Date(pago.fecha).toISOString().split('T')[0]) : '',
                                      mes: pago.mes || '',
                                      propiedad: pago.propiedad || '',
                                      folio: pago.folio || '',
                                      concepto: pago.concepto || '',
                                      categoria_concepto: pago.categoria_concepto || '',
                                      descripcion: pago.descripcion || '',
                                      monto: pago.monto || '',
                                      recargos: pago.recargos || '',
                                      depositado: pago.depositado || false,
                                      concepto_pago_id: pago.concepto_pago_id != null ? pago.concepto_pago_id : '',
                                      estatus_recibo: pago.estatus_recibo || 'vigente',
                                    });
                                    setPagoDesdeCobranza(false);
                                    setShowDialogPago(true);
                                  }}
                                  title="Editar"
                                >
                                  <EditIcon />
                                </IconButton>
                                {(pago.estatus_recibo || 'vigente') === 'vigente' ? (
                                  <MuiTooltip title="Cancelar recibo (operación no realizada)">
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      onClick={() => window.confirm('¿Marcar este ingreso como cancelado? No se elimina, solo se anula.') && handleCambiarEstatusPago(pago, 'cancelado')}
                                    >
                                      <CancelIcon />
                                    </IconButton>
                                  </MuiTooltip>
                                ) : (
                                  <MuiTooltip title="Reactivar recibo">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleCambiarEstatusPago(pago, 'vigente')}
                                    >
                                      <RestoreIcon />
                                    </IconButton>
                                  </MuiTooltip>
                                )}
                                {/* Botones de Recibo */}
                                {pago.ruta_pdf ? (
                                  <>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={async () => {
                                        if (pago.propiedad) {
                                          setPropiedadDestinatarios(pago.propiedad);
                                          loadDestinatariosRecibo(pago.propiedad);
                                        }
                                        try {
                                          const token = localStorage.getItem('authToken');
                                          const response = await fetch(`${API_BASE_URL}/admin/recibos/ingreso/${pago.id}/descargar`, {
                                            headers: { 'Authorization': `Bearer ${token}` }
                                          });
                                          if (response.status === 401) {
                                            handleAuthError(response);
                                            return;
                                          }
                                          if (response.ok) {
                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `recibo_ING-${String(pago.folio_recibo).padStart(4, '0')}_${pago.id}.pdf`;
                                            document.body.appendChild(a);
                                            a.click();
                                            window.URL.revokeObjectURL(url);
                                            document.body.removeChild(a);
                                          } else {
                                            alert('Error al descargar el recibo');
                                          }
                                        } catch (error) {
                                          console.error('Error al descargar recibo:', error);
                                          alert('Error al descargar el recibo');
                                        }
                                      }}
                                      title="Descargar Recibo PDF"
                                    >
                                      <GetAppIcon />
                                    </IconButton>
                                    <MuiTooltip title="Regenerar Recibo PDF (actualiza con cambios)">
                                      <IconButton
                                        size="small"
                                        color="info"
                                        onClick={async () => {
                                          try {
                                            const token = localStorage.getItem('authToken');
                                            const response = await fetch(`${API_BASE_URL}/admin/recibos/ingreso/${pago.id}/generar`, {
                                              method: 'POST',
                                              headers: {
                                                'Authorization': `Bearer ${token}`,
                                                'Content-Type': 'application/json'
                                              }
                                            });
                                            if (response.status === 401) {
                                              handleAuthError(response);
                                              return;
                                            }
                                            const data = await response.json();
                                            if (response.ok) {
                                              alert(data.message || 'Recibo regenerado');
                                              loadPagos();
                                            } else {
                                              alert(data.error || 'Error al regenerar el recibo');
                                            }
                                          } catch (error) {
                                            console.error('Error al regenerar recibo:', error);
                                            alert('Error al regenerar el recibo');
                                          }
                                        }}
                                        title="Regenerar Recibo PDF"
                                      >
                                        <ReceiptIcon />
                                      </IconButton>
                                    </MuiTooltip>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={async () => {
                                        handleAbrirModalEnviarReciboIngreso(pago);
                                      }}
                                      title="Reenviar Recibo por Correo"
                                    >
                                      <SendIcon />
                                    </IconButton>
                                  </>
                                ) : (
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem('authToken');
                                        const response = await fetch(`${API_BASE_URL}/admin/recibos/ingreso/${pago.id}/generar`, {
                                          method: 'POST',
                                          headers: { 
                                            'Authorization': `Bearer ${token}`,
                                            'Content-Type': 'application/json'
                                          }
                                        });
                                        if (response.status === 401) {
                                          handleAuthError(response);
                                          return;
                                        }
                                        const data = await response.json();
                                        if (response.ok) {
                                          alert('Recibo generado correctamente');
                                          loadPagos();
                                        } else {
                                          alert(data.error || 'Error al generar el recibo');
                                        }
                                      } catch (error) {
                                        console.error('Error al generar recibo:', error);
                                        alert('Error al generar el recibo');
                                      }
                                    }}
                                    title="Generar Recibo PDF"
                                  >
                                    <ReceiptIcon />
                                  </IconButton>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setPagoARevertir(pago);
                                    setShowDialogRevertirPago(true);
                                  }}
                                  title="Revertir pago"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={pagosPaginacion.total}
                    page={pagosPaginacion.pagina}
                    onPageChange={(event, newPage) => {
                      loadPagos(newPage);
                    }}
                    rowsPerPage={pagosPaginacion.registrosPorPagina}
                    onRowsPerPageChange={(event) => {
                      const newRowsPerPage = parseInt(event.target.value, 10);
                      setPagosPaginacion(prev => ({
                        ...prev,
                        registrosPorPagina: newRowsPerPage,
                        pagina: 0
                      }));
                      loadPagos(0, newRowsPerPage);
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Filas:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                  />
                </CardContent>
              </Card>

              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Capital inicial</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Punto base para el capital disponible en la app de residentes: capital inicial + ingresos vigentes (histórico) − gastos vigentes (histórico). El valor mostrado a residentes se actualiza una vez por día (zona horaria México, cambio de día a las 02:00).
                  </Typography>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Capital inicial"
                        type="text"
                        fullWidth
                        size="small"
                        value={capitalInicialEdit}
                        onChange={(e) => setCapitalInicialEdit(e.target.value)}
                        inputProps={{ inputMode: 'decimal' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        variant="contained"
                        onClick={guardarCapitalInicialIngresos}
                        disabled={capitalInicialGuardando}
                        sx={{ height: '40px' }}
                      >
                        {capitalInicialGuardando ? <CircularProgress size={22} color="inherit" /> : 'Guardar'}
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={5}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={invalidarSnapshotCapitalDisponible}
                        disabled={capitalSnapshotInvalidando}
                        sx={{ height: '40px' }}
                      >
                        {capitalSnapshotInvalidando ? <CircularProgress size={22} /> : 'Forzar recálculo capital (hoy)'}
                      </Button>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Borra el snapshot del día (México, corte 02:00) para que residentes vean totales actualizados al abrir Gráficos.
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}

          {tabActual === 5 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Registra y consulta egresos (gastos). Puedes cargar desde Excel o ingresar manualmente.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>
              
              <Dialog
                open={openModalEnviarReciboEgreso}
                onClose={() => !enviandoReciboEgreso && setOpenModalEnviarReciboEgreso(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Enviar recibo de egreso por correo</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Selecciona a quién se enviará el recibo. Puedes agregar correos manuales para egresos (tesorería/administración).
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Agregar correo para egresos"
                        type="email"
                        placeholder="correo@ejemplo.com"
                        value={nuevoEmailEgreso}
                        onChange={(e) => setNuevoEmailEgreso(e.target.value)}
                        onKeyDown={async (e) => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          await handleAgregarEmailEgreso();
                          const list = await loadDestinatariosEgresos();
                          const emails = Array.isArray(list) ? list.map((x) => x.email).filter(Boolean) : [];
                          setEmailsSeleccionadosEgreso(emails);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          await handleAgregarEmailEgreso();
                          const list = await loadDestinatariosEgresos();
                          const emails = Array.isArray(list) ? list.map((x) => x.email).filter(Boolean) : [];
                          setEmailsSeleccionadosEgreso(emails);
                        }}
                        disabled={!nuevoEmailEgreso.trim()}
                        sx={{ width: '100%' }}
                      >
                        Agregar
                      </Button>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setEmailsSeleccionadosEgreso(Array.isArray(destinatariosEgresos) ? destinatariosEgresos.map((x) => x.email).filter(Boolean) : [])}
                      disabled={!Array.isArray(destinatariosEgresos) || destinatariosEgresos.length === 0}
                    >
                      Seleccionar todos
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setEmailsSeleccionadosEgreso([])}
                      disabled={!emailsSeleccionadosEgreso?.length}
                    >
                      Limpiar selección
                    </Button>
                  </Box>

                  {loadingDestinatariosEgresos && (
                    <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} /> <Typography variant="body2">Cargando...</Typography>
                    </Box>
                  )}

                  {!loadingDestinatariosEgresos && (
                    <Box sx={{ mt: 2 }}>
                      {(!Array.isArray(destinatariosEgresos) || destinatariosEgresos.length === 0) ? (
                        <Typography variant="body2" color="text.secondary">
                          No hay correos configurados para egresos. Puedes agregarlos arriba, o enviar al fallback (correos de todas las casas).
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Destinatarios ({destinatariosEgresos.length})
                          </Typography>
                          <Box sx={{ maxHeight: 260, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                            {destinatariosEgresos.map((m) => {
                              const email = m.email;
                              const checked = emailsSeleccionadosEgreso.includes(email);
                              return (
                                <Box key={`egsel-${m.id}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={(e) => {
                                          setEmailsSeleccionadosEgreso((prev) => {
                                            const set = new Set(prev || []);
                                            if (e.target.checked) set.add(email); else set.delete(email);
                                            return Array.from(set);
                                          });
                                        }}
                                      />
                                    }
                                    label={email}
                                  />
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={async () => {
                                      await handleQuitarEmailEgreso(m.id);
                                      const list = await loadDestinatariosEgresos();
                                      const emails = Array.isArray(list) ? list.map((x) => x.email).filter(Boolean) : [];
                                      setEmailsSeleccionadosEgreso((prev) => prev.filter((e) => emails.includes(e)));
                                    }}
                                  >
                                    Quitar
                                  </Button>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenModalEnviarReciboEgreso(false)} disabled={enviandoReciboEgreso}>Cancelar</Button>
                  <Button
                    variant="outlined"
                    onClick={handleEnviarReciboEgresoTodasLasCasas}
                    disabled={enviandoReciboEgreso || !enviarReciboEgresoId}
                  >
                    Enviar a todas las casas
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleEnviarReciboEgresoSeleccionados}
                    disabled={enviandoReciboEgreso || !enviarReciboEgresoId || !emailsSeleccionadosEgreso.length}
                  >
                    {enviandoReciboEgreso ? 'Enviando...' : 'Enviar'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Filtros */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Filtros</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Mes"
                        fullWidth
                        value={filtrosEgresos.mes}
                        onChange={(e) => setFiltrosEgresos({ ...filtrosEgresos, mes: e.target.value })}
                        size="small"
                      >
                        <MenuItem value="">Todos</MenuItem>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                          <MenuItem key={m} value={String(m)}>{new Date(2000, m - 1, 1).toLocaleString('es', { month: 'long' })}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Año"
                        fullWidth
                        value={filtrosEgresos.año}
                        onChange={(e) => setFiltrosEgresos({ ...filtrosEgresos, año: e.target.value })}
                        size="small"
                      >
                        <MenuItem value="">Todos</MenuItem>
                        {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                          <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Estatus"
                        fullWidth
                        value={filtrosEgresos.estatus_recibo}
                        onChange={(e) => setFiltrosEgresos({ ...filtrosEgresos, estatus_recibo: e.target.value })}
                        size="small"
                      >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="vigente">Vigente</MenuItem>
                        <MenuItem value="cancelado">Cancelado</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                          setGastosPaginacion(prev => ({ ...prev, pagina: 0 }));
                          loadGastos(0);
                        }}
                        sx={{ height: '40px' }}
                      >
                        Filtrar
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                          setFiltrosEgresos({ fechaInicio: '', fechaFin: '', mes: '', año: '', propiedad: '', estatus_recibo: '' });
                          setGastosPaginacion(prev => ({ ...prev, pagina: 0 }));
                          loadGastos(0);
                        }}
                        sx={{ height: '40px' }}
                      >
                        Limpiar
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h6">Egresos de la Comunidad</Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={eliminarTodoGastos}
                            onChange={(e) => setEliminarTodoGastos(e.target.checked)}
                            color="warning"
                            disabled={loadingCargaExcelGastos}
                          />
                        }
                        label="Eliminar todo antes de cargar"
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Año"
                        value={añoCargaExcelGastos}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          if (Number.isFinite(n) && n >= 1990 && n <= 2100) setAñoCargaExcelGastos(n);
                        }}
                        inputProps={{ min: 1990, max: 2100 }}
                        disabled={loadingCargaExcelGastos}
                        sx={{ width: 100 }}
                        title="Año contable cuando la columna FECHA está vacía (se usa con MES o con el mes en CONCEPTO)"
                      />
                      <input
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        id="excel-gastos-upload"
                        type="file"
                        disabled={loadingCargaExcelGastos}
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (f) await handleCargarExcelGastos(f);
                          e.target.value = '';
                        }}
                      />
                      <label htmlFor="excel-gastos-upload" style={{ cursor: loadingCargaExcelGastos ? 'default' : 'pointer' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          component="span"
                          startIcon={loadingCargaExcelGastos ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                          disabled={loadingCargaExcelGastos}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {loadingCargaExcelGastos ? 'Procesando…' : 'Cargar Excel'}
                        </Button>
                      </label>
                      <Button variant="outlined" size="small" startIcon={<GetAppIcon />} onClick={exportarEgresosExcel} sx={{ whiteSpace: 'nowrap' }}>
                        Exportar Excel (mes)
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setGastoEditando(null);
                          setFormGasto({
                            fecha: new Date().toISOString().split('T')[0],
                            mes: '',
                            concepto: '',
                            tesoreria: '',
                            condicion: '',
                            subtotal: '',
                            iva: '',
                            total: '',
                            metodo_pago: '',
                            estatus_recibo: 'vigente',
                          });
                          setShowDialogGasto(true);
                        }}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Agregar Gasto
                      </Button>
                    </Box>
                  </Box>
                  {loadingCargaExcelGastos && (
                    <Box sx={{ width: '100%', mb: 2 }}>
                      <LinearProgress />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Procesando archivo de egresos en el servidor… Puede tardar varios minutos con archivos grandes.
                      </Typography>
                    </Box>
                  )}
                  <TableContainer component={Paper} sx={{ maxHeight: '55vh', overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Folio Recibo</TableCell>
                          <TableCell>Concepto</TableCell>
                          <TableCell>Tesorería</TableCell>
                          <TableCell>Total</TableCell>
                          <TableCell>Estatus</TableCell>
                          <TableCell>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {gastos.map((gasto) => (
                          <TableRow key={gasto.id}>
                            <TableCell>{gasto.id}</TableCell>
                            <TableCell>{gasto.fecha ? gasto.fecha.split('T')[0] : ''}</TableCell>
                            <TableCell>
                              {gasto.folio_recibo 
                                ? `EGR-${String(gasto.folio_recibo).padStart(4, '0')}` 
                                : '-'}
                            </TableCell>
                            <TableCell>{gasto.concepto}</TableCell>
                            <TableCell>{gasto.tesoreria || 'N/A'}</TableCell>
                            <TableCell>${parseFloat(gasto.total || 0).toFixed(2)}</TableCell>
                            <TableCell>{(gasto.estatus_recibo || 'vigente') === 'cancelado' ? 'Cancelado' : 'Vigente'}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setGastoEditando(gasto);
                                  const totalNum = parseFloat(gasto.total);
                                  let subtotal = gasto.subtotal || '';
                                  let iva = gasto.iva || '';
                                  if (!isNaN(totalNum) && totalNum > 0) {
                                    const sub = Math.round((totalNum / 1.16) * 100) / 100;
                                    const i = Math.round((totalNum - sub) * 100) / 100;
                                    subtotal = String(sub);
                                    iva = String(i);
                                  }
                                  setFormGasto({
                                    fecha: gasto.fecha ? gasto.fecha.split('T')[0] : '',
                                    mes: gasto.mes || '',
                                    concepto: gasto.concepto || '',
                                    tesoreria: gasto.tesoreria || '',
                                    condicion: gasto.condicion || '',
                                    subtotal,
                                    iva,
                                    total: gasto.total || '',
                                    metodo_pago: gasto.metodo_pago || '',
                                    estatus_recibo: gasto.estatus_recibo || 'vigente',
                                  });
                                  setShowDialogGasto(true);
                                }}
                                title="Editar"
                              >
                                <EditIcon />
                              </IconButton>
                              {(gasto.estatus_recibo || 'vigente') === 'vigente' ? (
                                <MuiTooltip title="Cancelar recibo (operación no realizada)">
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => window.confirm('¿Marcar este egreso como cancelado? No se elimina, solo se anula.') && handleCambiarEstatusGasto(gasto, 'cancelado')}
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </MuiTooltip>
                              ) : (
                                <MuiTooltip title="Reactivar recibo">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleCambiarEstatusGasto(gasto, 'vigente')}
                                  >
                                    <RestoreIcon />
                                  </IconButton>
                                </MuiTooltip>
                              )}
                              {gasto.ruta_pdf ? (
                                <>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem('authToken');
                                        const response = await fetch(`${API_BASE_URL}/admin/recibos/egreso/${gasto.id}/descargar`, {
                                          headers: { 'Authorization': `Bearer ${token}` }
                                        });
                                        if (response.status === 401) {
                                          handleAuthError(response);
                                          return;
                                        }
                                        if (response.ok) {
                                          const blob = await response.blob();
                                          const url = window.URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          a.download = `recibo_EGR-${String(gasto.folio_recibo).padStart(4, '0')}_${gasto.id}.pdf`;
                                          document.body.appendChild(a);
                                          a.click();
                                          window.URL.revokeObjectURL(url);
                                          document.body.removeChild(a);
                                        } else {
                                          alert('Error al descargar el recibo');
                                        }
                                      } catch (error) {
                                        console.error('Error al descargar recibo:', error);
                                        alert('Error al descargar el recibo');
                                      }
                                    }}
                                    title="Descargar Recibo PDF"
                                  >
                                    <GetAppIcon />
                                  </IconButton>
                                  <MuiTooltip title="Regenerar Recibo PDF (actualiza con cambios)">
                                    <IconButton
                                      size="small"
                                      color="info"
                                      onClick={async () => {
                                        try {
                                          const token = localStorage.getItem('authToken');
                                          const response = await fetch(`${API_BASE_URL}/admin/recibos/egreso/${gasto.id}/generar`, {
                                            method: 'POST',
                                            headers: {
                                              'Authorization': `Bearer ${token}`,
                                              'Content-Type': 'application/json'
                                            }
                                          });
                                          if (response.status === 401) {
                                            handleAuthError(response);
                                            return;
                                          }
                                          const data = await response.json();
                                          if (response.ok) {
                                            alert(data.message || 'Recibo regenerado');
                                            loadGastos();
                                          } else {
                                            alert(data.error || 'Error al regenerar el recibo');
                                          }
                                        } catch (error) {
                                          console.error('Error al regenerar recibo:', error);
                                          alert('Error al regenerar el recibo');
                                        }
                                      }}
                                      title="Regenerar Recibo PDF"
                                    >
                                      <ReceiptIcon />
                                    </IconButton>
                                  </MuiTooltip>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => {
                                      handleAbrirModalEnviarReciboEgreso(gasto);
                                    }}
                                    title="Reenviar Recibo por Correo"
                                  >
                                    <SendIcon />
                                  </IconButton>
                                </>
                              ) : (
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('authToken');
                                      const response = await fetch(`${API_BASE_URL}/admin/recibos/egreso/${gasto.id}/generar`, {
                                        method: 'POST',
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'Content-Type': 'application/json'
                                        }
                                      });
                                      if (response.status === 401) {
                                        handleAuthError(response);
                                        return;
                                      }
                                      const data = await response.json();
                                      if (response.ok) {
                                        alert('Recibo generado correctamente');
                                        loadGastos();
                                      } else {
                                        alert(data.error || 'Error al generar el recibo');
                                      }
                                    } catch (error) {
                                      console.error('Error al generar recibo:', error);
                                      alert('Error al generar el recibo');
                                    }
                                  }}
                                  title="Generar Recibo PDF"
                                >
                                  <ReceiptIcon />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (window.confirm(`¿Estás seguro de que deseas eliminar el gasto "${gasto.concepto}"?`)) {
                                    handleEliminarGasto(gasto.id);
                                  }
                                }}
                                title="Eliminar"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={gastosPaginacion.total}
                    page={gastosPaginacion.pagina}
                    onPageChange={(event, newPage) => {
                      loadGastos(newPage);
                    }}
                    rowsPerPage={gastosPaginacion.registrosPorPagina}
                    onRowsPerPageChange={(event) => {
                      const newRowsPerPage = parseInt(event.target.value, 10);
                      setGastosPaginacion(prev => ({
                        ...prev,
                        registrosPorPagina: newRowsPerPage,
                        pagina: 0
                      }));
                      loadGastos(0, newRowsPerPage);
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Filas:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                  />
                </CardContent>
              </Card>

              {/* Diálogo para crear/editar gasto */}
              <Dialog open={showDialogGasto} onClose={() => setShowDialogGasto(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                  {gastoEditando ? 'Editar Gasto' : 'Nuevo Gasto'}
                </DialogTitle>
                <DialogContent>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Fecha"
                        type="date"
                        fullWidth
                        value={formGasto.fecha}
                        onChange={(e) => setFormGasto({ ...formGasto, fecha: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Concepto"
                        fullWidth
                        required
                        value={formGasto.concepto}
                        onChange={(e) => setFormGasto({ ...formGasto, concepto: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Subtotal"
                        type="number"
                        fullWidth
                        value={formGasto.subtotal}
                        InputProps={{ readOnly: true }}
                        helperText="Calculado (Total / 1.16)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="IVA (16%)"
                        type="number"
                        fullWidth
                        value={formGasto.iva}
                        InputProps={{ readOnly: true }}
                        helperText="Calculado"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Total"
                        type="number"
                        fullWidth
                        required
                        value={formGasto.total}
                        onChange={(e) => {
                          const totalVal = e.target.value;
                          const num = parseFloat(totalVal);
                          if (totalVal === '' || isNaN(num) || num <= 0) {
                            setFormGasto({ ...formGasto, total: totalVal, subtotal: '', iva: '' });
                            return;
                          }
                          const subtotal = Math.round((num / 1.16) * 100) / 100;
                          const iva = Math.round((num - subtotal) * 100) / 100;
                          setFormGasto({ ...formGasto, total: totalVal, subtotal: String(subtotal), iva: String(iva) });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Método de Pago</InputLabel>
                        <Select
                          value={formGasto.metodo_pago || ''}
                          label="Método de Pago"
                          onChange={(e) => setFormGasto({ ...formGasto, metodo_pago: e.target.value })}
                        >
                          <MenuItem value="efectivo">Efectivo</MenuItem>
                          <MenuItem value="tarjeta">Tarjeta</MenuItem>
                          <MenuItem value="transferencia">Transferencia</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => {
                    setShowDialogGasto(false);
                    setGastoEditando(null);
                  }}>
                    Cancelar
                  </Button>
                  <Button onClick={gastoEditando ? handleActualizarGasto : handleCrearGasto}>
                    {gastoEditando ? 'Actualizar' : 'Crear'}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {tabActual === 7 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Gestiona documentos del condominio. Los residentes pueden consultarlos desde la app.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h6">Documentos de la Comunidad</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setDocumentoEditando(null);
                        setFormDocumento({
                          nombre_mostrar: '',
                          activo: true,
                        });
                        setArchivoDocumento(null);
                        setShowDialogDocumento(true);
                      }}
                    >
                      Agregar Documento
                    </Button>
                  </Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {documentos.map((doc) => (
                          <TableRow key={doc.id}>
                            <TableCell>{doc.id}</TableCell>
                            <TableCell>{doc.nombre_mostrar}</TableCell>
                            <TableCell>{doc.tipo_archivo || 'N/A'}</TableCell>
                            <TableCell>{doc.activo ? 'Activo' : 'Inactivo'}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  window.open(`${API_BASE_URL}${doc.archivo_url}`, '_blank');
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDocumentoEditando(doc);
                                  setFormDocumento({
                                    nombre_mostrar: doc.nombre_mostrar || '',
                                    activo: doc.activo || false,
                                  });
                                  setShowDialogDocumento(true);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (window.confirm(`¿Estás seguro de que deseas eliminar el documento "${doc.nombre_mostrar}"?`)) {
                                    handleEliminarDocumento(doc.id);
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Diálogo para crear/editar documento */}
              <Dialog open={showDialogDocumento} onClose={() => setShowDialogDocumento(false)}>
                <DialogTitle>
                  {documentoEditando ? 'Editar Documento' : 'Nuevo Documento'}
                </DialogTitle>
                <DialogContent>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Nombre del documento"
                    fullWidth
                    variant="standard"
                    value={formDocumento.nombre_mostrar}
                    onChange={(e) => setFormDocumento({ ...formDocumento, nombre_mostrar: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  {!documentoEditando && (
                    <input
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
                      style={{ display: 'none' }}
                      id="documento-upload"
                      type="file"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setArchivoDocumento(e.target.files[0]);
                        }
                      }}
                    />
                  )}
                  {!documentoEditando && (
                    <label htmlFor="documento-upload">
                      <Button variant="outlined" component="span" fullWidth>
                        {archivoDocumento ? archivoDocumento.name : 'Seleccionar Archivo'}
                      </Button>
                    </label>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formDocumento.activo}
                        onChange={(e) => setFormDocumento({ ...formDocumento, activo: e.target.checked })}
                      />
                    }
                    label="Activo"
                    sx={{ mt: 2 }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => {
                    setShowDialogDocumento(false);
                    setDocumentoEditando(null);
                    setArchivoDocumento(null);
                  }}>
                    Cancelar
                  </Button>
                  <Button onClick={documentoEditando ? handleActualizarDocumento : handleCrearDocumento}>
                    {documentoEditando ? 'Actualizar' : 'Crear'}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {tabActual === 16 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Revisa sugerencias, guarda un borrador de respuesta y publícala cuando esté lista. Se envía correo si el residente tiene email; si no, solo verá la respuesta en la app.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Residente</TableCell>
                          <TableCell>Correo</TableCell>
                          <TableCell>Propiedad</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>Mensaje</TableCell>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Revisión</TableCell>
                          <TableCell>Respuesta</TableCell>
                          <TableCell align="right" sx={{ width: 1, whiteSpace: 'nowrap' }}>
                            Acciones
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sugerencias.map((sug) => {
                          const emailTrim = (sug.residente_email || '').trim();
                          const sinEmail = !emailTrim;
                          const publicada = !!sug.respuesta_enviada_at;
                          const borrador = !publicada && sug.respuesta_admin && String(sug.respuesta_admin).trim();
                          return (
                            <TableRow key={sug.id}>
                              <TableCell>{sug.id}</TableCell>
                              <TableCell>{sug.residente_nombre || 'N/A'}</TableCell>
                              <TableCell>
                                {sinEmail ? (
                                  <Chip size="small" color="warning" label="Sin correo" />
                                ) : (
                                  <MuiTooltip title={emailTrim}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                                      {emailTrim}
                                    </Typography>
                                  </MuiTooltip>
                                )}
                              </TableCell>
                              <TableCell>{sug.propiedad || 'N/A'}</TableCell>
                              <TableCell sx={{ maxWidth: 220 }}>
                                <MuiTooltip title={sug.mensaje || ''}>
                                  <Typography variant="body2" noWrap>
                                    {sug.mensaje || ''}
                                  </Typography>
                                </MuiTooltip>
                              </TableCell>
                              <TableCell>{sug.created_at ? formatFechaSoloMexicoCentralFromInstant(sug.created_at) : ''}</TableCell>
                              <TableCell>{sug.vista ? 'Vista' : 'No vista'}</TableCell>
                              <TableCell>
                                {publicada ? (
                                  <Chip size="small" color="success" label="Publicada" />
                                ) : borrador ? (
                                  <Chip size="small" color="info" label="Borrador" />
                                ) : (
                                  <Chip size="small" variant="outlined" label="Pendiente" />
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.5, verticalAlign: 'middle' }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'nowrap',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    gap: 0,
                                  }}
                                >
                                  <MuiTooltip
                                    title={publicada ? 'Ver respuesta publicada' : 'Responder o editar borrador'}
                                  >
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => abrirDialogSugerencia(sug)}
                                      aria-label={publicada ? 'Ver respuesta' : 'Responder'}
                                    >
                                      {publicada ? <VisibilityIcon fontSize="small" /> : <ReplyIcon fontSize="small" />}
                                    </IconButton>
                                  </MuiTooltip>
                                  {!sug.vista && (
                                    <MuiTooltip title="Marcar sugerencia como vista">
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleMarcarSugerenciaVista(sug.id)}
                                        aria-label="Marcar como vista"
                                      >
                                        <CheckCircleOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </MuiTooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              <Dialog open={!!sugerenciaDialog} onClose={cerrarDialogSugerencia} maxWidth="sm" fullWidth>
                {sugerenciaDialog && (
                  <>
                    <DialogTitle>
                      {sugerenciaDialog.respuesta_enviada_at ? 'Respuesta publicada' : 'Responder sugerencia'}
                    </DialogTitle>
                    <DialogContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                        Residente
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {sugerenciaDialog.residente_nombre || 'N/A'}
                        {' · '}
                        {sugerenciaDialog.propiedad || 'N/A'}
                      </Typography>
                      {(sugerenciaDialog.residente_email || '').trim() ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Correo: {(sugerenciaDialog.residente_email || '').trim()}
                        </Typography>
                      ) : (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          Este residente no tiene correo: al publicar, la respuesta solo se mostrará en la app.
                        </Alert>
                      )}
                      <Typography variant="subtitle2" color="text.secondary">
                        Mensaje del residente
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50', whiteSpace: 'pre-wrap' }}>
                        {sugerenciaDialog.mensaje || ''}
                      </Paper>
                      {sugerenciaDialog.respuesta_enviada_at ? (
                        <>
                          <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
                            Respuesta enviada
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {sugerenciaDialog.respuesta_enviada_at
                              ? formatFechaHoraMexicoCentral(sugerenciaDialog.respuesta_enviada_at)
                              : ''}
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover', whiteSpace: 'pre-wrap' }}>
                            {sugerenciaDialog.respuesta_admin || ''}
                          </Paper>
                        </>
                      ) : (
                        <>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Tu respuesta (borrador; puedes editar hasta publicar)
                          </Typography>
                          <TextField
                            multiline
                            minRows={6}
                            fullWidth
                            value={sugerenciaDialogTexto}
                            onChange={(e) => setSugerenciaDialogTexto(e.target.value)}
                            placeholder="Escribe la respuesta para el residente…"
                            inputProps={{ maxLength: 8000 }}
                            helperText={`${sugerenciaDialogTexto.length}/8000`}
                          />
                        </>
                      )}
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={cerrarDialogSugerencia}>Cerrar</Button>
                      {!sugerenciaDialog.respuesta_enviada_at && (
                        <>
                          <Button
                            onClick={guardarBorradorRespuestaSugerencia}
                            disabled={sugerenciaDialogSaving || sugerenciaDialogPublishing}
                            variant="outlined"
                          >
                            {sugerenciaDialogSaving ? 'Guardando…' : 'Guardar borrador'}
                          </Button>
                          <Button
                            onClick={publicarRespuestaSugerencia}
                            variant="contained"
                            color="primary"
                            disabled={sugerenciaDialogPublishing || sugerenciaDialogSaving || !sugerenciaDialogTexto.trim()}
                          >
                            {sugerenciaDialogPublishing ? 'Publicando…' : 'Publicar y enviar'}
                          </Button>
                        </>
                      )}
                    </DialogActions>
                  </>
                )}
              </Dialog>
            </>
          )}

          {tabActual === 17 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Revisa y aprueba o rechaza las solicitudes de residentes que piden eliminar sus datos personales.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  select
                  size="small"
                  label="Estado"
                  value={solicitudEliminacionFiltro}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSolicitudEliminacionFiltro(v);
                    loadSolicitudesEliminacion(v);
                  }}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pendiente">Pendientes</MenuItem>
                  <MenuItem value="aprobada">Aprobadas</MenuItem>
                  <MenuItem value="rechazada">Rechazadas</MenuItem>
                </TextField>
                <Button variant="outlined" onClick={loadSolicitudesEliminacion}>Actualizar</Button>
              </Box>
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Usuario</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Propiedad</TableCell>
                          <TableCell>Fecha solicitud</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Revisado</TableCell>
                          <TableCell>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {solicitudesEliminacion.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">No hay solicitudes</TableCell>
                          </TableRow>
                        ) : (
                          solicitudesEliminacion.map((s) => (
                            <TableRow key={s.id}>
                              <TableCell>{s.id}</TableCell>
                              <TableCell>{s.usuario_nombre || 'N/A'}</TableCell>
                              <TableCell>{s.usuario_email || 'N/A'}</TableCell>
                              <TableCell>{s.usuario_propiedad || 'N/A'}</TableCell>
                              <TableCell>{s.created_at ? formatFechaHoraMexicoCentral(s.created_at) : ''}</TableCell>
                              <TableCell>{s.estado}</TableCell>
                              <TableCell>
                                {s.revisado_at ? `${formatFechaSoloMexicoCentralFromInstant(s.revisado_at)}${s.revisado_por_nombre ? ` (${s.revisado_por_nombre})` : ''}` : '-'}
                              </TableCell>
                              <TableCell>
                                {s.estado === 'pendiente' && (
                                  <>
                                    <Button size="small" color="success" variant="outlined" sx={{ mr: 1 }} onClick={() => { setRevisarSolicitudId(s.id); setRevisarSolicitudAccion('aprobada'); setObservacionesSolicitud(''); }}>Aprobar</Button>
                                    <Button size="small" color="error" variant="outlined" onClick={() => { setRevisarSolicitudId(s.id); setRevisarSolicitudAccion('rechazada'); setObservacionesSolicitud(''); }}>Rechazar</Button>
                                  </>
                                )}
                                {s.estado !== 'pendiente' && s.observaciones_admin && (
                                  <Typography variant="caption" display="block">{s.observaciones_admin}</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              <Dialog open={revisarSolicitudId != null} onClose={() => { setRevisarSolicitudId(null); setRevisarSolicitudAccion(null); setObservacionesSolicitud(''); }}>
                <DialogTitle>{revisarSolicitudAccion === 'aprobada' ? 'Aprobar solicitud' : 'Rechazar solicitud'}</DialogTitle>
                <DialogContent>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Observaciones (opcional)"
                    value={observacionesSolicitud}
                    onChange={(e) => setObservacionesSolicitud(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setRevisarSolicitudId(null); setRevisarSolicitudAccion(null); setObservacionesSolicitud(''); }}>Cancelar</Button>
                  <Button variant="contained" color={revisarSolicitudAccion === 'aprobada' ? 'success' : 'error'} onClick={() => handleRevisarSolicitudEliminacion(revisarSolicitudId, revisarSolicitudAccion)}>
                    {revisarSolicitudAccion === 'aprobada' ? 'Aprobar' : 'Rechazar'}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {tabActual === 18 && (
            <EncuestasTab
              encuestas={encuestasList}
              loading={loadingEncuestas}
              error={errorEncuestas}
              onRefresh={loadEncuestas}
              onExportar={(e) => {}}
              onCerrar={handleCerrarEncuesta}
              onEliminar={handleEliminarEncuesta}
              fetchWithAuth={fetchWithAuth}
              API_BASE_URL={API_BASE_URL}
            />
          )}

          {tabActual === 2 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Define el orden en que aparecerán los contactos (residentes y contactos de la propiedad) cuando el guardia valide un QR. Solo se muestran quienes no están marcados como no marcar.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                <Autocomplete
                  options={[...(listadoPropiedades || [])].sort(compareNatural)}
                  getOptionLabel={(opt) => String(opt ?? '')}
                  value={ordenLlamadaPropiedad || null}
                  onChange={async (e, newValue) => {
                    const p = newValue ?? '';
                    setOrdenLlamadaPropiedad(p);
                    if (!p) { setOrdenLlamadaItems([]); return; }
                    setOrdenLlamadaLoading(true);
                    try {
                      const res = await fetchWithAuth(`${API_BASE_URL}/admin/propiedades/${encodeURIComponent(p)}/orden-llamada`);
                      const data = await res.json();
                      if (res.ok) setOrdenLlamadaItems(Array.isArray(data.items) ? data.items : []);
                      else setError(data.error || 'Error al cargar');
                    } catch (err) {
                      setError(err.message || 'Error al cargar orden de llamada');
                      setOrdenLlamadaItems([]);
                    } finally {
                      setOrdenLlamadaLoading(false);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Propiedad"
                      placeholder="Ej: 1, 2, 10, 1-A..."
                      size="small"
                    />
                  )}
                  sx={{ minWidth: 220 }}
                  isOptionEqualToValue={(opt, val) => (opt ?? '') === (val ?? '')}
                />
                {ordenLlamadaPropiedad && (
                  <Button
                    variant="contained"
                    disabled={ordenLlamadaSaving || ordenLlamadaItems.length === 0}
                    onClick={async () => {
                      setOrdenLlamadaSaving(true);
                      try {
                        const res = await fetchWithAuth(`${API_BASE_URL}/admin/propiedades/${encodeURIComponent(ordenLlamadaPropiedad)}/orden-llamada`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(ordenLlamadaItems.map(it => ({ tipo: it.tipo, id: it.id }))),
                        });
                        const data = await res.json();
                        if (res.ok) { setOrdenLlamadaItems(Array.isArray(data.items) ? data.items : []); setSuccess('Orden guardado.'); }
                        else setError(data.error || 'Error al guardar');
                      } catch (err) {
                        setError(err.message || 'Error al guardar');
                      } finally {
                        setOrdenLlamadaSaving(false);
                      }
                    }}
                  >
                    {ordenLlamadaSaving ? 'Guardando…' : 'Guardar orden'}
                  </Button>
                )}
              </Box>
              {ordenLlamadaLoading && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}><CircularProgress size={20} /> Cargando…</Box>}
              {ordenLlamadaPropiedad && !ordenLlamadaLoading && (
                <Card>
                  <CardContent>
                    {ordenLlamadaItems.length === 0 ? (
                      <Typography color="text.secondary">No hay contactos para llamada en esta propiedad. Agrega teléfonos de residentes (sin marcar &quot;no marcar&quot;) o contactos de la propiedad.</Typography>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell width={60}>Orden</TableCell>
                              <TableCell>Nombre</TableCell>
                              <TableCell>Teléfono</TableCell>
                              <TableCell>Tipo</TableCell>
                              <TableCell width={100}>Mover</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ordenLlamadaItems.map((it, idx) => (
                              <TableRow key={`${it.tipo}-${it.id}`}>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>{it.nombre || '—'}</TableCell>
                                <TableCell>{it.telefono && String(it.telefono).trim() ? it.telefono : 'Sin teléfono'}</TableCell>
                                <TableCell>{it.tipo === 'residente' ? 'Residente' : 'Contacto'}</TableCell>
                                <TableCell>
                                  <IconButton size="small" disabled={idx === 0} onClick={() => {
                                    const arr = [...ordenLlamadaItems];
                                    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                                    setOrdenLlamadaItems(arr);
                                  }}><ArrowUpwardIcon fontSize="small" /></IconButton>
                                  <IconButton size="small" disabled={idx === ordenLlamadaItems.length - 1} onClick={() => {
                                    const arr = [...ordenLlamadaItems];
                                    [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                                    setOrdenLlamadaItems(arr);
                                  }}><ArrowDownwardIcon fontSize="small" /></IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {tabActual === 8 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Consulta los pases generados por mes y propiedad. Revisa usos y detalle de ingresos por pase.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>

              {/* Filtros */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Filtros (carga por mes)</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Mes"
                        fullWidth
                        value={filtroPasesMes}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setFiltroPasesMes(v);
                          pasesFiltrosRef.current.mes = v;
                        }}
                        size="small"
                      >
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                          <MenuItem key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('es', { month: 'long' })}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Año"
                        fullWidth
                        value={filtroPasesAño}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setFiltroPasesAño(v);
                          pasesFiltrosRef.current.año = v;
                        }}
                        size="small"
                      >
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                          <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Estado"
                        fullWidth
                        value={filtroPasesUsado}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFiltroPasesUsado(v);
                          pasesFiltrosRef.current.usado = v;
                        }}
                        size="small"
                      >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="false">Activos</MenuItem>
                        <MenuItem value="true">Inactivos</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        label="Propiedad"
                        fullWidth
                        value={filtroPasesPropiedad}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFiltroPasesPropiedad(v);
                          pasesFiltrosRef.current.propiedad = v;
                        }}
                        size="small"
                        placeholder="Ej: 101"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Button
                        variant="contained"
                        onClick={() => loadPases({ ...pasesFiltrosRef.current })}
                        fullWidth
                        sx={{ height: '40px' }}
                      >
                        Filtrar
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const now = new Date();
                          const mes = now.getMonth() + 1;
                          const año = now.getFullYear();
                          setFiltroPasesMes(mes);
                          setFiltroPasesAño(año);
                          setFiltroPasesUsado('');
                          setFiltroPasesPropiedad('');
                          pasesFiltrosRef.current = { usado: '', propiedad: '', mes, año };
                          loadPases({ usado: '', propiedad: '', mes, año });
                        }}
                        fullWidth
                        sx={{ height: '40px' }}
                      >
                        Limpiar
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button variant="outlined" startIcon={<GetAppIcon />} onClick={exportarPasesCSV} disabled={pasesTotal === 0}>
                        Exportar CSV (mes seleccionado)
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Tabla de Pases */}
              <Card>
                <CardContent>
                  <TableContainer component={Paper} sx={{ maxHeight: '55vh' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>ID</strong></TableCell>
                          <TableCell><strong>Visitante</strong></TableCell>
                          <TableCell><strong>Residente</strong></TableCell>
                          <TableCell><strong>Propiedad</strong></TableCell>
                          <TableCell><strong>Tipo</strong></TableCell>
                          <TableCell><strong>Categoría</strong></TableCell>
                          <TableCell><strong>Formato</strong></TableCell>
                          <TableCell><strong>Código QR</strong></TableCell>
                          <TableCell><strong>Código Numérico</strong></TableCell>
                          <TableCell><strong>Fecha Validez</strong></TableCell>
                          <TableCell><strong>Fecha Fin Validez</strong></TableCell>
                          <TableCell><strong>Estado</strong></TableCell>
                          <TableCell><strong>Fecha Uso</strong></TableCell>
                          <TableCell><strong>Fecha Entrada</strong></TableCell>
                          <TableCell><strong>Fecha Salida</strong></TableCell>
                          <TableCell><strong>Núm. cono</strong></TableCell>
                          <TableCell><strong>Color cono</strong></TableCell>
                          <TableCell><strong>Guardia</strong></TableCell>
                          <TableCell><strong>Fecha Creación</strong></TableCell>
                          <TableCell><strong>Ingresos</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pases.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={20} align="center">
                              No hay pases disponibles
                            </TableCell>
                          </TableRow>
                        ) : (
                          pases.map((pase) => (
                            <TableRow key={pase.id}>
                              <TableCell>{pase.id}</TableCell>
                              <TableCell>{pase.visitante_nombre}</TableCell>
                              <TableCell>{pase.residente_nombre || 'N/A'}</TableCell>
                              <TableCell>{pase.propiedad || 'N/A'}</TableCell>
                              <TableCell>{pase.tipo_pase || 'N/A'}</TableCell>
                              <TableCell>{pase.categoria_visita || 'N/A'}</TableCell>
                              <TableCell>{pase.tipo_pase_formato || 'QR'}</TableCell>
                              <TableCell>
                                {pase.codigo_qr ? (
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {pase.codigo_qr.substring(0, 20)}...
                                  </Typography>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              <TableCell>{pase.codigo_numerico || '-'}</TableCell>
                              <TableCell>
                                {pase.fecha_validez ? formatFechaOnlyMexico(pase.fecha_validez) : '-'}
                              </TableCell>
                              <TableCell>
                                {pase.fecha_fin_validez ? formatFechaOnlyMexico(pase.fecha_fin_validez) : '-'}
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: 'inline-block',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: pase.usado ? '#e8f5e9' : '#fff3e0',
                                    color: pase.usado ? '#2e7d32' : '#e65100',
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {pase.usado ? 'Inactivo' : 'Activo'}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {pase.fecha_uso ? formatFechaHoraMexicoCentral(pase.fecha_uso) : '-'}
                              </TableCell>
                              <TableCell>
                                {pase.bitacora_entrada ? formatFechaHoraMexicoCentral(pase.bitacora_entrada) : '-'}
                              </TableCell>
                              <TableCell>
                                {pase.bitacora_salida ? formatFechaHoraMexicoCentral(pase.bitacora_salida) : '-'}
                              </TableCell>
                              <TableCell>{pase.numero_cono || '-'}</TableCell>
                              <TableCell>{pase.color_cono || '-'}</TableCell>
                              <TableCell>{pase.guardia_nombre || '-'}</TableCell>
                              <TableCell>
                                {pase.created_at ? formatFechaHoraMexicoCentral(pase.created_at) : '-'}
                              </TableCell>
                              <TableCell>
                                {pase.categoria_visita === 'Recurrente' ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleVerIngresosPase(pase)}
                                  >
                                    Ver ingresos
                                  </Button>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={pasesTotal}
                      page={pasesPagina}
                      onPageChange={(e, newPage) => loadPases({ pagina: newPage })}
                      rowsPerPage={pasesRegistrosPorPagina}
                      onRowsPerPageChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        setPasesRegistrosPorPagina(n);
                        loadPases({ pagina: 0, registrosPorPagina: n });
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                      labelRowsPerPage="Filas:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                    />
                  </TableContainer>
                </CardContent>
              </Card>

              <Dialog
                open={paseIngresosDialogOpen}
                onClose={() => {
                  setPaseIngresosDialogOpen(false);
                  setPaseIngresos([]);
                  setPaseIngresosSeleccionado(null);
                  setPaseIngresosError('');
                }}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle>
                  Detalle de ingresos — Pase #{paseIngresosSeleccionado?.id}
                  {paseIngresosSeleccionado && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({paseIngresosTotalUsos} uso{paseIngresosTotalUsos !== 1 ? 's' : ''})
                    </Typography>
                  )}
                </DialogTitle>
                <DialogContent dividers>
                  {paseIngresosLoading && (
                    <Typography variant="body2" sx={{ py: 2 }}>
                      Cargando ingresos…
                    </Typography>
                  )}
                  {paseIngresosError && (
                    <Typography color="error" variant="body2" sx={{ py: 2 }}>
                      {paseIngresosError}
                    </Typography>
                  )}
                  {!paseIngresosLoading && !paseIngresosError && paseIngresos.length === 0 && (
                    <Typography variant="body2" sx={{ py: 2 }}>
                      No se han registrado ingresos para este pase.
                    </Typography>
                  )}
                  {!paseIngresosLoading && paseIngresos.length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>#</strong></TableCell>
                            <TableCell><strong>Día</strong></TableCell>
                            <TableCell><strong>Hora entrada</strong></TableCell>
                            <TableCell><strong>Hora salida</strong></TableCell>
                            <TableCell><strong>Núm. cono</strong></TableCell>
                            <TableCell><strong>Color cono</strong></TableCell>
                            <TableCell><strong>Resultado</strong></TableCell>
                            <TableCell><strong>Guardia entrada</strong></TableCell>
                            <TableCell><strong>Guardia salida</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paseIngresos.map((ingreso) => {
                            const fechaEntrada = ingreso.fecha_entrada ? new Date(ingreso.fecha_entrada) : null;
                            const fechaSalida = ingreso.fecha_salida ? new Date(ingreso.fecha_salida) : null;
                            return (
                              <TableRow key={ingreso.id}>
                                <TableCell>{ingreso.id}</TableCell>
                                <TableCell>{fechaEntrada ? formatFechaSoloMexicoCentralFromInstant(ingreso.fecha_entrada) : '–'}</TableCell>
                                <TableCell>{fechaEntrada ? formatHoraMexicoCentralFromInstant(ingreso.fecha_entrada, { seconds: true }) : '–'}</TableCell>
                                <TableCell>{fechaSalida ? formatHoraMexicoCentralFromInstant(ingreso.fecha_salida, { seconds: true }) : '–'}</TableCell>
                                <TableCell>{ingreso.numero_cono || '–'}</TableCell>
                                <TableCell>{ingreso.color_cono || '–'}</TableCell>
                                <TableCell>{ingreso.resultado || '–'}</TableCell>
                                <TableCell>{ingreso.guardia_entrada_nombre || '–'}</TableCell>
                                <TableCell>{ingreso.guardia_salida_nombre || '–'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setPaseIngresosDialogOpen(false);
                      setPaseIngresos([]);
                      setPaseIngresosSeleccionado(null);
                      setPaseIngresosError('');
                    }}
                  >
                    Cerrar
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {tabActual === 9 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Historial de notificaciones de servicio (entradas/salidas). Filtra por estado, propiedad, mes y año.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Filtros (carga por mes)</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Mes"
                        fullWidth
                        value={filtroNotifMes}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setFiltroNotifMes(v);
                          notifFiltrosRef.current.mes = v;
                        }}
                        size="small"
                      >
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                          <MenuItem key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('es', { month: 'long' })}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Año"
                        fullWidth
                        value={filtroNotifAño}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setFiltroNotifAño(v);
                          notifFiltrosRef.current.año = v;
                        }}
                        size="small"
                      >
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                          <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Estado"
                        fullWidth
                        value={filtroNotifEstado}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFiltroNotifEstado(v);
                          notifFiltrosRef.current.estado = v;
                        }}
                        size="small"
                      >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="pendiente">Pendiente</MenuItem>
                        <MenuItem value="ingresado">Ingresado</MenuItem>
                        <MenuItem value="archivado">Archivado</MenuItem>
                        <MenuItem value="salida">Salida</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        label="Propiedad"
                        fullWidth
                        value={filtroNotifPropiedad}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFiltroNotifPropiedad(v);
                          notifFiltrosRef.current.propiedad = v;
                        }}
                        size="small"
                        placeholder="Ej: 101"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Button
                        variant="contained"
                        onClick={() => loadNotificaciones({ ...notifFiltrosRef.current })}
                        fullWidth
                        sx={{ height: '40px' }}
                      >
                        Filtrar
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const now = new Date();
                          const mes = now.getMonth() + 1;
                          const año = now.getFullYear();
                          setFiltroNotifMes(mes);
                          setFiltroNotifAño(año);
                          setFiltroNotifEstado('');
                          setFiltroNotifPropiedad('');
                          notifFiltrosRef.current = { estado: '', propiedad: '', mes, año };
                          loadNotificaciones({ estado: '', propiedad: '', mes, año });
                        }}
                        fullWidth
                        sx={{ height: '40px' }}
                      >
                        Limpiar
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button variant="outlined" startIcon={<GetAppIcon />} onClick={exportarNotificacionesCSV} disabled={notifTotal === 0}>
                        Exportar CSV (mes seleccionado)
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <TableContainer component={Paper} sx={{ maxHeight: '55vh' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>ID</strong></TableCell>
                          <TableCell><strong>Residente</strong></TableCell>
                          <TableCell><strong>Propiedad</strong></TableCell>
                          <TableCell><strong>Categoría</strong></TableCell>
                          <TableCell><strong>Servicio</strong></TableCell>
                          <TableCell><strong>Fecha entrada</strong></TableCell>
                          <TableCell><strong>Visitante</strong></TableCell>
                          <TableCell><strong>Placas</strong></TableCell>
                          <TableCell><strong>Estado</strong></TableCell>
                          <TableCell><strong>Fecha ingreso</strong></TableCell>
                          <TableCell><strong>Guardia ingreso</strong></TableCell>
                          <TableCell><strong>Fecha salida</strong></TableCell>
                          <TableCell><strong>Guardia salida</strong></TableCell>
                          <TableCell><strong>Creación</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {notificaciones.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={14} align="center">
                              No hay notificaciones disponibles
                            </TableCell>
                          </TableRow>
                        ) : (
                          notificaciones.map((n) => (
                            <TableRow key={n.id}>
                              <TableCell>{n.id}</TableCell>
                              <TableCell>{n.residente_nombre || 'N/A'}</TableCell>
                              <TableCell>{n.propiedad || 'N/A'}</TableCell>
                              <TableCell>{n.categoria_nombre || '-'}</TableCell>
                              <TableCell>{n.nombre_servicio_nombre || n.nombre_servicio_manual || '-'}</TableCell>
                              <TableCell>
                                {n.fecha_entrada ? formatFechaOnlyMexico(n.fecha_entrada) : '-'}
                              </TableCell>
                              <TableCell>{n.nombre_visitante || '-'}</TableCell>
                              <TableCell>{n.placas_vehiculo || '-'}</TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: 'inline-block',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    backgroundColor:
                                      n.estado === 'pendiente' ? '#fff3e0' :
                                      n.estado === 'ingresado' ? '#e3f2fd' :
                                      n.estado === 'salida' ? '#e8f5e9' : '#f5f5f5',
                                    color:
                                      n.estado === 'pendiente' ? '#e65100' :
                                      n.estado === 'ingresado' ? '#1565c0' :
                                      n.estado === 'salida' ? '#2e7d32' : '#616161',
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {n.estado || '-'}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {n.fecha_ingreso ? formatFechaHoraMexicoCentral(n.fecha_ingreso) : '-'}
                              </TableCell>
                              <TableCell>{n.guardia_ingreso_nombre || '-'}</TableCell>
                              <TableCell>
                                {n.fecha_salida ? formatFechaHoraMexicoCentral(n.fecha_salida) : '-'}
                              </TableCell>
                              <TableCell>{n.guardia_salida_nombre || '-'}</TableCell>
                              <TableCell>
                                {n.created_at ? formatFechaHoraMexicoCentral(n.created_at) : '-'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={notifTotal}
                      page={notifPagina}
                      onPageChange={(e, newPage) => loadNotificaciones({ pagina: newPage })}
                      rowsPerPage={notifRegistrosPorPagina}
                      onRowsPerPageChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        setNotifRegistrosPorPagina(n);
                        loadNotificaciones({ pagina: 0, registrosPorPagina: n });
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                      labelRowsPerPage="Filas:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                    />
                  </TableContainer>
                </CardContent>
              </Card>
            </>
          )}

          {tabActual === 10 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Registro de visitas registradas manualmente por el guardia. Filtra por estado, propiedad, mes y año.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Filtros (carga por mes)</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Mes"
                        fullWidth
                        value={filtroVisitaMes}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setFiltroVisitaMes(v);
                          visitasFiltrosRef.current.mes = v;
                        }}
                        size="small"
                      >
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                          <MenuItem key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('es', { month: 'long' })}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Año"
                        fullWidth
                        value={filtroVisitaAño}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setFiltroVisitaAño(v);
                          visitasFiltrosRef.current.año = v;
                        }}
                        size="small"
                      >
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                          <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Estado"
                        fullWidth
                        value={filtroVisitaEstado}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFiltroVisitaEstado(v);
                          visitasFiltrosRef.current.estado = v;
                        }}
                        size="small"
                      >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="autorizado">Autorizado</MenuItem>
                        <MenuItem value="denegado">Denegado</MenuItem>
                        <MenuItem value="activo">Autorizado activo (sin salida)</MenuItem>
                        <MenuItem value="finalizado">Autorizado con salida</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        label="Propiedad"
                        fullWidth
                        value={filtroVisitaPropiedad}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFiltroVisitaPropiedad(v);
                          visitasFiltrosRef.current.propiedad = v;
                        }}
                        size="small"
                        placeholder="Ej: 101"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        select
                        label="Tipo visita"
                        fullWidth
                        value={filtroVisitaSoloOficial}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFiltroVisitaSoloOficial(val);
                          visitasFiltrosRef.current.solo_oficial = val;
                        }}
                        size="small"
                      >
                        <MenuItem value="">Todas</MenuItem>
                        <MenuItem value="1">Solo oficiales</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Button
                        variant="contained"
                        onClick={() => loadVisitasManuales({ ...visitasFiltrosRef.current })}
                        fullWidth
                        sx={{ height: '40px' }}
                      >
                        Filtrar
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const now = new Date();
                          const mes = now.getMonth() + 1;
                          const año = now.getFullYear();
                          setFiltroVisitaMes(mes);
                          setFiltroVisitaAño(año);
                          setFiltroVisitaEstado('');
                          setFiltroVisitaPropiedad('');
                          setFiltroVisitaSoloOficial('');
                          visitasFiltrosRef.current = { estado: '', propiedad: '', mes, año, solo_oficial: '' };
                          loadVisitasManuales({ estado: '', propiedad: '', mes, año, solo_oficial: '' });
                        }}
                        fullWidth
                        sx={{ height: '40px' }}
                      >
                        Limpiar
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button variant="outlined" startIcon={<GetAppIcon />} onClick={exportarVisitasManualesCSV} disabled={visitasTotal === 0}>
                        Exportar CSV (mes seleccionado)
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <TableContainer component={Paper} sx={{ maxHeight: '55vh' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>ID bitácora</strong></TableCell>
                          <TableCell><strong>ID pase</strong></TableCell>
                          <TableCell><strong>Visitante</strong></TableCell>
                          <TableCell><strong>Residente</strong></TableCell>
                          <TableCell><strong>Propiedad</strong></TableCell>
                          <TableCell><strong>Resultado</strong></TableCell>
                          <TableCell><strong>Motivo denegación</strong></TableCell>
                          <TableCell><strong>Fecha ingreso</strong></TableCell>
                          <TableCell><strong>Fecha salida</strong></TableCell>
                          <TableCell><strong>Con auto</strong></TableCell>
                          <TableCell><strong>Ocupa est.</strong></TableCell>
                          <TableCell><strong>Pasajeros</strong></TableCell>
                          <TableCell><strong>Personas</strong></TableCell>
                          <TableCell><strong>Núm. cono</strong></TableCell>
                          <TableCell><strong>Color cono</strong></TableCell>
                          <TableCell><strong>Marca / Color vehículo</strong></TableCell>
                          <TableCell><strong>Placas</strong></TableCell>
                          <TableCell><strong>Guardia</strong></TableCell>
                          <TableCell><strong>Oficial</strong></TableCell>
                          <TableCell><strong>Detalle</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visitasManuales.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={20} align="center">
                              No hay visitas manuales registradas
                            </TableCell>
                          </TableRow>
                        ) : (
                          visitasManuales.map((v) => (
                            <TableRow key={v.id}>
                              <TableCell>{v.id}</TableCell>
                              <TableCell>{v.pase_id || '-'}</TableCell>
                              <TableCell>{v.visitante_nombre || '-'}</TableCell>
                              <TableCell>{v.residente_nombre || '-'}</TableCell>
                              <TableCell>{v.propiedad || v.residente_propiedad || '-'}</TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: 'inline-block',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    backgroundColor:
                                      v.resultado === 'autorizado' ? '#e8f5e9' :
                                      v.resultado === 'denegado' ? '#ffebee' : '#f5f5f5',
                                    color:
                                      v.resultado === 'autorizado' ? '#2e7d32' :
                                      v.resultado === 'denegado' ? '#c62828' : '#616161',
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {v.resultado || '-'}
                                </Box>
                              </TableCell>
                              <TableCell>{v.razon_denegacion || '-'}</TableCell>
                              <TableCell>{v.fecha_ingreso ? formatFechaHoraMexicoCentral(v.fecha_ingreso) : '-'}</TableCell>
                              <TableCell>{v.fecha_salida ? formatFechaHoraMexicoCentral(v.fecha_salida) : '-'}</TableCell>
                              <TableCell>{v.con_auto ? 'Sí' : 'No'}</TableCell>
                              <TableCell>
                                {!v.con_auto
                                  ? '—'
                                  : v.ocupa_estacionamiento === false
                                    ? 'No'
                                    : 'Sí'}
                              </TableCell>
                              <TableCell>{v.numero_pasajeros ?? '-'}</TableCell>
                              <TableCell>{v.numero_personas ?? '-'}</TableCell>
                              <TableCell>{v.numero_cono || '-'}</TableCell>
                              <TableCell>{v.color_cono || '-'}</TableCell>
                              <TableCell>
                                {v.vehiculo_marca || v.vehiculo_color
                                  ? `${v.vehiculo_marca || ''}${v.vehiculo_marca && v.vehiculo_color ? ' / ' : ''}${v.vehiculo_color || ''}`
                                  : '-'}
                              </TableCell>
                              <TableCell>{v.vehiculo_placas || '-'}</TableCell>
                              <TableCell>{v.guardia_nombre || '-'}</TableCell>
                              <TableCell>
                                {v.visita_oficial ? (
                                  <Chip size="small" label="Oficial" color="primary" variant="outlined" />
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                              <TableCell>
                                <Button size="small" variant="outlined" onClick={() => setDialogVisitaManualDetalle(v)}>
                                  Ver
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={visitasTotal}
                      page={visitasPagina}
                      onPageChange={(e, newPage) => loadVisitasManuales({ pagina: newPage })}
                      rowsPerPage={visitasRegistrosPorPagina}
                      onRowsPerPageChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        setVisitasRegistrosPorPagina(n);
                        loadVisitasManuales({ pagina: 0, registrosPorPagina: n });
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                      labelRowsPerPage="Filas:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                    />
                  </TableContainer>
                </CardContent>
              </Card>

              <Dialog
                open={dialogVisitaManualDetalle != null}
                onClose={() => setDialogVisitaManualDetalle(null)}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle>
                  Visita manual
                  {dialogVisitaManualDetalle ? ` · Bitácora #${dialogVisitaManualDetalle.id}` : ''}
                </DialogTitle>
                <DialogContent dividers>
                  {dialogVisitaManualDetalle && (
                    <>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Visitante: <strong>{dialogVisitaManualDetalle.visitante_nombre || '—'}</strong>
                        {' · '}
                        Propiedad: <strong>{dialogVisitaManualDetalle.propiedad || dialogVisitaManualDetalle.residente_propiedad || '—'}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Ingreso:{' '}
                        {dialogVisitaManualDetalle.fecha_ingreso
                          ? formatFechaHoraMexicoCentral(dialogVisitaManualDetalle.fecha_ingreso)
                          : '—'}
                        {' · '}
                        Salida:{' '}
                        {dialogVisitaManualDetalle.fecha_salida
                          ? formatFechaHoraMexicoCentral(dialogVisitaManualDetalle.fecha_salida)
                          : '—'}
                      </Typography>
                      {dialogVisitaManualDetalle.con_auto ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Tipo con auto:{' '}
                          <strong>
                            {dialogVisitaManualDetalle.ocupa_estacionamiento === false
                              ? 'Servicio (no ocupa cupo de estacionamiento)'
                              : 'Visita (ocupa cupo)'}
                          </strong>
                        </Typography>
                      ) : null}
                      {!dialogVisitaManualDetalle.visita_oficial ? (
                        <Typography variant="body2">Visita normal (no hay datos de autoridad ni documentos adjuntos).</Typography>
                      ) : (
                        <>
                          <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>
                            Visita oficial
                          </Typography>
                          {(() => {
                            const m = dialogVisitaManualDetalle.visita_oficial_meta;
                            const line = (label, key) => {
                              const val = m && typeof m === 'object' && m[key] != null && String(m[key]).trim() !== '' ? String(m[key]) : null;
                              if (!val) return null;
                              return (
                                <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>{label}:</strong> {val}
                                </Typography>
                              );
                            };
                            return (
                              <Box sx={{ mb: 2 }}>
                                {line('Tipo de autoridad', 'tipo_autoridad')}
                                {line('Institución', 'institucion')}
                                {line('Funcionario', 'funcionario_nombre')}
                                {line('Cargo', 'funcionario_cargo')}
                                {line('Tipo de documento o acto', 'tipo_documento')}
                                {line('Nº expediente / oficio', 'numero_expediente')}
                                {line('Fecha del documento', 'fecha_documento')}
                                {line('Resumen del motivo', 'resumen_motivo')}
                                {line('Acompañamiento administración/síndico', 'acompanamiento_admin')}
                                {line('Quien recibe o acompaña', 'nombre_quien_recibe')}
                              </Box>
                            );
                          })()}
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Fotos de documentos
                          </Typography>
                          {Array.isArray(dialogVisitaManualDetalle.fotos_oficiales) &&
                          dialogVisitaManualDetalle.fotos_oficiales.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                              {dialogVisitaManualDetalle.fotos_oficiales.map((u) => (
                                <Box
                                  key={u}
                                  component="img"
                                  src={getFotoFullUrl(u)}
                                  alt="Documento"
                                  sx={{
                                    maxWidth: 280,
                                    maxHeight: 360,
                                    objectFit: 'contain',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No hay fotos adjuntas.
                            </Typography>
                          )}
                        </>
                      )}
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDialogVisitaManualDetalle(null)}>Cerrar</Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {tabActual === 12 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Gestiona categorías y proveedores del directorio. Revisa reseñas pendientes de aprobación.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>

              {/* Categorías del Directorio */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h6">Categorías</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setDirectoryCategoryEditando(null);
                        setFormDirectoryCategory({ name: '', icon_name: '', active: true });
                        setShowDialogDirectoryCategory(true);
                      }}
                    >
                      Agregar Categoría
                    </Button>
                  </Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Icono</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {directoryCategories.map((cat) => (
                          <TableRow key={cat.id}>
                            <TableCell>{cat.id}</TableCell>
                            <TableCell>{cat.name}</TableCell>
                            <TableCell>{cat.icon_name || '-'}</TableCell>
                            <TableCell>{cat.active ? 'Activa' : 'Inactiva'}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDirectoryCategoryEditando(cat);
                                  setFormDirectoryCategory({ 
                                    name: cat.name, 
                                    icon_name: cat.icon_name || '', 
                                    active: cat.active 
                                  });
                                  setShowDialogDirectoryCategory(true);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleEliminarDirectoryCategory(cat.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Proveedores */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h6">Proveedores</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setDirectoryProviderEditando(null);
                        setFormDirectoryProvider({ 
                          category_id: '', name: '', phone: '', description: '', photo_url: '', is_verified: true, active: true 
                        });
                        setShowDialogDirectoryProvider(true);
                      }}
                    >
                      Agregar Proveedor
                    </Button>
                  </Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Categoría</TableCell>
                          <TableCell>Teléfono</TableCell>
                          <TableCell>Verificado</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {directoryProviders.map((prov) => (
                          <TableRow key={prov.id}>
                            <TableCell>{prov.id}</TableCell>
                            <TableCell>{prov.name}</TableCell>
                            <TableCell>{prov.category_name || '-'}</TableCell>
                            <TableCell>{prov.phone}</TableCell>
                            <TableCell>{prov.is_verified ? '✓' : '-'}</TableCell>
                            <TableCell>{prov.active ? 'Activo' : 'Inactivo'}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDirectoryProviderEditando(prov);
                                  setFormDirectoryProvider({ 
                                    category_id: prov.category_id ? prov.category_id.toString() : '', 
                                    name: prov.name, 
                                    phone: prov.phone, 
                                    description: prov.description || '', 
                                    photo_url: prov.photo_url || '', 
                                    is_verified: prov.is_verified, 
                                    active: prov.active 
                                  });
                                  setShowDialogDirectoryProvider(true);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleEliminarDirectoryProvider(prov.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Comentarios Pendientes */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Comentarios Pendientes de Moderación ({pendingReviews.length})
                  </Typography>
                  {pendingReviews.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No hay comentarios pendientes
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Proveedor</TableCell>
                            <TableCell>Vecino</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Calificación</TableCell>
                            <TableCell>Comentario</TableCell>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Acciones</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingReviews.map((review) => {
                            const getStatusBadge = (status) => {
                              switch(status) {
                                case 'pending':
                                  return <Chip label="Pendiente" color="warning" size="small" />;
                                case 'approved':
                                  return <Chip label="Aprobado" color="success" size="small" />;
                                case 'rejected':
                                  return <Chip label="Rechazado" color="error" size="small" />;
                                default:
                                  return <Chip label={status} size="small" />;
                              }
                            };
                            
                            return (
                              <TableRow key={review.id}>
                                <TableCell>{review.provider_name}</TableCell>
                                <TableCell>
                                  {review.resident_name}
                                  {review.resident_property && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      Casa {review.resident_property}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {getStatusBadge(review.status || 'pending')}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: '#FFA000', fontWeight: 'bold' }}>
                                    {'⭐'.repeat(review.rating)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                    {review.comment || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {formatFechaSoloMexicoCentralFromInstant(review.created_at)}
                                </TableCell>
                                <TableCell>
                                  {review.status === 'pending' && (
                                    <>
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleModerarReview(review.id, 'approved')}
                                        title="Aprobar"
                                        sx={{ mr: 1 }}
                                      >
                                        <CheckIcon />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                          if (window.confirm('¿Está seguro de que desea rechazar esta reseña?')) {
                                            handleModerarReview(review.id, 'rejected');
                                          }
                                        }}
                                        title="Rechazar"
                                      >
                                        <CloseIcon />
                                      </IconButton>
                                    </>
                                  )}
                                  {review.status !== 'pending' && (
                                    <Typography variant="caption" color="text.secondary">
                                      {review.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>

              {/* Diálogo para crear/editar categoría */}
              <Dialog open={showDialogDirectoryCategory} onClose={() => setShowDialogDirectoryCategory(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                  {directoryCategoryEditando ? 'Editar Categoría' : 'Nueva Categoría'}
                </DialogTitle>
                <DialogContent>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Nombre de la categoría"
                    fullWidth
                    variant="standard"
                    value={formDirectoryCategory.name}
                    onChange={(e) => setFormDirectoryCategory({ ...formDirectoryCategory, name: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    margin="dense"
                    label="Nombre del ícono (opcional)"
                    fullWidth
                    variant="standard"
                    value={formDirectoryCategory.icon_name}
                    onChange={(e) => setFormDirectoryCategory({ ...formDirectoryCategory, icon_name: e.target.value })}
                    placeholder="Ej: water-pump, wrench, etc."
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formDirectoryCategory.active}
                        onChange={(e) => setFormDirectoryCategory({ ...formDirectoryCategory, active: e.target.checked })}
                      />
                    }
                    label="Activa"
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => {
                    setShowDialogDirectoryCategory(false);
                    setDirectoryCategoryEditando(null);
                    setFormDirectoryCategory({ name: '', icon_name: '', active: true });
                  }}>
                    Cancelar
                  </Button>
                  <Button onClick={handleGuardarDirectoryCategory} variant="contained" disabled={loading}>
                    {directoryCategoryEditando ? 'Actualizar' : 'Crear'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Diálogo para crear/editar proveedor */}
              <Dialog open={showDialogDirectoryProvider} onClose={() => setShowDialogDirectoryProvider(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                  {directoryProviderEditando ? 'Editar Proveedor' : 'Nuevo Proveedor'}
                </DialogTitle>
                <DialogContent>
                  <TextField
                    select
                    margin="dense"
                    label="Categoría"
                    fullWidth
                    variant="standard"
                    value={formDirectoryProvider.category_id}
                    onChange={(e) => setFormDirectoryProvider({ ...formDirectoryProvider, category_id: e.target.value })}
                    sx={{ mb: 2 }}
                  >
                    <MenuItem value="">Sin categoría</MenuItem>
                    {directoryCategories.filter(c => c.active).map((cat) => (
                      <MenuItem key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Nombre del proveedor"
                    fullWidth
                    variant="standard"
                    value={formDirectoryProvider.name}
                    onChange={(e) => setFormDirectoryProvider({ ...formDirectoryProvider, name: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    margin="dense"
                    label="Teléfono"
                    fullWidth
                    variant="standard"
                    value={formDirectoryProvider.phone}
                    onChange={(e) => setFormDirectoryProvider({ ...formDirectoryProvider, phone: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    margin="dense"
                    label="Descripción"
                    fullWidth
                    variant="standard"
                    multiline
                    rows={3}
                    value={formDirectoryProvider.description}
                    onChange={(e) => setFormDirectoryProvider({ ...formDirectoryProvider, description: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    margin="dense"
                    label="URL de Foto (opcional)"
                    fullWidth
                    variant="standard"
                    value={formDirectoryProvider.photo_url}
                    onChange={(e) => setFormDirectoryProvider({ ...formDirectoryProvider, photo_url: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formDirectoryProvider.is_verified}
                        onChange={(e) => setFormDirectoryProvider({ ...formDirectoryProvider, is_verified: e.target.checked })}
                      />
                    }
                    label="Verificado (Recomendado por Admin)"
                    sx={{ mb: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formDirectoryProvider.active}
                        onChange={(e) => setFormDirectoryProvider({ ...formDirectoryProvider, active: e.target.checked })}
                      />
                    }
                    label="Activo"
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => {
                    setShowDialogDirectoryProvider(false);
                    setDirectoryProviderEditando(null);
                    setFormDirectoryProvider({ 
                      category_id: '', name: '', phone: '', description: '', photo_url: '', is_verified: true, active: true 
                    });
                  }}>
                    Cancelar
                  </Button>
                  <Button onClick={handleGuardarDirectoryProvider} variant="contained" disabled={loading}>
                    {directoryProviderEditando ? 'Actualizar' : 'Crear'}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {tabActual === 3 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Conceptos de pago, tabla de cobranza por propiedad y mes, y lista de morosidad con opción de enviar recordatorios.">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>
              <Tabs value={subTabCobranza} onChange={(e, v) => { setSubTabCobranza(v); if (v === 1) loadMorosidad(); }} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tab label="Tabla de cobranza" />
                <Tab label="Morosidad" />
                <Tab label="Conceptos de pago" />
              </Tabs>
              {subTabCobranza === 0 && (
                <Card>
                  <CardContent>
                    <Box display="flex" gap={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                      <TextField select SelectProps={{ native: true }} label="Año" value={anoCobranza} onChange={(e) => { setAnoCobranza(parseInt(e.target.value, 10) || new Date().getFullYear()); setTablaCobranzaPagina(0); setMesInicioVisibleCobranza(null); setMesFinVisibleCobranza(null); }} size="small" sx={{ minWidth: 100 }}>
                        {(() => { const y0 = new Date().getFullYear(); return Array.from({ length: 6 }, (_, i) => y0 + i); })().map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </TextField>
                      <TextField select SelectProps={{ native: true }} label="Concepto" value={conceptoPagoSeleccionado || (conceptosPago[0] && String(conceptosPago[0].id)) || ''} onChange={(e) => { setConceptoPagoSeleccionado(e.target.value); setTablaCobranzaPagina(0); }} size="small" sx={{ minWidth: 280 }}>
                        {conceptosPago.map((c) => {
                          const d = c.vigencia_desde ? String(c.vigencia_desde).slice(0, 10) : '';
                          const h = c.vigencia_hasta ? String(c.vigencia_hasta).slice(0, 10) : '';
                          const vig = d || h ? ` (${d || '…'} → ${h || '—'})` : '';
                          return (<option key={c.id} value={c.id}>{`${c.nombre}${vig}`}</option>);
                        })}
                      </TextField>
                      <TextField size="small" label="Casa" placeholder="Ej. 5" value={filtroPropiedadCobranza} onChange={(e) => { setFiltroPropiedadCobranza(e.target.value); setTablaCobranzaPagina(0); }} sx={{ width: 90 }} />
                      <TextField size="small" label="Desde" placeholder="1" value={filtroDesdeCobranza} onChange={(e) => { setFiltroDesdeCobranza(e.target.value); setTablaCobranzaPagina(0); }} sx={{ width: 70 }} />
                      <TextField size="small" label="Hasta" placeholder="50" value={filtroHastaCobranza} onChange={(e) => { setFiltroHastaCobranza(e.target.value); setTablaCobranzaPagina(0); }} sx={{ width: 70 }} />
                      <TextField select SelectProps={{ native: true }} label="Estatus" value={filtroEstatusCobranza} onChange={(e) => { setFiltroEstatusCobranza(e.target.value); setTablaCobranzaPagina(0); }} size="small" sx={{ minWidth: 140 }}>
                        <option value="todos">Todos</option>
                        <option value="mora">Moroso</option>
                        <option value="no_moroso">No moroso</option>
                        <option value="pendiente">Pendiente</option>
                        <option value="al_corriente">Al corriente</option>
                      </TextField>
                      <Button variant="contained" onClick={loadTablaCobranza}>Actualizar</Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>Verde</strong> pagado, <strong>amarillo</strong> pendiente, <strong>azul</strong> pago parcial (aún hay saldo), <strong>rojo</strong> mora. Celdas con saldo pendiente son clicables: verá pagado vs pendiente, recibos previos y podrá abonar hasta saldar. Cada abono genera su recibo en Ingresos.
                    </Typography>
                    {!tablaCobranzaData ? (
                      <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress /> Cargando tabla…</Box>
                    ) : (() => {
                      const totalPeriodos = tablaCobranzaData.concepto?.total_periodos ?? 12;
                      const todosMeses = tablaCobranzaData.meses || [];
                      const anoActual = new Date().getFullYear();
                      const mesActual = anoCobranza === anoActual ? new Date().getMonth() + 1 : (anoCobranza > anoActual ? 1 : 12);
                      const mesInicioDefault = Math.max(1, mesActual - 1);
                      const mesFinDefault = Math.min(12, mesActual + 3);
                      const mesInicio = mesInicioVisibleCobranza ?? mesInicioDefault;
                      const mesFin = mesFinVisibleCobranza ?? mesFinDefault;
                      const mesesVisibles = totalPeriodos === 12
                        ? todosMeses.filter(m => (m.mes ?? m.periodIndex) >= mesInicio && (m.mes ?? m.periodIndex) <= mesFin)
                        : todosMeses;
                      return (
                        <>
                          {totalPeriodos === 12 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                              <Button size="small" variant="outlined" disabled={mesInicio <= 1} onClick={() => { setMesInicioVisibleCobranza(Math.max(1, mesInicio - 1)); }}>← Más meses atrás</Button>
                              <Typography variant="body2" color="text.secondary">Meses {mesInicio}–{mesFin} de 12</Typography>
                              <Button size="small" variant="outlined" disabled={mesFin >= 12} onClick={() => { setMesFinVisibleCobranza(Math.min(12, mesFin + 1)); }}>Más meses adelante →</Button>
                            </Box>
                          )}
                          <TableContainer component={Paper} sx={{ maxHeight: '55vh', overflow: 'auto' }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold', minWidth: 90 }}>Propiedad</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Estatus</TableCell>
                                  {mesesVisibles.map((m) => (
                                    <TableCell key={m.periodoKey || `${m.year}-${m.mes}`} sx={{ fontWeight: 'bold', minWidth: 70 }} align="center">{m.etiqueta}</TableCell>
                                  ))}
                                  <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }} align="center">Varios pagos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {tablaCobranzaData.propiedades && (() => {
                                  const props = [...tablaCobranzaData.propiedades];
                                  const getNumFromPropiedad = (s) => {
                                    const m = String(s || '').match(/\d+/);
                                    return m ? parseInt(m[0], 10) : NaN;
                                  };
                                  props.sort((a, b) => {
                                    const na = getNumFromPropiedad(a), nb = getNumFromPropiedad(b);
                                    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
                                    if (!Number.isNaN(na)) return -1;
                                    if (!Number.isNaN(nb)) return 1;
                                    return String(a).localeCompare(String(b));
                                  });
                                  return props.map((prop) => {
                                    const celdasProp = tablaCobranzaData.celdas && tablaCobranzaData.celdas[prop] ? Object.values(tablaCobranzaData.celdas[prop]) : [];
                                    const esMorosa = celdasProp.some((c) => c.estado === 'mora');
                                    return (
                                      <TableRow key={prop}>
                                        <TableCell>{prop}</TableCell>
                                        <TableCell sx={{ fontWeight: 600, bgcolor: esMorosa ? 'error.light' : 'success.light' }}>
                                          {esMorosa ? 'Morosa' : 'Al corriente'}
                                        </TableCell>
                                        {mesesVisibles.map((m) => {
                                          const periodoKey = m.periodoKey || `${m.year}-${String(m.mes || m.periodIndex || 1).padStart(2, '0')}`;
                                          const key = `${prop}-${periodoKey}`;
                                          const celda = tablaCobranzaData.celdas && tablaCobranzaData.celdas[prop] && tablaCobranzaData.celdas[prop][periodoKey];
                                          const estado = celda?.estado || 'sin_cuota';
                                          const saldoNum = celda?.saldo != null ? Number(celda.saldo) : null;
                                          const tp = celda?.total_pagado != null ? Number(celda.total_pagado) : null;
                                          const te = celda?.total_esperado != null ? Number(celda.total_esperado) : null;
                                          const fmt = (n) =>
                                            Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
                                          const bg =
                                            estado === 'pagado'
                                              ? 'success.light'
                                              : estado === 'mora'
                                                ? 'error.light'
                                                : estado === 'parcial'
                                                  ? 'info.light'
                                                  : estado === 'pendiente'
                                                    ? 'warning.light'
                                                    : 'grey.100';
                                          const text =
                                            estado === 'pagado'
                                              ? 'PAGADO'
                                              : estado === 'mora'
                                                ? saldoNum != null
                                                  ? fmt(saldoNum)
                                                  : fmt(celda?.monto)
                                                : estado === 'pendiente'
                                                  ? 'Pend.'
                                                  : estado === 'parcial'
                                                    ? `${fmt(tp)}/${fmt(te)}`
                                                    : '—';
                                          const clicable =
                                            (estado === 'mora' || estado === 'pendiente' || estado === 'parcial') &&
                                            conceptoPagoSeleccionado &&
                                            tablaCobranzaData.concepto;
                                          const showCeldaTip =
                                            Boolean(celda) && ['pagado', 'mora', 'pendiente', 'parcial'].includes(estado);
                                          const tooltipTitle = showCeldaTip ? (
                                            <Box>
                                              <Typography variant="caption" display="block">
                                                Cuota: ${fmt(celda.monto_cuota ?? celda.monto)}
                                                {Number(celda.recargo_mora) > 0 ? ` · Mora: $${fmt(celda.recargo_mora)}` : ''}
                                              </Typography>
                                              {te != null ? (
                                                <Typography variant="caption" display="block">
                                                  Esperado: ${fmt(te)} · Pagado: ${fmt(tp)} · Saldo: ${fmt(saldoNum)}
                                                </Typography>
                                              ) : null}
                                              {celda.fecha_limite ? (
                                                <Typography variant="caption" display="block">
                                                  Límite: {celda.fecha_limite}
                                                </Typography>
                                              ) : null}
                                              {Array.isArray(celda.abonos) && celda.abonos.length > 0 ? (
                                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                  Abonos: {celda.abonos.length} recibo(s)
                                                </Typography>
                                              ) : null}
                                            </Box>
                                          ) : null;
                                          return (
                                            <MuiTooltip
                                              key={key}
                                              title={tooltipTitle || ''}
                                              disableHoverListener={!showCeldaTip}
                                              arrow
                                              placement="top"
                                            >
                                              <TableCell
                                                align="center"
                                                sx={{
                                                  bgcolor: bg,
                                                  fontWeight: estado === 'mora' ? 'bold' : estado === 'parcial' ? 600 : 'normal',
                                                  fontSize: estado === 'parcial' ? '0.7rem' : undefined,
                                                  cursor: clicable ? 'pointer' : 'default',
                                                  '&:hover': clicable ? { opacity: 0.85, outline: '1px solid', outlineColor: 'primary.main' } : {},
                                                }}
                                                onClick={clicable ? () => {
                                                  const hoy = new Date().toISOString().split('T')[0];
                                                  const saldoStr =
                                                    saldoNum != null && !Number.isNaN(saldoNum)
                                                      ? String(Math.round(saldoNum * 100) / 100)
                                                      : String(celda?.monto ?? '');
                                                  setPagoEditando(null);
                                                  setCobranzaResumenPeriodo({
                                                    monto_cuota: celda?.monto_cuota ?? celda?.monto,
                                                    recargo_mora: celda?.recargo_mora,
                                                    total_esperado: celda?.total_esperado,
                                                    total_pagado: celda?.total_pagado,
                                                    saldo: celda?.saldo,
                                                    fecha_limite: celda?.fecha_limite,
                                                    abonos: Array.isArray(celda?.abonos) ? [...celda.abonos] : [],
                                                  });
                                                  setFormPago({
                                                    fecha: hoy,
                                                    mes: periodoKey,
                                                    propiedad: prop,
                                                    folio: '',
                                                    concepto: tablaCobranzaData.concepto?.nombre || '',
                                                    categoria_concepto: '',
                                                    descripcion: '',
                                                    monto: saldoStr,
                                                    recargos: '',
                                                    depositado: false,
                                                    concepto_pago_id:
                                                      celda?.concepto_pago_id != null && celda.concepto_pago_id !== ''
                                                        ? String(celda.concepto_pago_id)
                                                        : conceptoPagoSeleccionado || undefined,
                                                  });
                                                  setPagoDesdeCobranza(true);
                                                  setShowDialogPago(true);
                                                } : undefined}
                                              >
                                                {text}
                                              </TableCell>
                                            </MuiTooltip>
                                          );
                                        })}
                                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                                          {conceptoPagoSeleccionado && tablaCobranzaData.concepto ? (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              color="primary"
                                              onClick={() => {
                                                const meses = tablaCobranzaData.meses || [];
                                                const first = meses[0];
                                                const last = meses[meses.length - 1];
                                                let pk0 = first ? (first.periodoKey || `${first.year}-${String(first.mes || first.periodIndex || 1).padStart(2, '0')}`) : '';
                                                let pk1 = last ? (last.periodoKey || `${last.year}-${String(last.mes || last.periodIndex || 1).padStart(2, '0')}`) : '';
                                                const cSel = conceptosPago.find((x) => String(x.id) === String(conceptoPagoSeleccionado));
                                                const vm = minMaxPeriodoMensualPorVigenciaConcepto(cSel);
                                                if (vm && pk0 && pk1) {
                                                  const r = ajustarRangoMesualAVigencia(pk0, pk1, vm.minKey, vm.maxKey);
                                                  pk0 = r.desde;
                                                  pk1 = r.hasta;
                                                }
                                                setPropiedadPagarVariosPeriodos(prop);
                                                setFormPagarVariosPeriodos({
                                                  fecha: new Date().toISOString().split('T')[0],
                                                  depositado: false,
                                                  periodo_desde: pk0,
                                                  periodo_hasta: pk1,
                                                });
                                                setShowDialogPagarVariosPeriodos(true);
                                              }}
                                            >
                                              Varios pagos
                                            </Button>
                                          ) : (
                                            '—'
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  });
                                })()}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      );
                    })()}
                    {tablaCobranzaData && (
                      <TablePagination
                        component="div"
                        count={tablaCobranzaData.total_propiedades || 0}
                        page={tablaCobranzaPagina}
                        onPageChange={(_, p) => setTablaCobranzaPagina(p)}
                        rowsPerPage={tablaCobranzaPageSize}
                        onRowsPerPageChange={(e) => { setTablaCobranzaPageSize(Number(e.target.value)); setTablaCobranzaPagina(0); }}
                        rowsPerPageOptions={[10, 25, 50]}
                        labelRowsPerPage="Filas:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                      />
                    )}
                  </CardContent>
                </Card>
              )}
              {subTabCobranza === 1 && (() => {
                const enMoraRaw = morosidadData?.en_mora || [];
                const busqueda = (morosidadFiltroBusqueda || '').trim().toLowerCase();
                const montoMin = morosidadFiltroMontoMin !== '' ? Number(morosidadFiltroMontoMin) : null;
                const gravedadMin = morosidadFiltroGravedadMin !== '' ? Math.max(0, parseInt(morosidadFiltroGravedadMin, 10)) : null;
                const filtered = enMoraRaw.filter((r) => {
                  if (busqueda) {
                    const matchProp = String(r.propiedad || '').toLowerCase().includes(busqueda);
                    const matchNom = String(r.nombre || '').toLowerCase().includes(busqueda);
                    if (!matchProp && !matchNom) return false;
                  }
                  const totalRow = (r.adeudos || []).reduce((s, a) => s + (Number(a.total) || 0), 0);
                  if (montoMin != null && totalRow < montoMin) return false;
                  const numAdeudos = (r.adeudos || []).length;
                  if (gravedadMin != null && numAdeudos < gravedadMin) return false;
                  return true;
                });
                const totalFiltered = filtered.length;
                const start = morosidadPagina * morosidadPageSize;
                const paginated = filtered.slice(start, start + morosidadPageSize);
                const formatAdeudosResumen = (adeudos) => {
                  if (!adeudos?.length) return { texto: '—', total: 0 };
                  const total = adeudos.reduce((s, a) => s + (Number(a.total) || 0), 0);
                  const nombres = [...new Set(adeudos.map((a) => a.concepto_nombre || 'Cuota'))];
                  if (nombres.length === 1 && adeudos.length > 1) {
                    return { texto: `${adeudos.length} ${nombres[0]} pendientes ($${total.toLocaleString('es-MX')})`, total };
                  }
                  return { texto: null, total };
                };
                return (
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                        <Typography variant="h6">Vista de morosidad</Typography>
                        <Button variant="contained" color="error" startIcon={<WarningAmberIcon />} onClick={handleAplicarBloqueoMorosidad}>
                          Aplicar bloqueo por morosidad
                        </Button>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                        <TextField size="small" label="Buscar por nombre o propiedad" placeholder="Ej. 5, Residente" value={morosidadFiltroBusqueda} onChange={(e) => { setMorosidadFiltroBusqueda(e.target.value); setMorosidadPagina(0); }} sx={{ minWidth: 220 }} />
                        <TextField size="small" type="number" label="Monto mín. $" placeholder="Ej. 2000" value={morosidadFiltroMontoMin} onChange={(e) => { setMorosidadFiltroMontoMin(e.target.value); setMorosidadPagina(0); }} sx={{ width: 120 }} inputProps={{ min: 0 }} />
                        <TextField size="small" type="number" label="Gravedad (mín. adeudos)" placeholder="Ej. 3" value={morosidadFiltroGravedadMin} onChange={(e) => { setMorosidadFiltroGravedadMin(e.target.value); setMorosidadPagina(0); }} sx={{ width: 160 }} inputProps={{ min: 0 }} />
                        <Button variant="outlined" size="small" onClick={loadMorosidad}>Actualizar</Button>
                      </Box>
                      {morosidadData === null ? (
                        <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress /> Cargando…</Box>
                      ) : (
                        <>
                          <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>Propiedades en mora ({totalFiltered}) — Solo cuotas vencidas y no pagadas</Typography>
                          <TableContainer component={Paper} sx={{ maxHeight: '55vh', overflow: 'auto', mb: 1 }}>
                            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 700 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ width: 90 }}>Propiedad</TableCell>
                                  <TableCell sx={{ width: 140 }}>Nombre</TableCell>
                                  <TableCell sx={{ maxWidth: 280, minWidth: 180 }}>Adeudos (vencidos no pagados)</TableCell>
                                  <TableCell align="right" sx={{ width: 120 }}>Total pendiente</TableCell>
                                  <TableCell align="center" sx={{ width: 140 }}>Acciones</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {paginated.length === 0 ? (
                                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>Ninguna propiedad en mora</TableCell></TableRow>
                                ) : (
                                  paginated.map((r) => {
                                    const adeudos = r.adeudos || [];
                                    const totalRow = adeudos.reduce((s, a) => s + (Number(a.total) || 0), 0);
                                    const resumen = formatAdeudosResumen(adeudos);
                                    const numAdeudos = adeudos.length;
                                    const rowBg = numAdeudos >= 3 ? 'rgba(211,47,47,0.08)' : numAdeudos === 1 ? 'rgba(255,193,7,0.12)' : undefined;
                                    const showPopover = adeudos.length > 2;
                                    const preview = adeudos.slice(0, 2);
                                    return (
                                      <TableRow key={r.propiedad} sx={{ backgroundColor: rowBg }}>
                                        <TableCell>{r.propiedad}</TableCell>
                                        <TableCell>{r.nombre}</TableCell>
                                        <TableCell sx={{ maxHeight: 80, overflow: 'hidden', verticalAlign: 'top' }}>
                                          {resumen.texto ? (
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>{resumen.texto}</Typography>
                                          ) : (
                                            <>
                                              {preview.map((a, i) => (
                                                <Typography key={i} variant="body2" sx={{ fontSize: '0.8rem' }}>{a.concepto_nombre} {a.periodo}: ${Number(a.total).toLocaleString('es-MX')}</Typography>
                                              ))}
                                              {showPopover && (
                                                <Chip size="small" label={`+${adeudos.length - 2} más`} onClick={(ev) => { setMorosidadPopoverAnchor(ev.currentTarget); setMorosidadPopoverRow(r); }} sx={{ mt: 0.5, cursor: 'pointer' }} />
                                              )}
                                            </>
                                          )}
                                        </TableCell>
                                        <TableCell align="right"><Typography fontWeight="700">${totalRow.toLocaleString('es-MX')}</Typography></TableCell>
                                        <TableCell align="center">
                                          <MuiTooltip title="Ver detalle">
                                            <IconButton size="small" onClick={() => setMorosidadDetalleRow(r)}><VisibilityIcon fontSize="small" /></IconButton>
                                          </MuiTooltip>
                                          <MuiTooltip title="Enviar recordatorio por correo">
                                            <IconButton size="small" disabled={morosidadEnviandoRecordatorio === r.residente_id} onClick={async () => {
                                              if (!r.residente_id) return;
                                              setMorosidadEnviandoRecordatorio(r.residente_id);
                                              try {
                                                const response = await fetchWithAuth(`${API_BASE_URL}/admin/morosidad/enviar-recordatorio`, { method: 'POST', body: JSON.stringify({ residente_id: r.residente_id }) });
                                                const data = response.ok ? await response.json() : await response.json().catch(() => ({}));
                                                if (response.ok) setSuccess(data.message || 'Recordatorio enviado');
                                                else setError(data.error || 'Error al enviar recordatorio');
                                              } catch (e) {
                                                setError(e.message === 'Sesión expirada' ? e.message : 'Error al enviar recordatorio');
                                              } finally {
                                                setMorosidadEnviandoRecordatorio(null);
                                              }
                                            }}><SendIcon fontSize="small" /></IconButton>
                                          </MuiTooltip>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <TablePagination
                            component="div"
                            count={totalFiltered}
                            page={morosidadPagina}
                            onPageChange={(_, p) => setMorosidadPagina(p)}
                            rowsPerPage={morosidadPageSize}
                            onRowsPerPageChange={(e) => { setMorosidadPageSize(Number(e.target.value)); setMorosidadPagina(0); }}
                            rowsPerPageOptions={[10, 25, 50]}
                            labelRowsPerPage="Filas:"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                          />
                          <Popover open={Boolean(morosidadPopoverAnchor)} anchorEl={morosidadPopoverAnchor} onClose={() => { setMorosidadPopoverAnchor(null); setMorosidadPopoverRow(null); }} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                            <Box sx={{ p: 2, maxWidth: 360, maxHeight: 320, overflow: 'auto' }}>
                              {morosidadPopoverRow && (morosidadPopoverRow.adeudos || []).map((a, i) => (
                                <Typography key={i} variant="body2" sx={{ py: 0.5, borderBottom: i < (morosidadPopoverRow.adeudos?.length || 0) - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                  {a.concepto_nombre} {a.periodo}: ${Number(a.total).toLocaleString('es-MX')} {a.fecha_limite ? `(venc. ${a.fecha_limite})` : ''}
                                </Typography>
                              ))}
                            </Box>
                          </Popover>
                          <Dialog open={Boolean(morosidadDetalleRow)} onClose={() => setMorosidadDetalleRow(null)} maxWidth="sm" fullWidth>
                            <DialogTitle>Detalle de adeudos — Propiedad {morosidadDetalleRow?.propiedad} · {morosidadDetalleRow?.nombre}</DialogTitle>
                            <DialogContent>
                              {morosidadDetalleRow && (morosidadDetalleRow.adeudos || []).length > 0 ? (
                                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                                  <Table size="small">
                                    <TableHead><TableRow><TableCell>Concepto</TableCell><TableCell>Periodo</TableCell><TableCell align="right">Monto</TableCell><TableCell>Vencimiento</TableCell></TableRow></TableHead>
                                    <TableBody>
                                      {(morosidadDetalleRow.adeudos || []).map((a, i) => (
                                        <TableRow key={i}><TableCell>{a.concepto_nombre}</TableCell><TableCell>{a.periodo}</TableCell><TableCell align="right">${Number(a.total).toLocaleString('es-MX')}</TableCell><TableCell>{a.fecha_limite || '—'}</TableCell></TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <Typography color="text.secondary">Sin adeudos.</Typography>
                              )}
                              {morosidadDetalleRow && (morosidadDetalleRow.adeudos || []).length > 0 && (
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Total pendiente: ${(morosidadDetalleRow.adeudos || []).reduce((s, a) => s + (Number(a.total) || 0), 0).toLocaleString('es-MX')}</Typography>
                              )}
                            </DialogContent>
                            <DialogActions><Button onClick={() => setMorosidadDetalleRow(null)}>Cerrar</Button></DialogActions>
                          </Dialog>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Propiedades al corriente ({morosidadData.total_al_corriente ?? morosidadData.al_corriente?.length ?? 0})</Typography>
                          <Typography variant="body2" color="text.secondary">Las propiedades al corriente pueden desbloquearse desde la sección Residentes.</Typography>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}
              {subTabCobranza === 2 && (
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6">Conceptos (cuota mensual, extraordinaria, etc.)</Typography>
                      <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setConceptoPagoEditando(null); setFormConceptoPago({ nombre: '', tipo: 'mensual', monto: '', recargo_morosidad_tipo: 'porcentaje', recargo_morosidad_valor: '', tipo_fecha_limite: 'dia_mes', dia_limite_pago: 10, fecha_limite: '', mes_inicio_periodo: 1, activo: true, vigencia_desde: '', vigencia_hasta: '' }); setShowDialogConceptoPago(true); }}>
                        Nuevo concepto
                      </Button>
                    </Box>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Nombre</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Monto</TableCell>
                            <TableCell>Recargo mora</TableCell>
                            <TableCell>Fecha límite</TableCell>
                            <TableCell>Vigencia</TableCell>
                            <TableCell>Aviso</TableCell>
                            <TableCell>Activo</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {conceptosPago.length === 0 ? (
                            <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}>No hay conceptos. Crea uno (ej. Cuota mensual).</TableCell></TableRow>
                          ) : (
                            conceptosPago.map((c) => (
                              <TableRow key={c.id}>
                                <TableCell>{c.nombre}</TableCell>
                                <TableCell>{c.tipo === 'mensual' ? 'Mensual' : c.tipo === 'anual' ? 'Anual' : c.tipo === 'bimestral' ? 'Bimestral' : c.tipo === 'trimestral' ? 'Trimestral' : c.tipo === 'semestral' ? 'Semestral' : (c.tipo === 'extraordinario' ? 'Anual' : c.tipo || '—')}</TableCell>
                                <TableCell>${Number(c.monto).toLocaleString('es-MX')}</TableCell>
                                <TableCell>{c.recargo_morosidad_tipo === 'porcentaje' ? `${c.recargo_morosidad_valor}%` : `$${Number(c.recargo_morosidad_valor).toLocaleString('es-MX')}`}</TableCell>
                                <TableCell>{c.tipo === 'anual' && c.tipo_fecha_limite === 'fecha_especifica' ? (c.fecha_limite || '—') : c.tipo_fecha_limite === 'dia_mes' ? `Día ${c.dia_limite_pago}${c.mes_inicio_periodo ? ` (inicio mes ${c.mes_inicio_periodo})` : ''}` : (c.fecha_limite || '—')}</TableCell>
                                <TableCell>
                                  {c.vigencia_desde || c.vigencia_hasta
                                    ? `${c.vigencia_desde ? String(c.vigencia_desde).slice(0, 10) : '—'} → ${c.vigencia_hasta ? String(c.vigencia_hasta).slice(0, 10) : 'sin fin'}`
                                    : '—'}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 220 }}>
                                  {c.alerta_vigencia ? (
                                    <Typography variant="caption" color="warning.main" component="span">{c.alerta_vigencia}</Typography>
                                  ) : '—'}
                                </TableCell>
                                <TableCell>{c.activo ? 'Sí' : 'No'}</TableCell>
                                <TableCell align="right">
                                  <IconButton size="small" onClick={() => { setConceptoPagoEditando(c); setFormConceptoPago({ nombre: c.nombre, tipo: c.tipo === 'extraordinario' ? 'anual' : c.tipo, monto: c.monto, recargo_morosidad_tipo: c.recargo_morosidad_tipo || 'porcentaje', recargo_morosidad_valor: c.recargo_morosidad_valor ?? '', tipo_fecha_limite: c.tipo === 'anual' || c.tipo === 'extraordinario' ? (c.fecha_limite ? 'fecha_especifica' : 'dia_mes') : (c.tipo_fecha_limite || 'dia_mes'), dia_limite_pago: c.dia_limite_pago ?? 10, fecha_limite: c.fecha_limite || '', mes_inicio_periodo: c.mes_inicio_periodo ?? 1, activo: c.activo !== false, vigencia_desde: c.vigencia_desde ? String(c.vigencia_desde).slice(0, 10) : '', vigencia_hasta: c.vigencia_hasta ? String(c.vigencia_hasta).slice(0, 10) : '' }); setShowDialogConceptoPago(true); }}><EditIcon /></IconButton>
                                  <IconButton size="small" onClick={() => { if (window.confirm('¿Eliminar este concepto?')) fetchWithAuth(`${API_BASE_URL}/admin/conceptos-pago/${c.id}`, { method: 'DELETE' }).then(() => { setSuccess('Concepto eliminado'); loadConceptosPago(); }).catch(() => setError('Error al eliminar')); }}><DeleteIcon /></IconButton>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}
              <Dialog open={showDialogConceptoPago} onClose={() => setShowDialogConceptoPago(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{conceptoPagoEditando ? 'Editar concepto' : 'Nuevo concepto de pago'}</DialogTitle>
                <DialogContent>
                  <TextField fullWidth label="Nombre" value={formConceptoPago.nombre} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, nombre: e.target.value })} margin="normal" placeholder="Ej. Cuota mensual" />
                  <TextField select SelectProps={{ native: true }} fullWidth label="Tipo" value={formConceptoPago.tipo} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, tipo: e.target.value })} margin="normal">
                    <option value="mensual">Mensual</option>
                    <option value="bimestral">Bimestral</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="semestral">Semestral</option>
                    <option value="anual">Anual</option>
                  </TextField>
                  <TextField fullWidth type="number" label="Monto" value={formConceptoPago.monto} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, monto: e.target.value })} margin="normal" inputProps={{ min: 0, step: 0.01 }} />
                  <TextField select SelectProps={{ native: true }} fullWidth label="Recargo por morosidad (tipo)" value={formConceptoPago.recargo_morosidad_tipo} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, recargo_morosidad_tipo: e.target.value })} margin="normal">
                    <option value="porcentaje">Porcentaje</option>
                    <option value="monto_fijo">Monto fijo</option>
                  </TextField>
                  <TextField fullWidth type="number" label="Recargo (valor)" value={formConceptoPago.recargo_morosidad_valor} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, recargo_morosidad_valor: e.target.value })} margin="normal" inputProps={{ min: 0, step: 0.01 }} />
                  {formConceptoPago.tipo === 'anual' && (
                    <TextField fullWidth type="date" label="Fecha esperada de pago (anual)" value={formConceptoPago.fecha_limite} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, fecha_limite: e.target.value })} margin="normal" InputLabelProps={{ shrink: true }} helperText="Una sola fecha por año (ej. 10 de enero)" />
                  )}
                  {formConceptoPago.tipo === 'mensual' && (
                    <TextField fullWidth type="number" label="Día del mes (1-31)" value={formConceptoPago.dia_limite_pago} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, dia_limite_pago: e.target.value })} margin="normal" inputProps={{ min: 1, max: 31 }} />
                  )}
                  {['bimestral', 'trimestral', 'semestral'].includes(formConceptoPago.tipo) && (
                    <>
                      <TextField fullWidth type="number" label="Mes de inicio del periodo (1-12)" value={formConceptoPago.mes_inicio_periodo} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, mes_inicio_periodo: e.target.value })} margin="normal" inputProps={{ min: 1, max: 12 }} />
                      <TextField fullWidth type="number" label="Día (1-28)" value={formConceptoPago.dia_limite_pago} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, dia_limite_pago: e.target.value })} margin="normal" inputProps={{ min: 1, max: 28 }} />
                    </>
                  )}
                  <TextField fullWidth type="date" label="Vigencia desde (inclusive)" value={formConceptoPago.vigencia_desde} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, vigencia_desde: e.target.value })} margin="normal" InputLabelProps={{ shrink: true }} helperText="Opcional; si lo dejas vacío el servidor puede asignar fecha por defecto." />
                  <TextField fullWidth type="date" label="Vigencia hasta (inclusive)" value={formConceptoPago.vigencia_hasta} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, vigencia_hasta: e.target.value })} margin="normal" InputLabelProps={{ shrink: true }} helperText="Vacío = sin fecha de fin. No debe solaparse con otra fila del mismo nombre y tipo en este condominio." />
                  <FormControlLabel control={<Switch checked={formConceptoPago.activo} onChange={(e) => setFormConceptoPago({ ...formConceptoPago, activo: e.target.checked })} />} label="Activo" />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowDialogConceptoPago(false)}>Cancelar</Button>
                  <Button variant="contained" onClick={handleSaveConceptoPago}>Guardar</Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {tabActual === 6 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <MuiTooltip title="Configura amenidades, horarios, bloqueos y aprueba solicitudes de reserva. Los residentes bloqueados no pueden reservar (usa el toggle en Residentes).">
                  <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
                </MuiTooltip>
              </Box>

              <Tabs value={amenidadesSeccion} onChange={(e, v) => { setAmenidadesSeccion(v); if (v === 'aprobaciones') loadReservasPendientes(); if (v === 'rechazos_cancelaciones') loadReservasRechazadasCanceladas(); if (v === 'calendario') loadCalendarioAmenidades(); if (v === 'nueva_reserva') loadPropiedades(); }} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tab label="Listado de amenidades" value="listado" />
                <Tab label="Aprobaciones" value="aprobaciones" />
                <Tab label="Rechazos y cancelaciones" value="rechazos_cancelaciones" />
                <Tab label="Calendario maestro" value="calendario" />
                <Tab label="Nueva reserva (en nombre de residente)" value="nueva_reserva" />
              </Tabs>

              {amenidadesSeccion === 'listado' && (
                <>
                  <Card sx={{ mb: 4 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="h6">Amenidades del condominio</Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModalAmenidad(null)}>
                          Nueva amenidad
                        </Button>
                      </Box>
                      {loadingAmenidades ? (
                        <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                      ) : (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Nombre</TableCell>
                                <TableCell>Modo aprobación</TableCell>
                                <TableCell>Reserva</TableCell>
                                <TableCell>Máx. reservas/residente</TableCell>
                                <TableCell>Cancelar (h antes)</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {amenidadesList.length === 0 ? (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}>No hay amenidades. Crea una arriba.</TableCell></TableRow>
                              ) : (
                                amenidadesList.map((a) => (
                                  <TableRow key={a.id}>
                                    <TableCell>{a.id}</TableCell>
                                    <TableCell>{a.nombre}</TableCell>
                                    <TableCell><Chip label={a.modo_aprobacion === 'automatica' ? 'Automática' : 'Manual'} size="small" variant="outlined" /></TableCell>
                                    <TableCell>{a.unidad_reserva === 'horas' ? `Por horas (máx. ${a.max_unidades_reserva ?? 1} h)` : `Por días (máx. ${a.max_unidades_reserva ?? 1})`}</TableCell>
                                    <TableCell>{a.max_reservas_concurrentes_por_residente}</TableCell>
                                    <TableCell>{a.cancelacion_min_horas_antelacion} h</TableCell>
                                    <TableCell><Chip label={a.activo ? 'Activa' : 'Inactiva'} size="small" color={a.activo ? 'success' : 'default'} variant="outlined" /></TableCell>
                                    <TableCell align="right">
                                      <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenModalAmenidad(a)}>Editar</Button>
                                      <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleEliminarAmenidad(a.id)} sx={{ ml: 1 }}>Eliminar</Button>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </CardContent>
                  </Card>

                  <Dialog open={openModalAmenidad} onClose={() => { setOpenModalAmenidad(false); setNuevoBloqueoAmenidad({ fecha_inicio: '', fecha_fin: '', motivo: '' }); }} maxWidth="sm" fullWidth>
                    <DialogTitle>{amenidadEditando ? 'Editar amenidad' : 'Nueva amenidad'}</DialogTitle>
                    <form onSubmit={handleSaveAmenidad}>
                      <DialogContent>
                        <TextField fullWidth label="Nombre" required value={formAmenidad.nombre} onChange={(e) => setFormAmenidad({ ...formAmenidad, nombre: e.target.value })} sx={{ mb: 2 }} />
                        <TextField fullWidth label="Descripción" multiline rows={2} value={formAmenidad.descripcion} onChange={(e) => setFormAmenidad({ ...formAmenidad, descripcion: e.target.value })} sx={{ mb: 2 }} />
                        <TextField fullWidth label="Reglamento" multiline rows={4} value={formAmenidad.reglamento} onChange={(e) => setFormAmenidad({ ...formAmenidad, reglamento: e.target.value })} placeholder="Texto que verá el residente al reservar" sx={{ mb: 2 }} />
                        <TextField fullWidth label="Imagen URL (opcional)" value={formAmenidad.imagen_url} onChange={(e) => setFormAmenidad({ ...formAmenidad, imagen_url: e.target.value })} sx={{ mb: 2 }} />
                        <TextField select fullWidth label="Modo de aprobación" value={formAmenidad.modo_aprobacion} onChange={(e) => setFormAmenidad({ ...formAmenidad, modo_aprobacion: e.target.value })} sx={{ mb: 2 }}>
                          <MenuItem value="manual">Manual (admin aprueba)</MenuItem>
                          <MenuItem value="automatica">Automática (se confirma al reservar)</MenuItem>
                        </TextField>
                        <TextField type="number" fullWidth label="Máx. reservas activas por residente" value={formAmenidad.max_reservas_concurrentes_por_residente} onChange={(e) => setFormAmenidad({ ...formAmenidad, max_reservas_concurrentes_por_residente: e.target.value })} inputProps={{ min: 1 }} sx={{ mb: 2 }} />
                        <TextField select fullWidth label="Reserva por" value={formAmenidad.unidad_reserva} onChange={(e) => setFormAmenidad({ ...formAmenidad, unidad_reserva: e.target.value })} sx={{ mb: 2 }}>
                          <MenuItem value="dia">Por día(s)</MenuItem>
                          <MenuItem value="horas">Por horas</MenuItem>
                        </TextField>
                        <TextField type="number" fullWidth label={formAmenidad.unidad_reserva === 'dia' ? 'Máx. días que puede reservar por acción' : 'Máx. horas que puede reservar por acción'} value={formAmenidad.max_unidades_reserva} onChange={(e) => setFormAmenidad({ ...formAmenidad, max_unidades_reserva: e.target.value })} inputProps={{ min: 1 }} sx={{ mb: 2 }} />
                        <TextField type="number" fullWidth label="Antelación mínima para cancelar (horas)" value={formAmenidad.cancelacion_min_horas_antelacion} onChange={(e) => setFormAmenidad({ ...formAmenidad, cancelacion_min_horas_antelacion: e.target.value })} inputProps={{ min: 0 }} sx={{ mb: 2 }} />
                        <FormControlLabel control={<Switch checked={formAmenidad.activo} onChange={(e) => setFormAmenidad({ ...formAmenidad, activo: e.target.checked })} />} label="Activa" />
                        {amenidadEditando && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Horarios (día 1=Lun … 7=Dom)</Typography>
                            {[1, 2, 3, 4, 5, 6, 7].map((dia) => {
                              const h = horariosAmenidad.find((x) => x.dia_semana === dia);
                              return (
                                <Box key={dia} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="body2" sx={{ minWidth: 60 }}>Día {dia}</Typography>
                                  <TextField type="time" size="small" value={h ? (typeof h.hora_inicio === 'string' ? h.hora_inicio.slice(0, 5) : '') : '08:00'} onChange={(e) => { const v = e.target.value; if (v) handleSaveHorarioAmenidad(amenidadEditando.id, dia, v, (h && h.hora_fin) ? (typeof h.hora_fin === 'string' ? h.hora_fin.slice(0, 5) : '17:00') : '17:00'); }} InputLabelProps={{ shrink: true }} sx={{ width: 120 }} />
                                  <Typography variant="body2">a</Typography>
                                  <TextField type="time" size="small" value={h ? (typeof h.hora_fin === 'string' ? h.hora_fin.slice(0, 5) : '') : '17:00'} onChange={(e) => { const v = e.target.value; if (v) handleSaveHorarioAmenidad(amenidadEditando.id, dia, (h && h.hora_inicio) ? (typeof h.hora_inicio === 'string' ? h.hora_inicio.slice(0, 5) : '08:00') : '08:00', v); }} InputLabelProps={{ shrink: true }} sx={{ width: 120 }} />
                                </Box>
                              );
                            })}
                            <Typography variant="subtitle2" sx={{ mt: 2 }}>Bloqueos (mantenimiento)</Typography>
                            {bloqueosAmenidad.map((b) => (
                              <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="body2">{formatFechaHoraMexicoCentral(b.fecha_inicio)} — {formatFechaHoraMexicoCentral(b.fecha_fin)}</Typography>
                                <Typography variant="caption" color="text.secondary">{b.motivo || '-'}</Typography>
                                <IconButton size="small" color="error" onClick={() => handleEliminarBloqueoAmenidad(amenidadEditando.id, b.id)}><DeleteIcon /></IconButton>
                              </Box>
                            ))}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                              <TextField type="date" size="small" label="Desde" value={nuevoBloqueoAmenidad.fecha_inicio} onChange={(e) => setNuevoBloqueoAmenidad({ ...nuevoBloqueoAmenidad, fecha_inicio: e.target.value })} InputLabelProps={{ shrink: true }} />
                              <TextField type="date" size="small" label="Hasta" value={nuevoBloqueoAmenidad.fecha_fin} onChange={(e) => setNuevoBloqueoAmenidad({ ...nuevoBloqueoAmenidad, fecha_fin: e.target.value })} InputLabelProps={{ shrink: true }} />
                              <TextField size="small" label="Motivo" placeholder="Mantenimiento" value={nuevoBloqueoAmenidad.motivo} onChange={(e) => setNuevoBloqueoAmenidad({ ...nuevoBloqueoAmenidad, motivo: e.target.value })} sx={{ minWidth: 140 }} />
                              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => handleAgregarBloqueoAmenidad(amenidadEditando.id)}>Agregar bloqueo</Button>
                            </Box>
                          </Box>
                        )}
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={() => { setOpenModalAmenidad(false); setNuevoBloqueoAmenidad({ fecha_inicio: '', fecha_fin: '', motivo: '' }); }}>Cancelar</Button>
                        <Button type="submit" variant="contained" disabled={loading}>{loading ? <CircularProgress size={24} /> : 'Guardar'}</Button>
                      </DialogActions>
                    </form>
                  </Dialog>
                </>
              )}

              {amenidadesSeccion === 'aprobaciones' && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Solicitudes pendientes de aprobación</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                      <TextField select size="small" label="Amenidad" value={filtroPendientesAmenidadId} onChange={(e) => setFiltroPendientesAmenidadId(e.target.value)} sx={{ minWidth: 200 }}>
                        <MenuItem value="">Todas</MenuItem>
                        {amenidadesList.map((a) => (<MenuItem key={a.id} value={String(a.id)}>{a.nombre}</MenuItem>))}
                      </TextField>
                      <TextField type="date" size="small" label="Desde" value={filtroPendientesFechaDesde} onChange={(e) => setFiltroPendientesFechaDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
                      <TextField type="date" size="small" label="Hasta" value={filtroPendientesFechaHasta} onChange={(e) => setFiltroPendientesFechaHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
                      <Button variant="contained" onClick={loadReservasPendientes}>Buscar</Button>
                    </Box>
                    {loadingReservasPendientes ? <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box> : (
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Residente</TableCell>
                              <TableCell>Amenidad</TableCell>
                              <TableCell>Fecha / Hora</TableCell>
                              <TableCell>Solicitado</TableCell>
                              <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reservasPendientes.length === 0 ? (
                              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>No hay solicitudes pendientes. Haz clic en Buscar para cargar.</TableCell></TableRow>
                            ) : (
                              reservasPendientes.map((r) => (
                                <TableRow key={r.id}>
                                  <TableCell>{r.residente_nombre}<br /><Typography variant="caption" color="text.secondary">{r.residente_email}</Typography></TableCell>
                                  <TableCell>{r.amenidad_nombre}</TableCell>
                                  <TableCell>{r.fecha_reserva} {r.hora_inicio} - {r.hora_fin}</TableCell>
                                  <TableCell>{r.created_at ? formatFechaHoraMexicoCentral(r.created_at) : '-'}</TableCell>
                                  <TableCell align="right">
                                    <Button size="small" color="success" startIcon={<CheckIcon />} onClick={() => handleAprobarReserva(r.id)}>Aprobar</Button>
                                    <Button size="small" color="error" startIcon={<CloseIcon />} onClick={() => { setReservaRechazarId(r.id); setMotivoRechazo(''); setOpenModalRechazar(true); }}>Rechazar</Button>
                                    <Button size="small" color="warning" startIcon={<CancelIcon />} onClick={() => handleCancelarReservaAdmin(r.id)} disabled={loading} sx={{ ml: 1 }}>Cancelar</Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              )}

              {amenidadesSeccion === 'rechazos_cancelaciones' && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Rechazos y cancelaciones</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                      <TextField
                        select
                        size="small"
                        label="Amenidad"
                        value={filtroIncidenciasAmenidadId}
                        onChange={(e) => setFiltroIncidenciasAmenidadId(e.target.value)}
                        sx={{ minWidth: 200 }}
                      >
                        <MenuItem value="">Todas</MenuItem>
                        {amenidadesList.map((a) => (
                          <MenuItem key={a.id} value={String(a.id)}>{a.nombre}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        type="date"
                        size="small"
                        label="Desde"
                        value={filtroIncidenciasFechaDesde}
                        onChange={(e) => setFiltroIncidenciasFechaDesde(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        type="date"
                        size="small"
                        label="Hasta"
                        value={filtroIncidenciasFechaHasta}
                        onChange={(e) => setFiltroIncidenciasFechaHasta(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <Button variant="contained" onClick={loadReservasRechazadasCanceladas}>Buscar</Button>
                    </Box>
                    {loadingReservasRechazadasCanceladas ? (
                      <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Residente</TableCell>
                              <TableCell>Amenidad</TableCell>
                              <TableCell>Fecha / Hora</TableCell>
                              <TableCell>Estado</TableCell>
                              <TableCell>Detalle</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reservasRechazadasCanceladas.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                  No hay rechazos ni cancelaciones. Haz clic en Buscar para cargar.
                                </TableCell>
                              </TableRow>
                            ) : (
                              reservasRechazadasCanceladas.map((r) => (
                                <TableRow key={r.id}>
                                  <TableCell>
                                    {r.residente_nombre || '-'}
                                    <br />
                                    <Typography variant="caption" color="text.secondary">
                                      {r.residente_email || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{r.amenidad_nombre || '-'}</TableCell>
                                  <TableCell>
                                    {r.fecha_reserva} {r.hora_inicio} - {r.hora_fin}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={r.estado === 'rechazada' ? 'Rechazada' : 'Cancelada'}
                                      size="small"
                                      variant="outlined"
                                      color={r.estado === 'rechazada' ? 'error' : 'success'}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {r.estado === 'rechazada' ? (
                                      <Typography variant="body2">
                                        Motivo: {r.motivo_rechazo || '-'}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2">
                                        Cancelada el: {r.cancelado_at ? formatFechaHoraMexicoCentral(r.cancelado_at) : '-'}
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              )}

              {amenidadesSeccion === 'calendario' && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Calendario maestro (ocupación por amenidad)</Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                      <TextField type="date" size="small" label="Desde" value={calendarioFechaDesde} onChange={(e) => setCalendarioFechaDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
                      <TextField type="date" size="small" label="Hasta" value={calendarioFechaHasta} onChange={(e) => setCalendarioFechaHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
                      <Button variant="contained" onClick={loadCalendarioAmenidades}>Cargar</Button>
                    </Box>
                    {loadingCalendario ? <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box> : (
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Amenidad</TableCell>
                              <TableCell>Fecha</TableCell>
                              <TableCell>Hora</TableCell>
                              <TableCell>Residente</TableCell>
                              <TableCell>Estado</TableCell>
                              <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {calendarioAmenidades.reservas.length === 0 && calendarioAmenidades.bloqueos.length === 0 ? (
                              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>Sin datos. Elige rango y Cargar.</TableCell></TableRow>
                            ) : (
                              <>
                                {calendarioAmenidades.reservas.map((r) => (
                                  <TableRow key={`r-${r.id}`}>
                                    <TableCell>{r.amenidad_nombre}</TableCell>
                                    <TableCell>{r.fecha_reserva}</TableCell>
                                    <TableCell>{r.hora_inicio} - {r.hora_fin}</TableCell>
                                    <TableCell>{r.residente_nombre || '-'}</TableCell>
                                    <TableCell><Chip label={r.estado} size="small" variant="outlined" /></TableCell>
                                    <TableCell align="right">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<CancelIcon />}
                                        onClick={() => handleCancelarReservaAdmin(r.id)}
                                        disabled={loading}
                                        sx={{ whiteSpace: 'nowrap' }}
                                      >
                                        Cancelar
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {calendarioAmenidades.bloqueos.map((b) => (
                                  <TableRow key={`b-${b.id}`} sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell>{b.amenidad_nombre}</TableCell>
                                    <TableCell colSpan={2}>{formatFechaHoraMexicoCentral(b.fecha_inicio)} — {formatFechaHoraMexicoCentral(b.fecha_fin)}</TableCell>
                                    <TableCell colSpan={3}>Bloqueo: {b.motivo || 'Mantenimiento'}</TableCell>
                                  </TableRow>
                                ))}
                              </>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              )}

              {amenidadesSeccion === 'nueva_reserva' && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Reservar amenidad en nombre de un residente</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Selecciona la casa (propiedad), la amenidad y la fecha (y horario si la amenidad es por horas). La reserva se creará a nombre del residente de esa casa.
                    </Typography>
                    <Grid container spacing={2} sx={{ maxWidth: 560 }}>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={[...listadoPropiedades].sort(compareNatural)}
                          getOptionLabel={(opt) => String(opt ?? '')}
                          value={formReservaAdmin.propiedad || null}
                          onChange={(e, newValue) => setFormReservaAdmin({ ...formReservaAdmin, propiedad: newValue ?? '' })}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Casa"
                              placeholder="Ej: 1, 2, 10, 1-A..."
                              size="small"
                            />
                          )}
                          isOptionEqualToValue={(opt, val) => (opt ?? '') === (val ?? '')}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={[...amenidadesList.filter((a) => a.activo)].sort((a, b) => compareNatural(a.nombre, b.nombre))}
                          getOptionLabel={(opt) => opt ? `${opt.nombre} (${opt.unidad_reserva === 'horas' ? 'por horas' : 'por días'})` : ''}
                          value={amenidadesList.find((a) => String(a.id) === String(formReservaAdmin.amenidad_id)) || null}
                          onChange={(e, newValue) => setFormReservaAdmin({ ...formReservaAdmin, amenidad_id: newValue ? newValue.id : '' })}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Amenidad"
                              placeholder="Escribe para filtrar..."
                              size="small"
                            />
                          )}
                          isOptionEqualToValue={(opt, val) => opt && val && String(opt.id) === String(val.id)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          type="date"
                          fullWidth
                          size="small"
                          label="Fecha"
                          value={formReservaAdmin.fecha_reserva}
                          onChange={(e) => setFormReservaAdmin({ ...formReservaAdmin, fecha_reserva: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      {formReservaAdmin.amenidad_id && amenidadesList.find((a) => String(a.id) === String(formReservaAdmin.amenidad_id))?.unidad_reserva === 'horas' && (
                        <>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              type="time"
                              fullWidth
                              size="small"
                              label="Hora inicio"
                              value={formReservaAdmin.hora_inicio}
                              onChange={(e) => setFormReservaAdmin({ ...formReservaAdmin, hora_inicio: e.target.value })}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              type="time"
                              fullWidth
                              size="small"
                              label="Hora fin"
                              value={formReservaAdmin.hora_fin}
                              onChange={(e) => setFormReservaAdmin({ ...formReservaAdmin, hora_fin: e.target.value })}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          onClick={handleCrearReservaAdmin}
                          disabled={creandoReservaAdmin || !formReservaAdmin.propiedad || !formReservaAdmin.amenidad_id}
                        >
                          {creandoReservaAdmin ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
                          {creandoReservaAdmin ? 'Creando…' : 'Crear reserva'}
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              <Dialog open={openModalRechazar} onClose={() => { setOpenModalRechazar(false); setReservaRechazarId(null); setMotivoRechazo(''); }} maxWidth="sm" fullWidth>
                <DialogTitle>Rechazar reserva</DialogTitle>
                <form onSubmit={handleRechazarReserva}>
                  <DialogContent>
                    <TextField fullWidth label="Motivo del rechazo (se envía al residente)" multiline rows={3} value={motivoRechazo} onChange={(e) => setMotivoRechazo(e.target.value)} placeholder="Obligatorio para informar al residente" />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => { setOpenModalRechazar(false); setReservaRechazarId(null); setMotivoRechazo(''); }}>Cancelar</Button>
                    <Button type="submit" variant="contained" color="error" disabled={loading}>{loading ? <CircularProgress size={24} /> : 'Rechazar'}</Button>
                  </DialogActions>
                </form>
              </Dialog>
            </>
          )}

          {/* Dialog para mostrar errores de Excel */}
          <Dialog 
            open={showErroresDialog} 
            onClose={() => setShowErroresDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              ⚠️ Errores al Procesar Archivo Excel
              {excelResultado && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 'bold' }}>
                  Resumen: {excelResultado.exitosos} exitosos, {excelResultado.fallidos} fallidos de {excelResultado.total} registros totales
                  {excelResultado.nota && (
                    <>
                      <br />
                      {excelResultado.nota}
                    </>
                  )}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Se encontraron {excelErrores.length} error(es) al procesar el archivo. Revisa los detalles a continuación.
                </Alert>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Fila</strong></TableCell>
                        <TableCell><strong>Error</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {excelErrores.map((error, index) => (
                        <TableRow key={index}>
                          <TableCell>{error.fila}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error">
                              {error.error}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {excelErrores.length >= 50 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Se muestran los primeros 50 errores. Puede haber más errores en el archivo.
                  </Alert>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowErroresDialog(false)} variant="contained">
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
          </Box>
      </Box>
        );
      })()}
    </ThemeProvider>
  );
};

export default App;

